import { buildAnnualProgressBrowsePath, markAnnualEntryFocusClick } from '../../utils/annualProgressEntryFocus';
import { gatePresentationOpen } from '../common/PresentationScreenGate';
import { shouldOuterPageIgnoreKey } from '../../utils/canvasEditorModalKeyGuard';
import { getClassMemberDisplayNumbers } from '../../utils/classMemberNumbers';
import { useDirtySinceOpen } from '../../hooks/useDirtySinceOpen';
import AiRecordFeedbackPanel, { type AiRecordFeedbackPanelHandle } from './AiRecordFeedbackPanel';
import LearningPlanRequestModal from './LearningPlanRequestModal';
import { LearningPlanGuideView, learningPlanGuideHasPlanMarkers, learningPlanGuideIsStructured } from './LearningPlanGuideView';
import AiSparkIcon from '../common/AiSparkIcon';
import { useAiAccess } from '../../hooks/useAiAccess';
import React from 'react';
import { renumberLearningSessions } from '../../utils/learningSessionNumbering';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getClassMemberGradeLayoutsMap } from '../../services/classMembersService';
import { touchAuthActivity } from '../../utils/authIdleTimeout';
import {
  markDevPresentationOpenTransferScope,
  recordDevLocalStorageProgramCacheUse,
} from '../../utils/devNetworkTransferMonitor';
import {
  ProgramFixedContentRowUpsert,
  ProgramProgressBgColorByField,
  ProgramProgressCell,
  ProgramProgressCellUpsert,
  type ProgramScopeType,
} from '../../types/domain';
import type { AnnualTimetableNaturalKey } from '../../services/annualTimetableService';
import { buildAnnualCellKey, parseAnnualCellKey } from '../../utils/annualTimetable';
import { ProgramProgressRow } from '../../utils/programProgressRows';
import {
  CLASS_MEMBERS_CHANGED_EVENT,
  emptyGradeLayout,
  flattenGradeLayout,
  hasClassMemberLocalStorageSnapshot,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGradeLayout,
  type ClassMemberRow,
} from '../../utils/classMembersStorage';
import { displayClassMemberName } from '../../utils/classMemberNameDisplay';
import {
  firstRecordGroupId,
  inferSingleClassRecordNoteGroupId,
  matchMembersByClassRecordTagKeyword,
  membersForRecordGroup,
  resolveDefaultRecordGroupId,
  type MemberWithGroupContext,
} from '../../utils/classMemberRecordGroupUtils';
import { loadSelectedClassGroupId } from '../../utils/selectedClassGroupPreference';
import {
  buildDefaultEvalMemberTagsNote,
  parseMemoFieldStored,
  renderNoteWithMemberTags,
} from '../../utils/classRecordMemoHashtags';
import { useSealedMemoTagLabels } from '../../hooks/useSealedMemoTagLabels';
import { coalesceFixedProgramProgressLearning } from '../../utils/programCellDisplayText';
import { mergeLearningTextOwnerPatchWithSavedBaseline } from '../../utils/programLearningTextPatchMerge';
import DeleteHoldButton from '../common/DeleteHoldButton';
import UnitLessonCountDelimiterModal from '../common/UnitLessonCountDelimiterModal';
import EvaluationPlanAppliedPickerModal from '../evaluationPlan/EvaluationPlanAppliedPickerModal';
import EvaluationPlanFormModal from '../evaluationPlan/EvaluationPlanFormModal';
import EvaluationPlanDeleteHoldButton from '../evaluationPlan/EvaluationPlanDeleteHoldButton';
import AssignmentMemoLinkPickerModal from '../assignmentPoints/AssignmentMemoLinkPickerModal';
import {
  resolveLinkedAssignmentMemoNote,
  type AssignmentMemoMemberRef,
} from '../../utils/classRecordMemosAssignmentNotes';
import { getAssignmentPointsNote } from '../../services/assignmentPointsNoteService';
import type { AssignmentPointsNoteDoc } from '../../types/assignmentPointsNote';
import CrossCurricularCatalogPickerModal from './CrossCurricularCatalogPickerModal';
import CrossTopicCountFloatingModal from './CrossTopicCountFloatingModal';
import { clearAnnualProgressCrossTopicsForTerm } from '../../utils/annualProgressCrossTopicRegistry';
import { type WorkspaceEvaluationPlanAppliedSnapshot } from '../../services/workspaceEvaluationPlanAppliedService';
import { resolveWorkspaceEvaluationPlanApplied } from '../../utils/workspaceEvaluationPlanAppliedCache';
import { DEFAULT_LEVEL_STEP_COUNT, formatPlanSummary } from '../../types/evaluationPlan';
import { computeLessonCountAssignments } from '../../utils/learningContentUnitGroupKey';
import {
  loadLessonCountUnitDelimiter,
  saveLessonCountUnitDelimiter,
} from '../../utils/lessonCountUnitDelimiterStorage';
import ClassRecordMemoNoteEditor, { type ClassRecordMemoNoteEditorHandle } from './ClassRecordMemoNoteEditor';
import {
  ProgramProgressLearningTextFitSpan,
} from './ProgramProgressLearningCellFit';
import RecordMemberGroupSelect from './RecordMemberGroupSelect';
import ProgramDataCleanupModal from './ProgramDataCleanupModal';
import ProgramCellBgColorModal from './ProgramCellBgColorModal';
import { useProgramCellBgPalette } from '../../hooks/useProgramCellBgPalette';
import { programCellBgRenderProps, programCellBgSwatchProps } from '../../utils/programCellBgStyle';
import ProgramPresentationListModal from './ProgramPresentationListModal';
import ProgramWorksheetListModal from './ProgramWorksheetListModal';
import ProgramProjectTimetableRenameModal from './ProgramProjectTimetableRenameModal';
import LearningContentWorksheetEditorModal from '../learningWorksheet/LearningContentWorksheetEditorModal';
import { requestAiLearningMaterial, type AiLearningMaterialContext } from '../../utils/aiLearningMaterial';
import {
  buildPresentationLayoutsFromAi,
  buildWorksheetDocumentFromAiV2,
  worksheetV2FromLegacyPages,
  type WorksheetBuildResult,
} from '../../utils/aiLearningMaterialCanvas';
import type { ProgramSlideLayoutV1 } from '../../types/programContentSlideCanvas';
import {
  computeLearningPlanHoverTooltipStyle,
  computeProgramProgressHoverTooltipStyle,
  computeProgramTableFloatingPopupStyle,
  getProgramProgressOverlayWidth,
  layoutWidthFromClientRect,
  PROGRAM_EVAL_DROPDOWN_WIDTH_PX,
  LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS,
  PROGRAM_TABLE_CELL_HOVER_TOOLTIP_DELAY_MS,
  resolveProgramTableFloatingHostRect,
  type ProgramProgressOverlayWidthKind,
} from '../../utils/programProgressHoverTooltip';
import { emitProgramProgressSaved } from '../../utils/programProgressSavedEvents';
import { isUnregisteredTimetableText } from '../../utils/trackWildcard';
import {
  applyCrossScopeCutClearToWorkspace,
  getProgramWorkspaceScopeEntry,
  loadProgramWorkspaceClipboard,
  PROGRAM_WORKSPACE_CHANGED_EVENT,
  saveProgramWorkspaceClipboard,
  type ProgramWorkspaceRowsChangeMeta,
} from '../../utils/programEditWorkspace';
import { saveProgramWorkspaceScopes } from '../../utils/programUnifiedSave';
import {
  collectDirtyWorkspaceScopeIdsForAttachmentSave,
  ProgramTableCoSaveCancelledError,
  programTableDirtyWithAttachmentSaveConfirmMessage,
} from '../../utils/programAttachmentCoSaveWorkspace';
import { GradeTrackLeaveContext } from '../../contexts/GradeTrackLeaveContext';
import { usePaidPassGate } from '../../contexts/PaidPassGateContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { useProgramSidebar } from '../../contexts/ProgramSidebarContext';
import {
  finishClassPresentationHandoffOnOpener,
  resolveClassPresentationSlideOpenOptions,
} from '../../utils/classPresentationTutorialHandoff';
import {
  isProgramProgressFamilyLeaveDirty,
  isProgramProgressFamilyRoute,
} from '../../utils/programRoutes';
import { isAppBrowseNavigationShortcutBlockedRoute, shouldIgnoreAppNavigationShortcut } from '../../utils/appNavigationShortcutGuard';
import { isElementVisibleForBrowseScrollFocus } from '../../utils/keepAliveFocusRelease';
import type { ClassScheduleRow } from '../../services/sectionLayoutService';
import { toISODate } from '../../utils/date';
import { pickDateOrNearestFutureAnnualCellKey, pickTodayLessonAnnualCellKey } from '../../utils/todayLessonAnnualKey';
import { scrollTableWrapForVerticalKeyboardArrow } from '../../utils/keyboardVerticalTableScroll';
import {
  parseMemoFieldStorageDeep,
  serializeClassRecordCellStorage,
  composeEvaluationMemoDisplayNote,
  resolveEvaluationPlanMemoAutoLines,
  getUnitTextAndNoteForDisplay,
  formatLearningTextForDisplay,
} from '../../utils/programCellDisplayText';
import { mapProgressLikeToDisplayFields, type ProgramProgressLikeRow } from '../../utils/annualProgressFieldDisplay';
import { upsertAnnualProgramDisplayOverlayForScope } from '../../utils/annualProgramDisplayOverlayStorage';
import {
  clearProgramContentSlideLearningUpdatedFlag,
  closeProgramContentSlideTabPlaceholder,
  notifyProgramContentSlideLearningUpdated,
  openProgramContentSlideFailureMessage,
  openProgramContentSlideInNewTab,
  openProgramContentSlideTabPlaceholder,
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT,
  PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY,
  PROGRAM_CONTENT_SLIDE_OWNER_REMAPPED_EVENT,
  remapProgramContentSlideStashOwnersAfterFixedReorder,
} from '../../utils/programContentSlideStorage';
import {
  getPresentationOverlayAnnualCellKeys,
  isProgramSlideEditScopeLocked,
  pruneStaleProgramSlideEditScopeLock,
  programSlideEditScopeLockStorageKeyPrefix,
  programSlidePresentationOverlayStorageKeyPrefix,
} from '../../utils/programSlideEditScopeLock';
import {
  isPersistableUuid,
  resolvePresentationRefIdFromLearningText,
  resolvePresentationRefIdFromLearningContent,
  resolveProgramContentSlideOwnerCandidates,
  resolveProgramContentSlideOwner,
  resolveProgramOpenSlideDbOwner,
} from '../../utils/programContentSlideOwner';
import type { ProgramContentSlideOwnerRef } from '../../types/programContentSlideCanvas';
import {
  copyProgramContentSlidePagesBetweenOwners,
  deleteProgramContentSlidePagesForOwner,
  fetchProgramContentSlidePresenceKeySet,
  hasProgramContentSlideDraftForOwner,
  invalidateProgramContentSlidePresenceCache,
  mergePresentationRefsIntoTarget,
  mergeProgramContentSlidePagesIntoOwner,
  PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT,
  PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX,
  programContentSlideOwnerHasMeaningfulExtraPageDraft,
  programContentSlidePresenceKey,
  queueProgramContentSlideCopyDraft,
  queueProgramContentSlideCloneIntent,
  queueProgramContentSlideDeleteDraft,
  queueProgramContentSlideMoveDrafts,
  resolveSlideTabOwnerForSave,
} from '../../services/programContentSlideService';
import { resolveWeeklyLearningGuideTrackBackgroundHex } from '../../utils/weeklyLearningGuideTrackColorResolve';
import { useProgramPresentationAuditQuery } from '../../query/hooks/useProgramPresentationAuditQuery';
import { invalidateProgramPresentationAuditQuery } from '../../query/cacheUpdates';
import { programPresentationAuditScopeSignature } from '../../utils/presentationAuditSnapshotCache';
import {
  deletePresentationAuditSelections,
  parsePresentationAuditSelectionKey,
  type PresentationAuditEntry,
} from '../../services/programPresentationAuditService';
import {
  filterPresentationOrphansToPageScope,
  mergeOrphanPresentationListItems,
  resolvePresentationListSlideOwner,
  sortPresentationListItems,
  type ProgramPresentationListItem,
} from '../../utils/programPresentationList';
import { readProgramPresentationListStatsCache, removeProgramPresentationListStatsCacheKeys } from '../../utils/programPresentationListStatsCache';
import {
  computeProgramPresentationIndicatorVisible,
  computeProgramWorksheetIndicatorVisible,
} from '../../utils/annualProgressLearningIndicators';
import {
  LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT,
  clearLearningAttachmentIndicatorDeleted,
  readLearningAttachmentIndicatorDeletedHints,
  markLearningAttachmentIndicatorDeletedForSyncKeys,
  clearLearningAttachmentIndicatorWorkspace,
  removeLearningAttachmentIndicatorEntry,
} from '../../utils/learningAttachmentIndicatorStorage';
import { resolveProgramTableIndicatorDraftLearningText, resolveProgramCanonicalLearningTextRaw } from '../../utils/learningAttachmentIndicatorResolve';
import { cellLearningTextForEditor, buildDraftLearningTextFromCell, readProgramLearningContentFields, resolveLearningAttachmentRefsFromSources, serializeProgramLearningContentFieldsForEditor, parseProgramLearningContentFieldsFromEditor, resolveDraftLearningTextAfterBaselineArrival, writeProgramLearningContentFields } from '../../utils/programLearningContentFields';
import {
  resolveSyncMirrorLearningTextForIndicators,
  resolveSyncMirrorSlideOwnerCandidatesForIndicator,
  resolveSyncMirrorWorksheetRefIdForIndicator,
  resolveWorksheetDraftStorageRowKey,
} from '../../utils/programSyncMirrorIndicators';
import { resolveWorksheetRefIdFromLearningText, resolveWorksheetRefIdFromLearningContent, resolveProgramWorksheetRefIdForIndicator } from '../../utils/programContentWorksheetOwner';
import {
  buildWorksheetListItemFromAnnualRow,
  sortWorksheetListItems,
  type ProgramWorksheetListItem,
} from '../../utils/programWorksheetList';
import {
  clearLearningContentWorksheetDraft,
  LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT,
  loadLearningContentWorksheetDraft,
  saveLearningContentWorksheetDraft,
} from '../../utils/learningContentWorksheetCanvasDraftStorage';
import {
  discardPendingLearningWorksheetFlush,
  discardPendingLearningWorksheetFlushForAnnualKey,
  flushPendingLearningWorksheetDrafts,
  flushPendingLearningWorksheetDraftForRef,
  hasPendingLearningWorksheetFlush,
  hasPendingLearningWorksheetFlushForAnnualKeys,
  queueLearningWorksheetPendingFlush,
  resolvePendingWorksheetRefIdForAnnualKeys,
  resolveWorksheetDocumentForEditor,
  LEARNING_WORKSHEET_PENDING_CHANGED_EVENT,
} from '../../utils/learningContentWorksheetPendingStorage';
import {
  type LearningContentWorksheetDocumentV1,
} from '../../types/learningContentWorksheetCanvas';
import {
  createWorksheetRefOnSave,
  copyWorksheetBetweenOwners,
  deleteProgramContentWorksheet,
  fetchProgramContentWorksheetPresence,
  learningTextWithWorksheetRef,
  type ProgramWorksheetLearningTextPatch,
} from '../../services/programContentWorksheetService';

/** 학습내용 미니 모달: 프레젠테이션·활동지가 있으면 초록 체크, 없으면 빈 원 */
function LearningMaterialStatusMark({ done }: { done: boolean }) {
  return (
    <span
      className={'cp-learning-material-status' + (done ? ' is-done' : '')}
      role="img"
      aria-label={done ? '만들어짐' : '없음'}
    >
      {done ? (
        <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden focusable="false">
          <circle cx="5" cy="5" r="5" className="cp-learning-material-status-fill" />
          <path d="M2.8 5.1 4.4 6.6 7.3 3.6" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : null}
    </span>
  );
}

function isProgramTableTextControlTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  return !!target.closest('input, textarea, [contenteditable="true"]');
}

const PROGRAM_PROGRESS_TABLE_NOMINAL_ROW_PX = 32;

function escapeRegExpForFixedLearning(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function areStringSetsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  let isEqual = true;
  a.forEach((value) => {
    if (!b.has(value)) isEqual = false;
  });
  return isEqual;
}

/**
 * 메모 행사명에 `-`가 포함(예: `4-6학년 운동회`)이면 `^◆[^-]+-` 파싱이 첫 하이픈에서 잘린다.
 * 일정에서 알려진 행사명으로 만든 접두 `◆{행사}-`를 반복 제거한 뒤, 잔여 잘못된 단일 ◆…- 접두만 정리한다.
 */
function stripConflictingFixedLearningDiamondPrefixes(raw: string, scheduleEventNameRaw: string): string {
  const ev = (scheduleEventNameRaw ?? '').trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
  let out = (raw ?? '').trim();
  if (ev) {
    const re = new RegExp(`^◆\\s*${escapeRegExpForFixedLearning(ev)}\\s*-`);
    while (re.test(out)) out = out.replace(re, '');
  }
  return out.replace(/^◆[^-]*-?/, '');
}

/**
 * 부모(ProgramFixedPage 등)가 워크스페이스 객체를 읽을 때마다 `rows` 배열 참조가 바뀌어도,
 * 아래 지문이 같으면 draft 기준선이 동일하다고 본다. 그렇지 않으면 탭 포커스·툴바 갱신만으로
 * draft 전량 재동기화 effect가 매 렌더 돌아 편집·단원 표시가 새로고침과 달라질 수 있다.
 */
function fingerprintProgramRowsForDraftSync(rows: ProgramProgressRow[]): string {
  return rows
    .map((r) =>
      [
        r.annualCellKey,
        r.fixedContentId ?? '',
        String(r.fixedOrderIndex ?? ''),
        r.fixedSourceAnnualCellKey ?? '',
        r.fixedEventName ?? '',
        r.memo ?? '',
        r.timetableText ?? '',
        r.templateLabel ?? '',
        r.programKindLabel ?? '',
        String(r.isScheduleSlotOverflow ?? ''),
        r.isScheduleAvailable === false ? '0' : '1',
      ].join('\u001f')
    )
    .join('\u001e');
}

function fingerprintProgramCellsForDraftSync(cells: Record<string, ProgramProgressCell>): string {
  return Object.keys(cells)
    .sort()
    .map((k) => {
      const c = cells[k];
      if (!c) return `${k}|`;
      return [
        k,
        c.id ?? '',
        c.learning_text ?? '',
        c.learning_note ?? '',
        c.presentation_ref_id ?? '',
        c.worksheet_ref_id ?? '',
        c.unit_text ?? '',
        c.page_text ?? '',
        c.timetable_text ?? '',
        c.assistant_page_text ?? '',
        c.current_period_text ?? '',
        c.total_period_text ?? '',
        c.material_text ?? '',
        c.record_cross_curricular_text ?? '',
        c.record_memo_text ?? '',
        c.record_evaluation_text ?? '',
        c.record_class_text ?? '',
      ].join('\u001f');
    })
    .join('\u001e');
}

/** 엑셀 등 TSV·다중 행 붙여넣기: 탭 또는 둘 이상의 비어 있지 않은 줄 */
function shouldHandleExternalGridPaste(text: string): boolean {
  if (text.includes('\t')) return true;
  const lines = text.split(/\r\n|\n|\r/).filter((l) => l.trim());
  return lines.length >= 2;
}

/** 클립보드 plain 텍스트 → 행별 셀 문자열 (빈 줄로 끝나는 경우 제거) */
function parseExternalGridPasteLines(raw: string): string[][] {
  const text = raw.replace(/^\uFEFF/, '');
  const lines = text.split(/\r\n|\n|\r/);
  const rows = lines.map((line) => line.split('\t').map((c) => c.replace(/\r$/, '')));
  while (rows.length && rows[rows.length - 1].every((cell) => !(cell ?? '').trim())) {
    rows.pop();
  }
  return rows;
}

/** 시스템 클립보드 readText와 앱에서 쓴 TSV를 동일 규칙으로 맞춰 비교 */
function normalizeClipboardTextForCompare(text: string): string {
  return text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/**
 * 쪽수 셀 원문에서 첫 구분 문자열 위치를 기준으로 분리.
 * 구분 기호 앞(끝 공백만 제거) → 쪽수, 구분 기호부터 문자열 끝까지(구분 기호 포함) → 보조쪽수.
 * 예: `28-29(30-32)` + 구분 기호 `(` → page `28-29`, assistant `(30-32)`
 */
function splitPageCellByDelimiter(pageCellRaw: string, delimiter: string): { page: string; assistant: string } | null {
  const d = delimiter ?? '';
  if (!d.length) return null;
  const s = String(pageCellRaw ?? '');
  const idx = s.indexOf(d);
  if (idx < 0) return null;
  const page = s.slice(0, idx).trimEnd();
  const assistant = s.slice(idx);
  return { page, assistant };
}

/** 행 인덱스 i의 `source_annual_cell_key`를 i번째 연간 일정칸에 맞춘다(넘치는 행은 null). */
function remapFixedWorkspaceRowsToScheduleSlotSources(
  rows: ProgramFixedContentRowUpsert[],
  slotAnnualCellKeys: readonly string[]
): ProgramFixedContentRowUpsert[] {
  if (!slotAnnualCellKeys.length) {
    return rows.map((row, j) => ({ ...row, order_index: j }));
  }
  const n = slotAnnualCellKeys.length;
  return rows.map((row, j) => ({
    ...row,
    order_index: j,
    source_annual_cell_key: j < n ? slotAnnualCellKeys[j] ?? null : null,
  }));
}

/** fixed_content pending 행 → 표 `annualCellKey`. 일정 초과(orphan) 행은 DB에 `source_annual_cell_key`가 null이다. */
function resolveFixedContentPendingAnnualKey(
  pending: ProgramFixedContentRowUpsert,
  tableRows: readonly ProgramProgressRow[]
): string {
  const fromSource = (pending.source_annual_cell_key ?? '').trim();
  if (fromSource) return fromSource;
  const rowId = (pending.id ?? '').trim();
  if (rowId) {
    const orphanKey = `orphan:${rowId}`;
    if (tableRows.some((row) => row.annualCellKey === orphanKey)) return orphanKey;
    const matched = tableRows.find((row) => (row.fixedContentId ?? '').trim() === rowId);
    if (matched?.annualCellKey) return matched.annualCellKey;
  }
  const orderIndex = pending.order_index;
  if (Number.isInteger(orderIndex) && orderIndex >= 0 && orderIndex < tableRows.length) {
    return tableRows[orderIndex]?.annualCellKey ?? '';
  }
  return '';
}

function fixedContentPendingMatchesAnnualKey(
  pending: ProgramFixedContentRowUpsert,
  annualKey: string,
  tableRows: readonly ProgramProgressRow[]
): boolean {
  const key = annualKey.trim();
  if (!key) return false;
  return resolveFixedContentPendingAnnualKey(pending, tableRows) === key;
}

function createPresentationRefId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (c) => {
    const n = Number(c);
    const r = Math.floor(Math.random() * 16);
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

function blankFixedContentRowForSchedulePad(
  template: ProgramFixedContentRowUpsert,
  orderIndex: number,
  sourceKey: string,
  tempId: string
): ProgramFixedContentRowUpsert {
  return {
    id: tempId,
    workspace_id: template.workspace_id,
    grade_id: template.grade_id,
    academic_year: template.academic_year,
    term_number: template.term_number,
    order_index: orderIndex,
    source_annual_cell_key: sourceKey || null,
    event_name: null,
    timetable_text: '',
    unit_text: '',
    learning_text: '',
    page_text: '',
    assistant_page_text: '',
    current_period_text: '',
    total_period_text: '',
    material_text: '',
    record_cross_curricular_text: '',
    record_memo_text: '',
    record_evaluation_text: '',
    record_class_text: '',
    bg_color_by_field: {},
  };
}

/**
 * 삭제·통합 후: 연간 일정 개수(n)만큼 앞쪽 행은 항상 유지하고,
 * 남은 내용 행을 위에서부터 순서대로 채운 뒤 부족한 칸은 빈 내용으로 패딩.
 * 내용이 n보다 많으면 뒤쪽은 source=null 오버플로.
 */
function compactOrPadWorkspaceRowsToScheduleSlotLayout(
  survivorsOrdered: ProgramFixedContentRowUpsert[],
  slotAnnualCellKeys: readonly string[],
  templateFallback: ProgramFixedContentRowUpsert | null
): ProgramFixedContentRowUpsert[] {
  const n = slotAnnualCellKeys.length;
  if (!n) {
    return survivorsOrdered.map((row, j) => ({ ...row, order_index: j }));
  }
  const payloads = [...survivorsOrdered].sort((a, b) => a.order_index - b.order_index);
  const k = payloads.length;
  const baseTemplate = payloads[0] ?? templateFallback;
  if (!baseTemplate) {
    return [];
  }
  const total = Math.max(n, k);
  const out: ProgramFixedContentRowUpsert[] = [];
  let tempSeq = 0;
  for (let j = 0; j < total; j += 1) {
    const p = payloads[j];
    if (j < n) {
      const key = slotAnnualCellKeys[j] ?? '';
      if (p) {
        out.push({
          ...p,
          order_index: j,
          source_annual_cell_key: key || null,
        });
      } else {
        out.push(
          blankFixedContentRowForSchedulePad(
            baseTemplate,
            j,
            key,
            `temp-pad-${Date.now()}-${tempSeq++}`
          )
        );
      }
    } else if (p) {
      out.push({
        ...p,
        order_index: j,
        source_annual_cell_key: null,
      });
    }
  }
  return out;
}

/** TSV 한 칸: 탭은 엑셀 호환을 위해 공백으로 치환(단순 split과 맞춤) */
function escapeCellPlainForTsv(value: string): string {
  return (value ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\t/g, ' ');
}

function joinUnitNoteLines(raw: string): string {
  return (raw ?? '')
    .split('\n')
    .map((v) => v.trim())
    .filter(Boolean)
    .join('\n');
}

function serializeUnitStorage(textRaw: string, noteRaw: string, joinNoteLines: (s: string) => string): string {
  const text = (textRaw ?? '').trim();
  const note = joinNoteLines(noteRaw ?? '');
  if (!text && !note) return '';
  if (!note) return text;
  return JSON.stringify({ text, note });
}

function serializeCrossStorage(textRaw: string, noteRaw: string): string {
  const text = (textRaw ?? '').trim();
  const note = (noteRaw ?? '').trim();
  if (!text && !note) return '';
  if (!note) return text;
  return JSON.stringify({ text, note });
}

/**
 * 서버/DB에서 읽은 단원 값 → 항상 `{ text, note }` 형태 문자열로 한 번 정규화.
 * (예전 name/criterion JSON·평문 단원명 모두 처리; 이후 앱 로직은 text/note 만 사용)
 */
function migrateUnitCellFromDbToCanonicalStored(raw: string): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (!t.startsWith('{')) {
    return serializeUnitStorage(t, '', joinUnitNoteLines);
  }
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    const text =
      (typeof o.text === 'string' ? o.text : typeof o.name === 'string' ? o.name : '')?.trim() ?? '';
    const noteRaw =
      typeof o.note === 'string' ? o.note : typeof o.criterion === 'string' ? o.criterion : '';
    return serializeUnitStorage(text, noteRaw, joinUnitNoteLines);
  } catch {
    return serializeUnitStorage(t, '', joinUnitNoteLines);
  }
}

/**
 * 서버/DB에서 읽은 범교과 값 → 항상 `{ text, note }` 형태 문자열로 한 번 정규화.
 * (예전 topic/content JSON·평문 모두 처리)
 */
function migrateCrossCellFromDbToCanonicalStored(raw: string): string {
  const t = (raw ?? '').trim();
  if (!t) return '';
  if (!t.startsWith('{')) {
    return serializeCrossStorage(t, '');
  }
  try {
    const o = JSON.parse(t) as Record<string, unknown>;
    const text =
      (typeof o.text === 'string' ? o.text : typeof o.topic === 'string' ? o.topic : '')?.trim() ?? '';
    const note =
      (typeof o.note === 'string' ? o.note : typeof o.content === 'string' ? o.content : '')?.trim() ?? '';
    return serializeCrossStorage(text, note);
  } catch {
    return serializeCrossStorage(t, '');
  }
}

/** 런타임: JSON에는 text·note 키만 해석 (저장값은 migrate 후 항상 이 형식) */
function parseUnitStorageRaw(raw: string): { text: string; note: string } {
  const t = (raw ?? '').trim();
  if (!t) return { text: '', note: '' };
  if (!t.startsWith('{')) return { text: t, note: '' };
  try {
    const o = JSON.parse(t) as { text?: unknown; note?: unknown };
    if (typeof o !== 'object' || o === null) return { text: t, note: '' };
    const text = typeof o.text === 'string' ? o.text.trim() : '';
    const note = typeof o.note === 'string' ? o.note.trim() : '';
    return { text, note };
  } catch {
    return { text: t, note: '' };
  }
}

function parseCrossStorageRaw(raw: string): { text: string; note: string } {
  const t = (raw ?? '').trim();
  if (!t) return { text: '', note: '' };
  if (!t.startsWith('{')) return { text: t, note: '' };
  try {
    const o = JSON.parse(t) as { text?: unknown; note?: unknown };
    if (typeof o !== 'object' || o === null) return { text: t, note: '' };
    const text = typeof o.text === 'string' ? o.text.trim() : '';
    const note = typeof o.note === 'string' ? o.note.trim() : '';
    return { text, note };
  } catch {
    return { text: t, note: '' };
  }
}

/**
 * 메모·단원·범교과 JSON/중첩 JSON을 쪽수·준비물 등 단순 셀에 붙일 때: 본문(text)만.
 * (note·criterion·content 는 버림)
 */
function primaryTextOnlyFromPastedStructuredValue(raw: string): string {
  const s = (raw ?? '').replace(/\r$/, '').trim();
  if (!s) return '';
  if (!s.startsWith('{')) return s;
  try {
    const o = JSON.parse(s) as Record<string, unknown>;
    const structured =
      typeof o.text === 'string' ||
      typeof o.note === 'string' ||
      typeof o.name === 'string' ||
      typeof o.criterion === 'string' ||
      typeof o.topic === 'string' ||
      typeof o.content === 'string';
    if (!structured) return s;
    let text = s;
    for (let i = 0; i < 5; i += 1) {
      const c = text.trim();
      if (!c.startsWith('{')) break;
      try {
        const p = JSON.parse(c) as { text?: unknown; note?: unknown };
        text = typeof p.text === 'string' ? p.text : '';
      } catch {
        break;
      }
    }
    const unwrapped = text.trim();
    if (unwrapped) return unwrapped;
    if (typeof o.text === 'string' && o.text.trim()) return o.text.trim();
    if (typeof o.name === 'string' && o.name.trim()) return o.name.trim();
    if (typeof o.topic === 'string' && o.topic.trim()) return o.topic.trim();
    return '';
  } catch {
    return s;
  }
}

/** 부모 툴바(학년도 옆 되돌리기/다시 실행)에서 표의 로컬 실행 취소와 동일 동작을 호출할 때 사용 */
export type ProgramProgressUndoRedoApi = {
  undo: () => boolean;
  redo: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
};

interface ProgramProgressTableProps {
  rows: ProgramProgressRow[];
  mode?: 'default' | 'fixed_to_unit' | 'project_to_template';
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  focusAnnualCellKey?: string | null;
  onSelectedAnnualCellKeyChange?: (annualCellKey: string | null) => void;
  programCellsByAnnualKey?: Record<string, ProgramProgressCell>;
  /** 동기화 미러: 대표 scoped 행 — 슬라이드·활동지·학습메모 인디케이터 조회용 */
  syncMirrorIndicatorCellsByAnnualKey?: Record<string, ProgramProgressCell>;
  /** 동기화 미러 프로그램 표 — 인디케이터는 대표 scoped 행 기준 */
  syncMirrorView?: boolean;
  /** 동기화 미러: 미러 annualCellKey → 대표 annualCellKey (활동지 localStorage 초안 조회) */
  syncMirrorRepAnnualCellKeyByMirrorKey?: Record<string, string>;
  /** 동기화 미러: 대표 annualCellKey → 미러 annualCellKey 목록 (삭제 힌트 전파) */
  syncMirrorMirrorAnnualCellKeysByRepKey?: Record<string, string[]>;
  onUpsertProgramCells?: (rows: ProgramProgressCellUpsert[]) => Promise<void> | void;
  onUpsertFixedContentRows?: (rows: ProgramFixedContentRowUpsert[]) => Promise<void> | void;
  saveMode?: 'program_cells' | 'fixed_content';
  onBindSaveAction?: (saveAction: (() => Promise<void>) | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  workspaceScopeId?: string;
  /**
   * 프레젠 로컬 초안 인디케이터 허용 스코프(동기화 대표·미러).
   * 없으면 `workspaceScopeId`만 사용.
   */
  slideDraftAllowedScopeIds?: readonly string[];
  onWorkspaceRowsChange?: (
    rows: ProgramFixedContentRowUpsert[] | null,
    meta?: ProgramWorkspaceRowsChangeMeta
  ) => void;
  /** fixed_content: 워크스페이스에서 행이 제거될 때(수업 삭제 등) — 고정 페이지에서 진도 셀 등 후처리 */
  onRemovedFixedWorkspaceRows?: (removed: ProgramFixedContentRowUpsert[]) => void;
  onEnsureWorkspaceSnapshot?: (rows: ProgramFixedContentRowUpsert[]) => void;
  /** true이면 편집·저장·삭제/붙여넣기 등 변경을 막고 보기·복사만 허용 */
  readOnly?: boolean;
  /** fixed_content 저장 시 program_cells 로 별도 저장할 필드들(동기화 예외 등) */
  fixedModeProgramCellFields?: string[];
  onUpsertProgramCellsInFixedMode?: (rows: ProgramProgressCellUpsert[]) => Promise<void> | void;
  /** 편집/변경은 잠그되 셀 선택은 허용할 필드 */
  lockedFieldIds?: string[];
  /** 행 구조 변경/대량 수정 액션(붙여넣기/잘라내기/삭제 등) 제한 */
  restrictMutatingActions?: boolean;
  /** 이 표에서 «오늘 학습내용 보기» 시각 매칭용 기초시간표 교시 */
  classScheduleSectionRows?: ClassScheduleRow[];
  /** 편제/기준 시수에 등록된 시간표명 목록(시간표 셀 유효성 검사) */
  registeredTimetableNames?: readonly string[];
  /** 편제 와일드카드 문자(연간시간표 `(X)` 접미사와 동일 규칙) */
  registeredTimetableWildcardChars?: readonly string[];
  /** 프로젝트 모드: 연간시간표→테이블 시간표명 가져오기 */
  onImportProjectTimetableFromAnnual?: () => Promise<string[]>;
  /** 프로젝트 모드: 테이블→연간시간표 시간표명 내보내기 */
  onExportProjectTimetableToAnnual?: (timetableTexts: string[]) => Promise<void>;
  /** 프로젝트 페이지 툴바에서 시간표 가져오기/내보내기를 호출할 수 있도록 연결 */
  onBindProjectTimetableSyncActions?: (
    api: {
      importFromAnnual: () => void;
      exportToAnnual: () => void;
      busy: boolean;
    } | null
  ) => void;
  /** 편제/프로젝트: 행 i의 일정 열이 쓸 연간 `annualCellKey` 순서. 있으면 수업 추가·행 이동 시 source를 이 순서에 맞춘다 */
  scheduleSlotAnnualCellKeys?: readonly string[];
  /** 학년도 툴바 등에서 `undo`/`redo`를 호출할 수 있도록 연결 */
  programUndoRedoApiRef?: React.MutableRefObject<ProgramProgressUndoRedoApi | null>;
  /** 로컬 실행 취소 스택 변화 시 툴바 버튼 disabled 갱신용 */
  onProgramUndoRedoAvailabilityChange?: (state: { canUndo: boolean; canRedo: boolean }) => void;
  /** 설정 시 툴바에 «프로그램 데이터 정리» — 현재 표·스코프 범위 DB 조회·선택 삭제 */
  programDataCleanupGradeId?: string;
  /** 편제(track)·프로젝트(project): 스코프 행 조회 범위 */
  programDataCleanupScopeType?: ProgramScopeType;
  programDataCleanupScopeKey?: string;
  /** 고정 프로그램 페이지에서만 true — 고정 행 테이블 포함 */
  programDataCleanupIncludeFixedRows?: boolean;
  onProgramDataCleanupComplete?: () => void;
}

function isClassMemberGradeLayoutEmpty(l: ClassMemberGradeLayout): boolean {
  return l.groups.length === 0 && Object.keys(l.membersByGroupId).length === 0;
}

/** props 참조만 바뀌어도 내용이 같으면 setState 생략(무한 렌더 방지). */
function programDraftMapsContentEqual(
  a: Record<string, Record<string, unknown>>,
  b: Record<string, Record<string, unknown>>
): boolean {
  const keysA = Object.keys(a).sort();
  const keysB = Object.keys(b).sort();
  if (keysA.length !== keysB.length) return false;
  for (let i = 0; i < keysA.length; i++) {
    if (keysA[i] !== keysB[i]) return false;
    const k = keysA[i];
    if (JSON.stringify(a[k]) !== JSON.stringify(b[k])) return false;
  }
  return true;
}

/** 연간진도표와 동일 구조(행동튄성 열 없음). 학습내용 열 폭은 프로그램 전용 CSS로 조정 */

/** AI 응답(v2 블록 또는 예전 pages) → 활동지 문서 + 안내 */
function buildWorksheetFromAiMaterial(
  material: Awaited<ReturnType<typeof requestAiLearningMaterial>>
): WorksheetBuildResult {
  if (material.kind !== 'worksheet') throw new Error('활동지를 만들지 못했습니다.');
  const worksheet = 'worksheet' in material ? material.worksheet : worksheetV2FromLegacyPages(material.pages);
  if (!worksheet.blocks.length) throw new Error('만든 내용이 비어 있습니다. 한 번 더 시도해 주세요.');
  return buildWorksheetDocumentFromAiV2(worksheet);
}

const ProgramProgressTable: React.FC<ProgramProgressTableProps> = ({
  rows,
  mode = 'default',
  workspaceId,
  academicYear,
  termNumber,
  focusAnnualCellKey = null,
  onSelectedAnnualCellKeyChange,
  programCellsByAnnualKey = {},
  syncMirrorIndicatorCellsByAnnualKey = {},
  syncMirrorView = false,
  syncMirrorRepAnnualCellKeyByMirrorKey = {},
  syncMirrorMirrorAnnualCellKeysByRepKey = {},
  onUpsertProgramCells,
  onUpsertFixedContentRows,
  saveMode = 'program_cells',
  onBindSaveAction,
  onDirtyChange,
  workspaceScopeId = '',
  slideDraftAllowedScopeIds,
  onWorkspaceRowsChange,
  onRemovedFixedWorkspaceRows,
  onEnsureWorkspaceSnapshot,
  readOnly = false,
  fixedModeProgramCellFields = [],
  onUpsertProgramCellsInFixedMode,
  lockedFieldIds = [],
  restrictMutatingActions = false,
  classScheduleSectionRows = [],
  registeredTimetableNames = [],
  registeredTimetableWildcardChars = [],
  onImportProjectTimetableFromAnnual,
  onExportProjectTimetableToAnnual,
  onBindProjectTimetableSyncActions,
  scheduleSlotAnnualCellKeys,
  programUndoRedoApiRef,
  onProgramUndoRedoAvailabilityChange,
  programDataCleanupGradeId = '',
  programDataCleanupScopeType,
  programDataCleanupScopeKey,
  programDataCleanupIncludeFixedRows = false,
  onProgramDataCleanupComplete,
}) => {
  const tutorial = useTutorial();
  const { isFeatureLocked, requireFeature } = usePaidPassGate();
  const aiAccess = useAiAccess();
  const { grades } = useProgramSidebar();
  const presentationLocked = isFeatureLocked('presentation');
  const worksheetLocked = isFeatureLocked('worksheet');
  const classRecordLocked = isFeatureLocked('class_record');
  const passLockClassOf = (locked: boolean) => (locked ? ' is-pass-locked' : '');
  const onProgramUndoRedoAvailabilityChangeRef = React.useRef(onProgramUndoRedoAvailabilityChange);
  React.useEffect(() => {
    onProgramUndoRedoAvailabilityChangeRef.current = onProgramUndoRedoAvailabilityChange;
  }, [onProgramUndoRedoAvailabilityChange]);

  const readOnlyRef = React.useRef(readOnly);
  React.useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);
  const mutationLockedRef = React.useRef(readOnly || restrictMutatingActions);
  React.useEffect(() => {
    mutationLockedRef.current = readOnly || restrictMutatingActions;
  }, [readOnly, restrictMutatingActions]);
  const mutationLocked = readOnly || restrictMutatingActions;
  const [programDataCleanupOpen, setProgramDataCleanupOpen] = React.useState(false);
  const [rowBgModalOpen, setRowBgModalOpen] = React.useState(false);
  const [lastRowBgColor, setLastRowBgColor] = React.useState<string | null>(null);
  const rowBgPalette = useProgramCellBgPalette(!readOnly);
  const [programPresentationListOpen, setProgramPresentationListOpen] = React.useState(false);
  const [programWorksheetListOpen, setProgramWorksheetListOpen] = React.useState(false);
  const [worksheetEditorOpen, setWorksheetEditorOpen] = React.useState(false);
  const [worksheetEditorAnnualKey, setWorksheetEditorAnnualKey] = React.useState<string | null>(null);
  const [worksheetEditorInitialDocument, setWorksheetEditorInitialDocument] =
    React.useState<LearningContentWorksheetDocumentV1 | null>(null);
  const [worksheetEditorInitialDirty, setWorksheetEditorInitialDirty] = React.useState(false);
  const [learningMaterialBusy, setLearningMaterialBusy] = React.useState<null | 'presentation' | 'worksheet'>(null);
  /** 이번에 AI로 만든 활동지의 요청 문맥(같은 편집기 안에서 '다른 구성으로 다시 만들기'에 씀) */
  const [worksheetAiRequest, setWorksheetAiRequest] = React.useState<{
    annualKey: string;
    input: AiLearningMaterialContext;
  } | null>(null);
  const worksheetAiPreviewRef = React.useRef<{
    draftKey: string;
    previous: LearningContentWorksheetDocumentV1 | null;
    saved: boolean;
  } | null>(null);
  React.useEffect(() => {
    if (worksheetEditorOpen) return;
    const session = worksheetAiPreviewRef.current;
    if (!session) return;
    worksheetAiPreviewRef.current = null;
    if (session.saved) return;
    const draftKey = session.draftKey;
    const previous = session.previous;
    queueMicrotask(() => {
      clearLearningContentWorksheetDraft(draftKey);
      if (previous) saveLearningContentWorksheetDraft(draftKey, previous);
    });
  }, [worksheetEditorOpen]);
  const [worksheetPresenceRefIds, setWorksheetPresenceRefIds] = React.useState<Set<string>>(new Set());
  const [worksheetPresenceLoading, setWorksheetPresenceLoading] = React.useState(false);
  type FieldId =
    | 'timetable_text'
    | 'unit_text'
    | 'learning_text'
    | 'page_text'
    | 'assistant_page_text'
    | 'current_period_text'
    | 'total_period_text'
    | 'material_text'
    | 'record_cross_curricular_text'
    | 'record_memo_text'
    | 'record_evaluation_text'
    | 'record_class_text';
  const lockedFieldSet = React.useMemo(
    () => new Set<FieldId>((lockedFieldIds as FieldId[]) ?? []),
    [lockedFieldIds]
  );
  const isFieldLocked = React.useCallback((fieldId: FieldId) => lockedFieldSet.has(fieldId), [lockedFieldSet]);

  type MemoFieldPopup = {
    annualKey: string;
    fieldId: FieldId;
    text: string;
    note: string;
    noteLesson?: string;
    noteEval?: string;
    /** 수업기록 모달: 멤버 태그·#전체·#이름- 기준(null이면 학급 전체) */
    recordMemberGroupId?: string | null;
    /**
     * 평가기록: 칩·멤버 해석에 쓰는 그룹(멤버 태그 생성 시 확정). 드롭다운(recordMemberGroupId)과 분리.
     * 수업기록에서는 사용하지 않음.
     */
    evalMemoMemberGroupId?: string | null;
    /** 평가 셀에 연결된 클래스 적용 평가계획(applied row id) */
    evaluationPlanAppliedId?: string | null;
    /** 버튼에 표시할 요약 문구 */
    evaluationPlanSummary?: string | null;
    /** 메모 상단 고정: 평가요소 */
    evaluationPlanElement?: string | null;
    /** 메모 상단 고정: 평가방법 */
    evaluationPlanMethod?: string | null;
    /** 수업·평가기록에 연결된 과제 체크 item id */
    assignmentMemoItemId?: string | null;
    /** 연결 과제 제목(표시용 캐시) */
    assignmentMemoItemTitle?: string | null;
    /** 연결 시 선택한 멤버 그룹(학급) */
    assignmentMemoGroupId?: string | null;
    /** 연결 그룹 이름(표시용 캐시) */
    assignmentMemoGroupTitle?: string | null;
  };

  type UnitPopupSnapshot = { text: string; note: string };
  type MemoFieldPopupSnapshot =
    | {
        fieldId: 'record_class_text';
        text: string;
        noteLesson: string;
        noteEval: string;
        recordMemberGroupId: string | null;
        evalMemoMemberGroupId: string | null;
        assignmentMemoItemId: string | null;
        assignmentMemoItemTitle: string | null;
        assignmentMemoGroupId: string | null;
        assignmentMemoGroupTitle: string | null;
      }
    | {
        fieldId: Exclude<FieldId, 'record_class_text'>;
        text: string;
        note: string;
        evaluationPlanAppliedId?: string | null;
        evaluationPlanSummary?: string | null;
        evaluationPlanElement?: string | null;
        evaluationPlanMethod?: string | null;
      };

  type DraftRow = Record<FieldId, string> & {
    bg_color_by_field: ProgramProgressBgColorByField;
    record_member_group_id: string | null;
  };

  const protectedBgFields: Set<FieldId> = React.useMemo(
    () =>
      new Set<FieldId>([
        'record_cross_curricular_text',
        'record_memo_text',
        'record_evaluation_text',
        'record_class_text',
      ]),
    []
  );

  /** 툴바 행 배경색 적용 대상 열 */
  const rowBgColorFields: readonly FieldId[] = React.useMemo(
    () => [
      'unit_text',
      'learning_text',
      'page_text',
      'assistant_page_text',
      'current_period_text',
      'total_period_text',
      'material_text',
    ],
    []
  );

  const editableFieldOrder = React.useMemo<FieldId[]>(() => {
    const base: FieldId[] =
      mode === 'project_to_template'
        ? ['timetable_text', 'unit_text']
        : mode === 'fixed_to_unit'
          ? []
          : ['unit_text'];
    return [
      ...base,
      'learning_text',
      'page_text',
      'assistant_page_text',
      'current_period_text',
      'total_period_text',
      'material_text',
      'record_cross_curricular_text',
      'record_memo_text',
      'record_evaluation_text',
      'record_class_text',
    ];
  }, [mode]);
  /** 단원(또는 고정→단원 시 학습내용) ~ 수업기록 열 구간 — 수업 추가/삭제 후 선택 영역 */
  const getContentColumnFieldsForSelection = React.useCallback((): FieldId[] => {
    const startCandidates: readonly FieldId[] =
      mode === 'project_to_template'
        ? ['timetable_text', 'unit_text', 'learning_text']
        : ['unit_text', 'learning_text'];
    const start = startCandidates.find((f) => editableFieldOrder.includes(f));
    const end: FieldId = 'record_class_text';
    if (!start || !editableFieldOrder.includes(end)) {
      return editableFieldOrder.length ? [...editableFieldOrder] : [];
    }
    const si = editableFieldOrder.indexOf(start);
    const ei = editableFieldOrder.indexOf(end);
    if (si < 0 || ei < 0 || si > ei) return [...editableFieldOrder];
    return editableFieldOrder.slice(si, ei + 1);
  }, [editableFieldOrder, mode]);
  const isSelectableField = React.useCallback(
    (fieldId: FieldId) => editableFieldOrder.includes(fieldId),
    [editableFieldOrder]
  );
  const isLongPressPopupField = React.useCallback(
    (fieldId: FieldId) =>
      fieldId === 'unit_text' || fieldId === 'learning_text' || fieldId === 'record_evaluation_text',
    []
  );

  const fieldLabelForPasteHint = React.useMemo(
    (): Record<FieldId, string> => ({
      timetable_text: '시간표',
      unit_text: '단원',
      learning_text: '학습내용',
      page_text: '쪽수',
      assistant_page_text: '보조 쪽수',
      current_period_text: '해당 차시',
      total_period_text: '전체 차시',
      material_text: '준비물',
      record_cross_curricular_text: '범교과',
      record_memo_text: '메모',
      record_evaluation_text: '평가',
      record_class_text: '수업기록',
    }),
    []
  );

  /** 행 구조의 단일 진실 공급원은 부모가 전달하는 rows(워크스페이스/서버 저장 틀 순서) */
  const tableRows = rows;

  const programDataCleanupScheduleSlotKeys = React.useMemo((): AnnualTimetableNaturalKey[] => {
    const seen = new Set<string>();
    const out: AnnualTimetableNaturalKey[] = [];
    for (const r of tableRows) {
      const gid = (r.gradeId ?? '').trim();
      const mon = (r.mondayIso ?? '').trim();
      const wk = (r.weekdayKey ?? '').trim();
      if (!gid || !mon || !wk) continue;
      const dedupe = `${gid}|${mon}|${wk}|${r.slotIndex}`;
      if (seen.has(dedupe)) continue;
      seen.add(dedupe);
      out.push({
        gradeId: gid,
        mondayIso: mon,
        weekdayKey: wk,
        slotIndex: r.slotIndex,
      });
    }
    return out;
  }, [tableRows]);

  const programPresentationAuditScopeSig = React.useMemo(
    () =>
      programPresentationAuditScopeSignature({
        scopeType: programDataCleanupScopeType,
        scopeKey: programDataCleanupScopeKey,
        includeFixedRows: programDataCleanupIncludeFixedRows,
        scheduleSlotKeysSignature: programDataCleanupScheduleSlotKeys
          .map((k) => `${k.gradeId}|${k.mondayIso}|${k.weekdayKey}|${k.slotIndex}`)
          .sort()
          .join('\u0001'),
      }),
    [
      programDataCleanupIncludeFixedRows,
      programDataCleanupScheduleSlotKeys,
      programDataCleanupScopeKey,
      programDataCleanupScopeType,
    ]
  );

  const programPresentationAuditQuery = useProgramPresentationAuditQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: (programDataCleanupGradeId ?? '').trim(),
    scheduleSlotKeys: programDataCleanupScheduleSlotKeys,
    scopeType: programDataCleanupScopeType,
    scopeKey: programDataCleanupScopeKey,
    includeFixedRows: programDataCleanupIncludeFixedRows,
    scopeSignature: programPresentationAuditScopeSig,
    enabled:
      programPresentationListOpen &&
      !!workspaceId.trim() &&
      !!(programDataCleanupGradeId ?? '').trim(),
  });

  const orphanPresentationEntries = React.useMemo((): PresentationAuditEntry[] => {
    if (!programPresentationListOpen) return [];
    const snapshot = programPresentationAuditQuery.data;
    if (!snapshot?.presentationEntries?.length) return [];
    const scopedRowIds = new Set(snapshot.scopedRows.map((row) => row.id.trim()).filter(Boolean));
    const fixedRowIds = new Set(snapshot.fixedRows.map((row) => row.id.trim()).filter(Boolean));
    const programCellIds = new Set(
      snapshot.programProgressCells.map((cell) => cell.id.trim()).filter(Boolean)
    );
    return filterPresentationOrphansToPageScope(snapshot.presentationEntries, {
      scopedRowIds,
      fixedRowIds,
      programCellIds,
    });
  }, [programPresentationAuditQuery.data, programPresentationListOpen]);

  const progressGradeId = React.useMemo(() => {
    const hit = tableRows.find((r) => !!r.gradeId);
    return hit?.gradeId ?? '';
  }, [tableRows]);

  const [classMembers, setClassMembers] = React.useState<ClassMemberRow[]>([]);
  const [memberGradeLayout, setMemberGradeLayout] = React.useState<ClassMemberGradeLayout>(emptyGradeLayout);

  React.useEffect(() => {
    if (!workspaceId || !progressGradeId) {
      setClassMembers((prev) => (prev.length === 0 ? prev : []));
      setMemberGradeLayout((prev) => (isClassMemberGradeLayoutEmpty(prev) ? prev : emptyGradeLayout()));
      return undefined;
    }
    const layoutLs = loadClassMemberGradeLayoutFromStorage(workspaceId, progressGradeId);
    if (layoutLs) {
      setMemberGradeLayout(layoutLs);
      setClassMembers(flattenGradeLayout(layoutLs));
    } else {
      setClassMembers([]);
      setMemberGradeLayout(emptyGradeLayout());
    }
    const hasSnapshot = hasClassMemberLocalStorageSnapshot(workspaceId, progressGradeId);
    if (hasSnapshot && layoutLs != null) {
      recordDevLocalStorageProgramCacheUse('학급멤버·LS');
      return undefined;
    }
    let cancelled = false;
    void getClassMemberGradeLayoutsMap(workspaceId, [progressGradeId])
      .then((m) => {
        if (cancelled) return;
        const lay = m[progressGradeId];
        if (lay) {
          setMemberGradeLayout(lay);
          setClassMembers(flattenGradeLayout(lay));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [progressGradeId, workspaceId]);

  React.useEffect(() => {
    const onChanged = () => {
      if (!workspaceId || !progressGradeId) return;
      const lay = loadClassMemberGradeLayoutFromStorage(workspaceId, progressGradeId) ?? emptyGradeLayout();
      setMemberGradeLayout(lay);
      setClassMembers(flattenGradeLayout(lay));
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
    return () => window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onChanged as EventListener);
  }, [progressGradeId, workspaceId]);

  const rowByAnnualKey = React.useMemo(
    () => Object.fromEntries(tableRows.map((row) => [row.annualCellKey, row] as const)),
    [tableRows]
  );

  const normalizeFixedEventName = React.useCallback((raw: string) => raw.trim().replace(/^◆+/, '').replace(/-+$/, '').trim(), []);
  const parseFixedLearningPrefix = React.useCallback(
    (learning: string, scheduleEventNameRaw?: string) => {
      const raw = (learning ?? '').trim();
      const scheduleEv = normalizeFixedEventName((scheduleEventNameRaw ?? '').trim());
      if (scheduleEv) {
        const prefix = `◆${scheduleEv}-`;
        if (raw.startsWith('{')) {
          try {
            const deep = parseMemoFieldStorageDeep(raw);
            const text = (deep.text ?? '').trim();
            if (text.startsWith(prefix)) {
              return { eventName: scheduleEv, body: text.slice(prefix.length).trim() };
            }
          } catch {
            /* ignore */
          }
        } else if (raw.startsWith(prefix)) {
          return { eventName: scheduleEv, body: raw.slice(prefix.length).trim() };
        }
      }
      const m = raw.match(/^◆\s*([^-]+?)\s*-(.*)$/);
      if (!m) return { eventName: '', body: '' };
      return { eventName: normalizeFixedEventName(m[1] ?? ''), body: (m[2] ?? '').trim() };
    },
    [normalizeFixedEventName]
  );
  const isRecordedFixedLearning = React.useCallback(
    (learning: string, scheduleEventNameRaw?: string) => {
      const raw = (learning ?? '').trim();
      if (!raw) return false;
      const parsed = parseFixedLearningPrefix(raw, scheduleEventNameRaw);
      if (parsed.eventName) return parsed.body.length > 0;
      return raw.length > 0;
    },
    [parseFixedLearningPrefix]
  );
  const fixedEventNameByRow = React.useCallback(
    (row: ProgramProgressRow) =>
      normalizeFixedEventName(row.memo ?? row.fixedEventName ?? row.timetableText ?? ''),
    [normalizeFixedEventName]
  );
  const normalizeFixedLearningValue = React.useCallback(
    (eventName: string, rawLearning: string) => {
      const normalizedEvent = normalizeFixedEventName(eventName);
      if (!normalizedEvent) return rawLearning;
      const prefix = `◆${normalizedEvent}-`;
      if (rawLearning.startsWith(prefix)) return rawLearning;
      return `${prefix}${stripConflictingFixedLearningDiamondPrefixes(rawLearning, normalizedEvent)}`;
    },
    [normalizeFixedEventName]
  );
  type MemoFieldData = {
    text: string;
    note: string;
    behaviorNote?: string;
    presentationRefId?: string;
    worksheetRefId?: string;
    evaluationPlanAppliedId?: string;
    evaluationPlanSummary?: string;
    evaluationPlanElement?: string;
    evaluationPlanMethod?: string;
    assignmentMemoItemId?: string;
    assignmentMemoItemTitle?: string;
    assignmentMemoGroupId?: string;
    assignmentMemoGroupTitle?: string;
  };
  const parseMemoFieldData = React.useCallback((raw: string): MemoFieldData => {
    const {
      text,
      note,
      behaviorNote,
      presentationRefId,
      worksheetRefId,
      evaluationPlanAppliedId,
      evaluationPlanSummary,
      evaluationPlanElement,
      evaluationPlanMethod,
      assignmentMemoItemId,
      assignmentMemoItemTitle,
      assignmentMemoGroupId,
      assignmentMemoGroupTitle,
    } = parseMemoFieldStorageDeep(raw);
    return {
      text,
      note,
      behaviorNote: behaviorNote || undefined,
      presentationRefId: presentationRefId || undefined,
      worksheetRefId: worksheetRefId || undefined,
      evaluationPlanAppliedId: evaluationPlanAppliedId || undefined,
      evaluationPlanSummary: evaluationPlanSummary || undefined,
      evaluationPlanElement: evaluationPlanElement || undefined,
      evaluationPlanMethod: evaluationPlanMethod || undefined,
      assignmentMemoItemId: assignmentMemoItemId || undefined,
      assignmentMemoItemTitle: assignmentMemoItemTitle || undefined,
      assignmentMemoGroupId: assignmentMemoGroupId || undefined,
      assignmentMemoGroupTitle: assignmentMemoGroupTitle || undefined,
    };
  }, []);
  const serializeMemoFieldData = React.useCallback(
    (
      textRaw: string,
      noteRaw: string,
      presentationRefIdRaw?: string | null,
      worksheetRefIdRaw?: string | null,
      evaluationPlanAppliedIdRaw?: string | null,
      evaluationPlanSummaryRaw?: string | null,
      evaluationPlanElementRaw?: string | null,
      evaluationPlanMethodRaw?: string | null
    ) => {
      const text = textRaw ?? '';
      const note = noteRaw ?? '';
      const presentationRefId = (presentationRefIdRaw ?? '').trim();
      const worksheetRefId = (worksheetRefIdRaw ?? '').trim();
      const evaluationPlanAppliedId = (evaluationPlanAppliedIdRaw ?? '').trim();
      const evaluationPlanSummary = (evaluationPlanSummaryRaw ?? '').trim();
      const evaluationPlanElement = (evaluationPlanElementRaw ?? '').trim();
      const evaluationPlanMethod = (evaluationPlanMethodRaw ?? '').trim();
      if (
        !text.trim() &&
        !note.trim() &&
        !presentationRefId &&
        !worksheetRefId &&
        !evaluationPlanAppliedId
      ) {
        return '';
      }
      if (!note.trim() && !presentationRefId && !worksheetRefId && !evaluationPlanAppliedId) return text;
      const payload: {
        text: string;
        note?: string;
        presentationRefId?: string;
        worksheetRefId?: string;
        evaluationPlanAppliedId?: string;
        evaluationPlanSummary?: string;
        evaluationPlanElement?: string;
        evaluationPlanMethod?: string;
      } = { text };
      if (note.trim()) payload.note = note;
      if (presentationRefId) payload.presentationRefId = presentationRefId;
      if (worksheetRefId) payload.worksheetRefId = worksheetRefId;
      if (evaluationPlanAppliedId) payload.evaluationPlanAppliedId = evaluationPlanAppliedId;
      if (evaluationPlanAppliedId && evaluationPlanSummary) {
        payload.evaluationPlanSummary = evaluationPlanSummary;
      }
      if (evaluationPlanAppliedId && evaluationPlanElement) {
        payload.evaluationPlanElement = evaluationPlanElement;
      }
      if (evaluationPlanAppliedId && evaluationPlanMethod) {
        payload.evaluationPlanMethod = evaluationPlanMethod;
      }
      return JSON.stringify(payload);
    },
    []
  );
  /** 고정→단원 모드: 학습내용이 JSON(본문+메모)일 때 접두사를 본문에만 적용하고 메모는 유지 */
  const normalizeFixedLearningValueMemoAware = React.useCallback(
    (eventName: string, rawLearning: string) => {
      const raw = (rawLearning ?? '').trim();
      if (!raw) return '';
      if (!raw.startsWith('{')) {
        return normalizeFixedLearningValue(eventName, raw);
      }
      const { text, note, presentationRefId, worksheetRefId } = parseMemoFieldData(raw);
      return serializeMemoFieldData(
        normalizeFixedLearningValue(eventName, text),
        note,
        presentationRefId,
        worksheetRefId
      );
    },
    [normalizeFixedLearningValue, parseMemoFieldData, serializeMemoFieldData]
  );
  const resolveFixedLearningPrefix = React.useCallback(
    (annualKey: string, row: ProgramProgressRow | undefined, candidateValue: string) => {
      const parsed = parseFixedLearningPrefix(candidateValue, row?.memo ?? row?.fixedEventName ?? '');
      if (parsed.eventName) return `◆${parsed.eventName}-`;
      /** 일정 행사명을 단원보다 우선: 단원만 다른 행사로 남은 경우 빈 학습 편집 시 ◆가 단원 쪽으로 붙는 오류 방지 */
      const rowEvent = normalizeFixedEventName((row?.memo ?? row?.fixedEventName ?? '').trim());
      if (rowEvent) return `◆${rowEvent}-`;
      const draft = draftRef.current[annualKey];
      const unitEvent = normalizeFixedEventName(parseUnitStorageRaw(draft?.unit_text ?? '').text);
      if (unitEvent) return `◆${unitEvent}-`;
      return '';
    },
    [normalizeFixedEventName, parseFixedLearningPrefix]
  );

  const makeDraftRow = React.useCallback(
    (row: ProgramProgressRow): DraftRow => {
      const cell = programCellsByAnnualKey[row.annualCellKey];
      const migratedUnit = migrateUnitCellFromDbToCanonicalStored(cell?.unit_text ?? '');
      const migratedCross = migrateCrossCellFromDbToCanonicalStored(cell?.record_cross_curricular_text ?? '');
      const fixedEventName = (row.memo ?? row.fixedEventName ?? row.timetableText ?? '')
        .trim()
        .replace(/^◆+/, '')
        .trim();
      const fixedUnitPrefix = fixedEventName ? `◆${fixedEventName}` : '';
      const fixedLearningPrefix = fixedEventName ? `◆${fixedEventName}-` : '';
      const scheduleEvtRaw = (row.memo ?? row.fixedEventName ?? '').trim();
      const cellBody = readProgramLearningContentFields(cell).body;
      const learningRecorded = isRecordedFixedLearning(cellBody, scheduleEvtRaw);
      const transformBody = (body: string) => {
        if (mode !== 'fixed_to_unit') return body;
        if (learningRecorded) return body;
        if (!fixedLearningPrefix) return body;
        if (body.startsWith(fixedLearningPrefix)) return body;
        return `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(body, scheduleEvtRaw)}`;
      };
      return {
        timetable_text:
          mode === 'project_to_template'
            ? (cell?.timetable_text ?? '')
            : (cell?.timetable_text ?? row.timetableText ?? ''),
        unit_text:
          mode === 'fixed_to_unit'
            ? learningRecorded
              ? migratedUnit || fixedUnitPrefix
              : fixedUnitPrefix || migratedUnit || ''
            : migratedUnit,
        learning_text: buildDraftLearningTextFromCell(cell, transformBody),
        page_text: cell?.page_text ?? '',
        assistant_page_text: cell?.assistant_page_text ?? '',
        current_period_text: cell?.current_period_text ?? '',
        total_period_text: cell?.total_period_text ?? '',
        material_text: cell?.material_text ?? '',
        record_cross_curricular_text: migratedCross,
        record_memo_text: mode === 'fixed_to_unit' ? cell?.record_memo_text ?? '' : cell?.record_memo_text ?? row.memo ?? '',
        record_evaluation_text: cell?.record_evaluation_text ?? '',
        record_class_text: cell?.record_class_text ?? '',
        record_member_group_id: cell?.record_member_group_id ?? null,
        bg_color_by_field: { ...(cell?.bg_color_by_field ?? {}) },
      };
    },
    [isRecordedFixedLearning, mode, programCellsByAnnualKey]
  );

  const [draftByAnnualKey, setDraftByAnnualKey] = React.useState<Record<string, DraftRow>>({});
  const draftRef = React.useRef<Record<string, DraftRow>>({});
  /** 셀 선택·Backspace 등으로 비운 학습내용 — 저장 후 pending 해제돼도 baseline 갱신 전까지 복원하지 않음 */
  const [locallyClearedLearningKeys, setLocallyClearedLearningKeys] = React.useState<ReadonlySet<string>>(
    () => new Set()
  );
  type PendingSaveRow = ProgramProgressCellUpsert | ProgramFixedContentRowUpsert;
  const pendingRowsRef = React.useRef<Record<string, PendingSaveRow>>({});
  const programProgressScrollRef = React.useRef<HTMLDivElement | null>(null);
  /** 인라인 편집 커밋 직후 리렌더로 scrollTop이 초기화되는 것만 복원 — applyPatches 전역 잠금은 휠 스크롤과 충돌 */
  const programProgressScrollRestoreTopRef = React.useRef<number | null>(null);
  const requestProgramProgressScrollRestore = React.useCallback(() => {
    const wrap = programProgressScrollRef.current;
    if (wrap) programProgressScrollRestoreTopRef.current = wrap.scrollTop;
  }, []);
  React.useLayoutEffect(() => {
    if (programProgressScrollRestoreTopRef.current == null) return;
    const top = programProgressScrollRestoreTopRef.current;
    programProgressScrollRestoreTopRef.current = null;
    const wrap = programProgressScrollRef.current;
    if (wrap) wrap.scrollTop = top;
  });
  const resolveRepDraftLearningText = React.useCallback(
    (annualKey: string): string => {
      if (!syncMirrorView) return '';
      const repKey = syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim();
      if (!repKey) return '';
      return draftRef.current[repKey]?.learning_text ?? draftByAnnualKey[repKey]?.learning_text ?? '';
    },
    [draftByAnnualKey, syncMirrorRepAnnualCellKeyByMirrorKey, syncMirrorView]
  );
  const resolveLearningAttachmentDeleteHintAnnualKeys = React.useCallback(
    (annualKey: string, patchAnnualKey: string): string[] => {
      const keys = [annualKey.trim(), patchAnnualKey.trim()].filter(Boolean);
      const mirrorKeys = syncMirrorMirrorAnnualCellKeysByRepKey[patchAnnualKey.trim()] ?? [];
      return [...keys, ...mirrorKeys];
    },
    [syncMirrorMirrorAnnualCellKeysByRepKey]
  );
  const resolveLearningTextForMemoAtAnnualKey = React.useCallback(
    (annualKey: string): string => {
      const draftLt = draftRef.current[annualKey]?.learning_text ?? '';
      const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      return resolveSyncMirrorLearningTextForIndicators(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draftLt,
        cellLt,
        resolveRepDraftLearningText(annualKey)
      );
    },
    [
      programCellsByAnnualKey,
      resolveRepDraftLearningText,
      syncMirrorIndicatorCellsByAnnualKey,
      syncMirrorView,
    ]
  );
  const resolveLearningAttachmentRefsForMemoCommit = React.useCallback(
    (annualKey: string): { presentationRefId?: string; worksheetRefId?: string } => {
      const draftRaw = draftRef.current[annualKey]?.learning_text;
      if (draftRaw !== undefined) {
        const parsed = parseMemoFieldData(draftRaw ?? '');
        return {
          presentationRefId: parsed.presentationRefId,
          worksheetRefId: parsed.worksheetRefId,
        };
      }
      const cell = programCellsByAnnualKey[annualKey];
      return resolveLearningAttachmentRefsFromSources(cell);
    },
    [parseMemoFieldData, programCellsByAnnualKey]
  );
  /** draft 동기화 effect보다 먼저 선언 — effect 종료 시 스택 길이로 부모 툴바 갱신 */
  const notifyUndoRedoStacksRef = React.useRef<() => void>(() => {});
  /** 행 집합·순서(연간 셀 키) — 일정 구조가 같으면 programCells만 바뀌어도 로컬 실행 취소 스택을 유지한다 */
  const rowsStructureKey = React.useMemo(() => rows.map((r) => r.annualCellKey).join('\u0001'), [rows]);
  const rowsBaselineFingerprint = React.useMemo(() => fingerprintProgramRowsForDraftSync(rows), [rows]);
  const programCellsFingerprint = React.useMemo(
    () => fingerprintProgramCellsForDraftSync(programCellsByAnnualKey),
    [programCellsByAnnualKey]
  );
  const draftSyncHistoryBarrierRef = React.useRef<{
    workspaceScopeId: string;
    rowsStructureKey: string;
    mode: typeof mode;
    saveMode: typeof saveMode;
  } | null>(null);
  const prevProgramCellsFingerprintRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const prevBarrier = draftSyncHistoryBarrierRef.current;
    const barrierChanged =
      !prevBarrier ||
      prevBarrier.workspaceScopeId !== workspaceScopeId ||
      prevBarrier.rowsStructureKey !== rowsStructureKey ||
      prevBarrier.mode !== mode ||
      prevBarrier.saveMode !== saveMode;
    if (barrierChanged) {
      localUndoStackRef.current = [];
      localRedoStackRef.current = [];
      setLocallyClearedLearningKeys(new Set());
    }
    draftSyncHistoryBarrierRef.current = {
      workspaceScopeId,
      rowsStructureKey,
      mode,
      saveMode,
    };
    const cellsChanged =
      prevProgramCellsFingerprintRef.current !== null &&
      prevProgramCellsFingerprintRef.current !== programCellsFingerprint;
    prevProgramCellsFingerprintRef.current = programCellsFingerprint;
    /**
     * 프로젝트: 연간 내보내기 등으로 일정 표시(편제)만 바뀌고 programCells는 그대로일 때
     * draft를 셀 기준으로 다시 만들면, 빈 칸이 캐시·옛 본문으로 되살아날 수 있다.
     */
    const preserveProjectDraftContent =
      mode === 'project_to_template' && !barrierChanged && !cellsChanged;
    const pendingAnnualKeys = new Set<string>();
    Object.values(pendingRowsRef.current).forEach((pending) => {
      if (saveMode === 'fixed_content') {
        const ak = resolveFixedContentPendingAnnualKey(pending as ProgramFixedContentRowUpsert, rows);
        if (ak) pendingAnnualKeys.add(ak);
      } else {
        const r = pending as ProgramProgressCellUpsert;
        if (!r.monday_iso?.trim() || r.weekday_key == null || r.slot_index == null) return;
        pendingAnnualKeys.add(buildAnnualCellKey(r.grade_id, r.monday_iso, r.weekday_key, r.slot_index));
      }
    });
    const prevDraft = draftRef.current;
    const next: Record<string, DraftRow> = {};
    /** 통합 저장 직후 서버 재조회 전 — flush로 넣은 첨부 ref가 cell에 아직 없으면 draft에서 유지 */
    const preserveAttachmentRefsFromPrevDraft = (ak: string, learningText: string): string => {
      const prevLt = Object.prototype.hasOwnProperty.call(prevDraft, ak)
        ? prevDraft[ak]?.learning_text ?? ''
        : undefined;
      if (locallyClearedLearningKeys.has(ak)) {
        const nextFields = parseProgramLearningContentFieldsFromEditor(learningText);
        const bodyEmpty = !nextFields.body.trim();
        if (bodyEmpty) {
          setLocallyClearedLearningKeys((prev) => {
            if (!prev.has(ak)) return prev;
            const nextSet = new Set(prev);
            nextSet.delete(ak);
            return nextSet;
          });
        }
      }
      return resolveDraftLearningTextAfterBaselineArrival({
        prevLearningText: prevLt,
        nextLearningText: learningText,
        rowHasLocalEdits:
          pendingAnnualKeys.has(ak) || locallyClearedLearningKeys.has(ak),
      });
    };
    if (mode !== 'fixed_to_unit') {
      rows.forEach((row) => {
        const ak = row.annualCellKey;
        if (pendingAnnualKeys.has(ak) && prevDraft[ak]) {
          next[ak] = prevDraft[ak];
          return;
        }
        if (preserveProjectDraftContent && prevDraft[ak]) {
          next[ak] = prevDraft[ak];
          return;
        }
        const base = makeDraftRow(row);
        next[ak] = {
          ...base,
          learning_text: preserveAttachmentRefsFromPrevDraft(ak, base.learning_text ?? ''),
        };
      });
    } else {
      /** 행 키(연간 셀·orphan)별 programCells 맵을 그대로 쓴다. 이벤트명 FIFO 매칭은 단원·학습내용이 꼬이므로 하지 않는다 */
      rows.forEach((row) => {
        const ak = row.annualCellKey;
        if (pendingAnnualKeys.has(ak) && prevDraft[ak]) {
          next[ak] = prevDraft[ak];
          return;
        }
        const cell = programCellsByAnnualKey[row.annualCellKey];
        const targetEventName = fixedEventNameByRow(row);
        const baseDraft = cell
          ? ({
              timetable_text: cell?.timetable_text ?? '',
              unit_text: migrateUnitCellFromDbToCanonicalStored(cell?.unit_text ?? ''),
              learning_text: cellLearningTextForEditor(cell),
              page_text: cell?.page_text ?? '',
              assistant_page_text: cell?.assistant_page_text ?? '',
              current_period_text: cell?.current_period_text ?? '',
              total_period_text: cell?.total_period_text ?? '',
              material_text: cell?.material_text ?? '',
              record_cross_curricular_text: migrateCrossCellFromDbToCanonicalStored(
                cell?.record_cross_curricular_text ?? ''
              ),
              record_memo_text: cell?.record_memo_text ?? '',
              record_evaluation_text: cell?.record_evaluation_text ?? '',
              record_class_text: cell?.record_class_text ?? '',
              record_member_group_id: cell?.record_member_group_id ?? null,
              bg_color_by_field: { ...(cell?.bg_color_by_field ?? {}) },
            } as DraftRow)
          : makeDraftRow(row);
        const fixedUnitPrefix = targetEventName ? `◆${targetEventName}` : '';
        const mergedCanonical = coalesceFixedProgramProgressLearning(
          targetEventName,
          baseDraft.unit_text ?? '',
          baseDraft.learning_text ?? ''
        );
        const learningValue = normalizeFixedLearningValueMemoAware(targetEventName, mergedCanonical);
        next[ak] = {
          ...baseDraft,
          timetable_text: row.timetableText ?? '',
          unit_text: fixedUnitPrefix,
          learning_text: preserveAttachmentRefsFromPrevDraft(ak, learningValue),
        };
      });
    }
    const prevSnap = draftRef.current;
    draftRef.current = next;
    /*
     * barrierChanged(스코프·행 집합·모드 등)일 때는 내용 비교와 무관하게 state를 갱신한다.
     * 그렇지 않으면 ref만 next로 바뀌고 draftByAnnualKey는 이전 편제 그대로 남아 화면이 어긋날 수 있다.
     */
    if (
      barrierChanged ||
      !programDraftMapsContentEqual(
        prevSnap as Record<string, Record<string, unknown>>,
        next as Record<string, Record<string, unknown>>
      )
    ) {
      setDraftByAnnualKey(next);
    }
    notifyUndoRedoStacksRef.current();
  }, [
    fixedEventNameByRow,
    mode,
    normalizeFixedLearningValueMemoAware,
    programCellsFingerprint,
    rows,
    rowsBaselineFingerprint,
    rowsStructureKey,
    saveMode,
    workspaceScopeId,
    // makeDraftRow: programCellsByAnnualKey·mode·isRecordedFixedLearning 반영 — 동일 deps로 충분
    isRecordedFixedLearning,
    locallyClearedLearningKeys,
  ]);
  React.useEffect(() => {
    // 확장 행이 추가될 때 기존 draft를 보존한 채 누락 키만 채운다.
    const cur = draftRef.current;
    let changed = false;
    const next: Record<string, DraftRow> = { ...cur };
    tableRows.forEach((row) => {
      if (next[row.annualCellKey]) return;
      next[row.annualCellKey] = makeDraftRow(row);
      changed = true;
    });
    if (!changed) return;
    draftRef.current = next;
    setDraftByAnnualKey(next);
  }, [makeDraftRow, rowsStructureKey]);

  /** 행·셀 id·학습내용 draft가 바뀔 때마다 DB 슬라이드 presence를 다시 조회한다 */
  const slidePresenceReloadDepsSig = React.useMemo(
    () =>
      tableRows
        .map((r) => {
          const c = programCellsByAnnualKey[r.annualCellKey];
          const draftLt = draftByAnnualKey[r.annualCellKey]?.learning_text ?? '';
          const cellLt = cellLearningTextForEditor(c);
          const lt = resolveSyncMirrorLearningTextForIndicators(
            r.annualCellKey,
            syncMirrorView,
            syncMirrorIndicatorCellsByAnnualKey,
            draftLt,
            cellLt,
            resolveRepDraftLearningText(r.annualCellKey)
          );
          const rep = syncMirrorIndicatorCellsByAnnualKey[r.annualCellKey];
          return `${r.annualCellKey}:${(c?.id ?? '').trim()}:${(r.fixedContentId ?? '').trim()}:${(rep?.id ?? '').trim()}:${lt}`;
        })
        .join('|'),
    [draftByAnnualKey, programCellsByAnnualKey, syncMirrorIndicatorCellsByAnnualKey, syncMirrorView, tableRows]
  );

  const [programContentSlidePresenceKeys, setProgramContentSlidePresenceKeys] = React.useState<Set<string>>(
    () => new Set()
  );
  const slidePresenceReloadSeqRef = React.useRef(0);
  const slidePresenceReloadSigRef = React.useRef('');

  const reloadProgramContentSlidePresence = React.useCallback((options?: { forceRefresh?: boolean }) => {
    const sid = workspaceId?.trim();
    if (!sid) {
      setProgramContentSlidePresenceKeys((prev) => (prev.size === 0 ? prev : new Set()));
      return () => undefined;
    }
    const reloadSig = options?.forceRefresh
      ? `${slidePresenceReloadDepsSig}|force:${Date.now()}`
      : slidePresenceReloadDepsSig;
    if (!options?.forceRefresh && slidePresenceReloadSigRef.current === reloadSig) {
      return () => undefined;
    }
    slidePresenceReloadSigRef.current = reloadSig;

    const requestSeq = ++slidePresenceReloadSeqRef.current;
    let cancelled = false;
    const owners = tableRows.flatMap((row) => {
      const annualKey = row.annualCellKey;
      const draftLt = draftRef.current[annualKey]?.learning_text ?? '';
      const cell = programCellsByAnnualKey[annualKey];
      const cellLt = cellLearningTextForEditor(cell);
      const lt = resolveSyncMirrorLearningTextForIndicators(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draftLt,
        cellLt,
        resolveRepDraftLearningText(annualKey)
      );
      return resolveSyncMirrorSlideOwnerCandidatesForIndicator({
        syncMirrorView,
        annualKey,
        syncMirrorIndicatorCellsByAnnualKey,
        mode,
        row,
        cell,
        learningText: lt,
      });
    });
    const unique: ProgramContentSlideOwnerRef[] = [];
    const seen = new Set<string>();
    for (const o of owners) {
      const k = programContentSlidePresenceKey(o);
      if (seen.has(k)) continue;
      seen.add(k);
      unique.push(o);
    }
    void fetchProgramContentSlidePresenceKeySet(sid, unique, options)
      .then((set) => {
        if (!cancelled && requestSeq === slidePresenceReloadSeqRef.current) {
          setProgramContentSlidePresenceKeys((prev) => (areStringSetsEqual(prev, set) ? prev : set));
        }
      })
      .catch(() => {
        if (!cancelled && requestSeq === slidePresenceReloadSeqRef.current) {
          setProgramContentSlidePresenceKeys((prev) => (prev.size === 0 ? prev : new Set()));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    mode,
    programCellsByAnnualKey,
    resolveRepDraftLearningText,
    slidePresenceReloadDepsSig,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorView,
    tableRows,
    workspaceId,
  ]);

  React.useEffect(() => {
    slidePresenceReloadSigRef.current = '';
    const cleanup = reloadProgramContentSlidePresence();
    return () => cleanup();
    // reloadProgramContentSlidePresence는 slidePresenceReloadDepsSig에 묶여 있으므로 sig만 의존한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slidePresenceReloadDepsSig, workspaceId]);

  /** 슬라이드·활동지 초안(localStorage) 변경 시 학습열 인디케이터 재계산 */
  const [slideDraftRev, setSlideDraftRev] = React.useState(0);
  const [worksheetDraftRev, setWorksheetDraftRev] = React.useState(0);
  const [indicatorHintRev, setIndicatorHintRev] = React.useState(0);
  React.useEffect(() => {
    const bumpSlides = () => setSlideDraftRev((n) => n + 1);
    const bumpWorksheets = () => setWorksheetDraftRev((n) => n + 1);
    const bumpIndicatorHints = () => setIndicatorHintRev((n) => n + 1);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, bumpSlides);
    window.addEventListener(LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT, bumpWorksheets);
    window.addEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, bumpWorksheets);
    window.addEventListener(LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT, bumpIndicatorHints);
    return () => {
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT, bumpSlides);
      window.removeEventListener(LEARNING_CONTENT_WORKSHEET_DRAFTS_CHANGED_EVENT, bumpWorksheets);
      window.removeEventListener(LEARNING_WORKSHEET_PENDING_CHANGED_EVENT, bumpWorksheets);
      window.removeEventListener(LEARNING_ATTACHMENT_INDICATOR_CHANGED_EVENT, bumpIndicatorHints);
    };
  }, []);

  /** 학습내용 셀 표시(본문·note)와 ref 인디케이터가 동일한 canonical learning_text를 참조 */
  const canonicalLearningTextByAnnualKey = React.useMemo(() => {
    const out: Record<string, string> = {};
    const isPendingOrLocallyCleared = (ak: string): boolean => {
      const key = ak.trim();
      if (!key) return false;
      if (locallyClearedLearningKeys.has(key)) return true;
      return Object.values(pendingRowsRef.current).some((pending) => {
        if (saveMode === 'fixed_content') {
          return fixedContentPendingMatchesAnnualKey(
            pending as ProgramFixedContentRowUpsert,
            key,
            tableRows
          );
        }
        const r = pending as ProgramProgressCellUpsert;
        if (!r.monday_iso?.trim() || r.weekday_key == null || r.slot_index == null) return false;
        return buildAnnualCellKey(r.grade_id, r.monday_iso, r.weekday_key, r.slot_index) === key;
      });
    };
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()].trim()
          : annualKey;
      const draftLt = draftByAnnualKey[annualKey]?.learning_text ?? '';
      const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const cellLtForPatch = cellLearningTextForEditor(programCellsByAnnualKey[patchAnnualKey]) || cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      out[annualKey] = resolveProgramCanonicalLearningTextRaw({
        annualKey,
        patchAnnualKey,
        draftLearningText: draftByAnnualKey[patchAnnualKey]?.learning_text ?? draftLt,
        cellLearningText: cellLtForPatch,
        hasPendingWorkspaceSave:
          isPendingOrLocallyCleared(annualKey) || isPendingOrLocallyCleared(patchAnnualKey),
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        repDraftLearningText: resolveRepDraftLearningText(annualKey),
      });
    }
    return out;
  }, [
    draftByAnnualKey,
    locallyClearedLearningKeys,
    programCellsByAnnualKey,
    resolveRepDraftLearningText,
    saveMode,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
    syncMirrorView,
    tableRows,
  ]);
  const annualProgramDisplayOverlayByAnnualKey = React.useMemo(() => {
    const out: Record<string, ProgramProgressLikeRow> = {};
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      const draft = draftByAnnualKey[annualKey];
      const cell = programCellsByAnnualKey[annualKey];
      out[annualKey] = {
        unit_text: draft?.unit_text ?? (cell?.unit_text ?? ''),
        learning_text:
          canonicalLearningTextByAnnualKey[annualKey] ??
          draft?.learning_text ??
          cellLearningTextForEditor(cell),
        page_text: draft?.page_text ?? (cell?.page_text ?? ''),
        assistant_page_text: draft?.assistant_page_text ?? (cell?.assistant_page_text ?? ''),
        current_period_text: draft?.current_period_text ?? (cell?.current_period_text ?? ''),
        total_period_text: draft?.total_period_text ?? (cell?.total_period_text ?? ''),
        material_text: draft?.material_text ?? (cell?.material_text ?? ''),
        record_cross_curricular_text:
          draft?.record_cross_curricular_text ?? (cell?.record_cross_curricular_text ?? ''),
        record_memo_text: draft?.record_memo_text ?? (cell?.record_memo_text ?? ''),
        record_evaluation_text: draft?.record_evaluation_text ?? (cell?.record_evaluation_text ?? ''),
        record_class_text: draft?.record_class_text ?? (cell?.record_class_text ?? ''),
        record_member_group_id: draft?.record_member_group_id ?? cell?.record_member_group_id ?? null,
        bg_color_by_field: draft?.bg_color_by_field ?? cell?.bg_color_by_field ?? {},
      };
    }
    return out;
  }, [canonicalLearningTextByAnnualKey, draftByAnnualKey, programCellsByAnnualKey, tableRows]);
  React.useEffect(() => {
    const sid = workspaceId?.trim();
    const gid = progressGradeId?.trim();
    const scopeId = workspaceScopeId?.trim();
    if (!sid || !gid || !scopeId) return;
    upsertAnnualProgramDisplayOverlayForScope({
      workspaceId: sid,
      academicYear,
      termNumber,
      gradeId: gid,
      scopeId,
      byAnnualKey: annualProgramDisplayOverlayByAnnualKey,
    });
  }, [
    academicYear,
    annualProgramDisplayOverlayByAnnualKey,
    progressGradeId,
    termNumber,
    workspaceId,
    workspaceScopeId,
  ]);

  /** ref·슬라이드·draft 중 하나라도 있으면 삼각형 — ref는 슬라이드가 비어 있어도 유지 */
  const learningSlidePageIndicatorAnnualKeys = React.useMemo(() => {
    const sid = workspaceId?.trim();
    const out = new Set<string>();
    if (!sid) return out;
    const deletedHints = readLearningAttachmentIndicatorDeletedHints(sid);
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()].trim()
          : annualKey;
      const draftLt = draftByAnnualKey[annualKey]?.learning_text ?? '';
      const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const cellLtForPatch = cellLearningTextForEditor(programCellsByAnnualKey[patchAnnualKey]) || cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const indicatorDraftLearningText = resolveProgramTableIndicatorDraftLearningText({
        annualKey,
        patchAnnualKey,
        draftLearningText: draftByAnnualKey[patchAnnualKey]?.learning_text ?? draftLt,
        cellLearningText: cellLtForPatch,
      });
      if (
        computeProgramPresentationIndicatorVisible({
          workspaceId: sid,
          mode,
          syncMirrorView,
          syncMirrorIndicatorCellsByAnnualKey,
          repDraftLearningText: resolveRepDraftLearningText(annualKey),
          slidePresenceKeys: programContentSlidePresenceKeys,
          deletedHints,
          scopeId: workspaceScopeId,
          allowedScopeIds: slideDraftAllowedScopeIds,
          ctx: {
            annualKey,
            row,
            cell: programCellsByAnnualKey[annualKey],
            patchAnnualKey,
            draftLearningText: indicatorDraftLearningText,
            cellLearningText: cellLtForPatch,
            canonicalLearningText: canonicalLearningTextByAnnualKey[annualKey],
          },
        })
      ) {
        out.add(annualKey);
      }
    }
    return out;
  }, [
    canonicalLearningTextByAnnualKey,
    draftByAnnualKey,
    mode,
    programCellsByAnnualKey,
    programContentSlidePresenceKeys,
    resolveRepDraftLearningText,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
    syncMirrorView,
    workspaceId,
    workspaceScopeId,
    slideDraftAllowedScopeIds,
    slideDraftRev,
    indicatorHintRev,
    tableRows,
  ]);

  const learningWorksheetIndicatorAnnualKeys = React.useMemo(() => {
    const sid = workspaceId?.trim() ?? '';
    const out = new Set<string>();
    const deletedHints = sid ? readLearningAttachmentIndicatorDeletedHints(sid) : {};
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()].trim()
          : annualKey;
      const draftLt = draftByAnnualKey[annualKey]?.learning_text ?? '';
      const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const cellLtForPatch = cellLearningTextForEditor(programCellsByAnnualKey[patchAnnualKey]) || cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const indicatorDraftLearningText = resolveProgramTableIndicatorDraftLearningText({
        annualKey,
        patchAnnualKey,
        draftLearningText: draftByAnnualKey[patchAnnualKey]?.learning_text ?? draftLt,
        cellLearningText: cellLtForPatch,
      });
      if (
        computeProgramWorksheetIndicatorVisible({
          workspaceId: sid,
          syncMirrorView,
          syncMirrorRepAnnualCellKeyByMirrorKey,
          syncMirrorIndicatorCellsByAnnualKey,
          repDraftLearningText: resolveRepDraftLearningText(annualKey),
          worksheetPresenceRefIds,
          deletedHints,
          ctx: {
            annualKey,
            row,
            cell: programCellsByAnnualKey[annualKey],
            patchAnnualKey,
            draftLearningText: indicatorDraftLearningText,
            cellLearningText: cellLtForPatch,
            canonicalLearningText: canonicalLearningTextByAnnualKey[annualKey],
          },
        })
      ) {
        out.add(annualKey);
      }
    }
    return out;
  }, [
    canonicalLearningTextByAnnualKey,
    draftByAnnualKey,
    programCellsByAnnualKey,
    resolveRepDraftLearningText,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
    syncMirrorView,
    tableRows,
    worksheetDraftRev,
    indicatorHintRev,
    worksheetPresenceRefIds,
    workspaceId,
  ]);

  const worksheetPresenceReloadDepsSig = React.useMemo(
    () =>
      tableRows
        .map((r) => {
          const draftLt = draftByAnnualKey[r.annualCellKey]?.learning_text ?? '';
          const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[r.annualCellKey]);
          const lt = resolveSyncMirrorLearningTextForIndicators(
            r.annualCellKey,
            syncMirrorView,
            syncMirrorIndicatorCellsByAnnualKey,
            draftLt,
            cellLt,
            resolveRepDraftLearningText(r.annualCellKey)
          );
          return `${r.annualCellKey}:${resolveWorksheetRefIdFromLearningText(lt)}`;
        })
        .join('|'),
    [
      draftByAnnualKey,
      programCellsByAnnualKey,
      resolveRepDraftLearningText,
      syncMirrorIndicatorCellsByAnnualKey,
      syncMirrorView,
      tableRows,
      worksheetDraftRev,
    ]
  );

  React.useEffect(() => {
    const sid = workspaceId?.trim();
    if (!sid) {
      setWorksheetPresenceRefIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    const refIds = tableRows
      .map((row) =>
        resolveSyncMirrorWorksheetRefIdForIndicator(
          row.annualCellKey,
          syncMirrorView,
          syncMirrorIndicatorCellsByAnnualKey,
          draftByAnnualKey[row.annualCellKey]?.learning_text ?? '',
          cellLearningTextForEditor(programCellsByAnnualKey[row.annualCellKey]),
          resolveRepDraftLearningText(row.annualCellKey),
          programCellsByAnnualKey[row.annualCellKey]
        )
      )
      .filter(Boolean) as string[];
    if (!refIds.length) {
      setWorksheetPresenceRefIds((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    let cancelled = false;
    setWorksheetPresenceLoading(true);
    void fetchProgramContentWorksheetPresence(sid, refIds)
      .then((presence) => {
        if (!cancelled) {
          setWorksheetPresenceRefIds((prev) => (areStringSetsEqual(prev, presence) ? prev : presence));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWorksheetPresenceRefIds((prev) => (prev.size === 0 ? prev : new Set()));
        }
      })
      .finally(() => {
        if (!cancelled) setWorksheetPresenceLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [worksheetPresenceReloadDepsSig, workspaceId]);

  const worksheetListItems = React.useMemo(() => {
    const sid = workspaceId?.trim() ?? '';
    const items: ProgramWorksheetListItem[] = [];
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      if (!learningWorksheetIndicatorAnnualKeys.has(annualKey)) continue;
      const draft = draftByAnnualKey[annualKey];
      const learningText = draft?.learning_text ?? cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      let refId = resolveSyncMirrorWorksheetRefIdForIndicator(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draft?.learning_text ?? '',
        cellLearningTextForEditor(programCellsByAnnualKey[annualKey]),
        resolveRepDraftLearningText(annualKey),
        programCellsByAnnualKey[annualKey]
      );
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );
      if (!refId && sid) refId = resolvePendingWorksheetRefIdForAnnualKeys(sid, annualKey, draftRowKey);
      const hasDb = !!refId && worksheetPresenceRefIds.has(refId);
      const hasLocalDraft = !!loadLearningContentWorksheetDraft(draftRowKey);
      const hasPending =
        (!!refId && !!sid && hasPendingLearningWorksheetFlush(sid, refId)) ||
        (!!sid && hasPendingLearningWorksheetFlushForAnnualKeys(sid, annualKey, draftRowKey));
      const like: ProgramProgressLikeRow = {
        unit_text: draft?.unit_text ?? '',
        learning_text: learningText,
        page_text: draft?.page_text ?? '',
        assistant_page_text: draft?.assistant_page_text ?? '',
        current_period_text: draft?.current_period_text ?? '',
        total_period_text: draft?.total_period_text ?? '',
        material_text: draft?.material_text ?? '',
        record_cross_curricular_text: draft?.record_cross_curricular_text ?? '',
        record_memo_text: draft?.record_memo_text ?? '',
        record_evaluation_text: draft?.record_evaluation_text ?? '',
        record_class_text: draft?.record_class_text ?? '',
        record_member_group_id: draft?.record_member_group_id ?? null,
      };
      const d = mapProgressLikeToDisplayFields(like);
      items.push(
        buildWorksheetListItemFromAnnualRow({
          annualCellKey: annualKey,
          dateLabel: row.dateLabel,
          weekdayLabel: row.weekdayLabel,
          periodLabel: row.periodLabel,
          templateLabel: row.templateLabel,
          timetableText: row.timetableText,
          unitText: d.progressUnit,
          learningTextRaw: learningText,
          sortKey: `${row.mondayIso}|${row.weekdayKey}|${String(row.slotIndex).padStart(2, '0')}|${String(row.fixedOrderIndex ?? 0).padStart(5, '0')}`,
          isDraftOnly: !hasDb && (hasLocalDraft || hasPending),
          hasLocalDraft,
          hasDbDocument: hasDb,
          draftStorageRowKey: draftRowKey,
          isSyncMirror: syncMirrorView,
        })
      );
    }
    return sortWorksheetListItems(items);
  }, [
    draftByAnnualKey,
    learningWorksheetIndicatorAnnualKeys,
    programCellsByAnnualKey,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorRepAnnualCellKeyByMirrorKey,
    syncMirrorView,
    tableRows,
    worksheetDraftRev,
    worksheetPresenceRefIds,
  ]);

  const presentationListItems = React.useMemo(() => {
    const sid = workspaceId?.trim() ?? '';
    const items: ProgramPresentationListItem[] = [];
    for (const row of tableRows) {
      const annualKey = row.annualCellKey;
      if (!learningSlidePageIndicatorAnnualKeys.has(annualKey)) continue;
      const draft = draftByAnnualKey[annualKey];
      const learningText = draft?.learning_text ?? cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const cell = programCellsByAnnualKey[annualKey];
      const lt = resolveSyncMirrorLearningTextForIndicators(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draft?.learning_text ?? '',
        cell?.learning_text ?? '',
        resolveRepDraftLearningText(annualKey)
      );
      const candidates = resolveSyncMirrorSlideOwnerCandidatesForIndicator({
        syncMirrorView,
        annualKey,
        syncMirrorIndicatorCellsByAnnualKey,
        mode,
        row,
        cell,
        learningText: lt,
      });
      const hasDb = candidates.some((o) =>
        programContentSlidePresenceKeys.has(programContentSlidePresenceKey(o))
      );
      const slideOwner = resolvePresentationListSlideOwner({
        candidates,
        presenceKeys: programContentSlidePresenceKeys,
        fallbackOwner: resolveProgramOpenSlideDbOwner(mode, row, cell, lt),
      });
      const isDraftOnly =
        !hasDb && !!sid && candidates.some((o) => hasProgramContentSlideDraftForOwner(sid, o));
      const like: ProgramProgressLikeRow = {
        unit_text: draft?.unit_text ?? '',
        learning_text: learningText,
        page_text: draft?.page_text ?? '',
        assistant_page_text: draft?.assistant_page_text ?? '',
        current_period_text: draft?.current_period_text ?? '',
        total_period_text: draft?.total_period_text ?? '',
        material_text: draft?.material_text ?? '',
        record_cross_curricular_text: draft?.record_cross_curricular_text ?? '',
        record_memo_text: draft?.record_memo_text ?? '',
        record_evaluation_text: draft?.record_evaluation_text ?? '',
        record_class_text: draft?.record_class_text ?? '',
        record_member_group_id: draft?.record_member_group_id ?? null,
      };
      const d = mapProgressLikeToDisplayFields(like);
      items.push({
        annualCellKey: annualKey,
        dateLabel: row.dateLabel,
        weekdayLabel: row.weekdayLabel,
        periodLabel: row.periodLabel,
        templateLabel: row.templateLabel,
        timetableText: row.timetableText,
        unitText: d.progressUnit,
        learningText: d.progressLearning,
        sortKey: `${row.mondayIso}|${row.weekdayKey}|${String(row.slotIndex).padStart(2, '0')}|${String(row.fixedOrderIndex ?? 0).padStart(5, '0')}`,
        slideOwner,
        isDraftOnly,
        hasPresentationRef: !!resolvePresentationRefIdFromLearningText(lt),
        slideOwnerCandidates: candidates,
      });
    }
    return sortPresentationListItems(items);
  }, [
    draftByAnnualKey,
    learningSlidePageIndicatorAnnualKeys,
    mode,
    programCellsByAnnualKey,
    programContentSlidePresenceKeys,
    syncMirrorIndicatorCellsByAnnualKey,
    syncMirrorView,
    tableRows,
    workspaceId,
  ]);

  const presentationListItemsMerged = React.useMemo(() => {
    const cellById = new Map<string, ProgramProgressCell>();
    Object.values(programCellsByAnnualKey).forEach((cell) => {
      if (cell?.id?.trim()) cellById.set(cell.id, cell);
    });
    return mergeOrphanPresentationListItems(presentationListItems, orphanPresentationEntries, {
      cellById,
      periodLabelForSlot: (slotIndex) => {
        const hit = tableRows.find((row) => row.slotIndex === slotIndex);
        return hit?.periodLabel || `${slotIndex + 1}타임`;
      },
      weekdayLabelForKey: (weekdayKey) => {
        const hit = tableRows.find((row) => row.weekdayKey === weekdayKey);
        return hit?.weekdayLabel || weekdayKey;
      },
      shortDateLabelForIso: (iso) => {
        const hit = tableRows.find((row) => (row.mondayIso ?? '').trim() === iso.trim());
        return hit?.dateLabel || iso;
      },
    });
  }, [orphanPresentationEntries, presentationListItems, programCellsByAnnualKey, tableRows]);

  /** 삼각형 표시와 동일 조건(학습내용 셀에 프레젠테이션·슬라이드 연결 여부) */
  const learningTextAnnualKeyHasPresentationUi = React.useCallback(
    (annualKey: string): boolean => {
      const sid = workspaceId?.trim();
      if (!sid) return false;
      const row = tableRows.find((r) => r.annualCellKey === annualKey);
      if (!row) return false;
      const draftLt = draftRef.current[annualKey]?.learning_text ?? '';
      const cellLt = cellLearningTextForEditor(programCellsByAnnualKey[annualKey]);
      const lt = resolveSyncMirrorLearningTextForIndicators(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draftLt,
        cellLt,
        resolveRepDraftLearningText(annualKey)
      );
      const candidates = resolveSyncMirrorSlideOwnerCandidatesForIndicator({
        syncMirrorView,
        annualKey,
        syncMirrorIndicatorCellsByAnnualKey,
        mode,
        row,
        cell: programCellsByAnnualKey[annualKey],
        learningText: lt,
      });
      if (resolvePresentationRefIdFromLearningText(lt)) return true;
      const ownerKeys = candidates.map((o) => programContentSlidePresenceKey(o));
      const statsMap = readProgramPresentationListStatsCache(sid, ownerKeys);
      if (ownerKeys.some((key) => (statsMap.get(key)?.pageCount ?? 0) > 0)) return true;
      if (candidates.some((o) => programContentSlidePresenceKeys.has(programContentSlidePresenceKey(o)))) return true;
      if (candidates.some((o) => programContentSlideOwnerHasMeaningfulExtraPageDraft(sid, o))) return true;
      if (candidates.some((o) => hasProgramContentSlideDraftForOwner(sid, o))) return true;
      return false;
    },
    [mode, programCellsByAnnualKey, programContentSlidePresenceKeys, syncMirrorIndicatorCellsByAnnualKey, syncMirrorView, workspaceId, tableRows]
  );

  const [selectedCells, setSelectedCells] = React.useState<string[]>([]);
  /** «오늘 학습내용» 버튼만: 연속 클릭 시 오늘 교시 순환(단축키는 시각 고정이라 이 값을 비움). */
  const [lastTodaySelectionKey, setLastTodaySelectionKey] = React.useState<string | null>(null);
  /** 연간진도표와 동일: «내일 학습내용 보기» 반복 시 대상(내일/가장 가까운 미래) 행 교시 순환 */
  const [lastTomorrowSelectionKey, setLastTomorrowSelectionKey] = React.useState<string | null>(null);
  const selectedCellsRef = React.useRef<string[]>([]);
  const pendingStructuredSelectionRef = React.useRef<{ annualKey?: string; rowIndex?: number } | null>(null);
  const pendingDuplicateTimetableCopyRef = React.useRef<{ annualKey: string; value: string } | null>(null);
  const tempFrameIdSeqRef = React.useRef(0);
  React.useEffect(() => {
    selectedCellsRef.current = selectedCells;
  }, [selectedCells]);
  const pointerDownRef = React.useRef<{
    cellId: string;
    annualKey: string;
    fieldId: FieldId;
    didDrag: boolean;
    openedByLongPress: boolean;
  } | null>(null);
  /** Shift+클릭 범위 선택의 기준 셀(마지막 일반 클릭·드래그 시작 셀) */
  const shiftSelectionAnchorCellIdRef = React.useRef<string | null>(null);
  /** 드래그 중 문서 mousemove 제거용 */
  const cellDragDocCleanupRef = React.useRef<(() => void) | null>(null);
  const longPressTimerRef = React.useRef<number | null>(null);
  /** 평가 셀 길게 누름 직후 mouseup 이 상태 드롭다운을 다시 열지 않도록(React setState 레이스 방지) */
  const evalLongPressSuppressClickRef = React.useRef(false);
  const [editingCell, setEditingCell] = React.useState<{
    annualKey: string;
    fieldId: FieldId;
    draft: string;
    /** 표시 모드에서 측정한 font-size(px). 편집 input이 표시보다 작아지지 않게 전달 */
    displayFontSizePx?: number;
  } | null>(null);
  /** keydown 리스너는 effect 클로저의 editingCell 이 한 박자 늦을 수 있음 → 단축키 붙여넣기가 막히거나 잘못 분기되는 것 방지 */
  const editingCellRef = React.useRef<typeof editingCell>(null);
  editingCellRef.current = editingCell;
  const beginEditRef = React.useRef<((annualKey: string, fieldId: FieldId, seed?: string) => void) | null>(null);
  const editingInputRef = React.useRef<HTMLInputElement | null>(null);
  /** mousedown에서 commitEditingNow로 이미 반영된 뒤 같은 틱 blur의 commitEditing이 한 번 더 도는 것 차단 */
  const blurCommitGuardCellRef = React.useRef<string | null>(null);
  const isComposingRef = React.useRef(false);
  const tableWrapRef = React.useRef<HTMLDivElement | null>(null);

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  type LocalEditHistoryFrame = { drafts: Record<string, DraftRow>; pending: Record<string, PendingSaveRow> };
  const LOCAL_EDIT_HISTORY_MAX = 50;
  const localUndoStackRef = React.useRef<LocalEditHistoryFrame[]>([]);
  const localRedoStackRef = React.useRef<LocalEditHistoryFrame[]>([]);
  const applyingLocalEditHistoryRef = React.useRef(false);
  const tryLocalUndoRef = React.useRef<() => boolean>(() => false);
  const tryLocalRedoRef = React.useRef<() => boolean>(() => false);
  const lastUndoRedoNotifyRef = React.useRef<{ canUndo: boolean; canRedo: boolean } | null>(null);
  const notifyProgramUndoRedoAvailabilityFromStacks = React.useCallback(() => {
    const canUndo = localUndoStackRef.current.length > 0;
    const canRedo = localRedoStackRef.current.length > 0;
    const last = lastUndoRedoNotifyRef.current;
    if (last && last.canUndo === canUndo && last.canRedo === canRedo) return;
    lastUndoRedoNotifyRef.current = { canUndo, canRedo };
    onProgramUndoRedoAvailabilityChangeRef.current?.({ canUndo, canRedo });
  }, []);
  notifyUndoRedoStacksRef.current = notifyProgramUndoRedoAvailabilityFromStacks;
  const copiedRef = React.useRef<{
    cells: {
      rowOffset: number;
      colOffset: number;
      fieldId: FieldId;
      value: string;
      bgColor?: string;
      recordMemberGroupId?: string | null;
      slideCopyFrom?: { kind: ProgramContentSlideOwnerRef['kind']; id: string };
    }[];
    isCut: boolean;
    sourceCellIds: string[];
    sourceScopeId: string;
  } | null>(null);
  const learningPresentationClipboardRef = React.useRef<{
    owner: ProgramContentSlideOwnerRef;
  } | null>(null);
  const [hasLearningPresentationClipboard, setHasLearningPresentationClipboard] = React.useState(false);
  const learningWorksheetClipboardRef = React.useRef<{
    worksheetRefId: string | null;
    draftRowKey: string | null;
  } | null>(null);
  const [hasLearningWorksheetClipboard, setHasLearningWorksheetClipboard] = React.useState(false);
  /** 앱에서 복사·잘라내기 시 클립보드에 쓴 TSV(정규화) — 붙여넣기 시 엑셀 내용과 구분 */
  const lastInternalClipNormalizedRef = React.useRef<string | null>(null);

  const parseCellId = React.useCallback((cellId: string) => {
    const at = cellId.indexOf('::');
    if (at < 0) return null;
    const annualKey = cellId.slice(0, at);
    const fieldId = cellId.slice(at + 2) as FieldId;
    return { annualKey, fieldId };
  }, []);

  const makeCellId = React.useCallback((annualKey: string, fieldId: FieldId) => `${annualKey}::${fieldId}`, []);

  React.useEffect(() => {
    const saved = loadProgramWorkspaceClipboard();
    if (!saved?.cells?.length) return;
    copiedRef.current = {
      cells: saved.cells as Array<{
        rowOffset: number;
        colOffset: number;
        fieldId: FieldId;
        value: string;
        bgColor?: string;
        recordMemberGroupId?: string | null;
        slideCopyFrom?: { kind: ProgramContentSlideOwnerRef['kind']; id: string };
      }>,
      isCut: !!saved.isCut,
      sourceCellIds: Array.isArray(saved.sourceCellIds) ? saved.sourceCellIds : [],
      sourceScopeId: saved.sourceScopeId ?? '',
    };
  }, []);

  const rowIdxByAnnualKey = React.useMemo(
    () => Object.fromEntries(tableRows.map((row, idx) => [row.annualCellKey, idx] as const)),
    [tableRows]
  );
  const colIdxByField = React.useMemo(
    () => Object.fromEntries(editableFieldOrder.map((field, idx) => [field, idx] as const)),
    [editableFieldOrder]
  );
  const applyContentRowSelection = React.useCallback(
    (annualKey: string | null) => {
      const fields = getContentColumnFieldsForSelection();
      if (!annualKey || !fields.length) {
        setSelectedCells([]);
        selectedCellsRef.current = [];
        return;
      }
      const next = fields.map((fieldId) => makeCellId(annualKey, fieldId));
      setSelectedCells(next);
      selectedCellsRef.current = next;
    },
    [getContentColumnFieldsForSelection, makeCellId]
  );
  const applyContentRowsSelectionByIndices = React.useCallback(
    (rowIndices: number[]) => {
      const fields = getContentColumnFieldsForSelection();
      const uniq = Array.from(new Set(rowIndices))
        .filter((idx) => idx >= 0 && idx < tableRows.length)
        .sort((a, b) => a - b);
      if (!uniq.length || !fields.length) {
        setSelectedCells([]);
        selectedCellsRef.current = [];
        return;
      }
      const next = uniq.flatMap((idx) => {
        const annualKey = tableRows[idx]?.annualCellKey;
        return annualKey ? fields.map((fieldId) => makeCellId(annualKey, fieldId)) : [];
      });
      setSelectedCells(next);
      selectedCellsRef.current = next;
    },
    [getContentColumnFieldsForSelection, makeCellId, tableRows]
  );
  React.useEffect(() => {
    const pending = pendingStructuredSelectionRef.current;
    if (!pending) return;
    const annualKey =
      pending.annualKey ??
      (pending.rowIndex !== undefined
        ? tableRows[Math.max(0, Math.min(tableRows.length - 1, pending.rowIndex))]?.annualCellKey ?? null
        : null);
    if (!annualKey) return;
    const exists = tableRows.some((row) => row.annualCellKey === annualKey);
    if (!exists) return;
    pendingStructuredSelectionRef.current = null;
    applyContentRowSelection(annualKey);
  }, [applyContentRowSelection, tableRows]);
  const isSelectedCell = React.useCallback(
    (annualKey: string, fieldId: FieldId) => selectedCells.includes(makeCellId(annualKey, fieldId)),
    [makeCellId, selectedCells]
  );
  const selectedCellSet = React.useMemo(() => new Set(selectedCells), [selectedCells]);
  const contentColumnFieldSet = React.useMemo(
    () => new Set(getContentColumnFieldsForSelection()),
    [getContentColumnFieldsForSelection]
  );
  const contextWashRowAnnualKeys = React.useMemo(() => {
    if (selectedCells.length === 0) return null;
    const isTrackProgram = mode !== 'project_to_template' && mode !== 'fixed_to_unit';
    if (!isTrackProgram && selectedCells.length !== 1) return null;
    const keys = new Set<string>();
    for (const cellId of selectedCells) {
      const annualKey = parseCellId(cellId)?.annualKey;
      if (annualKey) keys.add(annualKey);
    }
    return keys.size ? keys : null;
  }, [mode, parseCellId, selectedCells]);
  const isContentRowContextWash = React.useCallback(
    (annualKey: string, fieldId: FieldId, selected: boolean) => {
      if (selected || !contextWashRowAnnualKeys) return false;
      if (!contextWashRowAnnualKeys.has(annualKey)) return false;
      return contentColumnFieldSet.has(fieldId);
    },
    [contentColumnFieldSet, contextWashRowAnnualKeys]
  );
  /** 선택 행의 프로그램 일정 열(순~구분·시간표)에도 동일 회색 워시 */
  const isScheduleRowContextWash = React.useCallback(
    (annualKey: string) => Boolean(contextWashRowAnnualKeys?.has(annualKey)),
    [contextWashRowAnnualKeys]
  );
  const canMutateCellSelection = React.useCallback(
    (cellIds: string[]) => {
      if (readOnly) return false;
      if (!cellIds.length) return false;
      if (!restrictMutatingActions) return true;
      return cellIds.every((cellId) => {
        const parsed = parseCellId(cellId);
        return !!parsed && !isFieldLocked(parsed.fieldId);
      });
    },
    [isFieldLocked, parseCellId, readOnly, restrictMutatingActions]
  );
  const canMutateSelectedCells = React.useMemo(
    () => canMutateCellSelection(selectedCells),
    [canMutateCellSelection, selectedCells]
  );
  React.useEffect(() => {
    if (!onSelectedAnnualCellKeyChange) return;
    const one = selectedCells[0];
    if (!one) {
      onSelectedAnnualCellKeyChange(null);
      return;
    }
    const parsed = parseCellId(one);
    onSelectedAnnualCellKeyChange(parsed?.annualKey ?? null);
  }, [onSelectedAnnualCellKeyChange, parseCellId, selectedCells]);
  const focusHandledKeyRef = React.useRef<string | null>(null);
  /** focusAnnualCellKey가 같을 때 rowIdx 맵만 갱신되면(셀 편집 커밋 등) 전 행 선택을 다시 덮어쓰지 않도록 함 */
  const focusSelectionFingerprintRef = React.useRef<string | null>(null);
  const focusScrollTokenRef = React.useRef(0);
  /** URL 진입 스크롤 완료 키 — 성공 시에만 기록(행 구조·annualKey 기준). rowIdx 참조 변경만으로는 재시도 안 함 */
  const focusScrollDoneForKeyRef = React.useRef<string | null>(null);
  const focusScrollAttemptCapRef = React.useRef(72);
  React.useEffect(() => {
    const annualKey = (focusAnnualCellKey ?? '').trim();
    if (!annualKey) {
      focusHandledKeyRef.current = null;
      focusSelectionFingerprintRef.current = null;
      focusScrollDoneForKeyRef.current = null;
      return;
    }
    const rowIdx = rowIdxByAnnualKey[annualKey];
    if (!rowIdx && rowIdx !== 0) return;
    const focusFields = getContentColumnFieldsForSelection();
    if (!focusFields.length) return;
    const scrollTargetKey = `${annualKey}\u0001${rowsStructureKey}`;
    const selectionFingerprint = `${annualKey}\t${focusFields.join(',')}`;
    if (focusSelectionFingerprintRef.current !== selectionFingerprint) {
      focusSelectionFingerprintRef.current = selectionFingerprint;
      focusHandledKeyRef.current = annualKey;
      const nextSelected = focusFields.map((fieldId) => makeCellId(annualKey, fieldId));
      const hasSameSelection =
        selectedCells.length === nextSelected.length &&
        nextSelected.every((cellId) => selectedCellSet.has(cellId));
      if (!hasSameSelection) {
        setSelectedCells(nextSelected);
      }
    }
    if (focusScrollDoneForKeyRef.current === scrollTargetKey) return;

    const scrollToken = focusScrollTokenRef.current + 1;
    focusScrollTokenRef.current = scrollToken;
    const anchorFieldId = focusFields.includes('learning_text') ? 'learning_text' : focusFields[0];
    const anchorKey = `${annualKey}\t${anchorFieldId}`;
    const rowIdxStatic = rowIdx;

    const measureContentMidY = (wrap: HTMLElement, headerHeight: number) => {
      const wrapRect = wrap.getBoundingClientRect();
      const thead = wrap.querySelector('table thead');
      const headBottomMeasured = thead instanceof HTMLElement ? thead.getBoundingClientRect().bottom : wrapRect.top + headerHeight;
      const headBottomFromWrap = wrapRect.top + headerHeight;
      const headBottom = Math.max(headBottomMeasured, headBottomFromWrap);
      return headBottom + (wrapRect.bottom - headBottom) / 2;
    };

    /** 연간진도표·프로그램 표 진입: sticky thead 아래 가시 영역 중앙에 행을 맞춤 */
    const tryScrollIntoCenter = (attempt: number) => {
      if (focusScrollTokenRef.current !== scrollToken) return;
      if (attempt > focusScrollAttemptCapRef.current) return;
      const el = cellAnchorByKeyRef.current.get(anchorKey);
      const wrap = programProgressScrollRef.current;
      if (!el || !wrap) {
        if (attempt < focusScrollAttemptCapRef.current) {
          window.requestAnimationFrame(() => tryScrollIntoCenter(attempt + 1));
        }
        return;
      }
      const thead = wrap.querySelector('table thead');
      const H = thead instanceof HTMLElement ? thead.offsetHeight : 0;
      const V = wrap.clientHeight;
      if (V <= 0) {
        if (attempt < focusScrollAttemptCapRef.current) {
          window.requestAnimationFrame(() => tryScrollIntoCenter(attempt + 1));
        }
        return;
      }
      const rowEl = el.closest('tr');
      const rowHeight = rowEl instanceof HTMLElement ? rowEl.offsetHeight : 32;
      const minNeededScrollHeight = H + (rowIdxStatic + 1) * Math.max(rowHeight, 1);
      if (wrap.scrollHeight + 8 < minNeededScrollHeight && attempt < focusScrollAttemptCapRef.current) {
        window.requestAnimationFrame(() => tryScrollIntoCenter(attempt + 1));
        return;
      }
      const maxTop = Math.max(0, wrap.scrollHeight - V);
      const elRect = el.getBoundingClientRect();
      const elMidY = elRect.top + elRect.height / 2;
      const midY = measureContentMidY(wrap, H);
      const err = elMidY - midY;
      if (Math.abs(err) >= 1.5) {
        wrap.scrollTop = Math.min(maxTop, Math.max(0, wrap.scrollTop + err));
        if (attempt < focusScrollAttemptCapRef.current) {
          window.requestAnimationFrame(() => tryScrollIntoCenter(attempt + 1));
        }
        return;
      }
      focusScrollDoneForKeyRef.current = scrollTargetKey;
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => tryScrollIntoCenter(0));
    });

    return () => {
      focusScrollTokenRef.current += 1;
    };
  }, [
    focusAnnualCellKey,
    getContentColumnFieldsForSelection,
    makeCellId,
    rowsStructureKey,
    tableRows.length,
  ]);
  /** 복사/잘라내기 원본 영역(연간시간표와 동일한 점선 표시) */
  const [copiedSourceRegion, setCopiedSourceRegion] = React.useState<string[] | null>(null);
  const copiedSourceSet = React.useMemo(() => new Set(copiedSourceRegion ?? []), [copiedSourceRegion]);
  const [pasteColumnMismatchToast, setPasteColumnMismatchToast] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!pasteColumnMismatchToast) return;
    const t = window.setTimeout(() => setPasteColumnMismatchToast(null), 4200);
    return () => window.clearTimeout(t);
  }, [pasteColumnMismatchToast]);
  const [mergeTimetableMismatchToast, setMergeTimetableMismatchToast] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!mergeTimetableMismatchToast) return;
    const t = window.setTimeout(() => setMergeTimetableMismatchToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [mergeTimetableMismatchToast]);
  const [projectTimetableSyncToast, setProjectTimetableSyncToast] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!projectTimetableSyncToast) return;
    const t = window.setTimeout(() => setProjectTimetableSyncToast(null), 2600);
    return () => window.clearTimeout(t);
  }, [projectTimetableSyncToast]);
  const [learningSlideDeleteToast, setLearningSlideDeleteToast] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!learningSlideDeleteToast) return;
    const t = window.setTimeout(() => setLearningSlideDeleteToast(null), 3200);
    return () => window.clearTimeout(t);
  }, [learningSlideDeleteToast]);
  const [learningBackspaceDeleteConfirmOpen, setLearningBackspaceDeleteConfirmOpen] = React.useState(false);
  const learningBackspaceDeleteConfirmOpenRef = React.useRef(false);
  React.useEffect(() => {
    learningBackspaceDeleteConfirmOpenRef.current = learningBackspaceDeleteConfirmOpen;
  }, [learningBackspaceDeleteConfirmOpen]);
  const pendingBackspaceDeletePatchesRef = React.useRef<
    Array<{ annualKey: string; fieldId: FieldId; value?: string; bgColor?: string | null }>
  >([]);
  const [projectTimetableSyncBusy, setProjectTimetableSyncBusy] = React.useState<null | 'import' | 'export'>(null);
  const [projectTimetableRenameModalOpen, setProjectTimetableRenameModalOpen] = React.useState(false);
  const [projectTimetableRenameNames, setProjectTimetableRenameNames] = React.useState<string[]>([]);
  const [slideEditStructureBlockedModalOpen, setSlideEditStructureBlockedModalOpen] = React.useState(false);
  const selectedAnnualKeys = React.useMemo(() => {
    const out: string[] = [];
    selectedCells.forEach((cellId) => {
      const parsed = parseCellId(cellId);
      if (!parsed) return;
      out.push(parsed.annualKey);
    });
    return Array.from(new Set(out));
  }, [parseCellId, selectedCells]);
  const navigate = useNavigate();
  const { programProgressDirty, programPendingLeaveTo, pendingLeaveTo, setProgramPendingLeaveTo } =
    React.useContext(GradeTrackLeaveContext);
  const leaveSaveModalOpen =
    programPendingLeaveTo != null || pendingLeaveTo != null;
  const selectedAnnualKeyForAnnualProgressBrowse = React.useMemo(() => {
    const one = selectedCells[0];
    if (!one) return null;
    const parsed = parseCellId(one);
    if (!parsed?.annualKey) return null;
    return parsed.annualKey;
  }, [parseCellId, selectedCells]);
  const annualProgressBrowseTarget = React.useMemo(() => {
    if (!selectedAnnualKeyForAnnualProgressBrowse) return null;
    const row = rowByAnnualKey[selectedAnnualKeyForAnnualProgressBrowse];
    if (!row) return null;
    const gradeId = row.gradeId || progressGradeId;
    if (!gradeId) return null;
    /** 표 행 키 = 순서 매칭으로 짝지어진 실제 시간표 칸(annualCellKey). 짝 없는 행(orphan)은 탐색 불가 */
    return buildAnnualProgressBrowsePath({
      annualCellKey: row.annualCellKey || selectedAnnualKeyForAnnualProgressBrowse,
      isScheduleAvailable: row.isScheduleAvailable,
      termNumber,
      gradeId,
      contentRowId: row.fixedContentId ?? null,
    });
  }, [progressGradeId, rowByAnnualKey, selectedAnnualKeyForAnnualProgressBrowse, termNumber]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (leaveSaveModalOpen) return;
      if (isAppBrowseNavigationShortcutBlockedRoute(window.location.pathname)) return;
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyF') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      const target = annualProgressBrowseTarget;
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      const toPath = target.split('?')[0] ?? '';
      if (isProgramProgressFamilyLeaveDirty(programProgressDirty) && !isProgramProgressFamilyRoute(toPath)) {
        setProgramPendingLeaveTo(target);
        return;
      }
      markAnnualEntryFocusClick();
      navigate(target);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [annualProgressBrowseTarget, leaveSaveModalOpen, navigate, programProgressDirty, setProgramPendingLeaveTo]);

  const getSelectedRowIndexSet = React.useCallback(
    (cellIds: string[]) => {
      const out = new Set<number>();
      cellIds.forEach((cellId) => {
        const parsed = parseCellId(cellId);
        if (!parsed) return;
        const rowIdx = rowIdxByAnnualKey[parsed.annualKey];
        if (Number.isInteger(rowIdx)) out.add(rowIdx);
      });
      return out;
    },
    [parseCellId, rowIdxByAnnualKey]
  );
  const selectedRowIndexSet = React.useMemo(() => getSelectedRowIndexSet(selectedCells), [getSelectedRowIndexSet, selectedCells]);
  const canMoveSelectedRowsUp = React.useMemo(
    () => Array.from(selectedRowIndexSet).some((rowIdx) => rowIdx > 0 && !selectedRowIndexSet.has(rowIdx - 1)),
    [selectedRowIndexSet]
  );
  const canMoveSelectedRowsDown = React.useMemo(
    () => Array.from(selectedRowIndexSet).some((rowIdx) => rowIdx < tableRows.length - 1 && !selectedRowIndexSet.has(rowIdx + 1)),
    [tableRows.length, selectedRowIndexSet]
  );
  const [slideEditLockRev, setSlideEditLockRev] = React.useState(0);
  const slideEditStructureBlocked = React.useMemo(() => {
    const sid = workspaceId?.trim();
    const sc = workspaceScopeId?.trim();
    if (!sid || !sc) return false;
    return isProgramSlideEditScopeLocked(sid, sc);
  }, [workspaceId, workspaceScopeId, slideEditLockRev]);

  const [presentationLockedAnnualCellKeys, setPresentationLockedAnnualCellKeys] = React.useState<Set<string>>(
    () => new Set()
  );

  const refreshPresentationLockedAnnualCellKeys = React.useCallback(() => {
    const sid = workspaceId?.trim();
    if (!sid) {
      setPresentationLockedAnnualCellKeys(new Set());
      return;
    }
    setPresentationLockedAnnualCellKeys(new Set(getPresentationOverlayAnnualCellKeys(sid)));
  }, [workspaceId]);

  React.useEffect(() => {
    refreshPresentationLockedAnnualCellKeys();
  }, [refreshPresentationLockedAnnualCellKeys, slideEditLockRev]);

  React.useEffect(() => {
    const sid = workspaceId?.trim();
    if (!sid) return;
    const id = window.setInterval(() => refreshPresentationLockedAnnualCellKeys(), 20_000);
    return () => window.clearInterval(id);
  }, [refreshPresentationLockedAnnualCellKeys, workspaceId]);

  React.useEffect(() => {
    const sid = workspaceId?.trim();
    const sc = workspaceScopeId?.trim();
    if (!sid || !sc) return;
    const id = window.setInterval(() => {
      if (pruneStaleProgramSlideEditScopeLock(sid, sc)) setSlideEditLockRev((x) => x + 1);
    }, 20_000);
    return () => window.clearInterval(id);
  }, [workspaceId, workspaceScopeId]);

  React.useEffect(() => {
    const bump = () => setSlideEditLockRev((x) => x + 1);
    const lockPrefix = programSlideEditScopeLockStorageKeyPrefix();
    const overlayPrefix = programSlidePresentationOverlayStorageKeyPrefix();
    const onStorage = (e: StorageEvent) => {
      if (!e.key) return;
      if (e.key.startsWith(lockPrefix) || e.key.startsWith(overlayPrefix)) bump();
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', bump);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', bump);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const notifySlideEditStructureBlocked = React.useCallback(() => {
    const sid = workspaceId?.trim();
    const sc = workspaceScopeId?.trim();
    if (!sid || !sc || !isProgramSlideEditScopeLocked(sid, sc)) return false;
    setSlideEditStructureBlockedModalOpen(true);
    return true;
  }, [workspaceId, workspaceScopeId]);

  const selectedRowCount = selectedRowIndexSet.size;
  const canMergeLessons = mode !== 'fixed_to_unit' && selectedRowCount >= 2;
  const canDuplicateLesson = mode !== 'fixed_to_unit' && selectedRowCount === 1;
  const memoModalFieldSet = React.useMemo(
    () =>
      new Set<FieldId>([
        'learning_text',
        'record_memo_text',
        'record_evaluation_text',
        'record_class_text',
      ]),
    []
  );
  const memoFieldPrimaryPlaceholder = React.useCallback((fieldId: FieldId) => {
    if (fieldId === 'learning_text') return '학습내용';
    if (fieldId === 'record_memo_text') return '메모';
    if (fieldId === 'record_evaluation_text') return '평가';
    return '수업기록';
  }, []);
  const memoFieldNotePlaceholder = React.useCallback((fieldId: FieldId) => {
    if (fieldId === 'learning_text') return '학습계획 메모';
    if (fieldId === 'record_memo_text') return '메모 상세';
    if (fieldId === 'record_evaluation_text') return '평가 메모';
    return '수업기록 메모';
  }, []);
  const isEvaluationField = React.useCallback((fieldId: FieldId) => fieldId === 'record_evaluation_text', []);
  const isClassRecordField = React.useCallback((fieldId: FieldId) => fieldId === 'record_class_text', []);
  /** 더블클릭 메모·수업기록 모달: 바깥 클릭 시 dirty 면 유지, 닫기로만 닫기 */
  const isDoubleClickMemoOrClassRecordField = React.useCallback(
    (id: FieldId) =>
      id === 'learning_text' ||
      id === 'record_memo_text' ||
      id === 'record_evaluation_text' ||
      id === 'record_class_text',
    []
  );
  const isUnitPopupDirtyFn = React.useCallback(
    (popup: { text: string; note: string }, snap: UnitPopupSnapshot | null) => {
      if (!snap) return false;
      return popup.text !== snap.text || popup.note !== snap.note;
    },
    []
  );
  const isMemoFieldPopupDirtyFn = React.useCallback(
    (popup: MemoFieldPopup, snap: MemoFieldPopupSnapshot | null) => {
      if (!snap || popup.fieldId !== snap.fieldId) return false;
      if (popup.fieldId === 'record_class_text' && snap.fieldId === 'record_class_text') {
        return (
          popup.text !== snap.text ||
          (popup.noteLesson ?? '') !== snap.noteLesson ||
          (popup.noteEval ?? '') !== snap.noteEval ||
          (popup.recordMemberGroupId ?? null) !== snap.recordMemberGroupId ||
          (popup.evalMemoMemberGroupId ?? null) !== snap.evalMemoMemberGroupId ||
          (popup.assignmentMemoItemId ?? null) !== snap.assignmentMemoItemId ||
          (popup.assignmentMemoItemTitle ?? null) !== snap.assignmentMemoItemTitle ||
          (popup.assignmentMemoGroupId ?? null) !== snap.assignmentMemoGroupId ||
          (popup.assignmentMemoGroupTitle ?? null) !== snap.assignmentMemoGroupTitle
        );
      }
      if (popup.fieldId !== 'record_class_text' && snap.fieldId !== 'record_class_text') {
        return (
          popup.text !== snap.text ||
          popup.note !== snap.note ||
          (popup.evaluationPlanAppliedId ?? '') !== (snap.evaluationPlanAppliedId ?? '') ||
          (popup.evaluationPlanSummary ?? '') !== (snap.evaluationPlanSummary ?? '') ||
          (popup.evaluationPlanElement ?? '') !== (snap.evaluationPlanElement ?? '') ||
          (popup.evaluationPlanMethod ?? '') !== (snap.evaluationPlanMethod ?? '')
        );
      }
      return true;
    },
    []
  );
  const evalStatusOptions = React.useMemo(
    () => [
      { value: '1', label: '평가계획', tone: 'plan' as const },
      { value: '2', label: '평가중', tone: 'progress' as const },
      { value: '3', label: '평가완료', tone: 'done' as const },
    ],
    []
  );
  const evalDropdownRows = React.useMemo(
    () => [{ value: '', label: '입력없음', tone: 'clear' as const }, ...evalStatusOptions],
    [evalStatusOptions]
  );
  /** 수업기록 열: 셀 text에 저장되는 구분값 (기존 자유 입력값은 라벨 매칭 실패 시 그대로 표시) */
  const classRecordStatusOptions = React.useMemo(
    () =>
      [
        { value: 'lesson', label: '수업기록', tone: 'lesson' as const },
        { value: 'eval_log', label: '평가기록', tone: 'eval_log' as const },
      ] as const,
    []
  );
  const classRecordStatusLabel = React.useCallback(
    (valueRaw: string) => {
      const v = (valueRaw ?? '').trim();
      if (!v) return '';
      const hit = classRecordStatusOptions.find((o) => o.value === v);
      return hit ? hit.label : v;
    },
    [classRecordStatusOptions]
  );
  const normalizeClassRecordType = React.useCallback(
    (valueRaw: string, noteRaw?: string) => {
      const value = (valueRaw ?? '').trim();
      if (value === 'lesson' || value === 'eval_log') return value;
      if ((noteRaw ?? '').trim()) return 'lesson';
      return '';
    },
    []
  );
  const evalStatusLabel = React.useCallback(
    (valueRaw: string) => {
      const v = (valueRaw ?? '').trim();
      const hit = evalStatusOptions.find((o) => o.value === v);
      return hit ? hit.label : v;
    },
    [evalStatusOptions]
  );
  /** 단원 셀: 본문 text · 메모 note (JSON 저장 키 동일) */
  type UnitData = { text: string; note: string };
  const parseUnitCriterionList = React.useCallback((raw: string) => {
    return (raw ?? '')
      .split('\n')
      .map((v) => v.trim())
      .filter(Boolean);
  }, []);
  const parseUnitData = React.useCallback((raw: string): UnitData => parseUnitStorageRaw(raw), []);
  const serializeUnitData = React.useCallback(
    (textRaw: string, noteRaw: string) =>
      serializeUnitStorage(textRaw, noteRaw, (s) => parseUnitCriterionList(s).join('\n')),
    [parseUnitCriterionList]
  );
  /** 범교과 셀: 본문 text · 메모 note (JSON 저장 키 동일) */
  type CrossCurricularData = { text: string; note: string };
  const parseCrossCurricularData = React.useCallback((raw: string): CrossCurricularData => parseCrossStorageRaw(raw), []);
  const serializeCrossCurricularData = React.useCallback(
    (textRaw: string, noteRaw: string) => serializeCrossStorage(textRaw, noteRaw),
    []
  );

  const normalizeExternalPasteCell = React.useCallback(
    (
      fieldId: FieldId,
      raw: string,
      annualKey: string,
      row: ProgramProgressRow | undefined,
      existingCellValue?: string
    ): string => {
      const existing = existingCellValue ?? draftRef.current[annualKey]?.[fieldId] ?? '';
      const s = (raw ?? '').replace(/\r$/, '');
      if (fieldId === 'unit_text') {
        const inc = parseMemoFieldData(s);
        const prev = parseUnitData(existing);
        const nextText = inc.text.trim() || primaryTextOnlyFromPastedStructuredValue(s).trim() || s.trim();
        const nextNote = inc.note.trim() ? inc.note : prev.note;
        return serializeUnitData(nextText, nextNote);
      }
      if (fieldId === 'timetable_text') {
        return primaryTextOnlyFromPastedStructuredValue(s).trim();
      }
      if (fieldId === 'record_cross_curricular_text') {
        const inc = parseMemoFieldData(s);
        const prev = parseCrossCurricularData(existing);
        const nextText = inc.text.trim() || parseCrossStorageRaw(s).text.trim() || s.trim();
        const nextNote = inc.note.trim() ? inc.note : prev.note;
        return serializeCrossCurricularData(nextText, nextNote);
      }
      if (memoModalFieldSet.has(fieldId)) {
        if (mode === 'fixed_to_unit' && fieldId === 'learning_text' && row) {
          const inc = parseMemoFieldData(s);
          const prev = parseMemoFieldData(existing);
          const nextTextRaw =
            inc.text.trim() || primaryTextOnlyFromPastedStructuredValue(s).trim() || s.trim();
          const nextText = normalizeFixedLearningValue(fixedEventNameByRow(row), nextTextRaw);
          const nextNote = inc.note.trim() ? inc.note : prev.note;
          return serializeMemoFieldData(nextText, nextNote, prev.presentationRefId, prev.worksheetRefId);
        }
        const inc = parseMemoFieldData(s);
        const prev = parseMemoFieldData(existing);
        const nextText = inc.text.trim() || s.trim();
        const nextNote = inc.note.trim() ? inc.note : prev.note;
        return serializeMemoFieldData(
          nextText,
          nextNote,
          fieldId === 'learning_text' ? prev.presentationRefId : undefined,
          fieldId === 'learning_text' ? prev.worksheetRefId : undefined
        );
      }
      return primaryTextOnlyFromPastedStructuredValue(s).trim();
    },
    [
      fixedEventNameByRow,
      memoModalFieldSet,
      mode,
      normalizeFixedLearningValue,
      parseCrossCurricularData,
      parseMemoFieldData,
      parseUnitData,
      serializeCrossCurricularData,
      serializeMemoFieldData,
      serializeUnitData,
    ]
  );

  const ensureDraftRow = React.useCallback(
    (next: Record<string, DraftRow>, annualKey: string): DraftRow => {
      if (next[annualKey]) return next[annualKey];
      const row = rowByAnnualKey[annualKey];
      const draft = row ? makeDraftRow(row) : null;
      if (!draft) {
        return {
          timetable_text: '',
          unit_text: '',
          learning_text: '',
          page_text: '',
          assistant_page_text: '',
          current_period_text: '',
          total_period_text: '',
          material_text: '',
          record_cross_curricular_text: '',
          record_memo_text: '',
          record_evaluation_text: '',
          record_class_text: '',
          record_member_group_id: null,
          bg_color_by_field: {},
        };
      }
      next[annualKey] = draft;
      return draft;
    },
    [makeDraftRow, rowByAnnualKey]
  );

  const buildGridTsvForRect = React.useCallback(
    (rect: { minRow: number; maxRow: number; minCol: number; maxCol: number }) => {
      const lines: string[] = [];
      for (let r = rect.minRow; r <= rect.maxRow; r += 1) {
        const annualKey = tableRows[r]?.annualCellKey;
        const parts: string[] = [];
        for (let c = rect.minCol; c <= rect.maxCol; c += 1) {
          const fieldId = editableFieldOrder[c];
          let cell = '';
          if (annualKey && fieldId) {
            const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
            cell = escapeCellPlainForTsv(rowDraft[fieldId] ?? '');
          }
          parts.push(cell);
        }
        lines.push(parts.join('\t'));
      }
      return lines.join('\n');
    },
    [editableFieldOrder, ensureDraftRow, tableRows]
  );

  const buildProgramUpsertRows = React.useCallback(
    (next: Record<string, DraftRow>, changedAnnualKeys: Set<string>): ProgramProgressCellUpsert[] => {
      const out: ProgramProgressCellUpsert[] = [];
      changedAnnualKeys.forEach((annualKey) => {
        const row = rowByAnnualKey[annualKey];
        const draft = next[annualKey];
        if (!row || !draft) return;
        const learning = readProgramLearningContentFields({ learning_text: draft.learning_text });
        let mondayIso = row.mondayIso ?? '';
        let weekdayKey = row.weekdayKey;
        let slotIndex = row.slotIndex;
        if ((!mondayIso.trim() || row.annualCellKey.startsWith('orphan:')) && row.fixedSourceAnnualCellKey) {
          const parsed = parseAnnualCellKey(row.fixedSourceAnnualCellKey);
          if (parsed) {
            mondayIso = parsed.mondayIso;
            weekdayKey = parsed.weekdayKey;
            slotIndex = parsed.slotIndex;
          }
        }
        if (!mondayIso.trim()) return;
        const bg: ProgramProgressBgColorByField = {};
        Object.entries(draft.bg_color_by_field ?? {}).forEach(([k, v]) => {
          if (!v) return;
          bg[k as FieldId] = v;
        });
        const source = programCellsByAnnualKey[annualKey];
        const eventForCoalesce = fixedEventNameByRow(row);
        const unitPersist = mode === 'fixed_to_unit' ? '' : draft.unit_text;
        const learningPersist =
          mode === 'fixed_to_unit'
            ? coalesceFixedProgramProgressLearning(eventForCoalesce, draft.unit_text, draft.learning_text)
            : draft.learning_text;
        out.push({
          id: source?.id,
          workspace_id: workspaceId,
          grade_id: row.gradeId,
          academic_year: academicYear,
          term_number: termNumber,
          monday_iso: mondayIso,
          weekday_key: weekdayKey,
          slot_index: slotIndex,
          timetable_text: mode === 'project_to_template' ? draft.timetable_text : '',
          unit_text: unitPersist,
          learning_text: learningPersist,
          page_text: draft.page_text,
          assistant_page_text: draft.assistant_page_text,
          current_period_text: draft.current_period_text,
          total_period_text: draft.total_period_text,
          material_text: draft.material_text,
          record_cross_curricular_text: draft.record_cross_curricular_text,
          record_memo_text: draft.record_memo_text,
          record_evaluation_text: draft.record_evaluation_text,
          record_class_text: draft.record_class_text,
          record_member_group_id: draft.record_member_group_id ?? null,
          bg_color_by_field: bg,
        });
      });
      return out;
    },
    [academicYear, fixedEventNameByRow, mode, programCellsByAnnualKey, rowByAnnualKey, workspaceId, termNumber]
  );
  const buildFixedContentUpsertRows = React.useCallback(
    (next: Record<string, DraftRow>, changedAnnualKeys: Set<string>): ProgramFixedContentRowUpsert[] => {
      const out: ProgramFixedContentRowUpsert[] = [];
      changedAnnualKeys.forEach((annualKey) => {
        const row = rowByAnnualKey[annualKey];
        const draft = next[annualKey];
        if (!row || !draft) return;
        const learning = readProgramLearningContentFields({ learning_text: draft.learning_text });
        const learningWritten = writeProgramLearningContentFields(learning);
        const bg: ProgramProgressBgColorByField = {};
        Object.entries(draft.bg_color_by_field ?? {}).forEach(([k, v]) => {
          if (!v) return;
          bg[k as FieldId] = v;
        });
        const rowOrderIndex = row.fixedOrderIndex ?? rowIdxByAnnualKey[annualKey] ?? 0;
        const eventName = normalizeFixedEventName(
          row.memo || row.fixedEventName || parseUnitStorageRaw(draft.unit_text ?? '').text || draft.learning_text || ''
        );
        out.push({
          id: row.fixedContentId || undefined,
          workspace_id: workspaceId,
          grade_id: row.gradeId,
          academic_year: academicYear,
          term_number: termNumber,
          order_index: rowOrderIndex,
          source_annual_cell_key: row.fixedSourceAnnualCellKey ?? (row.isScheduleAvailable === false ? null : row.annualCellKey),
          event_name: eventName || null,
          timetable_text: draft.timetable_text,
          unit_text: draft.unit_text,
          learning_text: learningWritten.learning_text,
          learning_note: learningWritten.learning_note,
          presentation_ref_id: learningWritten.presentation_ref_id,
          worksheet_ref_id: learningWritten.worksheet_ref_id,
          page_text: draft.page_text,
          assistant_page_text: draft.assistant_page_text,
          current_period_text: draft.current_period_text,
          total_period_text: draft.total_period_text,
          material_text: draft.material_text,
          record_cross_curricular_text: draft.record_cross_curricular_text,
          record_memo_text: draft.record_memo_text,
          record_evaluation_text: draft.record_evaluation_text,
          record_class_text: draft.record_class_text,
          record_member_group_id: draft.record_member_group_id ?? null,
          bg_color_by_field: bg,
        });
      });
      return out;
    },
    [academicYear, mode, normalizeFixedEventName, rowByAnnualKey, rowIdxByAnnualKey, workspaceId, termNumber]
  );
  const buildAllFixedContentWorkspaceRows = React.useCallback(
    (next: Record<string, DraftRow>): ProgramFixedContentRowUpsert[] =>
      tableRows.map((row, orderIndex) => {
        const draft = next[row.annualCellKey] ?? makeDraftRow(row);
        const learning = readProgramLearningContentFields({ learning_text: draft.learning_text });
        const learningWritten = writeProgramLearningContentFields(learning);
        const bg: ProgramProgressBgColorByField = {};
        Object.entries(draft.bg_color_by_field ?? {}).forEach(([kk, vv]) => {
          if (!vv) return;
          bg[kk as FieldId] = vv;
        });
        const eventName = normalizeFixedEventName(
          row.memo ||
            row.fixedEventName ||
            parseUnitStorageRaw(draft.unit_text ?? '').text ||
            draft.learning_text ||
            ''
        );
        return {
          id: row.fixedContentId || undefined,
          workspace_id: workspaceId,
          grade_id: row.gradeId,
          academic_year: academicYear,
          term_number: termNumber,
          order_index: orderIndex,
          source_annual_cell_key:
            row.fixedSourceAnnualCellKey ?? (row.isScheduleAvailable === false ? null : row.annualCellKey),
          event_name: eventName || null,
          timetable_text: mode === 'project_to_template' ? draft.timetable_text : '',
          unit_text: draft.unit_text,
          learning_text: learningWritten.learning_text,
          learning_note: learningWritten.learning_note,
          presentation_ref_id: learningWritten.presentation_ref_id,
          worksheet_ref_id: learningWritten.worksheet_ref_id,
          page_text: draft.page_text,
          assistant_page_text: draft.assistant_page_text,
          current_period_text: draft.current_period_text,
          total_period_text: draft.total_period_text,
          material_text: draft.material_text,
          record_cross_curricular_text: draft.record_cross_curricular_text,
          record_memo_text: draft.record_memo_text,
          record_evaluation_text: draft.record_evaluation_text,
          record_class_text: draft.record_class_text,
          record_member_group_id: draft.record_member_group_id ?? null,
          bg_color_by_field: bg,
        };
      }),
    [academicYear, makeDraftRow, mode, normalizeFixedEventName, workspaceId, tableRows, termNumber]
  );
  const queuePendingRows = React.useCallback(
    (next: Record<string, DraftRow>, changedAnnualKeys: Set<string>) => {
      if (!changedAnnualKeys.size) return;
      if (saveMode === 'fixed_content') {
        const upserts = buildFixedContentUpsertRows(next, changedAnnualKeys);
        upserts.forEach((row) => {
          const key = row.id
            ? `fixed:${row.id}`
            : `fixed:${row.grade_id}|${row.academic_year}|${row.term_number}|${row.order_index}`;
          pendingRowsRef.current[key] = row;
        });
      } else {
        const upserts = buildProgramUpsertRows(next, changedAnnualKeys);
        upserts.forEach((row) => {
          const key = `${row.grade_id}|${row.monday_iso}|${row.weekday_key}|${row.slot_index}`;
          pendingRowsRef.current[key] = row;
        });
      }
      const nextDirty = Object.keys(pendingRowsRef.current).length > 0;
      setHasUnsavedChanges(nextDirty);
      if (saveMode === 'fixed_content') {
        if (!nextDirty) {
          onWorkspaceRowsChange?.(null);
        } else {
          const patchRows = buildFixedContentUpsertRows(next, changedAnnualKeys);
          onWorkspaceRowsChange?.(patchRows, { persistKind: 'patch' });
        }
      }
    },
    [
      buildAllFixedContentWorkspaceRows,
      buildFixedContentUpsertRows,
      buildProgramUpsertRows,
      onWorkspaceRowsChange,
      saveMode,
    ]
  );

  const clearPendingFixedContentCellEditsAfterReplace = React.useCallback(() => {
    if (saveMode !== 'fixed_content') return;
    pendingRowsRef.current = {};
    /**
     * 행 삭제·추가 등 구조 replace는 pending 칸 편집이 없어도 워크스페이스가 dirty다.
     * 여기서 false로 두면 페이지 저장이 saveAction을 건너뛰고 excludeWorkspaceScopeId로
     * 해당 스코프를 빼 버려 DB에 행 삭제가 반영되지 않는다.
     */
    setHasUnsavedChanges(true);
  }, [saveMode]);

  const cloneLocalEditHistoryFrame = React.useCallback(
    (drafts: Record<string, DraftRow>, pending: Record<string, PendingSaveRow>): LocalEditHistoryFrame => ({
      drafts: JSON.parse(JSON.stringify(drafts)) as Record<string, DraftRow>,
      pending: JSON.parse(JSON.stringify(pending)) as Record<string, PendingSaveRow>,
    }),
    []
  );

  const captureLocalUndoBeforeMutation = React.useCallback(() => {
    if (applyingLocalEditHistoryRef.current) return;
    localUndoStackRef.current.push(cloneLocalEditHistoryFrame(draftRef.current, pendingRowsRef.current));
    if (localUndoStackRef.current.length > LOCAL_EDIT_HISTORY_MAX) {
      localUndoStackRef.current.shift();
    }
    localRedoStackRef.current = [];
    notifyUndoRedoStacksRef.current();
  }, [cloneLocalEditHistoryFrame]);

  const restoreLocalEditHistoryFrame = React.useCallback(
    (frame: LocalEditHistoryFrame) => {
      applyingLocalEditHistoryRef.current = true;
      try {
        const cloned = cloneLocalEditHistoryFrame(frame.drafts, frame.pending);
        draftRef.current = cloned.drafts;
        pendingRowsRef.current = cloned.pending;
        setDraftByAnnualKey(JSON.parse(JSON.stringify(cloned.drafts)) as Record<string, DraftRow>);
        setLocallyClearedLearningKeys(new Set());
        const dirty = Object.keys(pendingRowsRef.current).length > 0;
        setHasUnsavedChanges(dirty);
        if (saveMode === 'fixed_content') {
          if (dirty) {
            const fullSnapshot = buildAllFixedContentWorkspaceRows(draftRef.current);
            onWorkspaceRowsChange?.(fullSnapshot, { persistKind: 'replace' });
          } else {
            onWorkspaceRowsChange?.(null);
          }
        }
      } finally {
        applyingLocalEditHistoryRef.current = false;
        notifyUndoRedoStacksRef.current();
      }
    },
    [buildAllFixedContentWorkspaceRows, cloneLocalEditHistoryFrame, onWorkspaceRowsChange, saveMode]
  );

  const tryLocalUndo = React.useCallback(() => {
    if (readOnlyRef.current || mutationLockedRef.current) return false;
    if (editingCellRef.current) return false;
    const st = localUndoStackRef.current;
    if (!st.length) return false;
    const previous = st.pop()!;
    localRedoStackRef.current.push(cloneLocalEditHistoryFrame(draftRef.current, pendingRowsRef.current));
    if (localRedoStackRef.current.length > LOCAL_EDIT_HISTORY_MAX) {
      localRedoStackRef.current.shift();
    }
    restoreLocalEditHistoryFrame(previous);
    return true;
  }, [cloneLocalEditHistoryFrame, restoreLocalEditHistoryFrame]);

  const tryLocalRedo = React.useCallback(() => {
    if (readOnlyRef.current || mutationLockedRef.current) return false;
    if (editingCellRef.current) return false;
    const st = localRedoStackRef.current;
    if (!st.length) return false;
    const nextFrame = st.pop()!;
    localUndoStackRef.current.push(cloneLocalEditHistoryFrame(draftRef.current, pendingRowsRef.current));
    if (localUndoStackRef.current.length > LOCAL_EDIT_HISTORY_MAX) {
      localUndoStackRef.current.shift();
    }
    restoreLocalEditHistoryFrame(nextFrame);
    return true;
  }, [cloneLocalEditHistoryFrame, restoreLocalEditHistoryFrame]);

  React.useLayoutEffect(() => {
    tryLocalUndoRef.current = tryLocalUndo;
    tryLocalRedoRef.current = tryLocalRedo;
  }, [tryLocalRedo, tryLocalUndo]);

  React.useLayoutEffect(() => {
    const holder = programUndoRedoApiRef;
    if (!holder) return;
    holder.current = {
      undo: () => tryLocalUndo(),
      redo: () => tryLocalRedo(),
      canUndo: () => localUndoStackRef.current.length > 0,
      canRedo: () => localRedoStackRef.current.length > 0,
    };
    return () => {
      holder.current = null;
    };
  }, [programUndoRedoApiRef, tryLocalRedo, tryLocalUndo]);

  /**
   * document 단계보다 먼저 window(capture)에서 ⌘Z / Ctrl+Z 를 막아
   * 브라우저·입력 요소 기본 실행취소(history)가 먹는 것을 방지한다.
   */
  React.useLayoutEffect(() => {
    const onWindowKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.isComposing) return;
      const zk = (e.key || '').toLowerCase();
      if (e.code !== 'KeyZ' && zk !== 'z') return;
      if (programPendingLeaveTo != null || pendingLeaveTo != null) return;
      if (isAppBrowseNavigationShortcutBlockedRoute(window.location.pathname)) return;
      if (editingCellRef.current) return;
      const ae = document.activeElement as HTMLElement | null;
      if (ae) {
        if (memoFieldPopupRef.current?.contains(ae)) return;
        if (unitPopupRef.current?.contains(ae)) return;
        if (crossPopupRef.current?.contains(ae)) return;
        if (evalDropdownRef.current?.contains(ae)) return;
      }
      const inTable = !!(
        ae &&
        (tableWrapRef.current?.contains(ae) || programProgressScrollRef.current?.contains(ae))
      );
      const hasSel = selectedCellsRef.current.length > 0;
      if (!inTable && hasSel && !isElementVisibleForBrowseScrollFocus(tableWrapRef.current)) return;
      if (!inTable && !hasSel) return;
      if (readOnlyRef.current || mutationLockedRef.current) return;
      const canUndo = localUndoStackRef.current.length > 0;
      const canRedo = localRedoStackRef.current.length > 0;
      if (e.shiftKey ? !canRedo : !canUndo) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (e.shiftKey) tryLocalRedoRef.current();
      else tryLocalUndoRef.current();
    };
    window.addEventListener('keydown', onWindowKeyDown, true);
    return () => window.removeEventListener('keydown', onWindowKeyDown, true);
  }, [pendingLeaveTo, programPendingLeaveTo]);

  const applyPatches = React.useCallback(
    (
      patches: Array<{
        annualKey: string;
        fieldId: FieldId;
        value?: string;
        bgColor?: string | null;
        recordMemberGroupId?: string | null;
      }>,
      options?: { syncOnly?: boolean; skipUndoCapture?: boolean }
    ) => {
      if (!patches.length) return;
      const resolvePatchValue = (patch: {
        annualKey: string;
        fieldId: FieldId;
        value?: string;
      }): string | undefined => {
        if (patch.value === undefined) return undefined;
        if (patch.fieldId !== 'learning_text') return patch.value;
        const fields = parseProgramLearningContentFieldsFromEditor(patch.value);
        const cleared =
          !fields.body.trim() &&
          !fields.note.trim() &&
          !fields.presentationRefId &&
          !fields.worksheetRefId;
        if (cleared) {
          return serializeProgramLearningContentFieldsForEditor({
            body: '',
            note: '',
            presentationRefId: null,
            worksheetRefId: null,
          });
        }
        return patch.value;
      };
      const probe: Record<string, DraftRow> = { ...draftRef.current };
      let wouldChange = false;
      patches.forEach((patch) => {
        const rowDraft = ensureDraftRow(probe, patch.annualKey);
        const patchValue = resolvePatchValue(patch);
        if (patchValue !== undefined) {
          if (rowDraft[patch.fieldId] !== patchValue) wouldChange = true;
          rowDraft[patch.fieldId] = patchValue;
          if (patch.fieldId === 'record_class_text' && patchValue === '') {
            if ((rowDraft.record_member_group_id ?? null) !== null) wouldChange = true;
            rowDraft.record_member_group_id = null;
          }
        }
        if (patch.recordMemberGroupId !== undefined) {
          const cur = rowDraft.record_member_group_id ?? null;
          const want = patch.recordMemberGroupId;
          if (cur !== want) wouldChange = true;
          rowDraft.record_member_group_id = want;
        }
        if (patch.bgColor !== undefined && !protectedBgFields.has(patch.fieldId)) {
          const cur = rowDraft.bg_color_by_field?.[patch.fieldId] ?? '';
          const want = patch.bgColor ? patch.bgColor : '';
          if (cur !== want) wouldChange = true;
          const map = { ...(rowDraft.bg_color_by_field ?? {}) };
          if (patch.bgColor) map[patch.fieldId] = patch.bgColor;
          else delete map[patch.fieldId];
          rowDraft.bg_color_by_field = map;
        }
      });
      if (!wouldChange) return;

      const clearedLearningKeys = new Set<string>();
      const restoredLearningKeys = new Set<string>();
      const next: Record<string, DraftRow> = { ...draftRef.current };
      patches.forEach((patch) => {
        const rowDraft = ensureDraftRow(next, patch.annualKey);
        const patchValue = resolvePatchValue(patch);
        if (patchValue !== undefined) {
          rowDraft[patch.fieldId] = patchValue;
          if (patch.fieldId === 'record_class_text' && patchValue === '') {
            rowDraft.record_member_group_id = null;
          }
          if (patch.fieldId === 'learning_text') {
            const clearedFields = parseProgramLearningContentFieldsFromEditor(patchValue);
            const learningCleared =
              !clearedFields.body.trim() &&
              !clearedFields.note.trim() &&
              !clearedFields.presentationRefId &&
              !clearedFields.worksheetRefId;
            if (learningCleared) {
              clearedLearningKeys.add(patch.annualKey);
            } else {
              restoredLearningKeys.add(patch.annualKey);
            }
          }
        }
        if (patch.recordMemberGroupId !== undefined) {
          rowDraft.record_member_group_id = patch.recordMemberGroupId;
        }
        if (patch.bgColor !== undefined && !protectedBgFields.has(patch.fieldId)) {
          const map = { ...(rowDraft.bg_color_by_field ?? {}) };
          if (patch.bgColor) map[patch.fieldId] = patch.bgColor;
          else delete map[patch.fieldId];
          rowDraft.bg_color_by_field = map;
        }
      });
      draftRef.current = next;
      setDraftByAnnualKey(next);
      if (clearedLearningKeys.size || restoredLearningKeys.size) {
        setLocallyClearedLearningKeys((prev) => {
          const merged = new Set(prev);
          clearedLearningKeys.forEach((ak) => merged.add(ak));
          restoredLearningKeys.forEach((ak) => merged.delete(ak));
          return merged;
        });
      }
      if (workspaceId?.trim()) {
        const sid = workspaceId.trim();
        patches.forEach((patch) => {
          const patchValue = resolvePatchValue(patch);
          if (patch.fieldId === 'learning_text' && patchValue !== undefined) {
            const clearedFields = parseProgramLearningContentFieldsFromEditor(patchValue);
            const learningCleared =
              !clearedFields.body.trim() &&
              !clearedFields.note.trim() &&
              !clearedFields.presentationRefId &&
              !clearedFields.worksheetRefId;
            if (learningCleared) {
              const hintKeys = resolveLearningAttachmentDeleteHintAnnualKeys(
                patch.annualKey,
                patch.annualKey
              );
              markLearningAttachmentIndicatorDeletedForSyncKeys(sid, hintKeys, 'presentation');
              markLearningAttachmentIndicatorDeletedForSyncKeys(sid, hintKeys, 'worksheet');
              hintKeys.forEach((ak) => removeLearningAttachmentIndicatorEntry(sid, ak));
              setIndicatorHintRev((v) => v + 1);
            }
            const nextPresentationRef = resolvePresentationRefIdFromLearningText(patchValue);
            const nextWorksheetRef = resolveWorksheetRefIdFromLearningText(patchValue);
            if (nextPresentationRef) {
              clearLearningAttachmentIndicatorDeleted(sid, patch.annualKey, 'presentation');
            }
            if (nextWorksheetRef) {
              clearLearningAttachmentIndicatorDeleted(sid, patch.annualKey, 'worksheet');
            }
            /**
             * 연간진도표 표시/인디케이터의 단일 오버레이 채널:
             * 프로그램 표 학습내용 편집도 슬라이드 이벤트와 동일 페이로드로 즉시 반영한다.
             */
            notifyProgramContentSlideLearningUpdated({
              workspaceId: sid,
              scopeId: workspaceScopeId,
              annualKey: patch.annualKey,
              learningText: patchValue,
              syncAnnualLearningTextOverlay: true,
            });
          }
        });
      }
      /** 슬라이드·활동지 탭/연간진도표 동기화 — 통합 저장 대상이 아닌 표시용 패치 */
      if (options?.syncOnly) return;

      if (!options?.skipUndoCapture) {
        captureLocalUndoBeforeMutation();
      }
      const changed = new Set<string>();
      patches.forEach((patch) => {
        changed.add(patch.annualKey);
      });
      queuePendingRows(next, changed);
    },
    [
      captureLocalUndoBeforeMutation,
      ensureDraftRow,
      protectedBgFields,
      queuePendingRows,
      resolveLearningAttachmentDeleteHintAnnualKeys,
      workspaceId,
      workspaceScopeId,
    ]
  );
  const annualKeyHasPendingWorkspaceSave = React.useCallback(
    (annualKey: string): boolean => {
      const ak = annualKey.trim();
      if (!ak) return false;
      return Object.values(pendingRowsRef.current).some((pending) => {
        if (saveMode === 'fixed_content') {
          return fixedContentPendingMatchesAnnualKey(
            pending as ProgramFixedContentRowUpsert,
            ak,
            tableRows
          );
        }
        const r = pending as ProgramProgressCellUpsert;
        if (!r.monday_iso?.trim() || r.weekday_key == null || r.slot_index == null) return false;
        return buildAnnualCellKey(r.grade_id, r.monday_iso, r.weekday_key, r.slot_index) === ak;
      });
    },
    [saveMode, tableRows]
  );
  const applySlideLearningUpdateRef = React.useRef<(raw: string | null, source: string) => boolean>(() => false);
  const slideLearningUpdatedAtByAnnualKeyRef = React.useRef<Record<string, number>>({});

  React.useEffect(() => {
    const applySlideLearningUpdate = (raw: string | null, source: string) => {
      if (!raw) {
        clearProgramContentSlideLearningUpdatedFlag();
        return false;
      }
      try {
        const parsed = JSON.parse(raw) as {
          workspaceId?: unknown;
          scopeId?: unknown;
          annualKey?: unknown;
          learningText?: unknown;
          updatedAt?: unknown;
          presentationRefId?: unknown;
          owner?: unknown;
        };
        const eventSchoolId = String(parsed.workspaceId ?? '').trim();
        const eventScopeId = String(parsed.scopeId ?? '').trim();
        const learningText = String(parsed.learningText ?? '');

        if (!eventSchoolId || eventSchoolId !== workspaceId.trim()) return false;
        /** scopeId가 없거나 다른 표면 반영하지 않음 — annualKey만 같은 다른 편제 잔상 방지 */
        if (!eventScopeId || eventScopeId !== workspaceScopeId.trim()) return false;

        const refFromPayload = String(parsed.presentationRefId ?? '').trim();
        const refFromLearning = resolvePresentationRefIdFromLearningText(learningText);
        const ref =
          refFromPayload && isPersistableUuid(refFromPayload) ? refFromPayload : refFromLearning;

        let ownerParsed: ProgramContentSlideOwnerRef | null = null;
        const or = parsed.owner;
        if (or && typeof or === 'object') {
          const k = String((or as { kind?: unknown }).kind ?? '');
          const id = String((or as { id?: unknown }).id ?? '').trim();
          if (
            (k === 'program_cell' || k === 'fixed_row' || k === 'scoped_row' || k === 'presentation_ref') &&
            id &&
            isPersistableUuid(id)
          ) {
            ownerParsed = { kind: k as ProgramContentSlideOwnerRef['kind'], id };
          }
        }

        /**
         * 1) 슬라이드 탭이 넘긴 `annualKey`가 있으면 우선 사용(DB에 이미 반영된 학습내용과 동일 행).
         * 2) 없으면 owner·presentationRefId로 표 행을 역탐색(순서 변경 등 대비).
         */
        const resolveTargetAnnualKey = (): string => {
          const explicitFromPayload = String(parsed.annualKey ?? '').trim();
          if (explicitFromPayload) {
            if (tableRows.some((r) => r.annualCellKey === explicitFromPayload)) return explicitFromPayload;
            if (Object.prototype.hasOwnProperty.call(draftRef.current, explicitFromPayload)) {
              return explicitFromPayload;
            }
          }
          if (ownerParsed) {
            if (ownerParsed.kind === 'program_cell') {
              for (const row of tableRows) {
                const cid = programCellsByAnnualKey[row.annualCellKey]?.id?.trim();
                if (cid === ownerParsed.id) return row.annualCellKey;
              }
            }
            if (ownerParsed.kind === 'scoped_row') {
              for (const row of tableRows) {
                if ((row.fixedContentId ?? '').trim() === ownerParsed.id) return row.annualCellKey;
              }
            }
            if (ownerParsed.kind === 'fixed_row') {
              for (const row of tableRows) {
                const ak = row.annualCellKey;
                if (mode === 'fixed_to_unit') {
                  const cid = programCellsByAnnualKey[ak]?.id?.trim();
                  if (cid === ownerParsed.id) return ak;
                }
                if ((row.fixedContentId ?? '').trim() === ownerParsed.id) return ak;
              }
            }
            if (ownerParsed.kind === 'presentation_ref') {
              for (const row of tableRows) {
                const ak = row.annualCellKey;
                const cur = draftRef.current[ak]?.learning_text ?? '';
                if (resolvePresentationRefIdFromLearningText(cur) === ownerParsed.id) return ak;
                if (resolvePresentationRefIdFromLearningContent(programCellsByAnnualKey[ak]) === ownerParsed.id) return ak;
              }
            }
          }
          if (ref && isPersistableUuid(ref)) {
            for (const row of tableRows) {
              const ak = row.annualCellKey;
              const cur = draftRef.current[ak]?.learning_text ?? '';
              if (resolvePresentationRefIdFromLearningText(cur) === ref) return ak;
              if (resolvePresentationRefIdFromLearningContent(programCellsByAnnualKey[ak]) === ref) return ak;
            }
          }
          return '';
        };

        const targetAnnualKey = resolveTargetAnnualKey().trim();
        if (!targetAnnualKey) {
          clearProgramContentSlideLearningUpdatedFlag();
          return false;
        }
        const payloadUpdatedAt = Number(parsed.updatedAt ?? 0);
        if (Number.isFinite(payloadUpdatedAt) && payloadUpdatedAt > 0) {
          const prevUpdatedAt = slideLearningUpdatedAtByAnnualKeyRef.current[targetAnnualKey] ?? 0;
          if (payloadUpdatedAt <= prevUpdatedAt) {
            clearProgramContentSlideLearningUpdatedFlag();
            return false;
          }
        }
        const current = draftRef.current[targetAnnualKey]?.learning_text ?? '';
        const cellCurrent = cellLearningTextForEditor(programCellsByAnnualKey[targetAnnualKey]);
        const markHandled = () => {
          if (Number.isFinite(payloadUpdatedAt) && payloadUpdatedAt > 0) {
            slideLearningUpdatedAtByAnnualKeyRef.current[targetAnnualKey] = payloadUpdatedAt;
          }
        };
        const finishWithoutApply = () => {
          markHandled();
          clearProgramContentSlideLearningUpdatedFlag();
          return false;
        };
        if (current === learningText) return finishWithoutApply();
        /** 슬라이드·활동지 저장 직후 LS 플래그가 남아 있으면, 이후 로컬 편집을 stale payload로 덮어쓰지 않음 */
        const cellRow = programCellsByAnnualKey[targetAnnualKey];
        const incomingRefs = resolveLearningAttachmentRefsFromSources(learningText);
        const currentRefs = resolveLearningAttachmentRefsFromSources(cellRow, current);
        const cellRefs = resolveLearningAttachmentRefsFromSources(cellRow);
        const incomingPresentationRef =
          (ref && isPersistableUuid(ref) ? ref : '') ||
          incomingRefs.presentationRefId ||
          resolvePresentationRefIdFromLearningText(learningText);
        const incomingWorksheetRef =
          incomingRefs.worksheetRefId || resolveWorksheetRefIdFromLearningText(learningText);
        const incomingChangesPresentationRef = !!(
          incomingPresentationRef &&
          incomingPresentationRef !== (currentRefs.presentationRefId || cellRefs.presentationRefId)
        );
        const incomingChangesWorksheetRef = !!(
          incomingWorksheetRef &&
          incomingWorksheetRef !== (currentRefs.worksheetRefId || cellRefs.worksheetRefId)
        );
        const incomingChangesRef = incomingChangesPresentationRef || incomingChangesWorksheetRef;
        if (annualKeyHasPendingWorkspaceSave(targetAnnualKey) && !incomingChangesRef) {
          return finishWithoutApply();
        }
        if (
          !incomingChangesRef &&
          current.trim() !== cellCurrent.trim() &&
          current.trim() !== learningText.trim()
        ) {
          return finishWithoutApply();
        }
        /**
         * 표에서 본문을 지운 미저장 상태 — flush payload(DB 본문+ref)로 옛 글을 되살리지 않음.
         * 원래 빈 칸에 첨부를 처음 다는 경우(cell도 비어 있음)는 아래로 내려 ref만 반영한다.
         */
        if (!String(current).trim() && String(cellCurrent).trim()) {
          return finishWithoutApply();
        }
        const mergedLearningText = mergeLearningTextOwnerPatchWithSavedBaseline(
          learningText,
          current,
          cellRow
        );
        if (mergedLearningText === current) return finishWithoutApply();
        /**
         * 슬라이드·활동지 flush/탭 저장이 learningPatch로 DB ref를 이미 쓰거나 쓸 예정이므로
         * 표에는 표시용으로만 반영한다. (통합 저장 직후 재 dirty 방지)
         */
        applyPatches(
          [{ annualKey: targetAnnualKey, fieldId: 'learning_text', value: mergedLearningText }],
          { syncOnly: true }
        );
        /** 프레젠 setPresenceCached와 동일 — flush 직후 presence fetch 전에도 인디케이터 유지 */
        if (incomingWorksheetRef && isPersistableUuid(incomingWorksheetRef)) {
          setWorksheetPresenceRefIds((prev) => {
            if (prev.has(incomingWorksheetRef)) return prev;
            const next = new Set(prev);
            next.add(incomingWorksheetRef);
            return next;
          });
        }
        markHandled();
        setSlideDraftRev((v) => v + 1);
        setWorksheetDraftRev((v) => v + 1);
        clearProgramContentSlideLearningUpdatedFlag();
        return true;
      } catch (error) {
        console.warn('[program-slide] failed to parse learning update', { source, error });
        return false;
      }
    };
    applySlideLearningUpdateRef.current = applySlideLearningUpdate;
    /**
     * 슬라이드 탭이 DB 반영 후 `notifyProgramContentSlideLearningUpdated`를 호출한다.
     * 같은 창: CustomEvent. 다른 탭: localStorage `storage`.
     * 같은 탭에서 슬라이드만 전환한 뒤 표로 돌아올 때는 `visibilitychange`로 LS에 남은 플래그를 재적용한다.
     */
    const onStorage = (e: StorageEvent) => {
      if (e.key === PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_KEY) {
        if (!applySlideLearningUpdate(e.newValue, 'storage')) return;
        invalidateProgramContentSlidePresenceCache(workspaceId);
        reloadProgramContentSlidePresence({ forceRefresh: true });
        return;
      }
      if (!e.key || !e.key.startsWith(PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX)) return;
      invalidateProgramContentSlidePresenceCache(workspaceId);
      reloadProgramContentSlidePresence({ forceRefresh: true });
    };
    const onSlideLearningSameTab: EventListener = (e) => {
      const d = (e as CustomEvent<Record<string, unknown>>).detail;
      if (!d || typeof d !== 'object') return;
      if (!applySlideLearningUpdate(JSON.stringify(d), 'same-tab')) return;
      invalidateProgramContentSlidePresenceCache(workspaceId);
      reloadProgramContentSlidePresence({ forceRefresh: true });
    };
    const onWorkspaceChanged = (e: Event) => {
      // 다른 테이블에서 잘라내기가 발생하여 현재 워크스페이스가 변경된 경우
      const event = e as CustomEvent<{ scopeId?: string; clearedCellIds?: string[] }>;
      const detail = event.detail;

      // 현재 스코프가 아니면 무시
      if (detail?.scopeId && detail.scopeId !== workspaceScopeId) return;

      // 잘라내기로 지워진 셀 목록이 있으면 그것만 처리
      if (detail?.clearedCellIds && detail.clearedCellIds.length > 0) {
        const patches: Array<{ annualKey: string; fieldId: FieldId; value?: string; bgColor?: string | null }> = [];

        detail.clearedCellIds.forEach((cellId) => {
          const sep = cellId.includes('::') ? '::' : '\t';
          const [annualKey, fieldId] = cellId.split(sep);
          if (!annualKey || !fieldId) return;

          patches.push({
            annualKey,
            fieldId: fieldId as FieldId,
            value: '',
            bgColor: null,
          });
        });

        if (patches.length > 0) applyPatches(patches);
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSlideLearningSameTab);
    window.addEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, onWorkspaceChanged);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(PROGRAM_CONTENT_SLIDE_LEARNING_UPDATED_EVENT, onSlideLearningSameTab);
      window.removeEventListener(PROGRAM_WORKSPACE_CHANGED_EVENT, onWorkspaceChanged);
    };
  }, [
    annualKeyHasPendingWorkspaceSave,
    applyPatches,
    mode,
    programCellsByAnnualKey,
    programCellsFingerprint,
    reloadProgramContentSlidePresence,
    workspaceId,
    rowsBaselineFingerprint,
    workspaceScopeId,
  ]);

  // 컴포넌트 마운트 시 워크스페이스의 빈 셀들을 draft에 반영
  React.useEffect(() => {
    if (!workspaceScopeId) return;
    const entry = getProgramWorkspaceScopeEntry(workspaceScopeId);
    if (!entry || !entry.isDirty) return;

    // 워크스페이스의 빈 값들을 draft에 반영
    const patches: Array<{ annualKey: string; fieldId: FieldId; value?: string; bgColor?: string | null }> = [];
    const editableFields: FieldId[] = [
      'learning_text',
      'unit_text',
      'page_text',
      'assistant_page_text',
      'current_period_text',
      'total_period_text',
      'material_text',
      'record_cross_curricular_text',
      'record_memo_text',
      'record_evaluation_text',
      'record_class_text',
    ];

    entry.rows.forEach((row) => {
      const annualKey = row.source_annual_cell_key || `extra:${row.order_index}` || `orphan:${row.id}`;

      editableFields.forEach((fieldId) => {
        const fieldKey = fieldId.replace(/_text$/, '_text') as keyof typeof row;
        const value = row[fieldKey];

        // 워크스페이스에 빈 값('' 또는 null)이 있으면 patch로 추가
        if (value === '' || value === null) {
          patches.push({
            annualKey,
            fieldId,
            value: '',
            bgColor: null,
          });
        }
      });
    });

    if (patches.length > 0) applyPatches(patches);
  }, [workspaceScopeId, applyPatches]);

  /**
   * NOTE:
   * visibilitychange 재적용은 stale localStorage payload를 페이지 재진입마다 다시 반영해
   * 삭제된 메모/프레젠/활동지 인디케이터가 부활하는 원인이 되어 비활성화한다.
   * 슬라이드 저장 동기화는 same-tab CustomEvent + cross-tab storage 이벤트 경로로만 처리한다.
   */

  React.useEffect(() => {
    const pending = pendingDuplicateTimetableCopyRef.current;
    if (!pending) return;
    const exists = tableRows.some((row) => row.annualCellKey === pending.annualKey);
    if (!exists) return;
    pendingDuplicateTimetableCopyRef.current = null;
    applyPatches([{ annualKey: pending.annualKey, fieldId: 'timetable_text', value: pending.value }]);
  }, [applyPatches, tableRows]);
  const [pageSplitModalOpen, setPageSplitModalOpen] = React.useState(false);
  /** 학습내용 연차시 `(n/m)` 일괄 조정: 바뀔 패치 목록(열려 있을 때만) */
  const [learningSessionPlan, setLearningSessionPlan] = React.useState<Array<{
    annualKey: string;
    before: string;
    after: string;
  }> | null>(null);
  const [pageSplitDelimiterInput, setPageSplitDelimiterInput] = React.useState('');
  const pageSplitDelimiterInputRef = React.useRef<HTMLInputElement | null>(null);
  const [lessonCountModalOpen, setLessonCountModalOpen] = React.useState(false);
  const [lessonCountDelimiterInput, setLessonCountDelimiterInput] = React.useState('');
  const lessonCountDelimiterInputRef = React.useRef<HTMLInputElement | null>(null);

  const [crossPopup, setCrossPopup] = React.useState<{ annualKey: string; text: string; note: string } | null>(null);
  const crossPopupRef = React.useRef<HTMLDivElement | null>(null);
  const crossTopicInputRef = React.useRef<HTMLInputElement | null>(null);
  const crossContentInputRef = React.useRef<HTMLInputElement | null>(null);
  const crossPopupOpenedKeyRef = React.useRef<string | null>(null);
  const crossPopupOpenSnapshotRef = React.useRef<UnitPopupSnapshot | null>(null);
  const crossPopupWasOpenRef = React.useRef(false);
  const [unitPopup, setUnitPopup] = React.useState<{ annualKey: string; text: string; note: string } | null>(null);
  const [isUnitPopupExpanded, setIsUnitPopupExpanded] = React.useState(false);
  const unitPopupRef = React.useRef<HTMLDivElement | null>(null);
  const unitPopupOpenSnapshotRef = React.useRef<UnitPopupSnapshot | null>(null);
  const unitPopupWasOpenRef = React.useRef(false);
  const unitNameInputRef = React.useRef<HTMLInputElement | null>(null);
  const unitCriterionInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const unitPopupOpenedKeyRef = React.useRef<string | null>(null);
  const [memoFieldPopup, setMemoFieldPopup] = React.useState<MemoFieldPopup | null>(null);
  const [learningPlanRequestOpen, setLearningPlanRequestOpen] = React.useState(false);
  const learningPlanRequestOpenRef = React.useRef(false);
  learningPlanRequestOpenRef.current = learningPlanRequestOpen;
  React.useEffect(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'learning_text') setLearningPlanRequestOpen(false);
  }, [memoFieldPopup]);
  const learningPlanRequestContext = React.useMemo(() => {
    if (!learningPlanRequestOpen || memoFieldPopup?.fieldId !== 'learning_text') {
      return { subject: '', grade: '', unit: '', achievement: '', siblingLearnings: [] as string[] };
    }
    const annualKey = memoFieldPopup.annualKey;
    const row = rowByAnnualKey[annualKey];
    const unitParsed = getUnitTextAndNoteForDisplay(draftByAnnualKey[annualKey]?.unit_text ?? '');
    const learning = (memoFieldPopup.text || '').trim();
    const siblingLearnings: string[] = [];
    if (unitParsed.text) {
      for (const rowItem of tableRows) {
        if (rowItem.annualCellKey === annualKey) continue;
        const otherUnit = getUnitTextAndNoteForDisplay(draftByAnnualKey[rowItem.annualCellKey]?.unit_text ?? '').text;
        if (otherUnit !== unitParsed.text) continue;
        const title = formatLearningTextForDisplay(draftByAnnualKey[rowItem.annualCellKey]?.learning_text ?? '');
        if (!title || title === learning || siblingLearnings.includes(title)) continue;
        siblingLearnings.push(title);
        if (siblingLearnings.length >= 12) break;
      }
    }
    return {
      subject: row?.templateLabel ?? '',
      grade: row?.gradeLabel ?? '',
      unit: unitParsed.text,
      achievement: unitParsed.note,
      siblingLearnings,
    };
  }, [draftByAnnualKey, learningPlanRequestOpen, memoFieldPopup, rowByAnnualKey, tableRows]);
  const [evalPlanPickerOpen, setEvalPlanPickerOpen] = React.useState(false);
  const [assignmentMemoLinkPickerOpen, setAssignmentMemoLinkPickerOpen] = React.useState(false);
  const [assignmentPointsDocForLink, setAssignmentPointsDocForLink] =
    React.useState<AssignmentPointsNoteDoc | null>(null);
  const [crossCatalogPickerOpen, setCrossCatalogPickerOpen] = React.useState(false);
  const [crossTopicCountFloatOpen, setCrossTopicCountFloatOpen] = React.useState(false);
  /** 연간진도표 DB 집계에 덮어쓸 현재 편제 표 초안(실시간) */
  const currentTermDraftEntries = React.useMemo(() => {
    return tableRows.map((row) => {
      if (crossPopup && crossPopup.annualKey === row.annualCellKey) {
        return { annualCellKey: row.annualCellKey, topic: (crossPopup.text ?? '').trim() };
      }
      const draft = draftByAnnualKey[row.annualCellKey];
      const raw =
        draft?.record_cross_curricular_text ??
        programCellsByAnnualKey?.[row.annualCellKey]?.record_cross_curricular_text ??
        '';
      return { annualCellKey: row.annualCellKey, topic: parseCrossStorageRaw(raw).text };
    });
  }, [crossPopup, draftByAnnualKey, programCellsByAnnualKey, tableRows]);
  const [evalPlanViewSnap, setEvalPlanViewSnap] = React.useState<WorkspaceEvaluationPlanAppliedSnapshot | null>(
    null
  );
  const [evalPlanViewLoading, setEvalPlanViewLoading] = React.useState(false);
  const [isClassRecordModalExpanded, setIsClassRecordModalExpanded] = React.useState(false);
  const [isEvaluationModalExpanded, setIsEvaluationModalExpanded] = React.useState(false);
  const [isGenericMemoModalExpanded, setIsGenericMemoModalExpanded] = React.useState(false);
  React.useEffect(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') {
      setIsClassRecordModalExpanded(false);
      setAssignmentMemoLinkPickerOpen(false);
    }
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_evaluation_text') {
      setIsEvaluationModalExpanded(false);
      setEvalPlanPickerOpen(false);
      setEvalPlanViewSnap(null);
      setEvalPlanViewLoading(false);
    }
    if (
      !memoFieldPopup ||
      (memoFieldPopup.fieldId !== 'learning_text' && memoFieldPopup.fieldId !== 'record_memo_text')
    ) {
      setIsGenericMemoModalExpanded(false);
    }
  }, [memoFieldPopup]);
  const classRecordModalMembers = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return classMembers;
    if (memoFieldPopup.text === 'eval_log') {
      const gid = (memoFieldPopup.evalMemoMemberGroupId ?? '').trim();
      if (!gid) return classMembers;
      return membersForRecordGroup(memberGradeLayout, gid, classMembers);
    }
    return membersForRecordGroup(memberGradeLayout, memoFieldPopup.recordMemberGroupId, classMembers);
  }, [classMembers, memoFieldPopup, memberGradeLayout]);
  const evalSerializedNoteHasNonTagText = React.useCallback((serialized: string) => {
    const stripped = (serialized ?? '')
      .replace(/#[^\s#]+/g, '')
      .replace(/\u200b/g, '')
      .replace(/\s+/g, '')
      .trim();
    return stripped.length > 0;
  }, []);

  const finalizeClassRecordHashtagForMemberGroup = React.useCallback(
    (tagBody: string) => {
      const hits = matchMembersByClassRecordTagKeyword(memberGradeLayout, tagBody);
      if (hits.length === 0) return;
      if (hits.length === 1) {
        const gid = hits[0].groupId;
        setMemoFieldPopup((prev) =>
          prev && prev.fieldId === 'record_class_text'
            ? prev.text === 'eval_log'
              ? { ...prev, recordMemberGroupId: gid, evalMemoMemberGroupId: gid }
              : { ...prev, recordMemberGroupId: gid }
            : prev
        );
        return;
      }
      setClassRecordTagGroupPick({ keyword: tagBody, candidates: hits });
    },
    [memberGradeLayout]
  );
  const memoFieldPopupRef = React.useRef<HTMLDivElement | null>(null);
  const memoFieldPopupOpenSnapshotRef = React.useRef<MemoFieldPopupSnapshot | null>(null);
  const memoFieldPopupWasOpenRef = React.useRef(false);
  /** 평가계획 피커/조회 중에는 미니모달 바깥 클릭으로 닫지 않음 */
  const evalPlanPickerOpenRef = React.useRef(false);
  const evalPlanViewOpenRef = React.useRef(false);
  const crossCatalogPickerOpenRef = React.useRef(false);
  const assignmentMemoLinkPickerOpenRef = React.useRef(false);
  evalPlanPickerOpenRef.current = evalPlanPickerOpen;
  evalPlanViewOpenRef.current = !!evalPlanViewSnap;
  crossCatalogPickerOpenRef.current = crossCatalogPickerOpen;
  assignmentMemoLinkPickerOpenRef.current = assignmentMemoLinkPickerOpen;
  const memoFieldPopupStateRef = React.useRef(memoFieldPopup);
  memoFieldPopupStateRef.current = memoFieldPopup;
  /** 수업기록: 수업↔평가 탭 전환 시 스냅샷을 다시 잡아 자동 시드된 평가 메모를 dirty 로 보지 않음 */
  const lastClassRecMemoSnapshotKeyRef = React.useRef<string | null>(null);
  const memoFieldTextInputRef = React.useRef<HTMLInputElement | null>(null);
  const memoFieldNoteInputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const classRecordMemoNoteInputRef = React.useRef<ClassRecordMemoNoteEditorHandle | null>(null);
  const classRecordAiRef = React.useRef<AiRecordFeedbackPanelHandle>(null);
  const [classRecordAiDirty, setClassRecordAiDirty] = React.useState(false);
  const classRecordModalDirty =
    useDirtySinceOpen(
      memoFieldPopup?.fieldId === 'record_class_text' ? memoFieldPopup.annualKey : null,
      memoFieldPopup?.fieldId === 'record_class_text'
        ? JSON.stringify([
            memoFieldPopup.text ?? '',
            (memoFieldPopup.text ?? '') === 'eval_log' ? memoFieldPopup.noteEval ?? '' : memoFieldPopup.noteLesson ?? '',
            memoFieldPopup.assignmentMemoItemId ?? '',
          ])
        : ''
    ) || classRecordAiDirty;
  const mergeClassRecordPopupFromDom = React.useCallback((popup: MemoFieldPopup): MemoFieldPopup => {
    if (popup.fieldId !== 'record_class_text') return popup;
    const domNote = classRecordMemoNoteInputRef.current?.readSerializedNoteFromDom?.();
    if (domNote === undefined) return popup;
    if ((popup.text ?? '').trim() === 'eval_log') return { ...popup, noteEval: domNote };
    return { ...popup, noteLesson: domNote };
  }, []);
  const memoFieldPopupOpenedRef = React.useRef<string | null>(null);
  const [classRecordModalError, setClassRecordModalError] = React.useState<string | null>(null);
  /** 수업기록: #이름 태그 확정 시 동명이인·다중 그룹 선택 */
  const [classRecordTagGroupPick, setClassRecordTagGroupPick] = React.useState<{
    keyword: string;
    candidates: MemberWithGroupContext[];
  } | null>(null);
  /** 수업·평가기록: 멤버 태그 일괄 생성 시 기존 비태그 입력 초기화 확인 */
  const [evalTagGenConfirm, setEvalTagGenConfirm] = React.useState<{
    nextNote: string;
    sourceGroupId: string;
  } | null>(null);
  const classRecordPopupContext = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return null;
    const row = rowByAnnualKey[memoFieldPopup.annualKey];
    if (!row) return null;
    const learningRaw = draftByAnnualKey[memoFieldPopup.annualKey]?.learning_text ?? '';
    const learningText = (parseMemoFieldData(learningRaw).text || learningRaw || '').trim();
    const line1 = `${row.dateLabel} (${row.weekdayLabel}) ${row.periodLabel}${row.timetableText ? ` ${row.timetableText}` : ''}`;
    const line2 = learningText || '학습내용 없음';
    return { line1, line2 };
  }, [draftByAnnualKey, memoFieldPopup, parseMemoFieldData, rowByAnnualKey]);
  const evaluationPopupContext = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_evaluation_text') return null;
    const row = rowByAnnualKey[memoFieldPopup.annualKey];
    if (!row) return null;
    const learningRaw = draftByAnnualKey[memoFieldPopup.annualKey]?.learning_text ?? '';
    const learningText = (parseMemoFieldData(learningRaw).text || learningRaw || '').trim();
    const line1 = `${row.dateLabel} (${row.weekdayLabel}) ${row.periodLabel}${row.timetableText ? ` ${row.timetableText}` : ''}`;
    const line2 = learningText || '학습내용 없음';
    return { line1, line2 };
  }, [draftByAnnualKey, memoFieldPopup, parseMemoFieldData, rowByAnnualKey]);
  const [evalDropdown, setEvalDropdown] = React.useState<{ annualKey: string } | null>(null);
  const evalDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const cellAnchorByKeyRef = React.useRef<Map<string, HTMLTableCellElement>>(new Map());

  const floatingAnchor = React.useMemo(() => {
    if (unitPopup) return { kind: 'unit' as const, key: `${unitPopup.annualKey}\tunit_text` };
    if (crossPopup)
      return { kind: 'cross' as const, key: `${crossPopup.annualKey}\trecord_cross_curricular_text` };
    if (memoFieldPopup)
      return {
        kind: 'memo' as const,
        key: `${memoFieldPopup.annualKey}\t${memoFieldPopup.fieldId}`,
        fieldId: memoFieldPopup.fieldId,
      };
    if (evalDropdown)
      return { kind: 'evalDropdown' as const, key: `${evalDropdown.annualKey}\trecord_evaluation_text` };
    return null;
  }, [unitPopup, crossPopup, evalDropdown, memoFieldPopup]);

  React.useEffect(() => {
    if (memoFieldPopup?.fieldId === 'record_evaluation_text' && evalDropdown) {
      setEvalDropdown(null);
    }
  }, [memoFieldPopup, evalDropdown]);

  const [floatingAnchorStyle, setFloatingAnchorStyle] = React.useState<React.CSSProperties | null>(null);

  type HoverTableTooltipState = {
    anchorKey: string;
    kind: 'note' | 'unit' | 'cross' | 'material';
    note: string;
    isClassRecord: boolean;
    memberTagMembers?: ClassMemberRow[];
    /** 수업기록 툴팁: 그룹이 없는 레거시는 #전체·#이름- 규칙 비활성 */
    applyBulkMemberTagRules?: boolean;
    layoutKind: ProgramProgressOverlayWidthKind;
    style: React.CSSProperties;
  };
  const [hoverTableTooltip, setHoverTableTooltip] = React.useState<HoverTableTooltipState | null>(null);
  const hoverTableTooltipTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTableTooltipHideTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverTableTooltipRef = React.useRef<HoverTableTooltipState | null>(null);

  const clearHoverTableTooltipTimer = React.useCallback(() => {
    if (hoverTableTooltipTimerRef.current != null) {
      clearTimeout(hoverTableTooltipTimerRef.current);
      hoverTableTooltipTimerRef.current = null;
    }
  }, []);

  const scheduleHoverTableTooltip = React.useCallback(
    (payload: HoverTableTooltipState) => {
      clearHoverTableTooltipTimer();
      hoverTableTooltipTimerRef.current = setTimeout(() => {
        hoverTableTooltipTimerRef.current = null;
        setHoverTableTooltip(payload);
      }, PROGRAM_TABLE_CELL_HOVER_TOOLTIP_DELAY_MS);
    },
    [clearHoverTableTooltipTimer]
  );

  const clearHoverTableTooltipHideTimer = React.useCallback(() => {
    if (hoverTableTooltipHideTimerRef.current != null) {
      clearTimeout(hoverTableTooltipHideTimerRef.current);
      hoverTableTooltipHideTimerRef.current = null;
    }
  }, []);

  const scheduleHoverTableTooltipHide = React.useCallback((anchorKey: string) => {
    if (hoverTableTooltipHideTimerRef.current != null) return;
    hoverTableTooltipHideTimerRef.current = setTimeout(() => {
      hoverTableTooltipHideTimerRef.current = null;
      setHoverTableTooltip((prev) => (prev?.anchorKey === anchorKey ? null : prev));
    }, LEARNING_PLAN_HOVER_TOOLTIP_DISMISS_MS);
  }, []);

  React.useEffect(() => {
    hoverTableTooltipRef.current = hoverTableTooltip;
  }, [hoverTableTooltip]);

  const learningPlanHoverAnchorKey =
    hoverTableTooltip?.kind === 'note' &&
    !hoverTableTooltip.isClassRecord &&
    learningPlanGuideHasPlanMarkers(hoverTableTooltip.note)
      ? hoverTableTooltip.anchorKey
      : null;

  React.useEffect(() => {
    if (!learningPlanHoverAnchorKey) return;
    const anchorKey = learningPlanHoverAnchorKey;
    const onMove = (event: MouseEvent) => {
      const target = event.target;
      const onPopup = target instanceof Element && !!target.closest('.cp-program-table-hover-memo-tooltip');
      const cell = cellAnchorByKeyRef.current.get(anchorKey);
      const onCell = !!cell && target instanceof Node && (target === cell || cell.contains(target));
      if (onPopup || onCell) {
        clearHoverTableTooltipHideTimer();
        return;
      }
      scheduleHoverTableTooltipHide(anchorKey);
    };
    document.addEventListener('mousemove', onMove);
    return () => document.removeEventListener('mousemove', onMove);
  }, [clearHoverTableTooltipHideTimer, learningPlanHoverAnchorKey, scheduleHoverTableTooltipHide]);

  React.useEffect(() => () => {
    clearHoverTableTooltipTimer();
    clearHoverTableTooltipHideTimer();
  }, [clearHoverTableTooltipHideTimer, clearHoverTableTooltipTimer]);

  React.useEffect(() => {
    if (!memoFieldPopup) return;
    clearHoverTableTooltipTimer();
    clearHoverTableTooltipHideTimer();
    setHoverTableTooltip((prev) => (prev ? null : prev));
  }, [clearHoverTableTooltipHideTimer, clearHoverTableTooltipTimer, memoFieldPopup]);

  const sealedMemoTexts = React.useMemo(() => {
    const out: string[] = [];
    if (memoFieldPopup) {
      out.push(memoFieldPopup.note ?? '');
      out.push(memoFieldPopup.noteLesson ?? '');
      out.push(memoFieldPopup.noteEval ?? '');
    }
    if (hoverTableTooltip?.kind === 'note') out.push(hoverTableTooltip.note ?? '');
    Object.values(programCellsByAnnualKey).forEach((cell) => {
      if (!cell) return;
      out.push(parseMemoFieldStored(cell.record_class_text ?? '').note);
      out.push(parseMemoFieldStored(cell.record_evaluation_text ?? '').note);
    });
    return out;
  }, [memoFieldPopup, hoverTableTooltip, programCellsByAnnualKey]);
  const sealedLabelByToken = useSealedMemoTagLabels(sealedMemoTexts);

  const recomputeFloatingAnchor = React.useCallback(() => {
    if (!floatingAnchor) {
      setFloatingAnchorStyle(null);
      return;
    }
    const el = cellAnchorByKeyRef.current.get(floatingAnchor.key);
    if (!el) {
      setFloatingAnchorStyle(null);
      return;
    }
    const r = el.getBoundingClientRect();
    const pad = 8;
    // 스크롤 래퍼가 아니라 표 섹션(카드) 전체를 clamp 영역으로 사용 → 툴바까지 포함해 위·아래 잘림 완화
    const hostRect =
      resolveProgramTableFloatingHostRect(tableWrapRef.current) ??
      resolveProgramTableFloatingHostRect(programProgressScrollRef.current) ??
      programProgressScrollRef.current?.getBoundingClientRect() ??
      tableWrapRef.current?.getBoundingClientRect() ??
      null;
    const popupEl =
      floatingAnchor.kind === 'evalDropdown'
        ? evalDropdownRef.current
        : floatingAnchor.kind === 'unit'
          ? unitPopupRef.current
          : floatingAnchor.kind === 'cross'
            ? crossPopupRef.current
            : memoFieldPopupRef.current;
    const rawMeasured = popupEl?.offsetWidth ?? 0;
    const measuredW = rawMeasured > 1 ? rawMeasured : null;
    const cellLayoutWidth = { width: layoutWidthFromClientRect(r) };

    if (floatingAnchor.kind === 'evalDropdown') {
      setFloatingAnchorStyle(
        computeProgramTableFloatingPopupStyle({
          anchorRect: r,
          hostRect,
          preferredWidth: PROGRAM_EVAL_DROPDOWN_WIDTH_PX,
          measuredWidth: measuredW,
          alignX: 'right',
          pad,
          zIndex: 10060,
        })
      );
      return;
    }

    if (floatingAnchor.kind === 'unit') {
      const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(cellLayoutWidth, 'unit', pad);
      setFloatingAnchorStyle(
        computeProgramTableFloatingPopupStyle({
          anchorRect: r,
          hostRect,
          preferredWidth: w,
          measuredWidth: measuredW,
          alignX: 'left',
          pad,
          maxWidthCss: maxW,
          zIndex: 10060,
        })
      );
      return;
    }

    if (floatingAnchor.kind === 'cross') {
      const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(cellLayoutWidth, 'cross', pad);
      setFloatingAnchorStyle(
        computeProgramTableFloatingPopupStyle({
          anchorRect: r,
          hostRect,
          preferredWidth: w,
          measuredWidth: measuredW,
          alignX: 'right',
          pad,
          maxWidthCss: maxW,
          zIndex: 10060,
        })
      );
      return;
    }

    const fieldId = floatingAnchor.fieldId;
    const memoWidthKind: ProgramProgressOverlayWidthKind =
      fieldId === 'record_evaluation_text' || fieldId === 'record_class_text'
        ? 'memo_wide'
        : 'memo_narrow';
    const { width: w, maxWidth: maxW } = getProgramProgressOverlayWidth(
      cellLayoutWidth,
      memoWidthKind,
      pad
    );
    const learningMini = fieldId === 'learning_text' && !isGenericMemoModalExpanded;
    const contentHeight = learningMini ? popupEl?.scrollHeight ?? 0 : 0;
    setFloatingAnchorStyle(
      computeProgramTableFloatingPopupStyle({
        anchorRect: r,
        hostRect: learningMini ? null : hostRect,
        preferredWidth: w,
        measuredWidth: measuredW,
        alignX: 'right',
        pad,
        maxWidthCss: maxW,
        zIndex: 10060,
        contentHeight: contentHeight > 1 ? contentHeight : undefined,
      })
    );
  }, [floatingAnchor, isGenericMemoModalExpanded]);

  const recomputeHoverTableTooltip = React.useCallback(() => {
    setHoverTableTooltip((prev) => {
      if (!prev) return null;
      const el = cellAnchorByKeyRef.current.get(prev.anchorKey);
      if (!el) return null;
      return {
        ...prev,
        style:
          prev.kind === 'note' && !prev.isClassRecord && learningPlanGuideHasPlanMarkers(prev.note)
            ? computeLearningPlanHoverTooltipStyle(el.getBoundingClientRect())
            : computeProgramProgressHoverTooltipStyle(el.getBoundingClientRect(), prev.layoutKind),
      };
    });
  }, []);

  React.useLayoutEffect(() => {
    recomputeFloatingAnchor();
  }, [recomputeFloatingAnchor]);

  React.useLayoutEffect(() => {
    if (!floatingAnchor) return;
    const raf = window.requestAnimationFrame(() => {
      recomputeFloatingAnchor();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    floatingAnchor,
    memoFieldPopup?.text,
    memoFieldPopup?.note,
    memoFieldPopup?.noteLesson,
    memoFieldPopup?.noteEval,
    classRecordModalError,
    isClassRecordModalExpanded,
    isEvaluationModalExpanded,
    isGenericMemoModalExpanded,
    unitPopup?.text,
    unitPopup?.note,
    isUnitPopupExpanded,
    crossPopup?.text,
    crossPopup?.note,
    recomputeFloatingAnchor,
  ]);

  React.useEffect(() => {
    if (!floatingAnchor) return;
    const target =
      floatingAnchor.kind === 'evalDropdown'
        ? evalDropdownRef.current
        : floatingAnchor.kind === 'unit'
          ? unitPopupRef.current
          : floatingAnchor.kind === 'cross'
            ? crossPopupRef.current
            : memoFieldPopupRef.current;
    if (!target || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      recomputeFloatingAnchor();
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, [floatingAnchor, recomputeFloatingAnchor]);

  React.useEffect(() => {
    const scrollEl = programProgressScrollRef.current;
    const onMove = () => {
      recomputeFloatingAnchor();
      recomputeHoverTableTooltip();
    };
    scrollEl?.addEventListener('scroll', onMove, { passive: true });
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      scrollEl?.removeEventListener('scroll', onMove);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [recomputeFloatingAnchor, recomputeHoverTableTooltip]);

  React.useEffect(() => {
    if (floatingAnchor) {
      clearHoverTableTooltipTimer();
      setHoverTableTooltip(null);
    }
  }, [floatingAnchor, clearHoverTableTooltipTimer]);

  const commitCrossPopupNow = React.useCallback(
    (popup: { annualKey: string; text: string; note: string } | null) => {
      if (!popup) return;
      applyPatches([
        {
          annualKey: popup.annualKey,
          fieldId: 'record_cross_curricular_text',
          value: serializeCrossCurricularData(popup.text, popup.note),
        },
      ]);
    },
    [applyPatches, serializeCrossCurricularData]
  );
  const commitUnitPopupNow = React.useCallback(
    (popup: { annualKey: string; text: string; note: string } | null) => {
      if (!popup) return;
      applyPatches([
        {
          annualKey: popup.annualKey,
          fieldId: 'unit_text',
          value: serializeUnitData(popup.text, popup.note),
        },
      ]);
    },
    [applyPatches, serializeUnitData]
  );
  const commitMemoFieldPopupNow = React.useCallback(
    (popup: MemoFieldPopup | null) => {
      if (!popup) return;
      let popupText = popup.text;
      let popupNote =
        popup.fieldId === 'record_class_text'
          ? ''
          : popup.note;
      if (popup.fieldId === 'learning_text' || popup.fieldId === 'record_memo_text') {
        const domText = memoFieldTextInputRef.current?.value;
        const domNote = memoFieldNoteInputRef.current?.value;
        if (domText !== undefined) popupText = domText;
        if (domNote !== undefined) popupNote = domNote;
      }
      const normalizedRecordType =
        popup.fieldId === 'record_class_text'
          ? normalizeClassRecordType(
              popup.text,
              popup.text === 'eval_log' ? popup.noteEval : popup.noteLesson
            )
          : '';
      const note =
        popup.fieldId === 'record_class_text'
          ? normalizedRecordType === 'eval_log'
            ? (popup.noteEval ?? '')
            : (popup.noteLesson ?? '')
          : popupNote;
      const isExplicitClassRecordClear =
        popup.fieldId === 'record_class_text' &&
        !(popup.text ?? '').trim() &&
        !(popup.noteLesson ?? '').trim() &&
        !(popup.noteEval ?? '').trim();
      const preservedBehavior =
        popup.fieldId === 'record_class_text' && !isExplicitClassRecordClear
          ? (parseMemoFieldData(draftRef.current[popup.annualKey]?.record_class_text ?? '').behaviorNote ?? '').trim()
          : '';
      const preservedLearningRefs =
        popup.fieldId === 'learning_text'
          ? resolveLearningAttachmentRefsForMemoCommit(popup.annualKey)
          : { presentationRefId: undefined, worksheetRefId: undefined };
      const serialized = (() => {
        if (popup.fieldId === 'record_class_text') {
          return serializeClassRecordCellStorage(
            normalizedRecordType,
            note,
            preservedBehavior,
            popup.assignmentMemoItemId,
            popup.assignmentMemoItemTitle,
            popup.assignmentMemoGroupId,
            popup.assignmentMemoGroupTitle,
            {
              ...(parseMemoFieldStorageDeep(draftRef.current[popup.annualKey]?.record_class_text ?? '').aiFeedback ?? {}),
              ...(classRecordAiRef.current
                ? { [normalizedRecordType === 'eval_log' ? 'eval_log' : 'lesson']: classRecordAiRef.current.getEmbedValue() }
                : {}),
            }
          );
        }
        if (popup.fieldId === 'record_evaluation_text') {
          return serializeMemoFieldData(
            popupText,
            note,
            undefined,
            undefined,
            popup.evaluationPlanAppliedId,
            popup.evaluationPlanSummary,
            popup.evaluationPlanElement,
            popup.evaluationPlanMethod
          );
        }
        if (popup.fieldId !== 'learning_text') {
          return serializeMemoFieldData(
            popupText,
            note,
            preservedLearningRefs.presentationRefId,
            preservedLearningRefs.worksheetRefId
          );
        }
        const text = popupText ?? '';
        const noteValue = note ?? '';
        const pres = (preservedLearningRefs.presentationRefId ?? '').trim();
        const ws = (preservedLearningRefs.worksheetRefId ?? '').trim();
        if (!text.trim() && !noteValue.trim() && !pres && !ws) return '';
        return JSON.stringify({
          text,
          note: noteValue,
          ...(pres ? { presentationRefId: pres } : {}),
          ...(ws ? { worksheetRefId: ws } : {}),
        });
      })();
      applyPatches([
        {
          annualKey: popup.annualKey,
          fieldId: popup.fieldId,
          value: serialized,
          recordMemberGroupId:
            popup.fieldId === 'record_class_text'
              ? (() => {
                  if (popup.text === 'eval_log') {
                    if (!(popup.noteEval ?? '').trim()) return null;
                    const explicit =
                      (popup.evalMemoMemberGroupId ?? popup.recordMemberGroupId ?? '').trim() || null;
                    if (explicit) return explicit;
                    return inferSingleClassRecordNoteGroupId(popup.noteEval ?? '', memberGradeLayout);
                  }
                  const explicit = (popup.recordMemberGroupId ?? '').trim() || null;
                  if (explicit) return explicit;
                  return inferSingleClassRecordNoteGroupId(
                    popup.noteLesson ?? popup.note ?? '',
                    memberGradeLayout
                  );
                })()
              : undefined,
        },
      ]);
    },
    [
      applyPatches,
      memberGradeLayout,
      normalizeClassRecordType,
      parseMemoFieldData,
      resolveLearningAttachmentRefsForMemoCommit,
      serializeMemoFieldData,
    ]
  );
  /**
   * 학습내용 메모 모달 상태에서 첨부(프레젠/활동지) 액션을 누를 때,
   * 모달 입력값을 먼저 draft에 반영해 stale learning_text로 덮어쓰는 것을 방지한다.
   */
  const flushLearningMemoPopupBeforeAttachmentAction = React.useCallback(
    (annualKey: string) => {
      const popup = memoFieldPopup;
      if (!popup) return;
      if (popup.fieldId !== 'learning_text') return;
      if (popup.annualKey !== annualKey) return;
      commitMemoFieldPopupNow(popup);
    },
    [commitMemoFieldPopupNow, memoFieldPopup]
  );
  /** 수업기록 모달 확인·저장·dirty 상태 닫기: DOM 병합 후 검증·draft 반영 */
  const tryCommitClassRecordModalAndClose = React.useCallback(
    (popup: MemoFieldPopup): boolean => {
      if (popup.fieldId !== 'record_class_text') return true;
      const merged = mergeClassRecordPopupFromDom(popup);
      const activeNote = (merged.text === 'eval_log' ? merged.noteEval : merged.noteLesson) ?? '';
      const noteTrim = activeNote.trim();
      const existingBeh = (
        parseMemoFieldData(draftRef.current[merged.annualKey]?.record_class_text ?? '').behaviorNote ?? ''
      ).trim();
      if (!noteTrim && !existingBeh && !(merged.assignmentMemoItemId ?? '').trim()) {
        commitMemoFieldPopupNow({
          annualKey: merged.annualKey,
          fieldId: 'record_class_text',
          text: '',
          note: '',
          noteLesson: '',
          noteEval: '',
          recordMemberGroupId: null,
          evalMemoMemberGroupId: null,
          assignmentMemoItemId: null,
          assignmentMemoItemTitle: null,
          assignmentMemoGroupId: null,
          assignmentMemoGroupTitle: null,
        });
        setClassRecordModalError(null);
        setMemoFieldPopup(null);
        return true;
      }
      const textTrim = (merged.text ?? '').trim();
      if (textTrim !== 'lesson' && textTrim !== 'eval_log') {
        setClassRecordModalError('수업기록 또는 평가기록을 선택해 주세요.');
        return false;
      }
      commitMemoFieldPopupNow(merged);
      setClassRecordModalError(null);
      setMemoFieldPopup(null);
      return true;
    },
    [commitMemoFieldPopupNow, mergeClassRecordPopupFromDom, parseMemoFieldData]
  );
  const assignmentMemoMembersById = React.useMemo(() => {
    const map = new Map<string, AssignmentMemoMemberRef>();
    for (const g of memberGradeLayout.groups) {
      const list = memberGradeLayout.membersByGroupId[g.id] ?? [];
      const nums = getClassMemberDisplayNumbers(list);
      list.forEach((m, index) => {
        map.set(m.id, {
          id: m.id,
          name: displayClassMemberName(m).trim(),
          // 0-based 필드: 고정 번호 - 1 (번호가 없으면 순서)
          orderIndex: (nums[index] ?? index + 1) - 1,
          groupId: g.id,
        });
      });
    }
    return map;
  }, [memberGradeLayout]);

  React.useEffect(() => {
    const isClassRec =
      !!memoFieldPopup && memoFieldPopup.fieldId === 'record_class_text';
    if (!isClassRec) {
      setAssignmentPointsDocForLink(null);
      return;
    }
    const ws = workspaceId.trim();
    const gid = progressGradeId.trim();
    if (!ws || !gid) {
      setAssignmentPointsDocForLink(null);
      return;
    }
    let cancelled = false;
    void getAssignmentPointsNote(ws, gid)
      .then((doc) => {
        if (!cancelled) setAssignmentPointsDocForLink(doc);
      })
      .catch(() => {
        if (!cancelled) setAssignmentPointsDocForLink(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    memoFieldPopup?.fieldId,
    memoFieldPopup?.annualKey,
    progressGradeId,
    workspaceId,
  ]);

  const classRecordLinkedAssignmentPreview = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return '';
    const itemId = (memoFieldPopup.assignmentMemoItemId ?? '').trim();
    if (!itemId) return '';
    const groupId = (memoFieldPopup.assignmentMemoGroupId ?? '').trim();
    const allowedMemberIds = groupId
      ? new Set((memberGradeLayout.membersByGroupId[groupId] ?? []).map((m) => m.id))
      : null;
    return resolveLinkedAssignmentMemoNote({
      doc: assignmentPointsDocForLink,
      itemId,
      membersById: assignmentMemoMembersById,
      allowedMemberIds,
    });
  }, [
    assignmentMemoMembersById,
    assignmentPointsDocForLink,
    memberGradeLayout.membersByGroupId,
    memoFieldPopup,
  ]);

  const classRecordModalCanDelete = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return false;
    const parsed = parseMemoFieldData(draftRef.current[memoFieldPopup.annualKey]?.record_class_text ?? '');
    const draftText = (parsed.text ?? '').trim();
    const draftNote = (parsed.note ?? '').trim();
    if (draftNote) return true;
    if ((parsed.assignmentMemoItemId ?? '').trim()) return true;
    if (draftText === 'lesson' || draftText === 'eval_log') return true;
    // 레거시(평문) 기록: text 에 본문이 그대로 들어 있으면 삭제 가능
    if (draftText) return true;
    const popupText = (memoFieldPopup.text ?? '').trim();
    const popupNote =
      popupText === 'eval_log' ? (memoFieldPopup.noteEval ?? '') : (memoFieldPopup.noteLesson ?? '');
    if (popupNote.trim()) return true;
    if ((memoFieldPopup.assignmentMemoItemId ?? '').trim()) return true;
    if ((memoFieldPopup.noteLesson ?? '').trim() || (memoFieldPopup.noteEval ?? '').trim()) return true;
    return !!popupText;
  }, [memoFieldPopup, parseMemoFieldData]);
  const deleteClassRecordFromModal = React.useCallback(
    (popup: MemoFieldPopup) => {
      // 동기화 미러 뷰(restrictMutatingActions)에서도 기록 열은 저장과 동일하게 편집 가능 → 잠긴 필드만 차단
      if (popup.fieldId !== 'record_class_text' || readOnlyRef.current || isFieldLocked('record_class_text')) return;
      const preservedBehavior = (
        parseMemoFieldData(draftRef.current[popup.annualKey]?.record_class_text ?? '').behaviorNote ?? ''
      ).trim();
      applyPatches([
        {
          annualKey: popup.annualKey,
          fieldId: 'record_class_text',
          value: serializeClassRecordCellStorage('', '', preservedBehavior),
          recordMemberGroupId: null,
        },
      ]);
      setClassRecordModalError(null);
      setMemoFieldPopup(null);
    },
    [applyPatches, isFieldLocked, parseMemoFieldData]
  );
  const memoFieldPopupCanDelete = React.useMemo(() => {
    if (!memoFieldPopup || memoFieldPopup.fieldId === 'record_class_text') return false;
    if (memoFieldPopup.fieldId === 'learning_text' || memoFieldPopup.fieldId === 'record_memo_text') {
      const domText = (memoFieldTextInputRef.current?.value ?? memoFieldPopup.text ?? '').trim();
      const domNote = (memoFieldNoteInputRef.current?.value ?? memoFieldPopup.note ?? '').trim();
      return !!(domText || domNote);
    }
    if (memoFieldPopup.fieldId === 'record_evaluation_text') {
      return !!(
        (memoFieldPopup.text ?? '').trim() ||
        (memoFieldPopup.note ?? '').trim() ||
        (memoFieldPopup.evaluationPlanAppliedId ?? '').trim() ||
        (memoFieldPopup.evaluationPlanSummary ?? '').trim()
      );
    }
    return !!((memoFieldPopup.text ?? '').trim() || (memoFieldPopup.note ?? '').trim());
  }, [memoFieldPopup]);
  const clearMemoFieldPopupContent = React.useCallback(() => {
    if (!memoFieldPopup || readOnlyRef.current || mutationLockedRef.current) return;
    if (memoFieldPopup.fieldId === 'record_class_text') return;
    if (memoFieldPopup.fieldId === 'learning_text' || memoFieldPopup.fieldId === 'record_memo_text') {
      if (memoFieldTextInputRef.current) memoFieldTextInputRef.current.value = '';
      if (memoFieldNoteInputRef.current) memoFieldNoteInputRef.current.value = '';
    }
    if (memoFieldPopup.fieldId === 'record_evaluation_text') {
      setEvalPlanPickerOpen(false);
      setEvalPlanViewSnap(null);
      commitMemoFieldPopupNow({
        ...memoFieldPopup,
        text: '',
        note: '',
        evaluationPlanAppliedId: null,
        evaluationPlanSummary: null,
        evaluationPlanElement: null,
        evaluationPlanMethod: null,
      });
    } else {
      commitMemoFieldPopupNow({
        ...memoFieldPopup,
        text: '',
        note: '',
      });
    }
    setMemoFieldPopup(null);
  }, [commitMemoFieldPopupNow, memoFieldPopup]);
  const openEvaluationMemoModal = React.useCallback(
    (annualKey: string) => {
      const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
      const parsed = parseMemoFieldData(rowDraft.record_evaluation_text ?? '');
      setEvalDropdown(null);
      setMemoFieldPopup({
        annualKey,
        fieldId: 'record_evaluation_text',
        text: parsed.text ?? '',
        note: parsed.note ?? '',
        evaluationPlanAppliedId: parsed.evaluationPlanAppliedId ?? null,
        evaluationPlanSummary: parsed.evaluationPlanSummary ?? null,
        evaluationPlanElement: parsed.evaluationPlanElement ?? null,
        evaluationPlanMethod: parsed.evaluationPlanMethod ?? null,
      });
      setSelectedCells([makeCellId(annualKey, 'record_evaluation_text')]);
    },
    [ensureDraftRow, makeCellId, parseMemoFieldData]
  );

  const openClassRecordMemoModal = React.useCallback(
    (annualKey: string) => {
      setClassRecordTagGroupPick(null);
      setEvalTagGenConfirm(null);
      const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
      const parsed = parseMemoFieldData(rowDraft.record_class_text ?? '');
      const rawText = (parsed.text ?? '').trim();
      const text = rawText === 'lesson' || rawText === 'eval_log' ? rawText : 'lesson';
      const savedNote = parsed.note ?? '';
      let noteLesson = text === 'lesson' ? savedNote : '';
      let noteEval = text === 'eval_log' ? savedNote : '';
      setClassRecordModalError(null);
      let recordMemberGroupId: string | null = rowDraft.record_member_group_id ?? null;
      if (
        recordMemberGroupId &&
        memberGradeLayout.groups.length > 0 &&
        !memberGradeLayout.groups.some((g) => g.id === recordMemberGroupId)
      ) {
        recordMemberGroupId = null;
      }
      if (!recordMemberGroupId) {
        recordMemberGroupId = resolveDefaultRecordGroupId(
          memberGradeLayout,
          loadSelectedClassGroupId(workspaceId, progressGradeId)
        );
      }
      let evalMemoMemberGroupId: string | null = null;
      if (
        text === 'eval_log' &&
        (noteEval ?? '').trim() &&
        recordMemberGroupId &&
        memberGradeLayout.groups.some((g) => g.id === recordMemberGroupId)
      ) {
        evalMemoMemberGroupId = recordMemberGroupId;
      }
      setMemoFieldPopup({
        annualKey,
        fieldId: 'record_class_text',
        text,
        note: '',
        noteLesson,
        noteEval,
        recordMemberGroupId,
        evalMemoMemberGroupId,
        assignmentMemoItemId: (parsed.assignmentMemoItemId ?? '').trim() || null,
        assignmentMemoItemTitle: (parsed.assignmentMemoItemTitle ?? '').trim() || null,
        assignmentMemoGroupId: (parsed.assignmentMemoGroupId ?? '').trim() || null,
        assignmentMemoGroupTitle: (parsed.assignmentMemoGroupTitle ?? '').trim() || null,
      });
      setSelectedCells([makeCellId(annualKey, 'record_class_text')]);
    },
    [ensureDraftRow, makeCellId, memberGradeLayout, parseMemoFieldData, progressGradeId, workspaceId]
  );

  React.useEffect(() => {
    if (!memoFieldPopup) {
      setClassRecordTagGroupPick(null);
      setEvalTagGenConfirm(null);
    }
  }, [memoFieldPopup]);

  /** 셀 mouseup(단일 클릭)과 동일하게 메모·기록 열 모달·드롭다운을 연다. 선택 셀은 호출부에서 먼저 반영한다. */
  const openRecordFieldModalAfterSelect = React.useCallback(
    (annualKey: string, fieldId: FieldId) => {
      if (readOnlyRef.current || isFieldLocked(fieldId)) return;
      if (fieldId === 'record_class_text' && requireFeature('class_record')) return;
      if (fieldId === 'record_evaluation_text') {
        if (evalLongPressSuppressClickRef.current) {
          evalLongPressSuppressClickRef.current = false;
          return;
        }
        setMemoFieldPopup(null);
        setEvalDropdown({ annualKey });
        return;
      }
      if (fieldId === 'record_class_text') {
        setEvalDropdown(null);
        openClassRecordMemoModal(annualKey);
        return;
      }
      if (fieldId === 'record_cross_curricular_text') {
        const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
        const cross = parseCrossCurricularData(rowDraft.record_cross_curricular_text ?? '');
        setCrossPopup({
          annualKey,
          text: cross.text ?? '',
          note: cross.note ?? '',
        });
        return;
      }
      if (fieldId === 'record_memo_text') {
        const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
        const memo = parseMemoFieldData(rowDraft.record_memo_text ?? '');
        setEvalDropdown(null);
        setMemoFieldPopup({
          annualKey,
          fieldId: 'record_memo_text',
          text: memo.text ?? '',
          note: memo.note ?? '',
        });
      }
    },
    [ensureDraftRow, isFieldLocked, openClassRecordMemoModal, parseCrossCurricularData, parseMemoFieldData]
  );

  const clearLongPressTimer = React.useCallback(() => {
    if (longPressTimerRef.current == null) return;
    window.clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = null;
  }, []);
  const openLongPressPopup = React.useCallback(
    (annualKey: string, fieldId: FieldId) => {
      if (readOnlyRef.current || isFieldLocked(fieldId)) return;
      if (fieldId === 'unit_text') {
        const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
        const unit = parseUnitData(rowDraft.unit_text ?? '');
        setSelectedCells([makeCellId(annualKey, fieldId)]);
        setUnitPopup({ annualKey, text: unit.text ?? '', note: unit.note ?? '' });
        return;
      }
      if (fieldId === 'learning_text') {
        const learningRaw =
          draftRef.current[annualKey]?.learning_text ??
          ensureDraftRow(draftRef.current, annualKey).learning_text ??
          '';
        const memo = parseMemoFieldData(learningRaw);
        setSelectedCells([makeCellId(annualKey, fieldId)]);
        setEvalDropdown(null);
        setMemoFieldPopup({
          annualKey,
          fieldId: 'learning_text',
          text: memo.text ?? '',
          note: memo.note ?? '',
        });
        return;
      }
      if (fieldId === 'record_evaluation_text') {
        setEvalDropdown(null);
        setSelectedCells([makeCellId(annualKey, fieldId)]);
        openEvaluationMemoModal(annualKey);
      }
    },
    [ensureDraftRow, isFieldLocked, makeCellId, openEvaluationMemoModal, parseMemoFieldData, parseUnitData]
  );
  const closeFloatingOverlay = React.useCallback(() => {
    if (memoFieldPopup) {
      setClassRecordModalError(null);
      setMemoFieldPopup(null);
      return true;
    }
    if (unitPopup) {
      setUnitPopup(null);
      return true;
    }
    if (crossPopup) {
      setCrossPopup(null);
      return true;
    }
    if (evalDropdown) {
      setEvalDropdown(null);
      return true;
    }
    return false;
  }, [crossPopup, evalDropdown, memoFieldPopup, unitPopup]);

  React.useEffect(() => {
    if (unitPopup) {
      if (!unitPopupWasOpenRef.current) {
        unitPopupOpenSnapshotRef.current = { text: unitPopup.text, note: unitPopup.note };
      }
      unitPopupWasOpenRef.current = true;
    } else {
      unitPopupWasOpenRef.current = false;
      unitPopupOpenSnapshotRef.current = null;
    }
  }, [unitPopup]);

  React.useEffect(() => {
    if (crossPopup) {
      if (!crossPopupWasOpenRef.current) {
        crossPopupOpenSnapshotRef.current = { text: crossPopup.text, note: crossPopup.note };
      }
      crossPopupWasOpenRef.current = true;
      return;
    }
    crossPopupWasOpenRef.current = false;
    crossPopupOpenSnapshotRef.current = null;
    setCrossCatalogPickerOpen(false);
  }, [crossPopup]);

  React.useEffect(() => {
    if (!memoFieldPopup) {
      memoFieldPopupWasOpenRef.current = false;
      memoFieldPopupOpenSnapshotRef.current = null;
      lastClassRecMemoSnapshotKeyRef.current = null;
      return;
    }

    if (memoFieldPopup.fieldId === 'record_class_text') {
      memoFieldPopupWasOpenRef.current = true;
      const tabKey = `${memoFieldPopup.annualKey}\t${memoFieldPopup.text}`;
      if (lastClassRecMemoSnapshotKeyRef.current !== tabKey) {
        lastClassRecMemoSnapshotKeyRef.current = tabKey;
        memoFieldPopupOpenSnapshotRef.current = {
          fieldId: 'record_class_text',
          text: memoFieldPopup.text,
          noteLesson: memoFieldPopup.noteLesson ?? '',
          noteEval: memoFieldPopup.noteEval ?? '',
          recordMemberGroupId: memoFieldPopup.recordMemberGroupId ?? null,
          evalMemoMemberGroupId: memoFieldPopup.evalMemoMemberGroupId ?? null,
          assignmentMemoItemId: memoFieldPopup.assignmentMemoItemId ?? null,
          assignmentMemoItemTitle: memoFieldPopup.assignmentMemoItemTitle ?? null,
          assignmentMemoGroupId: memoFieldPopup.assignmentMemoGroupId ?? null,
          assignmentMemoGroupTitle: memoFieldPopup.assignmentMemoGroupTitle ?? null,
        };
      }
      return;
    }

    lastClassRecMemoSnapshotKeyRef.current = null;
    if (!memoFieldPopupWasOpenRef.current) {
      memoFieldPopupOpenSnapshotRef.current = {
        fieldId: memoFieldPopup.fieldId,
        text: memoFieldPopup.text,
        note: memoFieldPopup.note,
        evaluationPlanAppliedId: memoFieldPopup.evaluationPlanAppliedId ?? null,
        evaluationPlanSummary: memoFieldPopup.evaluationPlanSummary ?? null,
        evaluationPlanElement: memoFieldPopup.evaluationPlanElement ?? null,
        evaluationPlanMethod: memoFieldPopup.evaluationPlanMethod ?? null,
      };
    }
    memoFieldPopupWasOpenRef.current = true;
  }, [memoFieldPopup]);

  React.useEffect(() => {
    if (!crossPopup) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (crossPopupRef.current?.contains(target)) return;
      if (crossCatalogPickerOpenRef.current) return;
      const targetEl = target instanceof Element ? target : target.parentElement;
      /** 튜토리얼 자체 UI(말풍선 "다음" 버튼 등) 클릭은 "바깥 클릭"으로 취급하지 않는다.
       * 팝업을 닫고 다음 단계로 넘기는 책임은 튜토리얼 엔진이 전담한다. */
      if (targetEl?.closest('.cp-tutorial-root')) return;
      if (
        targetEl?.closest(
          '.cp-cross-curricular-catalog-picker-backdrop, .cp-cross-curricular-catalog-picker-modal'
        )
      ) {
        return;
      }
      /** 모달 state 는 열린 채로 그리드 복사·붙여넣기만 하면 draft 만 바뀜. 미수정이면 커밋하면 옛 값으로 덮어씀 */
      if (isUnitPopupDirtyFn(crossPopup, crossPopupOpenSnapshotRef.current)) {
        commitCrossPopupNow(crossPopup);
      }
      setCrossPopup(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [commitCrossPopupNow, crossPopup, isUnitPopupDirtyFn]);
  React.useEffect(() => {
    if (!unitPopup) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (unitPopupRef.current?.contains(target)) return;
      const targetEl = target instanceof Element ? target : target.parentElement;
      if (targetEl?.closest('.cp-tutorial-root')) return;
      if (isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) return;
      commitUnitPopupNow(unitPopup);
      setUnitPopup(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [commitUnitPopupNow, isUnitPopupDirtyFn, unitPopup]);
  React.useEffect(() => {
    if (!memoFieldPopup) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      const popupEl = memoFieldPopupRef.current;
      if (!popupEl) return;
      if (popupEl.contains(target)) return;
      /** 평가계획 가져오기/보기 모달 위 클릭은 미니모달을 닫지 않음(등록 전 mousedown에 닫히던 문제) */
      if (
        evalPlanPickerOpenRef.current ||
        evalPlanViewOpenRef.current ||
        assignmentMemoLinkPickerOpenRef.current ||
        learningPlanRequestOpenRef.current
      ) {
        return;
      }
      const targetEl = target instanceof Element ? target : target.parentElement;
      if (targetEl?.closest('.cp-tutorial-root')) return;
      if (
        targetEl?.closest(
          [
            '.cp-evaluation-plan-applied-picker-backdrop',
            '.cp-evaluation-plan-applied-picker-modal',
            '.cp-evaluation-plan-plan-form-modal',
            '.cp-evaluation-plan-web-doc-viewer-modal',
            '.cp-evaluation-plan-sheet-canvas-viewer-modal',
            '.cp-evaluation-plan-web-doc-backdrop',
            '.cp-evaluation-plan-sheet-canvas-backdrop',
            '.cp-evaluation-plan-attachment-preview-backdrop',
            '.cp-evaluation-plan-attachment-register-backdrop',
          ].join(', ')
        )
      ) {
        return;
      }
      const overlayBackdrop = targetEl?.closest('.cp-modal-backdrop');
      if (
        overlayBackdrop?.querySelector(
          '.cp-evaluation-plan-applied-picker-modal, .cp-evaluation-plan-plan-form-modal'
        )
      ) {
        return;
      }
      if (
        isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
        isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current)
      ) {
        return;
      }
      if (memoFieldPopup.fieldId === 'record_class_text') {
        setClassRecordModalError(null);
        setMemoFieldPopup(null);
        return;
      }
      /** 평가 메모: 바깥 클릭 시 미반영 닫기(확인에서만 반영) */
      if (memoFieldPopup.fieldId === 'record_evaluation_text') {
        setEvalPlanPickerOpen(false);
        setEvalPlanViewSnap(null);
        setMemoFieldPopup(null);
        return;
      }
      commitMemoFieldPopupNow(memoFieldPopup);
      setMemoFieldPopup(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [commitMemoFieldPopupNow, isDoubleClickMemoOrClassRecordField, isMemoFieldPopupDirtyFn, memoFieldPopup]);
  React.useEffect(() => {
    if (!evalDropdown) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (evalDropdownRef.current?.contains(target)) return;
      const targetEl = target instanceof Element ? target : target.parentElement;
      if (targetEl?.closest('.cp-tutorial-root')) return;
      setEvalDropdown(null);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [evalDropdown]);
  React.useEffect(() => {
    if (!memoFieldPopup && !unitPopup && !crossPopup && !evalDropdown) return;
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (learningPlanRequestOpenRef.current) {
        e.preventDefault();
        e.stopPropagation();
        setLearningPlanRequestOpen(false);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      closeFloatingOverlay();
    };
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => document.removeEventListener('keydown', onDocKeyDown, true);
  }, [closeFloatingOverlay, crossPopup, evalDropdown, memoFieldPopup, unitPopup]);
  React.useEffect(() => {
    if (!crossPopup) {
      crossPopupOpenedKeyRef.current = null;
      return;
    }
    // 팝업을 "처음 연 경우"에만 주제어 자동 포커스. 타이핑 중 재포커싱 방지.
    if (crossPopupOpenedKeyRef.current === crossPopup.annualKey) return;
    crossPopupOpenedKeyRef.current = crossPopup.annualKey;
    const t = window.setTimeout(() => {
      const input = crossTopicInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);
    return () => window.clearTimeout(t);
  }, [crossPopup?.annualKey]);
  React.useEffect(() => {
    if (!unitPopup) {
      unitPopupOpenedKeyRef.current = null;
      return;
    }
    if (unitPopupOpenedKeyRef.current === unitPopup.annualKey) return;
    unitPopupOpenedKeyRef.current = unitPopup.annualKey;
    const t = window.setTimeout(() => {
      const input = unitNameInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);
    return () => window.clearTimeout(t);
  }, [unitPopup?.annualKey]);

  React.useEffect(() => {
    if (!unitPopup) {
      setIsUnitPopupExpanded(false);
    }
  }, [unitPopup]);
  React.useEffect(() => {
    if (!memoFieldPopup) {
      memoFieldPopupOpenedRef.current = null;
      return;
    }
    const key =
      memoFieldPopup.fieldId === 'record_class_text'
        ? `${memoFieldPopup.annualKey}:${memoFieldPopup.fieldId}:${memoFieldPopup.text}`
        : `${memoFieldPopup.annualKey}:${memoFieldPopup.fieldId}`;
    if (memoFieldPopupOpenedRef.current === key) return;
    memoFieldPopupOpenedRef.current = key;
    const t = window.setTimeout(() => {
      if (memoFieldPopup.fieldId === 'record_class_text') {
        classRecordMemoNoteInputRef.current?.focus({ preventScroll: true });
        return;
      }
      const input = memoFieldTextInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }, 0);
    return () => window.clearTimeout(t);
  }, [
    memoFieldPopup?.annualKey,
    memoFieldPopup?.fieldId,
    memoFieldPopup && memoFieldPopup.fieldId === 'record_class_text' ? memoFieldPopup.text : undefined,
  ]);

  const getSelectionRect = React.useCallback(
    (cells: string[]) => {
      if (!cells.length) return null;
      const points = cells
        .map(parseCellId)
        .filter((v): v is { annualKey: string; fieldId: FieldId } => !!v)
        .map((v) => ({
          rowIdx: rowIdxByAnnualKey[v.annualKey],
          colIdx: colIdxByField[v.fieldId],
          annualKey: v.annualKey,
          fieldId: v.fieldId,
        }))
        .filter((v) => Number.isInteger(v.rowIdx) && Number.isInteger(v.colIdx));
      if (!points.length) return null;
      const minRow = Math.min(...points.map((p) => p.rowIdx));
      const maxRow = Math.max(...points.map((p) => p.rowIdx));
      const minCol = Math.min(...points.map((p) => p.colIdx));
      const maxCol = Math.max(...points.map((p) => p.colIdx));
      return { minRow, maxRow, minCol, maxCol };
    },
    [colIdxByField, parseCellId, rowIdxByAnnualKey]
  );

  const selectRect = React.useCallback(
    (anchorCellId: string, hoverCellId: string) => {
      const a = parseCellId(anchorCellId);
      const b = parseCellId(hoverCellId);
      if (!a || !b) return;
      const ar = rowIdxByAnnualKey[a.annualKey];
      const ac = colIdxByField[a.fieldId];
      const br = rowIdxByAnnualKey[b.annualKey];
      const bc = colIdxByField[b.fieldId];
      if ([ar, ac, br, bc].some((v) => !Number.isInteger(v))) return;
      const minRow = Math.min(ar, br);
      const maxRow = Math.max(ar, br);
      const minCol = Math.min(ac, bc);
      const maxCol = Math.max(ac, bc);
      const out: string[] = [];
      for (let r = minRow; r <= maxRow; r += 1) {
        const annualKey = tableRows[r]?.annualCellKey;
        if (!annualKey) continue;
        for (let c = minCol; c <= maxCol; c += 1) {
          const fieldId = editableFieldOrder[c];
          if (!fieldId) continue;
          out.push(makeCellId(annualKey, fieldId));
        }
      }
      setSelectedCells(out);
    },
    [colIdxByField, editableFieldOrder, makeCellId, parseCellId, rowIdxByAnnualKey, tableRows]
  );

  const attachCellDragMoveListener = React.useCallback(() => {
    cellDragDocCleanupRef.current?.();
    const onMove = (e: MouseEvent) => {
      const wrap = programProgressScrollRef.current;
      const pending = pointerDownRef.current;
      if (!wrap || !pending) return;
      const rect = wrap.getBoundingClientRect();
      const edge = 48;
      const maxStep = 16;
      if (e.clientY < rect.top + edge) {
        const t = Math.min(1, (rect.top + edge - e.clientY) / edge);
        wrap.scrollTop -= Math.max(1, Math.round(maxStep * t));
      } else if (e.clientY > rect.bottom - edge) {
        const t = Math.min(1, (e.clientY - (rect.bottom - edge)) / edge);
        wrap.scrollTop += Math.max(1, Math.round(maxStep * t));
      }
      if (e.clientX < rect.left + edge) {
        const t = Math.min(1, (rect.left + edge - e.clientX) / edge);
        wrap.scrollLeft -= Math.max(1, Math.round(maxStep * t));
      } else if (e.clientX > rect.right - edge) {
        const t = Math.min(1, (e.clientX - (rect.right - edge)) / edge);
        wrap.scrollLeft += Math.max(1, Math.round(maxStep * t));
      }
      const stack = document.elementsFromPoint(e.clientX, e.clientY);
      let td: HTMLElement | null = null;
      for (const el of stack) {
        if (el instanceof HTMLElement && el.matches('td[data-cp-annual-cell][data-cp-field-id]')) {
          td = el;
          break;
        }
      }
      if (!td) return;
      const ak = td.getAttribute('data-cp-annual-cell') ?? '';
      const fid = td.getAttribute('data-cp-field-id') as FieldId | null;
      if (!ak || !fid || !editableFieldOrder.includes(fid)) return;
      const hoverCellId = makeCellId(ak, fid);
      if (hoverCellId === pending.cellId && !pending.didDrag) return;
      pending.didDrag = true;
      selectRect(pending.cellId, hoverCellId);
    };
    document.addEventListener('mousemove', onMove);
    cellDragDocCleanupRef.current = () => {
      document.removeEventListener('mousemove', onMove);
    };
  }, [editableFieldOrder, makeCellId, selectRect]);

  React.useEffect(() => {
    const onUp = () => {
      cellDragDocCleanupRef.current?.();
      cellDragDocCleanupRef.current = null;
      const pending = pointerDownRef.current;
      clearLongPressTimer();
      const suppressEvalClick = evalLongPressSuppressClickRef.current;
      evalLongPressSuppressClickRef.current = false;
      if (!pending) return;
      pointerDownRef.current = null;
      if (pending.didDrag || pending.openedByLongPress || suppressEvalClick) return;
      setSelectedCells([makeCellId(pending.annualKey, pending.fieldId)]);
      openRecordFieldModalAfterSelect(pending.annualKey, pending.fieldId);
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, [clearLongPressTimer, makeCellId, openRecordFieldModalAfterSelect]);
  React.useEffect(() => () => clearLongPressTimer(), [clearLongPressTimer]);

  const moveSingleSelectionByFlatStep = React.useCallback(
    (step: number, startEdit = false) => {
      const one = selectedCellsRef.current[0];
      if (!one || selectedCellsRef.current.length !== 1) return;
      const parsed = parseCellId(one);
      if (!parsed) return;
      const rowIdx = rowIdxByAnnualKey[parsed.annualKey];
      const colIdx = colIdxByField[parsed.fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return;
      const colCount = editableFieldOrder.length;
      const maxFlat = tableRows.length * colCount - 1;
      if (maxFlat < 0) return;
      const curFlat = rowIdx * colCount + colIdx;
      const nextFlat = Math.min(maxFlat, Math.max(0, curFlat + step));
      const nr = Math.floor(nextFlat / colCount);
      const nc = nextFlat % colCount;
      const annualKey = tableRows[nr]?.annualCellKey;
      const fieldId = editableFieldOrder[nc];
      if (!annualKey || !fieldId) return;
      setSelectedCells([makeCellId(annualKey, fieldId)]);
      if (startEdit) {
        if (readOnlyRef.current || isFieldLocked(fieldId)) return;
        if (fieldId === 'record_evaluation_text') {
          setEvalDropdown({ annualKey });
        } else if (fieldId === 'record_class_text') {
          setEvalDropdown(null);
          openClassRecordMemoModal(annualKey);
        } else {
          beginEditRef.current?.(annualKey, fieldId);
        }
      }
    },
    [colIdxByField, editableFieldOrder, isFieldLocked, makeCellId, openClassRecordMemoModal, parseCellId, rowIdxByAnnualKey, tableRows]
  );

  const moveSelectionHorizontal = React.useCallback(
    (delta: number, startEdit = false) => {
      const one = selectedCellsRef.current[0];
      if (!one || selectedCellsRef.current.length !== 1) return;
      const parsed = parseCellId(one);
      if (!parsed) return;
      const rowIdx = rowIdxByAnnualKey[parsed.annualKey];
      const colIdx = colIdxByField[parsed.fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return;
      const nc = Math.max(0, Math.min(editableFieldOrder.length - 1, colIdx + delta));
      const annualKey = tableRows[rowIdx]?.annualCellKey;
      const fieldId = editableFieldOrder[nc];
      if (!annualKey || !fieldId) return;
      setSelectedCells([makeCellId(annualKey, fieldId)]);
      if (startEdit) {
        if (readOnlyRef.current || isFieldLocked(fieldId)) return;
        if (fieldId === 'record_evaluation_text') {
          setEvalDropdown({ annualKey });
        } else if (fieldId === 'record_class_text') {
          setEvalDropdown(null);
          openClassRecordMemoModal(annualKey);
        } else {
          beginEditRef.current?.(annualKey, fieldId);
        }
      }
    },
    [colIdxByField, editableFieldOrder, isFieldLocked, makeCellId, openClassRecordMemoModal, parseCellId, rowIdxByAnnualKey, tableRows]
  );

  /** 연간진도표와 동일 규칙: ArrowUp/Down 후 선택 행이 thead 아래~래퍼 하단 안에 오도록 스크롤 */
  const scrollProgramProgressForVerticalKey = React.useCallback(
    (rowIdx: number, annualKey: string, fieldId: FieldId, keyboardArrow: 'ArrowUp' | 'ArrowDown') => {
      const wrap = programProgressScrollRef.current;
      if (!wrap || rowIdx < 0) return;
      const thead = wrap.querySelector('table thead');
      scrollTableWrapForVerticalKeyboardArrow({
        wrap,
        thead: thead instanceof HTMLElement ? thead : null,
        fallbackHeaderPx: 64,
        anchorMap: cellAnchorByKeyRef.current,
        anchorKey: `${annualKey}\t${fieldId}`,
        rowIdx,
        nominalRowHeight: PROGRAM_PROGRESS_TABLE_NOMINAL_ROW_PX,
        keyboardArrow,
      });
    },
    []
  );

  const moveSelectionVertical = React.useCallback(
    (delta: number, startEdit = false) => {
      const one = selectedCellsRef.current[0];
      if (!one || selectedCellsRef.current.length !== 1) return;
      const parsed = parseCellId(one);
      if (!parsed) return;
      const rowIdx = rowIdxByAnnualKey[parsed.annualKey];
      const colIdx = colIdxByField[parsed.fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return;
      const nr = Math.max(0, Math.min(tableRows.length - 1, rowIdx + delta));
      const annualKey = tableRows[nr]?.annualCellKey;
      const fieldId = editableFieldOrder[colIdx];
      if (!annualKey || !fieldId) return;
      setSelectedCells([makeCellId(annualKey, fieldId)]);
      const kb = delta < 0 ? 'ArrowUp' : 'ArrowDown';
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scrollProgramProgressForVerticalKey(nr, annualKey, fieldId, kb);
        });
      });
      if (startEdit) {
        if (readOnlyRef.current || isFieldLocked(fieldId)) return;
        if (fieldId === 'record_evaluation_text') {
          setEvalDropdown({ annualKey });
        } else if (fieldId === 'record_class_text') {
          setEvalDropdown(null);
          openClassRecordMemoModal(annualKey);
        } else {
          beginEditRef.current?.(annualKey, fieldId);
        }
      }
    },
    [
      colIdxByField,
      editableFieldOrder,
      isFieldLocked,
      makeCellId,
      openClassRecordMemoModal,
      parseCellId,
      rowIdxByAnnualKey,
      scrollProgramProgressForVerticalKey,
      tableRows,
    ]
  );
  const moveSelectedRowsByOne = React.useCallback(
    (direction: 'up' | 'down') => {
      if (mutationLockedRef.current) return;
      if (notifySlideEditStructureBlocked()) return;
      const selectedSet = getSelectedRowIndexSet(selectedCellsRef.current);
      if (!selectedSet.size || !tableRows.length) return;
      const changedAnnualKeys = new Set<string>();
      const posToOld = tableRows.map((_, idx) => idx);
      const selectedRows = Array.from(selectedSet).sort((a, b) => a - b);
      const blocks: Array<{ start: number; end: number }> = [];
      selectedRows.forEach((rowIdx) => {
        const last = blocks[blocks.length - 1];
        if (!last || rowIdx > last.end + 1) blocks.push({ start: rowIdx, end: rowIdx });
        else last.end = rowIdx;
      });

      let moved = false;
      if (direction === 'up') {
        blocks.forEach(({ start, end }) => {
          if (start <= 0) return;
          const hold = posToOld[start - 1];
          for (let i = start - 1; i < end; i += 1) posToOld[i] = posToOld[i + 1];
          posToOld[end] = hold;
          moved = true;
        });
      } else {
        for (let bi = blocks.length - 1; bi >= 0; bi -= 1) {
          const { start, end } = blocks[bi];
          if (end >= tableRows.length - 1) continue;
          const hold = posToOld[end + 1];
          for (let i = end + 1; i > start; i -= 1) posToOld[i] = posToOld[i - 1];
          posToOld[start] = hold;
          moved = true;
        }
      }
      if (!moved) return;

      const collectSlideMoves = (): Array<{ from: ProgramContentSlideOwnerRef; to: ProgramContentSlideOwnerRef }> => {
        const sid = workspaceId?.trim();
        if (!sid) return [];
        const movesOut: Array<{ from: ProgramContentSlideOwnerRef; to: ProgramContentSlideOwnerRef }> = [];
        const seen = new Set<string>();
        posToOld.forEach((oldIdx, newIdx) => {
          if (oldIdx === newIdx) return;
          const sourceAnnualKey = tableRows[oldIdx]?.annualCellKey;
          const targetAnnualKey = tableRows[newIdx]?.annualCellKey;
          if (!sourceAnnualKey || !targetAnnualKey) return;
          const sourceRow = rowByAnnualKey[sourceAnnualKey];
          const targetRow = rowByAnnualKey[targetAnnualKey];
          if (!sourceRow || !targetRow) return;
          const sourceOwner = resolveProgramContentSlideOwner(
            mode,
            sourceRow,
            programCellsByAnnualKey[sourceAnnualKey],
            draftRef.current[sourceAnnualKey]?.learning_text ?? ''
          );
          const targetOwner = resolveProgramContentSlideOwner(
            mode,
            targetRow,
            programCellsByAnnualKey[targetAnnualKey],
            draftRef.current[targetAnnualKey]?.learning_text ?? ''
          );
          if (!sourceOwner || !targetOwner) return;
          if (sourceOwner.kind === 'presentation_ref' || targetOwner.kind === 'presentation_ref') return;
          if (!isPersistableUuid(sourceOwner.id) || !isPersistableUuid(targetOwner.id)) return;
          const sourceKey = programContentSlidePresenceKey(sourceOwner);
          const targetKey = programContentSlidePresenceKey(targetOwner);
          if (sourceKey === targetKey) return;
          const hasSlides =
            programContentSlidePresenceKeys.has(sourceKey) || hasProgramContentSlideDraftForOwner(sid, sourceOwner);
          if (!hasSlides) return;
          const moveKey = `${sourceKey}->${targetKey}`;
          if (seen.has(moveKey)) return;
          seen.add(moveKey);
          movesOut.push({ from: sourceOwner, to: targetOwner });
        });
        return movesOut;
      };

      const applySlideMoves = (slideMoves: Array<{ from: ProgramContentSlideOwnerRef; to: ProgramContentSlideOwnerRef }>) => {
        const sid = workspaceId?.trim();
        if (!sid || !slideMoves.length) return;
        void (async () => {
          const movedFromKeys: string[] = [];
          const movedToKeys: string[] = [];
          try {
            if (workspaceScopeId.trim()) {
              const movedCount = await queueProgramContentSlideMoveDrafts({
                workspaceId: sid,
                scopeId: workspaceScopeId,
                moves: slideMoves,
              });
              if (movedCount <= 0) return;
              slideMoves.forEach(({ from, to }) => {
                movedFromKeys.push(programContentSlidePresenceKey(from));
                movedToKeys.push(programContentSlidePresenceKey(to));
              });
            } else {
              for (const { from, to } of slideMoves) {
                await copyProgramContentSlidePagesBetweenOwners(sid, from, to);
                await deleteProgramContentSlidePagesForOwner(sid, from);
                movedFromKeys.push(programContentSlidePresenceKey(from));
                movedToKeys.push(programContentSlidePresenceKey(to));
              }
            }
          } catch {
            return;
          }
          setProgramContentSlidePresenceKeys((prev) => {
            const n = new Set(prev);
            movedFromKeys.forEach((k) => n.delete(k));
            movedToKeys.forEach((k) => n.add(k));
            return n;
          });
        })();
      };

      /** fixed_content: 편집 내용은 연간 일정과 독립된 스코프 행 순서로 저장되므로, draft만 바꾸지 않고 워크스페이스 행을 재배열한다 */
      if (saveMode === 'fixed_content' && onWorkspaceRowsChange) {
        captureLocalUndoBeforeMutation();
        const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
        const reorderedRaw = posToOld.map((oldIdx, newIdx) => {
          const movedPayload = workspaceRows[oldIdx];
          const targetIdentity = workspaceRows[newIdx];
          if (!movedPayload) return movedPayload;
          return {
            ...movedPayload,
            id: targetIdentity?.id ?? movedPayload.id,
          };
        });
        const slotKeys = scheduleSlotAnnualCellKeys ?? [];
        const reordered = remapFixedWorkspaceRowsToScheduleSlotSources(reorderedRaw, slotKeys);
        remapProgramContentSlideStashOwnersAfterFixedReorder({
          scopeId: workspaceScopeId,
          workspaceRows,
          posToOld,
        });
        clearPendingFixedContentCellEditsAfterReplace();
        onWorkspaceRowsChange(reordered, { persistKind: 'replace' });
        applySlideMoves(collectSlideMoves());
        const oldToNew = new Map<number, number>();
        posToOld.forEach((oldIdx, newIdx) => {
          oldToNew.set(oldIdx, newIdx);
        });
        const nextSelectedRowIndices = selectedRows
          .map((oldIdx) => oldToNew.get(oldIdx))
          .filter((idx): idx is number => typeof idx === 'number');
        window.requestAnimationFrame(() => applyContentRowsSelectionByIndices(nextSelectedRowIndices));
        return;
      }

      const sourceByAnnualKey: Record<string, DraftRow> = {};
      tableRows.forEach((row) => {
        const base = draftRef.current[row.annualCellKey] ?? makeDraftRow(row);
        sourceByAnnualKey[row.annualCellKey] = {
          ...base,
          bg_color_by_field: { ...(base.bg_color_by_field ?? {}) },
        };
      });
      const next: Record<string, DraftRow> = { ...draftRef.current };

      posToOld.forEach((oldIdx, newIdx) => {
        if (oldIdx === newIdx) return;
        const targetAnnualKey = tableRows[newIdx]?.annualCellKey;
        const sourceAnnualKey = tableRows[oldIdx]?.annualCellKey;
        if (!targetAnnualKey || !sourceAnnualKey) return;
        const targetDraft = ensureDraftRow(next, targetAnnualKey);
        const sourceDraft = sourceByAnnualKey[sourceAnnualKey];
        if (!sourceDraft) return;
        const bgMap = { ...(targetDraft.bg_color_by_field ?? {}) };
        editableFieldOrder.forEach((fieldId) => {
          targetDraft[fieldId] = sourceDraft[fieldId] ?? '';
          const srcBg = sourceDraft.bg_color_by_field?.[fieldId] ?? '';
          if (srcBg) bgMap[fieldId] = srcBg;
          else delete bgMap[fieldId];
        });
        targetDraft.bg_color_by_field = bgMap;
        changedAnnualKeys.add(targetAnnualKey);
      });
      if (!changedAnnualKeys.size) return;

      captureLocalUndoBeforeMutation();
      draftRef.current = next;
      setDraftByAnnualKey(next);
      queuePendingRows(next, changedAnnualKeys);
      applySlideMoves(collectSlideMoves());

      const oldToNew = new Map<number, number>();
      posToOld.forEach((oldIdx, newIdx) => {
        oldToNew.set(oldIdx, newIdx);
      });
      const remapped = selectedCellsRef.current
        .map((cellId) => {
          const parsed = parseCellId(cellId);
          if (!parsed) return null;
          const currentRowIdx = rowIdxByAnnualKey[parsed.annualKey];
          if (!Number.isInteger(currentRowIdx)) return null;
          const nextRowIdx = oldToNew.get(currentRowIdx);
          if (typeof nextRowIdx !== 'number') return null;
          const nextAnnualKey = tableRows[nextRowIdx]?.annualCellKey;
          if (!nextAnnualKey) return null;
          return makeCellId(nextAnnualKey, parsed.fieldId);
        })
        .filter((v): v is string => !!v);
      if (remapped.length) {
        const unique = Array.from(new Set(remapped));
        setSelectedCells(unique);
        selectedCellsRef.current = unique;
      }
    },
    [
      buildAllFixedContentWorkspaceRows,
      applyContentRowsSelectionByIndices,
      captureLocalUndoBeforeMutation,
      clearPendingFixedContentCellEditsAfterReplace,
      editableFieldOrder,
      ensureDraftRow,
      getSelectedRowIndexSet,
      makeDraftRow,
      makeCellId,
      onWorkspaceRowsChange,
      parseCellId,
      mode,
      programCellsByAnnualKey,
      programContentSlidePresenceKeys,
      queuePendingRows,
      rowByAnnualKey,
      rowIdxByAnnualKey,
      saveMode,
      scheduleSlotAnnualCellKeys,
      workspaceId,
      tableRows,
      workspaceScopeId,
      notifySlideEditStructureBlocked,
    ]
  );
  const makeEmptyDraftRow = React.useCallback(
    (): DraftRow => ({
      timetable_text: '',
      unit_text: '',
      learning_text: '',
      page_text: '',
      assistant_page_text: '',
      current_period_text: '',
      total_period_text: '',
      material_text: '',
      record_cross_curricular_text: '',
      record_memo_text: '',
      record_evaluation_text: '',
      record_class_text: '',
      record_member_group_id: null,
      bg_color_by_field: {},
    }),
    []
  );
  const textMergeFields = React.useMemo<FieldId[]>(() => {
    const base: FieldId[] = [
      'unit_text',
      'learning_text',
      'material_text',
      'record_cross_curricular_text',
      'record_memo_text',
      'record_evaluation_text',
      'record_class_text',
    ];
    return mode === 'project_to_template' ? ['timetable_text', ...base] : base;
  }, [mode]);
  const pageRangeFields = React.useMemo<FieldId[]>(() => ['page_text', 'assistant_page_text'], []);
  const topKeepFields = React.useMemo<FieldId[]>(() => ['current_period_text', 'total_period_text'], []);
  const mergeTextValues = React.useCallback((values: string[]) => {
    const cleaned = values.map((v) => (v ?? '').trim()).filter(Boolean);
    if (!cleaned.length) return '';
    const unique = Array.from(new Set(cleaned));
    if (unique.length === 1) return unique[0];
    return unique.join(', ');
  }, []);
  const mergePageRangeValues = React.useCallback((values: string[]) => {
    const cleaned = values.map((v) => (v ?? '').trim()).filter(Boolean);
    if (!cleaned.length) return '';
    const unique = Array.from(new Set(cleaned));
    if (unique.length === 1) return unique[0];
    const parsed = cleaned
      .map((v) => {
        const m = v.match(/(\d+)\s*(?:-\s*(\d+))?/);
        if (!m) return null;
        const start = Number(m[1]);
        const end = Number(m[2] ?? m[1]);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
        return { start, end };
      })
      .filter((v): v is { start: number; end: number } => !!v);
    if (!parsed.length) return mergeTextValues(cleaned);
    const start = parsed[0].start;
    const end = parsed[parsed.length - 1].end;
    return start === end ? String(start) : `${start}-${end}`;
  }, [mergeTextValues]);
  const getSelectedRowIndices = React.useCallback(() => {
    return Array.from(getSelectedRowIndexSet(selectedCellsRef.current)).sort((a, b) => a - b);
  }, [getSelectedRowIndexSet]);
  const handleInsertContentRow = React.useCallback(() => {
    if (mutationLockedRef.current) return;
    if (notifySlideEditStructureBlocked()) return;
    if (saveMode !== 'fixed_content' || !onWorkspaceRowsChange) return;
    const idxs = getSelectedRowIndices();
    let anchorIdx: number;
    if (!idxs.length) {
      if (tableRows.length === 0) {
        anchorIdx = -1;
      } else {
        return;
      }
    } else {
      anchorIdx = idxs[idxs.length - 1];
    }
    captureLocalUndoBeforeMutation();
    const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
    const insertAt = anchorIdx + 1;
    const slotKeys = scheduleSlotAnnualCellKeys ?? [];
    const tempId = `temp-${Date.now()}-${tempFrameIdSeqRef.current++}`;
    const blank = makeEmptyDraftRow();
    const inserted: ProgramFixedContentRowUpsert = {
      id: tempId,
      workspace_id: workspaceId,
      grade_id: tableRows[anchorIdx]?.gradeId || progressGradeId,
      academic_year: academicYear,
      term_number: termNumber,
      order_index: insertAt,
      source_annual_cell_key: null,
      event_name: null,
      timetable_text: blank.timetable_text,
      unit_text: blank.unit_text,
      learning_text: blank.learning_text,
      page_text: blank.page_text,
      assistant_page_text: blank.assistant_page_text,
      current_period_text: blank.current_period_text,
      total_period_text: blank.total_period_text,
      material_text: blank.material_text,
      record_cross_curricular_text: blank.record_cross_curricular_text,
      record_memo_text: blank.record_memo_text,
      record_evaluation_text: blank.record_evaluation_text,
      record_class_text: blank.record_class_text,
      bg_color_by_field: {},
    };
    const nextRows =
      anchorIdx < 0
        ? [inserted]
        : [...workspaceRows.slice(0, insertAt), inserted, ...workspaceRows.slice(insertAt)];
    const normalized = remapFixedWorkspaceRowsToScheduleSlotSources(nextRows, slotKeys);
    if (slotKeys.length && insertAt < slotKeys.length) {
      pendingStructuredSelectionRef.current = { annualKey: slotKeys[insertAt] };
    } else if (slotKeys.length) {
      pendingStructuredSelectionRef.current = { rowIndex: insertAt };
    } else {
      pendingStructuredSelectionRef.current = { annualKey: `orphan:${tempId}` };
    }
    clearPendingFixedContentCellEditsAfterReplace();
    onWorkspaceRowsChange(normalized, { persistKind: 'replace' });
  }, [
    academicYear,
    buildAllFixedContentWorkspaceRows,
    captureLocalUndoBeforeMutation,
    clearPendingFixedContentCellEditsAfterReplace,
    getSelectedRowIndices,
    makeEmptyDraftRow,
    onWorkspaceRowsChange,
    progressGradeId,
    saveMode,
    scheduleSlotAnnualCellKeys,
    workspaceId,
    tableRows,
    termNumber,
    notifySlideEditStructureBlocked,
  ]);
  const handleDuplicateLesson = React.useCallback(() => {
    if (mutationLockedRef.current) return;
    if (notifySlideEditStructureBlocked()) return;
    if (mode === 'fixed_to_unit' || saveMode !== 'fixed_content' || !onWorkspaceRowsChange) return;
    const selectedIndices = getSelectedRowIndices();
    if (selectedIndices.length !== 1) return;
    captureLocalUndoBeforeMutation();
    const sourceIdx = selectedIndices[0];
    const sourceKey = tableRows[sourceIdx]?.annualCellKey;
    if (!sourceKey) return;
    const from = draftRef.current[sourceKey] ?? makeEmptyDraftRow();
    const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
    const tempId = `temp-${Date.now()}-${tempFrameIdSeqRef.current++}`;
    const sourceRowId = String(workspaceRows[sourceIdx]?.id ?? '').trim();
    const inserted: ProgramFixedContentRowUpsert = {
      ...workspaceRows[sourceIdx],
      id: tempId,
      order_index: sourceIdx + 1,
      record_class_text: '',
      bg_color_by_field: {
        ...(from.bg_color_by_field ?? {}),
      } as ProgramProgressBgColorByField,
    };
    if (isPersistableUuid(sourceRowId)) {
      inserted._slidePageCopyFromFixedRowId = sourceRowId;
    }
    if (inserted.bg_color_by_field) delete inserted.bg_color_by_field.record_class_text;
    const nextRows = [...workspaceRows];
    nextRows.splice(sourceIdx + 1, 0, inserted);
    const slotKeys = scheduleSlotAnnualCellKeys ?? [];
    const normalized = remapFixedWorkspaceRowsToScheduleSlotSources(nextRows, slotKeys);
    if (mode === 'project_to_template') {
      const insertedAnnualKey =
        slotKeys.length > sourceIdx + 1 ? (slotKeys[sourceIdx + 1] ?? '') : `orphan:${tempId}`;
      if (insertedAnnualKey) {
        pendingDuplicateTimetableCopyRef.current = {
          annualKey: insertedAnnualKey,
          value: from.timetable_text ?? '',
        };
      }
    }
    pendingStructuredSelectionRef.current =
      slotKeys.length > sourceIdx + 1 ? { rowIndex: sourceIdx + 1 } : { annualKey: `orphan:${tempId}` };
    clearPendingFixedContentCellEditsAfterReplace();
    onWorkspaceRowsChange(normalized, { persistKind: 'replace' });
  }, [
    buildAllFixedContentWorkspaceRows,
    captureLocalUndoBeforeMutation,
    clearPendingFixedContentCellEditsAfterReplace,
    getSelectedRowIndices,
    makeEmptyDraftRow,
    mode,
    onWorkspaceRowsChange,
    saveMode,
    scheduleSlotAnnualCellKeys,
    tableRows,
    isPersistableUuid,
    notifySlideEditStructureBlocked,
  ]);
  const handleDeleteContentRows = React.useCallback(() => {
    if (mutationLockedRef.current) return;
    if (notifySlideEditStructureBlocked()) return;
    if (saveMode !== 'fixed_content' || !onWorkspaceRowsChange) return;
    const selectedIndices = getSelectedRowIndices();
    const removeSet = new Set(selectedIndices);
    if (!removeSet.size) return;
    captureLocalUndoBeforeMutation();
    const topRemoved = selectedIndices[0];
    const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
    const removedRows = selectedIndices
      .map((idx) => workspaceRows[idx])
      .filter((r): r is ProgramFixedContentRowUpsert => !!r);
    const slotKeys = scheduleSlotAnnualCellKeys ?? [];
    const nextRowsRaw = workspaceRows
      .filter((_, idx) => !removeSet.has(idx))
      .sort((a, b) => a.order_index - b.order_index);
    const templateRow = nextRowsRaw[0] ?? removedRows[0] ?? workspaceRows[0] ?? null;
    let nextRows =
      slotKeys.length > 0
        ? compactOrPadWorkspaceRowsToScheduleSlotLayout(nextRowsRaw, slotKeys, templateRow)
        : remapFixedWorkspaceRowsToScheduleSlotSources(nextRowsRaw, slotKeys);
    /** 일정 슬롯이 없을 때는 빈 표가 되지 않도록 최소 1행 유지 */
    if (nextRows.length === 0 && slotKeys.length === 0 && templateRow) {
      const blank = makeEmptyDraftRow();
      const tempId = `temp-${Date.now()}-${tempFrameIdSeqRef.current++}`;
      nextRows = [
        {
          id: tempId,
          workspace_id: templateRow.workspace_id || workspaceId,
          grade_id: templateRow.grade_id || progressGradeId,
          academic_year: templateRow.academic_year || academicYear,
          term_number: templateRow.term_number || termNumber,
          order_index: 0,
          source_annual_cell_key: null,
          event_name: null,
          timetable_text: blank.timetable_text,
          unit_text: blank.unit_text,
          learning_text: blank.learning_text,
          page_text: blank.page_text,
          assistant_page_text: blank.assistant_page_text,
          current_period_text: blank.current_period_text,
          total_period_text: blank.total_period_text,
          material_text: blank.material_text,
          record_cross_curricular_text: blank.record_cross_curricular_text,
          record_memo_text: blank.record_memo_text,
          record_evaluation_text: blank.record_evaluation_text,
          record_class_text: blank.record_class_text,
          record_member_group_id: blank.record_member_group_id ?? null,
          bg_color_by_field: blank.bg_color_by_field ?? {},
        },
      ];
    }
    pendingStructuredSelectionRef.current =
      nextRows.length > 0 ? { rowIndex: Math.min(topRemoved, nextRows.length - 1) } : null;
    if (nextRows.length === 0) {
      applyContentRowSelection(null);
    }
    if (removedRows.length) onRemovedFixedWorkspaceRows?.(removedRows);
    clearPendingFixedContentCellEditsAfterReplace();
    /** 삭제·패딩 직후 draft를 워크스페이스와 맞춰, 연간 내보내기가 옛 시간표명을 쓰지 않게 한다 */
    const nextDraft: Record<string, DraftRow> = {};
    nextRows.forEach((wr, idx) => {
      const ak =
        (wr.source_annual_cell_key ?? '').trim() ||
        slotKeys[idx] ||
        tableRows[idx]?.annualCellKey ||
        `orphan:${(wr.id ?? '').trim() || idx}`;
      nextDraft[ak] = {
        timetable_text: wr.timetable_text ?? '',
        unit_text: wr.unit_text ?? '',
        learning_text: wr.learning_text ?? '',
        page_text: wr.page_text ?? '',
        assistant_page_text: wr.assistant_page_text ?? '',
        current_period_text: wr.current_period_text ?? '',
        total_period_text: wr.total_period_text ?? '',
        material_text: wr.material_text ?? '',
        record_cross_curricular_text: wr.record_cross_curricular_text ?? '',
        record_memo_text: wr.record_memo_text ?? '',
        record_evaluation_text: wr.record_evaluation_text ?? '',
        record_class_text: wr.record_class_text ?? '',
        record_member_group_id: wr.record_member_group_id ?? null,
        bg_color_by_field: { ...(wr.bg_color_by_field ?? {}) },
      };
    });
    draftRef.current = nextDraft;
    setDraftByAnnualKey(nextDraft);
    onWorkspaceRowsChange(nextRows, { persistKind: 'replace' });

    const sid = workspaceId?.trim();
    if (sid && removedRows.length) {
      const keysToDrop: string[] = [];
      for (const r of removedRows) {
        const presRefForPresence = resolvePresentationRefIdFromLearningText(r.learning_text);
        if (presRefForPresence) {
          keysToDrop.push(
            programContentSlidePresenceKey({ kind: 'presentation_ref', id: presRefForPresence })
          );
        }
        const worksheetRef = resolveWorksheetRefIdFromLearningText(r.learning_text);
        if (worksheetRef) {
          discardPendingLearningWorksheetFlush(sid, worksheetRef);
          void deleteProgramContentWorksheet(worksheetRef, sid).catch(() => undefined);
        }
        const id = (r.id ?? '').trim();
        if (!isPersistableUuid(id)) continue;
        const owner: ProgramContentSlideOwnerRef = { kind: 'fixed_row', id };
        keysToDrop.push(programContentSlidePresenceKey(owner));
      }
      if (keysToDrop.length) {
        setProgramContentSlidePresenceKeys((prev) => {
          const n = new Set(prev);
          keysToDrop.forEach((k) => n.delete(k));
          return n;
        });
      }
      void (async () => {
        for (const r of removedRows) {
          const id = (r.id ?? '').trim();
          const presRef = resolvePresentationRefIdFromLearningText(r.learning_text);
          if (presRef) {
            try {
              if (workspaceScopeId.trim()) {
                queueProgramContentSlideDeleteDraft({
                  workspaceId: sid,
                  scopeId: workspaceScopeId,
                  owner: { kind: 'presentation_ref', id: presRef },
                });
              } else {
                await deleteProgramContentSlidePagesForOwner(sid, { kind: 'presentation_ref', id: presRef });
              }
            } catch {
              /* */
            }
          }
          if (!isPersistableUuid(id)) continue;
          const owner: ProgramContentSlideOwnerRef = { kind: 'fixed_row', id };
          try {
            if (workspaceScopeId.trim()) {
              queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId: workspaceScopeId, owner });
            } else {
              await deleteProgramContentSlidePagesForOwner(sid, owner);
            }
          } catch {
            /* DB 삭제 실패 시 저장 단계 CASCADE에 맡김 */
          }
        }
      })();
    }
  }, [
    academicYear,
    applyContentRowSelection,
    buildAllFixedContentWorkspaceRows,
    captureLocalUndoBeforeMutation,
    clearPendingFixedContentCellEditsAfterReplace,
    getSelectedRowIndices,
    makeEmptyDraftRow,
    onRemovedFixedWorkspaceRows,
    onWorkspaceRowsChange,
    progressGradeId,
    saveMode,
    scheduleSlotAnnualCellKeys,
    termNumber,
    workspaceId,
    workspaceScopeId,
    notifySlideEditStructureBlocked,
  ]);
  const handleMergeLessons = React.useCallback(() => {
    if (mutationLockedRef.current) return;
    if (notifySlideEditStructureBlocked()) return;
    if (mode === 'fixed_to_unit' || saveMode !== 'fixed_content' || !onWorkspaceRowsChange) return;
    const selectedIndices = getSelectedRowIndices();
    if (selectedIndices.length < 2) return;
    const rowKeys = selectedIndices.map((idx) => tableRows[idx]?.annualCellKey).filter((v): v is string => !!v);
    if (rowKeys.length < 2) return;
    if (mode === 'project_to_template') {
      const timetableSet = new Set(
        rowKeys.map((k) => (draftRef.current[k]?.timetable_text ?? '').trim())
      );
      if (timetableSet.size > 1) {
        setMergeTimetableMismatchToast(
          '통합하려는 학습내용의 시간표가 달라 통합할 수 없습니다. 동일한 시간표 학습내용을 선택해 통합해주세요.'
        );
        return;
      }
    }
    const wsSnapBeforeMerge = buildAllFixedContentWorkspaceRows(draftRef.current);
    const survIdxForSlide = selectedIndices[0];
    const survivorSourceId = String(wsSnapBeforeMerge[survIdxForSlide]?.id ?? '').trim();
    const mergedAwaySlideIds = selectedIndices
      .slice(1)
      .map((i) => String(wsSnapBeforeMerge[i]?.id ?? '').trim())
      .filter((id) => id && isPersistableUuid(id));
    const topKey = rowKeys[0];
    const removeIndexSet = new Set(selectedIndices.slice(1));
    const rowIds = tableRows.map((r) => r.annualCellKey);
    const source: Record<string, DraftRow> = {};
    rowIds.forEach((annualKey) => {
      const d = draftRef.current[annualKey] ?? makeEmptyDraftRow();
      source[annualKey] = { ...d, bg_color_by_field: { ...(d.bg_color_by_field ?? {}) } };
    });

    const mergedTop: DraftRow = {
      ...source[topKey],
      bg_color_by_field: { ...(source[topKey]?.bg_color_by_field ?? {}) },
    };
    textMergeFields.forEach((fieldId) => {
      const values = rowKeys.map((k) => source[k]?.[fieldId] ?? '');
      if (fieldId === 'unit_text') {
        const texts = values.map((v) => parseUnitData(v).text);
        const notes = values.map((v) => parseUnitData(v).note);
        mergedTop[fieldId] = serializeUnitData(mergeTextValues(texts), mergeTextValues(notes));
      } else if (fieldId === 'record_cross_curricular_text') {
        const texts = values.map((v) => parseCrossCurricularData(v).text);
        const notes = values.map((v) => parseCrossCurricularData(v).note);
        mergedTop[fieldId] = serializeCrossCurricularData(mergeTextValues(texts), mergeTextValues(notes));
      } else if (memoModalFieldSet.has(fieldId)) {
        const mains = values.map((v) => parseMemoFieldData(v).text);
        const notes = values.map((v) => parseMemoFieldData(v).note);
        mergedTop[fieldId] = serializeMemoFieldData(mergeTextValues(mains), mergeTextValues(notes));
      } else {
        mergedTop[fieldId] = mergeTextValues(values);
      }
    });
    {
      const mains = rowKeys.map((k) => parseMemoFieldData(source[k]?.learning_text ?? '').text);
      const notes = rowKeys.map((k) => parseMemoFieldData(source[k]?.learning_text ?? '').note);
      const refCandidates = rowKeys
        .map((k) => parseMemoFieldData(source[k]?.learning_text ?? '').presentationRefId)
        .filter((id): id is string => !!(id && isPersistableUuid(id)));
      const worksheetRefCandidates = rowKeys
        .map((k) => parseMemoFieldData(source[k]?.learning_text ?? '').worksheetRefId)
        .filter((id): id is string => !!(id && isPersistableUuid(id)));
      const uniqRefs = Array.from(new Set(refCandidates));
      const uniqWorksheetRefs = Array.from(new Set(worksheetRefCandidates));
      const survivorRef = parseMemoFieldData(source[topKey]?.learning_text ?? '').presentationRefId;
      const survivorWorksheetRef = parseMemoFieldData(source[topKey]?.learning_text ?? '').worksheetRefId;
      const chosenRef =
        survivorRef && isPersistableUuid(survivorRef) ? survivorRef : uniqRefs[0];
      const chosenWorksheetRef =
        survivorWorksheetRef && isPersistableUuid(survivorWorksheetRef)
          ? survivorWorksheetRef
          : uniqWorksheetRefs[0];
      mergedTop.learning_text = serializeMemoFieldData(
        mergeTextValues(mains),
        mergeTextValues(notes),
        chosenRef,
        chosenWorksheetRef
      );
    }
    pageRangeFields.forEach((fieldId) => {
      const values = rowKeys.map((k) => source[k]?.[fieldId] ?? '');
      mergedTop[fieldId] = mergePageRangeValues(values);
    });
    topKeepFields.forEach((fieldId) => {
      mergedTop[fieldId] = source[topKey]?.[fieldId] ?? '';
    });

    const survivors = rowIds.filter((_, idx) => !removeIndexSet.has(idx));
    const next: Record<string, DraftRow> = { ...draftRef.current };
    rowIds.forEach((dstKey, dstIdx) => {
      const srcKey = survivors[dstIdx];
      const src = srcKey ? source[srcKey] : makeEmptyDraftRow();
      next[dstKey] = { ...src, bg_color_by_field: { ...(src.bg_color_by_field ?? {}) } };
    });
    next[topKey] = mergedTop;

    captureLocalUndoBeforeMutation();
    draftRef.current = next;
    setDraftByAnnualKey(next);
    const slotKeys = scheduleSlotAnnualCellKeys ?? [];
    const workspaceRowsBeforeMerge = [...wsSnapBeforeMerge].sort((a, b) => a.order_index - b.order_index);
    const mergedWorkspaceRowsRaw = workspaceRowsBeforeMerge
      .filter((_, idx) => !removeIndexSet.has(idx))
      .map((row, idx) => {
        if (idx !== selectedIndices[0]) return { ...row };
        return {
          ...row,
          unit_text: mergedTop.unit_text ?? '',
          learning_text: mergedTop.learning_text ?? '',
          page_text: mergedTop.page_text ?? '',
          assistant_page_text: mergedTop.assistant_page_text ?? '',
          current_period_text: mergedTop.current_period_text ?? '',
          total_period_text: mergedTop.total_period_text ?? '',
          material_text: mergedTop.material_text ?? '',
          record_cross_curricular_text: mergedTop.record_cross_curricular_text ?? '',
          record_memo_text: mergedTop.record_memo_text ?? '',
          record_evaluation_text: mergedTop.record_evaluation_text ?? '',
          record_class_text: mergedTop.record_class_text ?? '',
          bg_color_by_field: { ...(mergedTop.bg_color_by_field ?? {}) },
        };
      });
    const workspaceRows =
      slotKeys.length > 0
        ? mergedWorkspaceRowsRaw.length > slotKeys.length
          ? remapFixedWorkspaceRowsToScheduleSlotSources(mergedWorkspaceRowsRaw, slotKeys)
          : compactOrPadWorkspaceRowsToScheduleSlotLayout(
              mergedWorkspaceRowsRaw,
              slotKeys,
              mergedWorkspaceRowsRaw[0] ?? null
            )
        : remapFixedWorkspaceRowsToScheduleSlotSources(mergedWorkspaceRowsRaw, slotKeys);
    pendingStructuredSelectionRef.current = { annualKey: topKey };
    clearPendingFixedContentCellEditsAfterReplace();
    const sidMerge = workspaceId?.trim();
    if (sidMerge && survivorSourceId && isPersistableUuid(survivorSourceId)) {
      const refCandidates = rowKeys
        .map((k) => parseMemoFieldData(source[k]?.learning_text ?? '').presentationRefId)
        .filter((id): id is string => !!(id && isPersistableUuid(id)));
      const uniqRefs = Array.from(new Set(refCandidates));
      const targetRef = resolvePresentationRefIdFromLearningText(mergedTop.learning_text);
      const sourceRefs = targetRef ? uniqRefs.filter((id) => id !== targetRef) : [];
      if (targetRef && sourceRefs.length) {
        void mergePresentationRefsIntoTarget(sidMerge, targetRef, sourceRefs).catch(() => undefined);
      }
      if (mergedAwaySlideIds.length) {
        void mergeProgramContentSlidePagesIntoOwner({
          workspaceId: sidMerge,
          target: { kind: 'fixed_row', id: survivorSourceId },
          sourceOwners: mergedAwaySlideIds.map((id) => ({ kind: 'fixed_row' as const, id })),
        }).catch(() => undefined);
      }
    }
    onWorkspaceRowsChange(workspaceRows, { persistKind: 'replace' });
  }, [
    buildAllFixedContentWorkspaceRows,
    captureLocalUndoBeforeMutation,
    clearPendingFixedContentCellEditsAfterReplace,
    getSelectedRowIndices,
    isPersistableUuid,
    makeEmptyDraftRow,
    mergePageRangeValues,
    mergePresentationRefsIntoTarget,
    mergeProgramContentSlidePagesIntoOwner,
    mergeTextValues,
    memoModalFieldSet,
    mode,
    onWorkspaceRowsChange,
    pageRangeFields,
    parseMemoFieldData,
    workspaceId,
    parseUnitData,
    parseCrossCurricularData,
    saveMode,
    scheduleSlotAnnualCellKeys,
    serializeMemoFieldData,
    serializeUnitData,
    serializeCrossCurricularData,
    tableRows,
    textMergeFields,
    topKeepFields,
    setMergeTimetableMismatchToast,
    notifySlideEditStructureBlocked,
  ]);

  const captureCellDisplayFontSizePx = React.useCallback((annualKey: string, fieldId: FieldId): number | undefined => {
    const td = cellAnchorByKeyRef.current.get(`${annualKey}\t${fieldId}`);
    if (!td) return undefined;
    const textEl =
      (td.querySelector('.cp-program-progress-cell-text') as HTMLElement | null) ??
      (td.querySelector('.cp-annual-progress-timetable-text') as HTMLElement | null);
    if (!textEl) return undefined;
    const inline = textEl.style.fontSize?.trim();
    let raw = 0;
    if (inline && inline.endsWith('px')) {
      raw = parseFloat(inline);
    }
    if (!Number.isFinite(raw) || raw <= 0) {
      raw = parseFloat(window.getComputedStyle(textEl).fontSize || '0');
    }
    if (!Number.isFinite(raw) || raw <= 0) return undefined;
    return Math.round(raw * 100) / 100;
  }, []);

  const beginEdit = React.useCallback(
    (annualKey: string, fieldId: FieldId, seed?: string) => {
      if (readOnlyRef.current || isFieldLocked(fieldId)) return;
      clearLongPressTimer();
      blurCommitGuardCellRef.current = null;
      const draftRow = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
      const row = rowByAnnualKey[annualKey];
      const fixedLearningPrefix = resolveFixedLearningPrefix(annualKey, row, draftRow.learning_text ?? '');
      let value = seed !== undefined ? seed : draftRow[fieldId] ?? '';
      if (fieldId === 'unit_text') {
        value = seed !== undefined ? seed : parseUnitData(draftRow.unit_text ?? '').text;
      } else if (memoModalFieldSet.has(fieldId)) {
        value = seed !== undefined ? seed : parseMemoFieldData(draftRow[fieldId] ?? '').text;
      }
      if (mode === 'fixed_to_unit' && fieldId === 'learning_text' && fixedLearningPrefix && !value.startsWith(fixedLearningPrefix)) {
        value = `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(value, row?.memo ?? row?.fixedEventName ?? '')}`;
      }
      const displayFontSizePx = captureCellDisplayFontSizePx(annualKey, fieldId);
      setEditingCell({
        annualKey,
        fieldId,
        draft: value,
        ...(displayFontSizePx != null ? { displayFontSizePx } : {}),
      });
      setSelectedCells([makeCellId(annualKey, fieldId)]);
    },
    [
      captureCellDisplayFontSizePx,
      ensureDraftRow,
      isFieldLocked,
      makeCellId,
      memoModalFieldSet,
      mode,
      parseMemoFieldData,
      parseUnitData,
      resolveFixedLearningPrefix,
      rowByAnnualKey,
      clearLongPressTimer,
    ]
  );

  beginEditRef.current = beginEdit;

  const stopCellPointerDuringTextEdit = React.useCallback(
    (e: React.MouseEvent | React.PointerEvent) => {
      e.stopPropagation();
      clearLongPressTimer();
    },
    [clearLongPressTimer]
  );

  const editingCellIdentity = editingCell ? `${editingCell.annualKey}::${editingCell.fieldId}` : '';

  React.useLayoutEffect(() => {
    if (!editingCellIdentity || !editingInputRef.current) return;
    const input = editingInputRef.current;
    input.focus({ preventScroll: true });
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, [editingCellIdentity]);

  const resolveLearningPresentationActionState = React.useCallback(
    (annualKey: string) => {
      const sid = workspaceId?.trim();
      const orphanOwner = annualKey.startsWith('slide:') ? parsePresentationAuditSelectionKey(annualKey) : null;
      if (orphanOwner && sid) {
        return {
          sid,
          learningText: '',
          canDeletePresentation: true,
          copySourceOwner: orphanOwner,
        };
      }
      const row = rowByAnnualKey[annualKey];
      if (!sid || !row) {
        return {
          sid: sid ?? '',
          learningText: '',
          canDeletePresentation: false,
          copySourceOwner: null as ProgramContentSlideOwnerRef | null,
        };
      }
      const learningText = resolveLearningTextForMemoAtAnnualKey(annualKey);
      const parsedLearning = parseMemoFieldData(learningText);
      const popupHasRef = !!parsedLearning.presentationRefId && isPersistableUuid(parsedLearning.presentationRefId);
      const slideOwnerCandidates = resolveProgramContentSlideOwnerCandidates(
        mode,
        row,
        programCellsByAnnualKey[annualKey],
        learningText
      );
      const hasDraftSlide = slideOwnerCandidates.some((owner) =>
        programContentSlideOwnerHasMeaningfulExtraPageDraft(sid, owner)
      );
      const hasDbSlidePages = learningSlidePageIndicatorAnnualKeys.has(annualKey);
      const canDeletePresentation = hasDbSlidePages || popupHasRef || hasDraftSlide;
      const resolvedOwner = resolveProgramContentSlideOwner(mode, row, programCellsByAnnualKey[annualKey], learningText);
      const copySourceOwner =
        canDeletePresentation && resolvedOwner && isPersistableUuid(resolvedOwner.id) ? resolvedOwner : null;
      return { sid, learningText, canDeletePresentation, copySourceOwner };
    },
    [
      learningSlidePageIndicatorAnnualKeys,
      mode,
      parseMemoFieldData,
      programCellsByAnnualKey,
      resolveLearningTextForMemoAtAnnualKey,
      rowByAnnualKey,
      workspaceId,
    ]
  );

  const copyLearningPresentationAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      const state = resolveLearningPresentationActionState(annualKey);
      if (!state.copySourceOwner) return false;
      learningPresentationClipboardRef.current = {
        owner: state.copySourceOwner,
      };
      setHasLearningPresentationClipboard(true);
      setLearningSlideDeleteToast('프레젠테이션을 복사했습니다.');
      return true;
    },
    [resolveLearningPresentationActionState]
  );

  const resolveLearningWorksheetActionState = React.useCallback(
    (annualKey: string) => {
      const sid = workspaceId?.trim();
      const row = rowByAnnualKey[annualKey];
      if (!sid || !row) {
        return {
          canDelete: false,
          copySourceRefId: null as string | null,
          copyDraftRowKey: null as string | null,
        };
      }
      const refId = resolveSyncMirrorWorksheetRefIdForIndicator(
        annualKey,
        syncMirrorView,
        syncMirrorIndicatorCellsByAnnualKey,
        draftRef.current[annualKey]?.learning_text ?? '',
        cellLearningTextForEditor(programCellsByAnnualKey[annualKey]),
        resolveRepDraftLearningText(annualKey),
        programCellsByAnnualKey[annualKey]
      );
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );
      const hasLocalDraft = !!loadLearningContentWorksheetDraft(draftRowKey);
      const hasWorksheet = learningWorksheetIndicatorAnnualKeys.has(annualKey);
      const copySourceRefId =
        refId &&
        (worksheetPresenceRefIds.has(refId) ||
          hasLocalDraft ||
          !!resolveWorksheetRefIdFromLearningContent(programCellsByAnnualKey[annualKey]))
          ? refId
          : null;
      const copyDraftRowKey = hasLocalDraft ? draftRowKey : null;
      return {
        canDelete: hasWorksheet,
        copySourceRefId: syncMirrorView ? null : copySourceRefId,
        copyDraftRowKey: syncMirrorView ? null : copyDraftRowKey,
      };
    },
    [
      learningWorksheetIndicatorAnnualKeys,
      programCellsByAnnualKey,
      resolveRepDraftLearningText,
      rowByAnnualKey,
      syncMirrorIndicatorCellsByAnnualKey,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      worksheetPresenceRefIds,
      workspaceId,
    ]
  );

  const copyLearningWorksheetAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      const state = resolveLearningWorksheetActionState(annualKey);
      if (!state.copySourceRefId && !state.copyDraftRowKey) return false;
      learningWorksheetClipboardRef.current = {
        worksheetRefId: state.copySourceRefId,
        draftRowKey: state.copyDraftRowKey,
      };
      setHasLearningWorksheetClipboard(true);
      setLearningSlideDeleteToast('활동지를 복사했습니다.');
      return true;
    },
    [resolveLearningWorksheetActionState]
  );

  const pasteLearningWorksheetAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      if (mutationLocked || syncMirrorView) return false;
      const clipboard = learningWorksheetClipboardRef.current;
      if (!clipboard) return false;
      const sid = workspaceId?.trim();
      if (!sid) return false;

      const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
      const canonicalLt = canonicalLearningTextByAnnualKey[annualKey] ?? rowDraft.learning_text ?? '';
      const parsedLearning = parseMemoFieldData(canonicalLt);
      const nextRefId = createWorksheetRefOnSave();
      const nextLearningText = serializeMemoFieldData(
        parsedLearning.text,
        parsedLearning.note,
        parsedLearning.presentationRefId,
        nextRefId
      );
      applyPatches([{ annualKey, fieldId: 'learning_text', value: nextLearningText }]);

      const targetDraftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );

      let pastedDraft = false;
      if (clipboard.draftRowKey) {
        const sourceDraft = loadLearningContentWorksheetDraft(clipboard.draftRowKey);
        if (sourceDraft) {
          saveLearningContentWorksheetDraft(targetDraftRowKey, sourceDraft);
          setWorksheetDraftRev((v) => v + 1);
          pastedDraft = true;
        }
      }

      const sourceRefId = clipboard.worksheetRefId?.trim();
      if (sourceRefId && isPersistableUuid(sourceRefId)) {
        void copyWorksheetBetweenOwners(sid, sourceRefId, nextRefId)
          .then(() => {
            setWorksheetPresenceRefIds((prev) => {
              const next = new Set(prev);
              next.add(nextRefId);
              return next;
            });
            setLearningSlideDeleteToast('활동지를 붙여넣었습니다.');
          })
          .catch(() => {
            setLearningSlideDeleteToast('활동지 붙여넣기에 실패했습니다. 다시 시도해 주세요.');
          });
        return true;
      }

      if (pastedDraft) {
        setLearningSlideDeleteToast('활동지를 붙여넣었습니다.');
        return true;
      }

      return false;
    },
    [
      applyPatches,
      canonicalLearningTextByAnnualKey,
      ensureDraftRow,
      mutationLocked,
      parseMemoFieldData,
      serializeMemoFieldData,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      workspaceId,
    ]
  );

  const deleteWorksheetForLearningAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      const sid = workspaceId?.trim();
      if (!sid) return false;
      if (!resolveLearningWorksheetActionState(annualKey).canDelete) return false;
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()].trim()
          : annualKey;
      const canonicalLt = resolveLearningTextForMemoAtAnnualKey(annualKey);
      const cell = programCellsByAnnualKey[patchAnnualKey] ?? programCellsByAnnualKey[annualKey];
      const repCell = syncMirrorView ? syncMirrorIndicatorCellsByAnnualKey[annualKey] : undefined;
      const patchLearningText =
        draftRef.current[patchAnnualKey]?.learning_text ??
        draftRef.current[annualKey]?.learning_text ??
        cellLearningTextForEditor(cell);
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );
      let refId = resolveProgramWorksheetRefIdForIndicator(
        cell,
        repCell,
        null,
        draftRef.current[patchAnnualKey]?.learning_text,
        draftRef.current[annualKey]?.learning_text,
        patchLearningText,
        canonicalLt
      );
      if (!refId) {
        refId = resolveSyncMirrorWorksheetRefIdForIndicator(
          annualKey,
          syncMirrorView,
          syncMirrorIndicatorCellsByAnnualKey,
          draftRef.current[annualKey]?.learning_text ?? '',
          cellLearningTextForEditor(cell),
          resolveRepDraftLearningText(annualKey),
          cell
        );
      }
      if (!refId) refId = resolvePendingWorksheetRefIdForAnnualKeys(sid, annualKey, draftRowKey);
      if (!refId) {
        const presenceRef = resolveProgramWorksheetRefIdForIndicator(cell, repCell, null, patchLearningText, canonicalLt);
        if (presenceRef && worksheetPresenceRefIds.has(presenceRef)) refId = presenceRef;
      }
      const hadRef = !!refId && isPersistableUuid(refId);
      const hadDraft = !!loadLearningContentWorksheetDraft(draftRowKey);
      const hadPending =
        (!!refId && hasPendingLearningWorksheetFlush(sid, refId)) ||
        hasPendingLearningWorksheetFlushForAnnualKeys(sid, annualKey, draftRowKey);
      if (!hadRef && !hadDraft && !hadPending) return false;
      markLearningAttachmentIndicatorDeletedForSyncKeys(
        sid,
        resolveLearningAttachmentDeleteHintAnnualKeys(annualKey, patchAnnualKey),
        'worksheet'
      );

      if (hadDraft) clearLearningContentWorksheetDraft(draftRowKey);
      if (hadPending) {
        if (refId) discardPendingLearningWorksheetFlush(sid, refId);
        else discardPendingLearningWorksheetFlushForAnnualKey(sid, draftRowKey);
      }
      if (hadRef) {
        setWorksheetPresenceRefIds((prev) => {
          const next = new Set(prev);
          next.delete(refId);
          return next;
        });
        void deleteProgramContentWorksheet(refId, sid).catch(() => undefined);
      }
      const shouldStripRef =
        !!refId ||
        !!resolveWorksheetRefIdFromLearningContent(cell) ||
        !!resolveWorksheetRefIdFromLearningContent(repCell) ||
        !!resolveWorksheetRefIdFromLearningText(canonicalLt) ||
        !!resolveWorksheetRefIdFromLearningText(patchLearningText);
      if (shouldStripRef) {
        const baseParsed = parseMemoFieldData(patchLearningText || canonicalLt);
        const nextLearning = JSON.stringify({
          text: baseParsed.text ?? '',
          note: baseParsed.note ?? '',
          ...(baseParsed.presentationRefId ? { presentationRefId: baseParsed.presentationRefId } : {}),
          worksheetRefId: null,
        });
        applyPatches([
          {
            annualKey: patchAnnualKey,
            fieldId: 'learning_text',
            value: nextLearning,
          },
        ]);
        notifyProgramContentSlideLearningUpdated({
          workspaceId: sid,
          scopeId: workspaceScopeId,
          annualKey: patchAnnualKey,
          learningText: nextLearning,
          syncAnnualLearningTextOverlay: true,
        });
      }
      setWorksheetDraftRev((n) => n + 1);
      return true;
    },
    [
      applyPatches,
      programCellsByAnnualKey,
      resolveLearningAttachmentDeleteHintAnnualKeys,
      resolveLearningTextForMemoAtAnnualKey,
      resolveLearningWorksheetActionState,
      resolveRepDraftLearningText,
      syncMirrorIndicatorCellsByAnnualKey,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      worksheetPresenceRefIds,
      workspaceId,
      workspaceScopeId,
    ]
  );

  const pasteLearningPresentationAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      if (mutationLocked) return false;
      const clipboard = learningPresentationClipboardRef.current;
      if (!clipboard) return false;
      const state = resolveLearningPresentationActionState(annualKey);
      if (!state.sid) return false;
      const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
      const parsedLearning = parseMemoFieldData(rowDraft.learning_text ?? '');
      const nextRefId = createPresentationRefId();
      const nextLearningText = serializeMemoFieldData(
        parsedLearning.text,
        parsedLearning.note,
        nextRefId,
        parsedLearning.worksheetRefId
      );
      const toOwner: ProgramContentSlideOwnerRef = { kind: 'presentation_ref', id: nextRefId };
      applyPatches([{ annualKey, fieldId: 'learning_text', value: nextLearningText }]);
      notifyProgramContentSlideLearningUpdated({
        workspaceId: state.sid,
        scopeId: workspaceScopeId,
        annualKey,
        learningText: nextLearningText,
        presentationRefId: nextRefId,
      });
      if (programContentSlidePresenceKey(clipboard.owner) === programContentSlidePresenceKey(toOwner)) {
        setLearningSlideDeleteToast('프레젠테이션을 붙여넣었습니다.');
        return true;
      }
      if (workspaceScopeId.trim()) {
        queueProgramContentSlideCloneIntent({
          workspaceId: state.sid,
          scopeId: workspaceScopeId,
          from: clipboard.owner,
          to: toOwner,
        });
      }
      void (async () => {
        try {
          const copiedCount = workspaceScopeId.trim()
            ? await queueProgramContentSlideCopyDraft({
                workspaceId: state.sid,
                scopeId: workspaceScopeId,
                from: clipboard.owner,
                to: toOwner,
              })
            : (() => {
                return copyProgramContentSlidePagesBetweenOwners(state.sid, clipboard.owner, toOwner).then(() => 1);
              })();
          if (copiedCount > 0) {
            setProgramContentSlidePresenceKeys((prev) => {
              const n = new Set(prev);
              n.add(programContentSlidePresenceKey(toOwner));
              return n;
            });
            setLearningSlideDeleteToast('프레젠테이션을 붙여넣었습니다.');
          } else {
            setLearningSlideDeleteToast('복사한 프레젠테이션에 슬라이드가 없어 붙여넣을 내용이 없습니다.');
          }
        } catch {
          setLearningSlideDeleteToast('프레젠테이션 붙여넣기에 실패했습니다. 다시 시도해 주세요.');
        }
      })();
      return true;
    },
    [
      applyPatches,
      ensureDraftRow,
      mutationLocked,
      parseMemoFieldData,
      resolveLearningPresentationActionState,
      serializeMemoFieldData,
      workspaceId,
      workspaceScopeId,
    ]
  );

  const deleteSlidesForLearningAtAnnualKey = React.useCallback(
    (annualKey: string): boolean => {
      const sid = workspaceId?.trim();
      if (!sid) return false;
      if (annualKey.startsWith('slide:')) {
        const owner = parsePresentationAuditSelectionKey(annualKey);
        if (!owner) return false;
        invalidateProgramPresentationAuditQuery(
          sid,
          academicYear,
          termNumber,
          (programDataCleanupGradeId ?? '').trim(),
          programPresentationAuditScopeSig
        );
        setProgramContentSlidePresenceKeys((prev) => {
          const next = new Set(prev);
          next.delete(programContentSlidePresenceKey(owner));
          return next;
        });
        removeProgramPresentationListStatsCacheKeys(sid, [programContentSlidePresenceKey(owner)]);
        setSlideDraftRev((n) => n + 1);
        void deletePresentationAuditSelections({ workspaceId: sid, selectionKeys: [annualKey] });
        setLearningSlideDeleteToast('프레젠테이션을 삭제했습니다.');
        return true;
      }
      const row = rowByAnnualKey[annualKey];
      if (!row) return false;
      const cell = programCellsByAnnualKey[annualKey];
      const canonicalLt = resolveLearningTextForMemoAtAnnualKey(annualKey);
      const parsedCanonical = parseMemoFieldData(canonicalLt);
      const owners = resolveSyncMirrorSlideOwnerCandidatesForIndicator({
        syncMirrorView,
        annualKey,
        syncMirrorIndicatorCellsByAnnualKey,
        mode,
        row,
        cell,
        learningText: canonicalLt,
      }).filter((owner) => isPersistableUuid(owner.id));
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey.trim()].trim()
          : annualKey;
      const patchLearningText =
        draftRef.current[patchAnnualKey]?.learning_text ??
        draftRef.current[annualKey]?.learning_text ??
        cellLearningTextForEditor(cell) ??
        '';
      const hadRef =
        !!parsedCanonical.presentationRefId && isPersistableUuid(parsedCanonical.presentationRefId);
      const ownersToDelete = [...owners];
      if (hadRef && parsedCanonical.presentationRefId) {
        const refOwner: ProgramContentSlideOwnerRef = {
          kind: 'presentation_ref',
          id: parsedCanonical.presentationRefId.trim(),
        };
        if (
          !ownersToDelete.some(
            (owner) => programContentSlidePresenceKey(owner) === programContentSlidePresenceKey(refOwner)
          )
        ) {
          ownersToDelete.push(refOwner);
        }
      }
      const hasMeaningfulDraft = ownersToDelete.some((owner) =>
        hasProgramContentSlideDraftForOwner(sid, owner)
      );
      if (!ownersToDelete.length && !hadRef && !hasMeaningfulDraft) return false;
      markLearningAttachmentIndicatorDeletedForSyncKeys(
        sid,
        resolveLearningAttachmentDeleteHintAnnualKeys(annualKey, patchAnnualKey),
        'presentation'
      );
      const statsOwnerKeys = ownersToDelete.map((owner) => programContentSlidePresenceKey(owner));
      setProgramContentSlidePresenceKeys((prev) => {
        const n = new Set(prev);
        ownersToDelete.forEach((owner) => n.delete(programContentSlidePresenceKey(owner)));
        return n;
      });
      removeProgramPresentationListStatsCacheKeys(sid, statsOwnerKeys);
      if (ownersToDelete.length) {
        void (async () => {
          for (const owner of ownersToDelete) {
            try {
              if (workspaceScopeId.trim()) {
                queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId: workspaceScopeId, owner });
              } else {
                await deleteProgramContentSlidePagesForOwner(sid, owner);
              }
            } catch {
              /* */
            }
          }
        })();
      }
      const shouldStripRef =
        hadRef || !!resolvePresentationRefIdFromLearningText(patchLearningText || canonicalLt);
      if (shouldStripRef) {
        const baseParsed = parseMemoFieldData(patchLearningText || canonicalLt);
        const nextLearning = JSON.stringify({
          text: baseParsed.text ?? '',
          note: baseParsed.note ?? '',
          presentationRefId: null,
          ...(baseParsed.worksheetRefId ? { worksheetRefId: baseParsed.worksheetRefId } : {}),
        });
        applyPatches([
          {
            annualKey: patchAnnualKey,
            fieldId: 'learning_text',
            value: nextLearning,
          },
        ]);
        notifyProgramContentSlideLearningUpdated({
          workspaceId: sid,
          scopeId: workspaceScopeId,
          annualKey: patchAnnualKey,
          learningText: nextLearning,
          syncAnnualLearningTextOverlay: true,
        });
      }
      invalidateProgramContentSlidePresenceCache(sid);
      void reloadProgramContentSlidePresence({ forceRefresh: true });
      setSlideDraftRev((n) => n + 1);
      return true;
    },
    [
      academicYear,
      applyPatches,
      mode,
      parseMemoFieldData,
      programCellsByAnnualKey,
      programDataCleanupGradeId,
      programPresentationAuditScopeSig,
      reloadProgramContentSlidePresence,
      resolveLearningAttachmentDeleteHintAnnualKeys,
      resolveLearningTextForMemoAtAnnualKey,
      rowByAnnualKey,
      syncMirrorIndicatorCellsByAnnualKey,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      termNumber,
      workspaceId,
      workspaceScopeId,
    ]
  );

  /** 모달 확인 후: 슬라이드 DB·draft 제거 + 학습내용 셀 값 완전 삭제 */
  const purgeLearningPresentationAndBlankLearningText = React.useCallback(
    (annualKey: string): void => {
      const sid = workspaceId?.trim();
      if (!sid) return;
      const row = rowByAnnualKey[annualKey];
      if (!row) return;
      const learningText = draftRef.current[annualKey]?.learning_text ?? '';
      const parsedLearning = parseMemoFieldData(learningText);
      const owners = resolveProgramContentSlideOwnerCandidates(
        mode,
        row,
        programCellsByAnnualKey[annualKey],
        learningText
      ).filter((owner) => isPersistableUuid(owner.id));
      const hadRef =
        !!parsedLearning.presentationRefId && isPersistableUuid(parsedLearning.presentationRefId);
      if (!owners.length && !hadRef) return;
      setProgramContentSlidePresenceKeys((prev) => {
        const n = new Set(prev);
        owners.forEach((owner) => n.delete(programContentSlidePresenceKey(owner)));
        if (hadRef && parsedLearning.presentationRefId) {
          n.delete(
            programContentSlidePresenceKey({
              kind: 'presentation_ref',
              id: parsedLearning.presentationRefId.trim(),
            })
          );
        }
        return n;
      });
      if (owners.length) {
        void (async () => {
          for (const owner of owners) {
            try {
              if (workspaceScopeId.trim()) {
                queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId: workspaceScopeId, owner });
              } else {
                await deleteProgramContentSlidePagesForOwner(sid, owner);
              }
            } catch {
              /* */
            }
          }
        })();
      }
      applyPatches([{ annualKey, fieldId: 'learning_text', value: '' }]);
    },
    [applyPatches, mode, parseMemoFieldData, programCellsByAnnualKey, rowByAnnualKey, workspaceId, workspaceScopeId]
  );

  const commitEditing = React.useCallback(
    (nextValue?: string) => {
      if (!editingCell) return;
      const cellGuard = `${editingCell.annualKey}::${editingCell.fieldId}`;
      if (blurCommitGuardCellRef.current === cellGuard) {
        blurCommitGuardCellRef.current = null;
        setEditingCell(null);
        return;
      }
      const row = rowByAnnualKey[editingCell.annualKey];
      /** blur 직전 mousedown에서 commitEditingNow가 이미 DOM 기준으로 반영했을 수 있음 — draft만 쓰면 같은 틱에 두 번째 apply·스택 낭비 */
      const rawValue =
        nextValue !== undefined ? nextValue : (editingInputRef.current?.value ?? editingCell.draft ?? '');
      const fixedLearningPrefix = resolveFixedLearningPrefix(editingCell.annualKey, row, rawValue);
      const storedMemoField = draftRef.current[editingCell.annualKey]?.[editingCell.fieldId] ?? '';
    const memoFromStored = parseMemoFieldData(storedMemoField);
    const rawValueTrim = (rawValue ?? '').trim();
    const memoNoteFromStored = memoFromStored.note;
    const learningRefsFromStored =
      editingCell.fieldId === 'learning_text' && !rawValueTrim && !memoNoteFromStored.trim()
        ? { presentationRefId: undefined, worksheetRefId: undefined }
        : {
            presentationRefId: memoFromStored.presentationRefId,
            worksheetRefId: memoFromStored.worksheetRefId,
          };
      const value =
        mode === 'fixed_to_unit' && editingCell.fieldId === 'learning_text' && fixedLearningPrefix
          ? serializeMemoFieldData(
            rawValue.startsWith(fixedLearningPrefix)
              ? rawValue
              : `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(rawValue, row?.memo ?? row?.fixedEventName ?? '')}`,
            memoNoteFromStored,
            learningRefsFromStored.presentationRefId,
            learningRefsFromStored.worksheetRefId
          )
        : editingCell.fieldId === 'unit_text'
            ? serializeUnitData(rawValue, parseUnitData(draftRef.current[editingCell.annualKey]?.unit_text ?? '').note)
            : memoModalFieldSet.has(editingCell.fieldId)
              ? serializeMemoFieldData(
                  rawValue,
                  memoNoteFromStored,
                  editingCell.fieldId === 'learning_text' ? learningRefsFromStored.presentationRefId : undefined,
                  editingCell.fieldId === 'learning_text' ? learningRefsFromStored.worksheetRefId : undefined
                )
            : rawValue;
      const prevStored = draftRef.current[editingCell.annualKey]?.[editingCell.fieldId] ?? '';
      if (value === prevStored) {
        setEditingCell(null);
        return;
      }
      blurCommitGuardCellRef.current = cellGuard;
      requestProgramProgressScrollRestore();
      applyPatches([
        {
          annualKey: editingCell.annualKey,
          fieldId: editingCell.fieldId,
          value,
        },
      ]);
      setEditingCell(null);
    },
    [
      applyPatches,
      editingCell,
      memoModalFieldSet,
      mode,
      parseMemoFieldData,
      parseUnitData,
      requestProgramProgressScrollRestore,
      resolveFixedLearningPrefix,
      rowByAnnualKey,
      serializeMemoFieldData,
      serializeUnitData,
    ]
  );

  const commitEditingNow = React.useCallback(() => {
    if (!editingCell) return;
    const row = rowByAnnualKey[editingCell.annualKey];
    const liveValue = editingInputRef.current?.value ?? editingCell.draft ?? '';
    const fixedLearningPrefix = resolveFixedLearningPrefix(editingCell.annualKey, row, liveValue);
    const storedMemoFieldNow = draftRef.current[editingCell.annualKey]?.[editingCell.fieldId] ?? '';
    const memoFromStoredNow = parseMemoFieldData(storedMemoFieldNow);
    const liveValueTrim = (liveValue ?? '').trim();
    const memoNoteFromStoredNow = memoFromStoredNow.note;
    const learningRefsFromStoredNow =
      editingCell.fieldId === 'learning_text' && !liveValueTrim && !memoNoteFromStoredNow.trim()
        ? { presentationRefId: undefined, worksheetRefId: undefined }
        : {
            presentationRefId: memoFromStoredNow.presentationRefId,
            worksheetRefId: memoFromStoredNow.worksheetRefId,
          };
    const value =
      mode === 'fixed_to_unit' && editingCell.fieldId === 'learning_text' && fixedLearningPrefix
        ? serializeMemoFieldData(
            liveValue.startsWith(fixedLearningPrefix)
              ? liveValue
              : `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(liveValue, row?.memo ?? row?.fixedEventName ?? '')}`,
            memoNoteFromStoredNow,
            learningRefsFromStoredNow.presentationRefId,
            learningRefsFromStoredNow.worksheetRefId
          )
        : editingCell.fieldId === 'unit_text'
          ? serializeUnitData(liveValue, parseUnitData(draftRef.current[editingCell.annualKey]?.unit_text ?? '').note)
          : memoModalFieldSet.has(editingCell.fieldId)
            ? serializeMemoFieldData(
                liveValue,
                memoNoteFromStoredNow,
                editingCell.fieldId === 'learning_text' ? learningRefsFromStoredNow.presentationRefId : undefined,
                editingCell.fieldId === 'learning_text' ? learningRefsFromStoredNow.worksheetRefId : undefined
              )
          : liveValue;
    const prevStored = draftRef.current[editingCell.annualKey]?.[editingCell.fieldId] ?? '';
    if (value === prevStored) {
      setEditingCell(null);
      return;
    }
    blurCommitGuardCellRef.current = `${editingCell.annualKey}::${editingCell.fieldId}`;
    requestProgramProgressScrollRestore();
    applyPatches([
      {
        annualKey: editingCell.annualKey,
        fieldId: editingCell.fieldId,
        value,
      },
    ]);
    setEditingCell(null);
  }, [
    applyPatches,
    editingCell,
    memoModalFieldSet,
    mode,
    parseMemoFieldData,
    parseUnitData,
    requestProgramProgressScrollRestore,
    resolveFixedLearningPrefix,
    rowByAnnualKey,
    serializeMemoFieldData,
    serializeUnitData,
  ]);
  const tryApplyShiftRectSelect = React.useCallback(
    (e: React.MouseEvent, cellId: string): boolean => {
      if (!e.shiftKey) return false;
      const anchor = shiftSelectionAnchorCellIdRef.current;
      if (!anchor) return false;
      const a = parseCellId(anchor);
      const b = parseCellId(cellId);
      if (!a || !b) return false;
      if (!isSelectableField(a.fieldId) || !isSelectableField(b.fieldId)) return false;
      e.preventDefault();
      commitEditingNow();
      clearLongPressTimer();
      selectRect(anchor, cellId);
      pointerDownRef.current = null;
      cellDragDocCleanupRef.current?.();
      cellDragDocCleanupRef.current = null;
      return true;
    },
    [clearLongPressTimer, commitEditingNow, isSelectableField, parseCellId, selectRect]
  );
  const handleImportProjectTimetableFromAnnual = React.useCallback(async () => {
    if (!onImportProjectTimetableFromAnnual) return;
    if (projectTimetableSyncBusy) return;
    commitEditingNow();
    setProjectTimetableSyncBusy('import');
    try {
      const imported = await onImportProjectTimetableFromAnnual();
      if (!imported.length || !tableRows.length) {
        setProjectTimetableSyncToast('가져올 시간표명이 없습니다.');
        return;
      }
      const limit = Math.min(imported.length, tableRows.length);
      const patches: Array<{ annualKey: string; fieldId: FieldId; value: string }> = [];
      for (let i = 0; i < limit; i += 1) {
        const annualKey = tableRows[i]?.annualCellKey;
        if (!annualKey) continue;
        patches.push({ annualKey, fieldId: 'timetable_text', value: imported[i] ?? '' });
      }
      if (patches.length) {
        applyPatches(patches);
      }
      setProjectTimetableSyncToast(`연간시간표에서 시간표명 ${limit}개를 가져왔습니다.`);
    } catch (e) {
      setProjectTimetableSyncToast(e instanceof Error ? e.message : '연간시간표 가져오기에 실패했습니다.');
    } finally {
      setProjectTimetableSyncBusy(null);
    }
  }, [applyPatches, commitEditingNow, onImportProjectTimetableFromAnnual, projectTimetableSyncBusy, tableRows]);
  const handleExportProjectTimetableToAnnual = React.useCallback(async () => {
    if (!onExportProjectTimetableToAnnual) return;
    if (projectTimetableSyncBusy) return;
    commitEditingNow();
    setProjectTimetableSyncBusy('export');
    try {
      const orderedTexts = tableRows.map((row) => {
        const annualKey = row.annualCellKey;
        /** 프로젝트 시간표 열은 draft/표 내용이 권위. 연간 일정 text로 빈칸을 채우지 않는다. */
        if (Object.prototype.hasOwnProperty.call(draftRef.current, annualKey)) {
          return (draftRef.current[annualKey]?.timetable_text ?? '').trim();
        }
        return (row.timetableText ?? '').trim();
      });
      await onExportProjectTimetableToAnnual(orderedTexts);
      setProjectTimetableSyncToast('프로젝트 시간표를 연간시간표로 내보냈습니다.');
    } catch (e) {
      setProjectTimetableSyncToast(e instanceof Error ? e.message : '연간시간표 내보내기에 실패했습니다.');
    } finally {
      setProjectTimetableSyncBusy(null);
    }
  }, [commitEditingNow, onExportProjectTimetableToAnnual, projectTimetableSyncBusy, tableRows]);

  React.useEffect(() => {
    if (!onBindProjectTimetableSyncActions) return;
    if (!onImportProjectTimetableFromAnnual && !onExportProjectTimetableToAnnual) {
      onBindProjectTimetableSyncActions(null);
      return;
    }
    onBindProjectTimetableSyncActions({
      importFromAnnual: () => {
        void handleImportProjectTimetableFromAnnual();
      },
      exportToAnnual: () => {
        void handleExportProjectTimetableToAnnual();
      },
      busy: projectTimetableSyncBusy != null,
    });
    return () => onBindProjectTimetableSyncActions(null);
  }, [
    handleExportProjectTimetableToAnnual,
    handleImportProjectTimetableFromAnnual,
    onBindProjectTimetableSyncActions,
    onExportProjectTimetableToAnnual,
    onImportProjectTimetableFromAnnual,
    projectTimetableSyncBusy,
  ]);

  const openProjectTimetableRenameModal = React.useCallback(() => {
    commitEditingNow();
    const seen = new Set<string>();
    const names: string[] = [];
    tableRows.forEach((row) => {
      const name = (draftRef.current[row.annualCellKey]?.timetable_text ?? row.timetableText ?? '').trim();
      if (!name || seen.has(name)) return;
      seen.add(name);
      names.push(name);
    });
    setProjectTimetableRenameNames(names);
    setProjectTimetableRenameModalOpen(true);
  }, [commitEditingNow, tableRows]);

  const handleConfirmProjectTimetableRename = React.useCallback(
    (replacements: Array<{ from: string; to: string }>) => {
      if (!replacements.length) {
        setProjectTimetableRenameModalOpen(false);
        return;
      }
      const patches: Array<{ annualKey: string; fieldId: FieldId; value: string }> = [];
      tableRows.forEach((row) => {
        const annualKey = row.annualCellKey;
        const current = (draftRef.current[annualKey]?.timetable_text ?? row.timetableText ?? '').trim();
        if (!current) return;
        const hit = replacements.find((r) => r.from === current);
        if (!hit) return;
        patches.push({ annualKey, fieldId: 'timetable_text', value: hit.to });
      });
      if (patches.length) applyPatches(patches);
      setProjectTimetableRenameModalOpen(false);
      setProjectTimetableSyncToast(
        patches.length > 0
          ? `시간표명 ${patches.length}칸을 변경했습니다.`
          : '변경할 시간표명이 없습니다.'
      );
    },
    [applyPatches, tableRows]
  );

  const flushFloatingPopupsBeforeRecordHeaderClick = React.useCallback(
    (outsideTarget: Node) => {
      clearLongPressTimer();
      if (unitPopup && !unitPopupRef.current?.contains(outsideTarget)) {
        if (!isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) {
          commitUnitPopupNow(unitPopup);
          setUnitPopup(null);
        }
      }
      if (memoFieldPopup && memoFieldPopupRef.current) {
        const clickedInsideMemoPopup = memoFieldPopupRef.current.contains(outsideTarget);
        if (!clickedInsideMemoPopup) {
          const memoDirty =
            isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
            isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current);
          if (!memoDirty) {
            if (memoFieldPopup.fieldId === 'record_class_text') {
              setClassRecordModalError(null);
              setMemoFieldPopup(null);
            } else if (memoFieldPopup.fieldId === 'record_evaluation_text') {
              setEvalPlanPickerOpen(false);
              setEvalPlanViewSnap(null);
              setMemoFieldPopup(null);
            } else {
              commitMemoFieldPopupNow(memoFieldPopup);
              setMemoFieldPopup(null);
            }
          }
        }
      }
      if (evalDropdown) {
        setEvalDropdown(null);
      }
      if (crossPopup && !crossPopupRef.current?.contains(outsideTarget)) {
        if (crossCatalogPickerOpenRef.current) return;
        const outsideEl = outsideTarget instanceof Element ? outsideTarget : outsideTarget.parentElement;
        if (
          outsideEl?.closest(
            '.cp-cross-curricular-catalog-picker-backdrop, .cp-cross-curricular-catalog-picker-modal'
          )
        ) {
          return;
        }
        if (isUnitPopupDirtyFn(crossPopup, crossPopupOpenSnapshotRef.current)) {
          commitCrossPopupNow(crossPopup);
        }
        setCrossPopup(null);
      }
    },
    [
      clearLongPressTimer,
      commitCrossPopupNow,
      commitMemoFieldPopupNow,
      commitUnitPopupNow,
      crossPopup,
      evalDropdown,
      isDoubleClickMemoOrClassRecordField,
      isMemoFieldPopupDirtyFn,
      isUnitPopupDirtyFn,
      memoFieldPopup,
      unitPopup,
    ]
  );

  React.useEffect(() => {
    if (!pageSplitModalOpen) return;
    const id = window.setTimeout(() => {
      pageSplitDelimiterInputRef.current?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(id);
  }, [pageSplitModalOpen]);

  const openPageAssistantSplitModal = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      flushFloatingPopupsBeforeRecordHeaderClick(e.currentTarget);
      if (unitPopup && isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) return;
      if (memoFieldPopup) {
        const memoDirty =
          isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
          isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current);
        if (memoDirty) return;
      }
      commitEditingNow();
      setPageSplitDelimiterInput('');
      setPageSplitModalOpen(true);
    },
    [
      commitEditingNow,
      flushFloatingPopupsBeforeRecordHeaderClick,
      isDoubleClickMemoOrClassRecordField,
      isMemoFieldPopupDirtyFn,
      isUnitPopupDirtyFn,
      memoFieldPopup,
      unitPopup,
    ]
  );

  const commitPageAssistantSplit = React.useCallback(() => {
    const delimiter = pageSplitDelimiterInput;
    if (!delimiter.length) return;
    commitEditingNow();
    const patches: Array<{ annualKey: string; fieldId: FieldId; value: string }> = [];
    for (const tr of tableRows) {
      const k = tr.annualCellKey;
      if (!k) continue;
      const fromDraft = draftRef.current[k];
      const pageRaw =
        fromDraft !== undefined ? (fromDraft.page_text ?? '') : (makeDraftRow(tr).page_text ?? '');
      const split = splitPageCellByDelimiter(pageRaw, delimiter);
      if (!split) continue;
      patches.push({ annualKey: k, fieldId: 'page_text', value: split.page });
      patches.push({ annualKey: k, fieldId: 'assistant_page_text', value: split.assistant });
    }
    if (patches.length) applyPatches(patches);
    setPageSplitModalOpen(false);
  }, [applyPatches, commitEditingNow, makeDraftRow, pageSplitDelimiterInput, tableRows]);

  const openLearningSessionModal = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      flushFloatingPopupsBeforeRecordHeaderClick(e.currentTarget);
      if (unitPopup && isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) return;
      if (memoFieldPopup) {
        const memoDirty =
          isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
          isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current);
        if (memoDirty) return;
      }
      commitEditingNow();
      const keys: string[] = [];
      const texts: string[] = [];
      for (const tr of tableRows) {
        const k = tr.annualCellKey;
        if (!k) continue;
        const fromDraft = draftRef.current[k];
        keys.push(k);
        texts.push(
          fromDraft !== undefined ? (fromDraft.learning_text ?? '') : (makeDraftRow(tr).learning_text ?? '')
        );
      }
      const changes = renumberLearningSessions(texts);
      setLearningSessionPlan(
        Array.from(changes.entries())
          .sort((a, b) => a[0] - b[0])
          .map(([idx, after]) => ({ annualKey: keys[idx], before: texts[idx], after }))
      );
    },
    [
      commitEditingNow,
      flushFloatingPopupsBeforeRecordHeaderClick,
      isDoubleClickMemoOrClassRecordField,
      isMemoFieldPopupDirtyFn,
      isUnitPopupDirtyFn,
      makeDraftRow,
      memoFieldPopup,
      tableRows,
      unitPopup,
    ]
  );

  const commitLearningSessionPlan = React.useCallback(() => {
    if (!learningSessionPlan?.length) {
      setLearningSessionPlan(null);
      return;
    }
    applyPatches(
      learningSessionPlan.map((p) => ({ annualKey: p.annualKey, fieldId: 'learning_text' as FieldId, value: p.after }))
    );
    setLearningSessionPlan(null);
  }, [applyPatches, learningSessionPlan]);

  const onRecordHeadButtonClick = React.useCallback(
    (fieldId: FieldId, e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (fieldId === 'record_class_text' && requireFeature('class_record')) return;
      if (
        fieldId !== 'record_cross_curricular_text' &&
        fieldId !== 'record_memo_text' &&
        fieldId !== 'record_evaluation_text' &&
        fieldId !== 'record_class_text'
      ) {
        return;
      }
      if (selectedCellsRef.current.length === 0) return;

      /** 연간 일정 없는 스코프 전용 행(orphan·overflow)도 program_scoped_content_rows 와 매칭되므로 허용 */
      let annualKey: string | null = null;
      for (const cellId of selectedCellsRef.current) {
        const p = parseCellId(cellId);
        if (p?.annualKey && rowByAnnualKey[p.annualKey]) {
          annualKey = p.annualKey;
          break;
        }
      }
      if (!annualKey) return;

      flushFloatingPopupsBeforeRecordHeaderClick(e.currentTarget);
      if (unitPopup && isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) return;
      if (memoFieldPopup) {
        const memoDirty =
          isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
          isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current);
        if (memoDirty) return;
      }
      commitEditingNow();
      setSelectedCells([makeCellId(annualKey, fieldId)]);
      programProgressScrollRef.current?.focus({ preventScroll: true });
      openRecordFieldModalAfterSelect(annualKey, fieldId);
    },
    [
      commitEditingNow,
      flushFloatingPopupsBeforeRecordHeaderClick,
      isDoubleClickMemoOrClassRecordField,
      isMemoFieldPopupDirtyFn,
      isUnitPopupDirtyFn,
      makeCellId,
      memoFieldPopup,
      openRecordFieldModalAfterSelect,
      parseCellId,
      rowByAnnualKey,
      unitPopup,
    ]
  );

  /**
   * 한 행에서 편집 열 구간의 연속 블록만 선택된 경우(연간진도표 → 프로그램 탐색 후 단원~수업기록 등):
   * ArrowUp/Down 으로 같은 열 범위를 유지한 채 인접 행으로 이동.
   */
  const tryMoveSingleRowSelectionBandVertical = React.useCallback(
    (delta: number): boolean => {
      const cells = selectedCellsRef.current;
      if (cells.length < 2) return false;
      const rect = getSelectionRect(cells);
      if (!rect || rect.minRow !== rect.maxRow) return false;
      const colSpan = rect.maxCol - rect.minCol + 1;
      if (cells.length !== colSpan) return false;
      const selectedSet = new Set(cells);
      const rowIdx = rect.minRow;
      const curAnnual = tableRows[rowIdx]?.annualCellKey;
      if (!curAnnual) return false;
      for (let c = rect.minCol; c <= rect.maxCol; c += 1) {
        const fieldId = editableFieldOrder[c];
        if (!fieldId || !selectedSet.has(makeCellId(curAnnual, fieldId))) return false;
      }
      const clamped = Math.max(0, Math.min(tableRows.length - 1, rowIdx + delta));
      const outOfRange = rowIdx + delta < 0 || rowIdx + delta > tableRows.length - 1;
      if (clamped === rowIdx && outOfRange) return true;
      const nextAnnual = tableRows[clamped]?.annualCellKey;
      if (!nextAnnual) return false;
      const nextCells: string[] = [];
      for (let c = rect.minCol; c <= rect.maxCol; c += 1) {
        const fieldId = editableFieldOrder[c];
        if (fieldId) nextCells.push(makeCellId(nextAnnual, fieldId));
      }
      if (!nextCells.length) return false;
      commitEditingNow();
      setSelectedCells(nextCells);
      setCopiedSourceRegion(null);
      const anchorField = editableFieldOrder[rect.minCol];
      if (anchorField) {
        const kb = delta < 0 ? 'ArrowUp' : 'ArrowDown';
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollProgramProgressForVerticalKey(clamped, nextAnnual, anchorField, kb);
          });
        });
      }
      return true;
    },
    [commitEditingNow, editableFieldOrder, getSelectionRect, makeCellId, scrollProgramProgressForVerticalKey, tableRows]
  );

  /** «오늘/내일 학습내용»: 선택 직후 ref·레이아웃이 한 박자 늦을 수 있어 rAF로 앵커 셀을 찾을 때까지 재시도 후 세로 중앙 정렬 */
  const scrollProgramProgressSelectionRowCentered = React.useCallback((annualKey: string, anchorFieldId: FieldId) => {
    const anchorKey = `${annualKey}\t${anchorFieldId}`;
    let attempts = 0;
    const maxAttempts = 120;

    const measureContentMidY = (wrap: HTMLElement, headerHeight: number) => {
      const wrapRect = wrap.getBoundingClientRect();
      const thead = wrap.querySelector('table thead');
      const headBottomMeasured = thead instanceof HTMLElement ? thead.getBoundingClientRect().bottom : wrapRect.top + headerHeight;
      const headBottomFromWrap = wrapRect.top + headerHeight;
      const headBottom = Math.max(headBottomMeasured, headBottomFromWrap);
      return headBottom + (wrapRect.bottom - headBottom) / 2;
    };

    const tick = () => {
      attempts += 1;
      const wrap = programProgressScrollRef.current;
      const el = cellAnchorByKeyRef.current.get(anchorKey);
      if (!wrap || !el) {
        if (attempts < maxAttempts) requestAnimationFrame(tick);
        return;
      }
      const V = wrap.clientHeight;
      if (V <= 0) {
        if (attempts < maxAttempts) requestAnimationFrame(tick);
        return;
      }
      const thead = wrap.querySelector('table thead');
      const H = thead instanceof HTMLElement ? thead.offsetHeight : 0;
      const maxTop = Math.max(0, wrap.scrollHeight - V);
      const elRect = el.getBoundingClientRect();
      const elMidY = elRect.top + elRect.height / 2;
      const midY = measureContentMidY(wrap, H);
      const err = elMidY - midY;
      if (Math.abs(err) >= 1.5) {
        wrap.scrollTop = Math.min(maxTop, Math.max(0, wrap.scrollTop + err));
        if (attempts < maxAttempts) requestAnimationFrame(tick);
        return;
      }
      wrap.focus({ preventScroll: true });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(tick);
    });
  }, []);

  const handleTodayLessonInTable = React.useCallback((mode: 'cycle' | 'clock_only') => {
    commitEditingNow();
    const sectionRowsArg = classScheduleSectionRows.length ? classScheduleSectionRows : null;
    const key = pickTodayLessonAnnualCellKey(tableRows, sectionRowsArg, {
      lastTodaySelectionKey: mode === 'cycle' ? lastTodaySelectionKey : null,
      pickMode: mode,
    });
    if (!key) return;
    if (!Number.isInteger(rowIdxByAnnualKey[key]) && rowIdxByAnnualKey[key] !== 0) return;
    const selectableFields = editableFieldOrder;
    if (!selectableFields.length) return;
    const nextSelected = selectableFields.map((fieldId) => makeCellId(key, fieldId));
    setSelectedCells(nextSelected);
    if (mode === 'clock_only') {
      setLastTodaySelectionKey(null);
    } else {
      setLastTodaySelectionKey(key);
    }
    setCopiedSourceRegion(null);
    scrollProgramProgressSelectionRowCentered(key, selectableFields[0]);
  }, [
    classScheduleSectionRows,
    commitEditingNow,
    editableFieldOrder,
    lastTodaySelectionKey,
    makeCellId,
    rowIdxByAnnualKey,
    scrollProgramProgressSelectionRowCentered,
    tableRows,
  ]);

  const handleTomorrowLessonInTable = React.useCallback(() => {
    commitEditingNow();
    const base = new Date();
    base.setDate(base.getDate() + 1);
    const tomorrowIso = toISODate(base);
    const key = pickDateOrNearestFutureAnnualCellKey(tableRows, tomorrowIso, {
      lastSelectionKey: lastTomorrowSelectionKey,
    });
    if (!key) return;
    if (!Number.isInteger(rowIdxByAnnualKey[key]) && rowIdxByAnnualKey[key] !== 0) return;
    const selectableFields = editableFieldOrder;
    if (!selectableFields.length) return;
    const nextSelected = selectableFields.map((fieldId) => makeCellId(key, fieldId));
    setSelectedCells(nextSelected);
    setLastTomorrowSelectionKey(key);
    setCopiedSourceRegion(null);
    scrollProgramProgressSelectionRowCentered(key, selectableFields[0]);
  }, [
    commitEditingNow,
    editableFieldOrder,
    lastTomorrowSelectionKey,
    makeCellId,
    rowIdxByAnnualKey,
    scrollProgramProgressSelectionRowCentered,
    tableRows,
  ]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (leaveSaveModalOpen) return;
      if (isAppBrowseNavigationShortcutBlockedRoute(window.location.pathname)) return;
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyT' && e.code !== 'KeyY') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      if (!tableRows.length) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'KeyY') {
        handleTomorrowLessonInTable();
      } else {
        /* 실제 Shift+T는 버튼과 동일 순환. 합성 이벤트만 시각 고정(연간진도표 Shift+R 경로 등) */
        handleTodayLessonInTable(e.isTrusted ? 'cycle' : 'clock_only');
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [handleTodayLessonInTable, handleTomorrowLessonInTable, leaveSaveModalOpen, tableRows.length]);

  const openProgramContentSlideForAnnualKey = React.useCallback(
    (
      annualKey: string,
      openOptions?: { preOpenedWindow?: Window | null; aiPreviewLayouts?: ProgramSlideLayoutV1[] }
    ) => {
    const runPresentationOpen = () => {
      if (requireFeature('presentation')) {
        if (openOptions?.preOpenedWindow) closeProgramContentSlideTabPlaceholder(openOptions.preOpenedWindow);
        return;
      }
      if (annualKey.startsWith('slide:')) {
        const sid = workspaceId?.trim() ?? '';
        const owner = parsePresentationAuditSelectionKey(annualKey);
        if (!sid || !owner) {
          setLearningSlideDeleteToast('프레젠테이션을 열 수 없습니다.');
          return;
        }
        touchAuthActivity();
        markDevPresentationOpenTransferScope();
        const handoff = resolveClassPresentationSlideOpenOptions(tutorial);
        const opened = openProgramContentSlideInNewTab(
          {
            v: 1,
            workspaceId: sid,
            academicYear,
            termNumber,
            owner,
            extraSlideBodies: [''],
            meta: {
              templateLabel: '',
              unit: '',
              page: '',
              assistantPage: '',
              programContent: '',
              achievementText: '',
            },
          },
          handoff
            ? { tutorialId: handoff.tutorialId, tutorialStep: handoff.tutorialStep }
            : undefined
        );
        if (!opened.ok) {
          setLearningSlideDeleteToast(openProgramContentSlideFailureMessage(opened.reason));
        } else if (handoff) {
          finishClassPresentationHandoffOnOpener(tutorial);
        }
        return;
      }
      const row = rowByAnnualKey[annualKey];
      if (!row) {
        if (openOptions?.preOpenedWindow) closeProgramContentSlideTabPlaceholder(openOptions.preOpenedWindow);
        return;
      }
      touchAuthActivity();
      markDevPresentationOpenTransferScope();
      const slideTab =
        openOptions && 'preOpenedWindow' in openOptions
          ? openOptions.preOpenedWindow ?? null
          : openProgramContentSlideTabPlaceholder();
      const aiPreviewLayouts = openOptions?.aiPreviewLayouts?.length ? openOptions.aiPreviewLayouts : undefined;
      let draft = draftRef.current[annualKey];
      if (!draft) draft = makeDraftRow(row);
      const like: ProgramProgressLikeRow = {
        unit_text: draft.unit_text ?? '',
        learning_text: draft.learning_text ?? '',
        page_text: draft.page_text ?? '',
        assistant_page_text: draft.assistant_page_text ?? '',
        current_period_text: draft.current_period_text ?? '',
        total_period_text: draft.total_period_text ?? '',
        material_text: draft.material_text ?? '',
        record_cross_curricular_text: draft.record_cross_curricular_text ?? '',
        record_memo_text: draft.record_memo_text ?? '',
        record_evaluation_text: draft.record_evaluation_text ?? '',
        record_class_text: draft.record_class_text ?? '',
        record_member_group_id: draft.record_member_group_id ?? null,
      };
      const d = mapProgressLikeToDisplayFields(like);
      const gradeId = (row.gradeId || progressGradeId || '').trim();
      const cell = programCellsByAnnualKey[annualKey];
      const learningForOwner =
        canonicalLearningTextByAnnualKey[annualKey] ??
        draft.learning_text ??
        cellLearningTextForEditor(programCellsByAnnualKey[annualKey]) ??
        '';
      const parsedLearningForOwner = parseMemoFieldData(learningForOwner);
      const presentationRefId = parsedLearningForOwner.presentationRefId ?? '';
      const ownerCandidates = resolveProgramContentSlideOwnerCandidates(mode, row, cell, learningForOwner);
      const fallbackOwner = resolveProgramOpenSlideDbOwner(mode, row, cell, learningForOwner);
      const sourceAnnualCellKey =
        row.fixedSourceAnnualCellKey ?? (row.isScheduleAvailable === false ? null : row.annualCellKey);
      const metaBase = {
        templateLabel: (row.templateLabel ?? '').trim(),
        unit: (d.progressUnit ?? '').trim(),
        page: (draft.page_text ?? '').trim(),
        assistantPage: (draft.assistant_page_text ?? '').trim(),
        programContent: (d.progressLearning ?? '').trim(),
        achievementText: (d.progressUnitNote ?? '').trim(),
        learningMemoNote: (d.progressLearningNote ?? '').trim(),
        evaluationMemoNote: (d.progressEvalNote ?? '').trim(),
      };
      void (async () => {
        try {
        const weeklyGuideTrackColorHex =
          gradeId && metaBase.templateLabel
            ? await resolveWeeklyLearningGuideTrackBackgroundHex({
                workspaceId,
                academicYear,
                gradeId,
                trackDisplayName: metaBase.templateLabel,
                tracks: null,
              })
            : '';
        const owner = await resolveSlideTabOwnerForSave({
          workspaceId,
          candidates: ownerCandidates,
          presenceKeys: programContentSlidePresenceKeys,
          presenceKeyOf: programContentSlidePresenceKey,
          fallbackOwner,
        });
        // DB 행이 없으면 로컬 전용으로 연다(편집 가능·저장 불가).
        // 학습내용 초안이 있을 때만 첫 저장 시 바인딩 생성을 허용한다.
        const createOnSave =
          presentationRefId ||
          (!owner && !(learningForOwner ?? '').trim())
            ? undefined
            : {
                annualKey,
                learningText: learningForOwner,
                saveMode,
                mode,
                gradeId: row.gradeId,
                mondayIso: row.mondayIso,
                weekdayKey: row.weekdayKey,
                slotIndex: row.slotIndex,
                orderIndex: row.fixedOrderIndex ?? rowIdxByAnnualKey[annualKey] ?? 0,
                sourceAnnualCellKey,
                timetableText: draft.timetable_text ?? '',
                unitText: draft.unit_text ?? '',
                pageText: draft.page_text ?? '',
                assistantPageText: draft.assistant_page_text ?? '',
                currentPeriodText: draft.current_period_text ?? '',
                totalPeriodText: draft.total_period_text ?? '',
                materialText: draft.material_text ?? '',
                recordCrossCurricularText: draft.record_cross_curricular_text ?? '',
                recordMemoText: draft.record_memo_text ?? '',
                recordEvaluationText: draft.record_evaluation_text ?? '',
                recordClassText: draft.record_class_text ?? '',
              };
        const handoff = resolveClassPresentationSlideOpenOptions(tutorial);
        const opened = openProgramContentSlideInNewTab(
          {
            v: 1,
            workspaceId,
            scopeId: workspaceScopeId,
            annualCellKey: annualKey,
            contextGradeId: gradeId || undefined,
            academicYear,
            termNumber,
            owner,
            learningTextOwner: fallbackOwner,
            bindingLearningText: learningForOwner,
            createPresentationRefOnSave: createOnSave,
            extraSlideBodies: aiPreviewLayouts ? aiPreviewLayouts.map(() => '') : [''],
            aiPreviewLayouts,
            meta: {
              ...metaBase,
              weeklyGuideTrackColorHex: weeklyGuideTrackColorHex || undefined,
            },
          },
          {
            preOpenedWindow: slideTab ?? undefined,
            ...(handoff
              ? { tutorialId: handoff.tutorialId, tutorialStep: handoff.tutorialStep }
              : {}),
          }
        );
        if (!opened.ok) {
          closeProgramContentSlideTabPlaceholder(slideTab);
          setLearningSlideDeleteToast(openProgramContentSlideFailureMessage(opened.reason));
        } else if (handoff) {
          finishClassPresentationHandoffOnOpener(tutorial);
        }
        } catch {
          closeProgramContentSlideTabPlaceholder(slideTab);
          setLearningSlideDeleteToast('프레젠테이션을 열 수 없습니다.');
        }
      })();
    };
    if (openOptions && 'preOpenedWindow' in openOptions) runPresentationOpen();
    else gatePresentationOpen(runPresentationOpen);
  },
    [
      academicYear,
      makeDraftRow,
      mode,
      canonicalLearningTextByAnnualKey,
      parseMemoFieldData,
      programCellsByAnnualKey,
      programContentSlidePresenceKeys,
      progressGradeId,
      rowByAnnualKey,
      setLearningSlideDeleteToast,
      rowIdxByAnnualKey,
      tutorial,
      workspaceId,
      saveMode,
      termNumber,
      workspaceScopeId,
    ]
  );

  /** 툴바 «프레젠테이션»: Shift+U와 동일 — 선택 행이 정확히 하나일 때만 새 탭에서 연다 */
  const handleOpenPresentationForSelectedRow = React.useCallback(() => {
    commitEditingNow();
    if (selectedRowIndexSet.size !== 1) {
      setLearningSlideDeleteToast('프레젠테이션은 표에서 한 행만 선택했을 때 열 수 있습니다.');
      return;
    }
    const rowIdx = Array.from(selectedRowIndexSet)[0];
    const row = tableRows[rowIdx];
    if (!row) return;
    openProgramContentSlideForAnnualKey(row.annualCellKey);
  }, [
    commitEditingNow,
    openProgramContentSlideForAnnualKey,
    selectedRowIndexSet,
    setLearningSlideDeleteToast,
    tableRows,
  ]);

  const resolveWorksheetLearningTextPatch = React.useCallback(
    (
      annualKey: string,
      nextLearningText: string
    ): ProgramWorksheetLearningTextPatch | null => {
      const row = rowByAnnualKey[annualKey];
      if (!row) return null;
      const cell = programCellsByAnnualKey[annualKey];
      const dbOwner = resolveProgramOpenSlideDbOwner(mode, row, cell, nextLearningText);
      if (
        dbOwner &&
        (dbOwner.kind === 'program_cell' || dbOwner.kind === 'fixed_row' || dbOwner.kind === 'scoped_row')
      ) {
        return { kind: dbOwner.kind, id: dbOwner.id.trim(), learningText: nextLearningText };
      }
      return null;
    },
    [mode, programCellsByAnnualKey, rowByAnnualKey]
  );

  const openWorksheetEditorForAnnualKey = React.useCallback(
    (annualKey: string) => {
      if (requireFeature('worksheet')) return;
      const sid = workspaceId?.trim();
      if (!sid) {
        setLearningSlideDeleteToast('워크스페이스 정보가 없어 활동지를 열 수 없습니다.');
        return;
      }
      const cell = programCellsByAnnualKey[annualKey];
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );
      const learningText =
        canonicalLearningTextByAnnualKey[annualKey] ??
        draftRef.current[annualKey]?.learning_text ??
        cellLearningTextForEditor(cell);
      const existingRefId =
        resolveWorksheetRefIdFromLearningContent(cell) ||
        resolveWorksheetRefIdFromLearningText(learningText) ||
        resolvePendingWorksheetRefIdForAnnualKeys(sid, annualKey, draftRowKey);
      setWorksheetEditorInitialDirty(false);
      setWorksheetEditorAnnualKey(annualKey);
      setWorksheetEditorOpen(true);
      void resolveWorksheetDocumentForEditor({
        workspaceId: sid,
        worksheetRefId: existingRefId,
        draftStorageKey: draftRowKey,
      })
        .then((doc) => {
          setWorksheetEditorInitialDocument(doc);
        })
        .catch(() => {
          setWorksheetEditorInitialDocument(null);
          if (existingRefId) {
            setLearningSlideDeleteToast('활동지를 불러오지 못했습니다.');
          }
        });
    },
    [
      canonicalLearningTextByAnnualKey,
      programCellsByAnnualKey,
      setLearningSlideDeleteToast,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      workspaceId,
    ]
  );

  const handleRegenerateWorksheetAi = React.useCallback(
    async (avoidTypes: string[]) => {
      const req = worksheetAiRequest;
      if (!req) throw new Error('다시 만들 정보가 없습니다. 표에서 AI 생성을 다시 눌러 주세요.');
      if (requireFeature('worksheet')) throw new Error('이용권이 있어야 다시 만들 수 있습니다.');
      const material = await requestAiLearningMaterial({ ...req.input, kind: 'worksheet', avoidTypes });
      if (material.kind !== 'worksheet') throw new Error('활동지를 만들지 못했습니다.');
      const built = buildWorksheetFromAiMaterial(material);
      return { document: built.document, warnings: built.warnings };
    },
    [requireFeature, worksheetAiRequest]
  );

  const startAiLearningMaterial = React.useCallback(
    (kind: 'presentation' | 'worksheet', annualKey: string, planNote: string) => {
    const runPresentationOpen = () => {
      if (learningMaterialBusy) return;
      if (requireFeature(kind)) return;
      if (!workspaceId?.trim()) {
        setLearningSlideDeleteToast('워크스페이스 정보가 없어 만들지 못했습니다.');
        return;
      }
      const plan = planNote.trim();
      if (!plan) {
        setLearningSlideDeleteToast('학습계획을 입력한 뒤 생성할 수 있습니다.');
        return;
      }
      if (kind === 'presentation' && resolveLearningPresentationActionState(annualKey).canDeletePresentation) {
        const ok = window.confirm(
          '이미 프레젠테이션이 있습니다. AI가 만든 내용을 편집 창에 띄울까요? 저장하기 전에는 기존 내용이 유지됩니다.'
        );
        if (!ok) return;
      }
      if (kind === 'worksheet' && resolveLearningWorksheetActionState(annualKey).canDelete) {
        const ok = window.confirm(
          '이미 활동지가 있습니다. AI가 만든 내용을 편집 창에 띄울까요? 저장하기 전에는 기존 내용이 유지됩니다.'
        );
        if (!ok) return;
      }
      const slideTab = kind === 'presentation' ? openProgramContentSlideTabPlaceholder('ai-generating') : null;
      const row = rowByAnnualKey[annualKey];
      const unitParsed = getUnitTextAndNoteForDisplay(draftRef.current[annualKey]?.unit_text ?? '');
      const learningTitle = (memoFieldPopup?.annualKey === annualKey ? memoFieldPopup.text : '').trim();
      flushLearningMemoPopupBeforeAttachmentAction(annualKey);
      setLearningMaterialBusy(kind);
      void (async () => {
        try {
          const requestInput: AiLearningMaterialContext = {
            kind,
            plan,
            subject: row?.templateLabel ?? '',
            grade: row?.gradeLabel ?? '',
            unit: unitParsed.text,
            learning: learningTitle,
            achievement: unitParsed.note,
          };
          const material = await requestAiLearningMaterial(requestInput);
          if (kind === 'presentation') {
            if (material.kind !== 'presentation') throw new Error('프레젠테이션을 만들지 못했습니다.');
            const layouts = buildPresentationLayoutsFromAi(material.slides);
            if (!layouts.length) throw new Error('프레젠테이션을 만들지 못했습니다. 다시 시도해 주세요.');
            openProgramContentSlideForAnnualKey(annualKey, { preOpenedWindow: slideTab, aiPreviewLayouts: layouts });
            return;
          }
          if (material.kind !== 'worksheet') throw new Error('활동지를 만들지 못했습니다.');
          const built = buildWorksheetFromAiMaterial(material);
          const document = built.document;
          setWorksheetAiRequest({ annualKey, input: requestInput });
          if (built.warnings.length) setLearningSlideDeleteToast(built.warnings[0]);
          const draftRowKey = resolveWorksheetDraftStorageRowKey(
            annualKey,
            syncMirrorView,
            syncMirrorRepAnnualCellKeyByMirrorKey
          );
          worksheetAiPreviewRef.current = {
            draftKey: draftRowKey,
            previous: loadLearningContentWorksheetDraft(draftRowKey),
            saved: false,
          };
          clearLearningContentWorksheetDraft(draftRowKey);
          setWorksheetEditorInitialDirty(true);
          setWorksheetEditorAnnualKey(annualKey);
          setWorksheetEditorInitialDocument(document);
          setWorksheetEditorOpen(true);
        } catch (e) {
          if (slideTab) closeProgramContentSlideTabPlaceholder(slideTab);
          const session = worksheetAiPreviewRef.current;
          worksheetAiPreviewRef.current = null;
          if (session && !session.saved) {
            clearLearningContentWorksheetDraft(session.draftKey);
            if (session.previous) saveLearningContentWorksheetDraft(session.draftKey, session.previous);
          }
          setLearningSlideDeleteToast(e instanceof Error ? e.message : '자료를 만들지 못했습니다.');
        } finally {
          setLearningMaterialBusy(null);
        }
      })();
    };
    if (kind !== 'presentation') runPresentationOpen();
    else gatePresentationOpen(runPresentationOpen);
  },
    [
      flushLearningMemoPopupBeforeAttachmentAction,
      learningMaterialBusy,
      memoFieldPopup,
      openProgramContentSlideForAnnualKey,
      requireFeature,
      resolveLearningPresentationActionState,
      resolveLearningWorksheetActionState,
      rowByAnnualKey,
      setLearningSlideDeleteToast,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      workspaceId,
    ]
  );

  const handleOpenWorksheetForSelectedRow = React.useCallback(() => {
    commitEditingNow();
    if (selectedRowIndexSet.size !== 1) {
      setLearningSlideDeleteToast('활동지는 표에서 한 행만 선택했을 때 열 수 있습니다.');
      return;
    }
    const rowIdx = Array.from(selectedRowIndexSet)[0];
    const row = tableRows[rowIdx];
    if (!row) return;
    openWorksheetEditorForAnnualKey(row.annualCellKey);
  }, [
    commitEditingNow,
    openWorksheetEditorForAnnualKey,
    selectedRowIndexSet,
    setLearningSlideDeleteToast,
    tableRows,
  ]);

  const handleSaveWorksheetDocument = React.useCallback(
    async (
      document: LearningContentWorksheetDocumentV1,
      _meta?: { pageNames: string[]; pageIds: string[] },
      options?: { silent?: boolean }
    ) => {
      const silent = !!options?.silent;
      const sid = workspaceId?.trim();
      const scopeId = workspaceScopeId?.trim();
      const annualKey = worksheetEditorAnnualKey?.trim();
      if (!sid || !scopeId || !annualKey) throw new Error('missing scope');
      const dirtyIds = collectDirtyWorkspaceScopeIdsForAttachmentSave({
        scopeId,
        workspaceId: sid,
        gradeId: progressGradeId,
        academicYear,
        termNumber,
      });
      /** 자동 저장(5분·닫을 때)은 묻지 않고 활동지만 DB에 저장한다 */
      const coSaveProgramTable = dirtyIds.length > 0 && !silent;
      if (coSaveProgramTable) {
        const ok = window.confirm(programTableDirtyWithAttachmentSaveConfirmMessage('worksheet'));
        if (!ok) throw new ProgramTableCoSaveCancelledError();
      }
      const patchAnnualKey =
        syncMirrorView && syncMirrorRepAnnualCellKeyByMirrorKey[annualKey]?.trim()
          ? syncMirrorRepAnnualCellKeyByMirrorKey[annualKey].trim()
          : annualKey;
      const row = rowByAnnualKey[patchAnnualKey] ?? rowByAnnualKey[annualKey];
      if (!row) throw new Error('missing row');
      const canonicalLt =
        canonicalLearningTextByAnnualKey[patchAnnualKey] ??
        canonicalLearningTextByAnnualKey[annualKey] ??
        draftRef.current[patchAnnualKey]?.learning_text ??
        draftRef.current[annualKey]?.learning_text ??
        '';
      const parsedLearning = parseMemoFieldData(canonicalLt);
      let worksheetRefId = parsedLearning.worksheetRefId ?? '';
      if (!worksheetRefId || !isPersistableUuid(worksheetRefId)) {
        worksheetRefId = createWorksheetRefOnSave();
      }
      const nextLearningText = learningTextWithWorksheetRef(canonicalLt, worksheetRefId);
      const learningPatch = resolveWorksheetLearningTextPatch(patchAnnualKey, nextLearningText);
      /**
       * DB에 행이 있으면 활동지 저장이 learning_text(ref)까지 바로 반영하므로 표를 '미저장'으로 만들지 않는다.
       * 행이 아직 DB에 없거나 그 행에 저장 대기 중인 표 변경이 있으면 기존처럼 표 저장 대상으로 둔다.
       */
      const learningSyncOnly = !!learningPatch && !annualKeyHasPendingWorkspaceSave(patchAnnualKey);
      applyPatches(
        [{ annualKey: patchAnnualKey, fieldId: 'learning_text', value: nextLearningText }],
        learningSyncOnly ? { syncOnly: true } : undefined
      );
      const draftRowKey = resolveWorksheetDraftStorageRowKey(
        annualKey,
        syncMirrorView,
        syncMirrorRepAnnualCellKeyByMirrorKey
      );
      queueLearningWorksheetPendingFlush({
        workspaceId: sid,
        scopeId,
        worksheetRefId,
        annualKey: draftRowKey,
        document,
        learningPatch,
      });
      const dbOwner = resolveWorksheetLearningTextPatch(patchAnnualKey, nextLearningText);
      notifyProgramContentSlideLearningUpdated({
        workspaceId: sid,
        scopeId,
        annualKey: patchAnnualKey,
        learningText: nextLearningText,
        owner:
          dbOwner &&
          (dbOwner.kind === 'program_cell' || dbOwner.kind === 'fixed_row' || dbOwner.kind === 'scoped_row')
            ? { kind: dbOwner.kind, id: dbOwner.id.trim() }
            : null,
      });
      if (worksheetAiPreviewRef.current) worksheetAiPreviewRef.current.saved = true;
      setWorksheetDraftRev((n) => n + 1);
      if (coSaveProgramTable) {
        await flushPendingLearningWorksheetDrafts([scopeId]);
        await saveProgramWorkspaceScopes(dirtyIds);
        setLearningSlideDeleteToast('활동지와 프로그램 표를 저장했습니다.');
      } else {
        await flushPendingLearningWorksheetDraftForRef(sid, worksheetRefId);
        if (!silent) setLearningSlideDeleteToast('활동지를 저장했습니다.');
      }
    },
    [
      academicYear,
      annualKeyHasPendingWorkspaceSave,
      applyPatches,
      canonicalLearningTextByAnnualKey,
      parseMemoFieldData,
      progressGradeId,
      resolveWorksheetLearningTextPatch,
      rowByAnnualKey,
      setLearningSlideDeleteToast,
      syncMirrorRepAnnualCellKeyByMirrorKey,
      syncMirrorView,
      termNumber,
      worksheetEditorAnnualKey,
      workspaceId,
      workspaceScopeId,
    ]
  );

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (leaveSaveModalOpen) return;
      if (isAppBrowseNavigationShortcutBlockedRoute(window.location.pathname)) return;
      if (!e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.code !== 'KeyU' && e.code !== 'KeyI') return;
      if (e.repeat) return;
      if (shouldIgnoreAppNavigationShortcut(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.code === 'KeyU') {
        handleOpenPresentationForSelectedRow();
        return;
      }
      handleOpenWorksheetForSelectedRow();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [
    handleOpenPresentationForSelectedRow,
    handleOpenWorksheetForSelectedRow,
    leaveSaveModalOpen,
  ]);

  const handleAutoPeriodCount = React.useCallback(
    (delimiter: string) => {
      if (mutationLockedRef.current) return;
      commitEditingNow();
      if (!tableRows.length) return;
      const next: Record<string, DraftRow> = { ...draftRef.current };
      const unitTexts = tableRows.map((row) => {
        const draft = next[row.annualCellKey] ?? ensureDraftRow(next, row.annualCellKey);
        return parseUnitData(draft.unit_text ?? '').text;
      });
      const assignments = computeLessonCountAssignments(unitTexts, delimiter);
      const changed = new Set<string>();
      tableRows.forEach((row, idx) => {
        const assignment = assignments[idx];
        if (!assignment) return;
        const ak = row.annualCellKey;
        const rowDraft = ensureDraftRow(next, ak);
        const curStr = String(assignment.current);
        const totStr = String(assignment.total);
        if (rowDraft.current_period_text !== curStr || rowDraft.total_period_text !== totStr) {
          rowDraft.current_period_text = curStr;
          rowDraft.total_period_text = totStr;
          changed.add(ak);
        }
      });
      saveLessonCountUnitDelimiter(delimiter);
      setLessonCountModalOpen(false);
      if (!changed.size) return;
      if (saveMode === 'fixed_content' && onWorkspaceRowsChange && notifySlideEditStructureBlocked()) return;
      captureLocalUndoBeforeMutation();
      draftRef.current = next;
      setDraftByAnnualKey(next);
      if (saveMode === 'fixed_content' && onWorkspaceRowsChange) {
        const fullSnapshot = buildAllFixedContentWorkspaceRows(next);
        clearPendingFixedContentCellEditsAfterReplace();
        onWorkspaceRowsChange(fullSnapshot, { persistKind: 'replace' });
        return;
      }
      queuePendingRows(next, changed);
    },
    [
      buildAllFixedContentWorkspaceRows,
      captureLocalUndoBeforeMutation,
      clearPendingFixedContentCellEditsAfterReplace,
      commitEditingNow,
      ensureDraftRow,
      onWorkspaceRowsChange,
      parseUnitData,
      queuePendingRows,
      saveMode,
      tableRows,
      notifySlideEditStructureBlocked,
    ]
  );

  const openLessonCountModal = React.useCallback(() => {
    if (mutationLockedRef.current) return;
    setLessonCountDelimiterInput(loadLessonCountUnitDelimiter());
    setLessonCountModalOpen(true);
  }, []);

  const showPasteColumnMismatchToast = React.useCallback(
    (copiedCells: { fieldId: FieldId }[]) => {
      const uniqIds = Array.from(new Set(copiedCells.map((c) => c.fieldId)));
      const names = uniqIds.map((id) => fieldLabelForPasteHint[id]).join('·');
      if (uniqIds.length === 1) {
        setPasteColumnMismatchToast(`「${names}」열을 선택한 뒤 붙여넣기 해 주세요.`);
      } else {
        setPasteColumnMismatchToast(
          `복사한 열(${names})과 맞는 열을 선택한 뒤 붙여넣기 해 주세요.`
        );
      }
    },
    [fieldLabelForPasteHint]
  );

  const handleCopy = React.useCallback((): boolean => {
    if (notifySlideEditStructureBlocked()) return false;
    const rect = getSelectionRect(selectedCellsRef.current);
    if (!rect) return false;
    const cells: {
      rowOffset: number;
      colOffset: number;
      fieldId: FieldId;
      value: string;
      bgColor?: string;
      recordMemberGroupId?: string | null;
      slideCopyFrom?: { kind: ProgramContentSlideOwnerRef['kind']; id: string };
    }[] = [];
    selectedCellsRef.current.forEach((cellId) => {
      const parsed = parseCellId(cellId);
      if (!parsed) return;
      const rowIdx = rowIdxByAnnualKey[parsed.annualKey];
      const colIdx = colIdxByField[parsed.fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return;
      const rowDraft = draftRef.current[parsed.annualKey] ?? ensureDraftRow(draftRef.current, parsed.annualKey);
      const bg = rowDraft.bg_color_by_field?.[parsed.fieldId] ?? '';
      const pr = rowByAnnualKey[parsed.annualKey];
      let slideCopyFrom: { kind: ProgramContentSlideOwnerRef['kind']; id: string } | undefined;
      if (parsed.fieldId === 'learning_text' && pr && learningTextAnnualKeyHasPresentationUi(parsed.annualKey)) {
        const o = resolveProgramContentSlideOwner(
          mode,
          pr,
          programCellsByAnnualKey[parsed.annualKey],
          rowDraft.learning_text ?? ''
        );
        if (o && isPersistableUuid(o.id)) {
          slideCopyFrom = { kind: o.kind, id: o.id };
        }
      }
      cells.push({
        rowOffset: rowIdx - rect.minRow,
        colOffset: colIdx - rect.minCol,
        fieldId: parsed.fieldId,
        value: rowDraft[parsed.fieldId] ?? '',
        bgColor: bg || undefined,
        recordMemberGroupId:
          parsed.fieldId === 'record_class_text' ? rowDraft.record_member_group_id ?? null : undefined,
        ...(slideCopyFrom ? { slideCopyFrom } : {}),
      });
    });
    copiedRef.current = { cells, isCut: false, sourceCellIds: [...selectedCellsRef.current], sourceScopeId: workspaceScopeId };
    saveProgramWorkspaceClipboard({
      cells,
      isCut: false,
      sourceCellIds: [...selectedCellsRef.current],
      sourceScopeId: workspaceScopeId,
    });
    setCopiedSourceRegion([...selectedCellsRef.current]);
    const tsv = buildGridTsvForRect(rect);
    const norm = normalizeClipboardTextForCompare(tsv);
    /** 동기 설정: writeText 완료 전 Cmd+V 시 외부 그리드로 오인해 잘라내기(isCut)가 무시되는 것 방지 */
    lastInternalClipNormalizedRef.current = norm;
    void navigator.clipboard.writeText(tsv).catch(() => {
      lastInternalClipNormalizedRef.current = null;
    });
    return true;
  }, [
    buildGridTsvForRect,
    colIdxByField,
    ensureDraftRow,
    getSelectionRect,
    mode,
    parseCellId,
    programCellsByAnnualKey,
    rowByAnnualKey,
    rowIdxByAnnualKey,
    learningTextAnnualKeyHasPresentationUi,
    workspaceScopeId,
    notifySlideEditStructureBlocked,
  ]);

  const handleCut = React.useCallback(() => {
    if (!canMutateCellSelection(selectedCellsRef.current)) return;
    if (notifySlideEditStructureBlocked()) return;
    if (!handleCopy()) return;
    if (saveMode === 'fixed_content') {
      // 전체 스냅샷 저장 (replace 모드)
      const fullSnapshot = buildAllFixedContentWorkspaceRows(draftRef.current);
      onWorkspaceRowsChange?.(fullSnapshot, { persistKind: 'replace' });
    }
    if (copiedRef.current) {
      copiedRef.current = { ...copiedRef.current, isCut: true, sourceScopeId: workspaceScopeId };
      saveProgramWorkspaceClipboard({
        cells: copiedRef.current.cells,
        isCut: true,
        sourceCellIds: copiedRef.current.sourceCellIds,
        sourceScopeId: workspaceScopeId,
      });
    }
  }, [
    buildFixedContentUpsertRows,
    canMutateCellSelection,
    handleCopy,
    notifySlideEditStructureBlocked,
    onEnsureWorkspaceSnapshot,
    saveMode,
    tableRows,
    workspaceScopeId,
  ]);
  const buildDeletePatchesForSelectedCells = React.useCallback((): Array<{
    annualKey: string;
    fieldId: FieldId;
    value?: string;
    bgColor?: string | null;
  }> => {
    const patches: Array<{ annualKey: string; fieldId: FieldId; value?: string; bgColor?: string | null }> = [];
    selectedCellsRef.current.forEach((cellId) => {
      const parsed = parseCellId(cellId);
      if (!parsed) return;
      patches.push({ annualKey: parsed.annualKey, fieldId: parsed.fieldId, value: '' });
      if (!protectedBgFields.has(parsed.fieldId)) patches.push({ annualKey: parsed.annualKey, fieldId: parsed.fieldId, bgColor: '' });
    });
    return patches;
  }, [parseCellId, protectedBgFields]);

  const handleDeleteSelected = React.useCallback(() => {
    if (!canMutateCellSelection(selectedCellsRef.current)) return;
    if (notifySlideEditStructureBlocked()) return;
    const patches = buildDeletePatchesForSelectedCells();
    if (!patches.length) return;
    const presentationLearningKeys = new Set<string>();
    for (const p of patches) {
      if (p.fieldId !== 'learning_text') continue;
      if (p.value !== undefined && p.value !== '') continue;
      if (!learningTextAnnualKeyHasPresentationUi(p.annualKey)) continue;
      presentationLearningKeys.add(p.annualKey);
    }
    presentationLearningKeys.forEach((ak) => purgeLearningPresentationAndBlankLearningText(ak));
    const remainingPatches = patches.filter(
      (p) => !(p.fieldId === 'learning_text' && presentationLearningKeys.has(p.annualKey))
    );
    if (remainingPatches.length) applyPatches(remainingPatches);
  }, [
    applyPatches,
    buildDeletePatchesForSelectedCells,
    canMutateCellSelection,
    learningTextAnnualKeyHasPresentationUi,
    notifySlideEditStructureBlocked,
    purgeLearningPresentationAndBlankLearningText,
  ]);

  const rowBgTargetFields = React.useMemo(
    () =>
      rowBgColorFields.filter(
        (f) => isSelectableField(f) && !isFieldLocked(f) && !protectedBgFields.has(f)
      ),
    [rowBgColorFields, isFieldLocked, isSelectableField, protectedBgFields]
  );
  const canApplyRowBgColor =
    !mutationLocked && selectedAnnualKeys.length > 0 && rowBgTargetFields.length > 0;

  const applyRowBgColor = React.useCallback(
    (hex: string | null) => {
      if (!canApplyRowBgColor) return;
      commitEditingNow();
      const patches: Array<{ annualKey: string; fieldId: FieldId; bgColor: string }> = [];
      selectedAnnualKeys.forEach((annualKey) => {
        rowBgTargetFields.forEach((fieldId) => {
          patches.push({ annualKey, fieldId, bgColor: hex ?? '' });
        });
      });
      applyPatches(patches);
      if (hex) setLastRowBgColor(hex);
      setRowBgModalOpen(false);
    },
    [applyPatches, canApplyRowBgColor, commitEditingNow, rowBgTargetFields, selectedAnnualKeys]
  );

  const cancelLearningBackspaceDeleteConfirm = React.useCallback(() => {
    pendingBackspaceDeletePatchesRef.current = [];
    setLearningBackspaceDeleteConfirmOpen(false);
  }, []);

  const confirmLearningBackspaceDelete = React.useCallback(() => {
    if (notifySlideEditStructureBlocked()) return;
    const patches = pendingBackspaceDeletePatchesRef.current;
    pendingBackspaceDeletePatchesRef.current = [];
    setLearningBackspaceDeleteConfirmOpen(false);
    const presentationLearningKeys = new Set<string>();
    for (const p of patches) {
      if (p.fieldId !== 'learning_text') continue;
      if (p.value !== undefined && p.value !== '') continue;
      if (!learningTextAnnualKeyHasPresentationUi(p.annualKey)) continue;
      presentationLearningKeys.add(p.annualKey);
    }
    presentationLearningKeys.forEach((ak) => purgeLearningPresentationAndBlankLearningText(ak));
    applyPatches(patches);
  }, [
    applyPatches,
    learningTextAnnualKeyHasPresentationUi,
    notifySlideEditStructureBlocked,
    purgeLearningPresentationAndBlankLearningText,
  ]);

  React.useEffect(() => {
    if (!learningBackspaceDeleteConfirmOpen) return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      ev.preventDefault();
      cancelLearningBackspaceDeleteConfirm();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [learningBackspaceDeleteConfirmOpen, cancelLearningBackspaceDeleteConfirm]);

  const handlePaste = React.useCallback(() => {
    const copied = copiedRef.current;
    const selection = selectedCellsRef.current;
    if (!canMutateCellSelection(selection)) return;
    if (notifySlideEditStructureBlocked()) return;
    if (!copied?.cells?.length || !selection.length) return;
    const targetRect = getSelectionRect(selection);
    if (!targetRect) return;

    /** 내부 복사본은 원본 열(field)과 동일한 셀에만 붙여넣기 — 열이 다르면 토스트 안내 */
    if (copied.cells.length === 1) {
      const src = copied.cells[0];
      for (const cellId of selection) {
        const p = parseCellId(cellId);
        if (!p || p.fieldId !== src.fieldId) {
          showPasteColumnMismatchToast(copied.cells);
          return;
        }
      }
    } else {
      for (const src of copied.cells) {
        const targetCol = targetRect.minCol + src.colOffset;
        const fieldId = editableFieldOrder[targetCol];
        if (!fieldId || fieldId !== src.fieldId) {
          showPasteColumnMismatchToast(copied.cells);
          return;
        }
      }
    }

    setCopiedSourceRegion(null);

    captureLocalUndoBeforeMutation();

    // 붙여넣기 전에 필요한 만큼 행을 확보하고 동시에 데이터 채우기 (엑셀처럼)
    const maxTargetRow = Math.max(...copied.cells.map(c => targetRect.minRow + c.rowOffset));
    const currentRowCount = rows.length; // 원본 rows 사용
    const needsNewRows = saveMode === 'fixed_content' && maxTargetRow >= currentRowCount && onWorkspaceRowsChange;
    
    if (needsNewRows) {
      const newRowsCount = maxTargetRow - currentRowCount + 1;
      const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
      const slotKeys = scheduleSlotAnnualCellKeys ?? [];
      
      // 현재 워크스페이스에 새 행 추가 (먼저 확장)
      const extendedRows = [...workspaceRows];
      const newRowIds: string[] = [];
      for (let i = 0; i < newRowsCount; i++) {
        const tempId = `temp-${Date.now()}-${tempFrameIdSeqRef.current++}`;
        newRowIds.push(tempId);
        const blank = makeEmptyDraftRow();
        // order_index는 워크스페이스 배열 기준으로 계산 (중복 방지)
        const insertAt = workspaceRows.length + i;
        
        const inserted: ProgramFixedContentRowUpsert = {
          id: tempId,
          workspace_id: workspaceId,
          grade_id: rows[rows.length - 1]?.gradeId || progressGradeId,
          academic_year: academicYear,
          term_number: termNumber,
          order_index: insertAt,
          source_annual_cell_key: slotKeys[insertAt] || null,
          event_name: null,
          timetable_text: blank.timetable_text,
          unit_text: blank.unit_text,
          learning_text: blank.learning_text,
          page_text: blank.page_text,
          assistant_page_text: blank.assistant_page_text,
          current_period_text: blank.current_period_text,
          total_period_text: blank.total_period_text,
          material_text: blank.material_text,
          record_cross_curricular_text: blank.record_cross_curricular_text,
          record_memo_text: blank.record_memo_text,
          record_evaluation_text: blank.record_evaluation_text,
          record_class_text: blank.record_class_text,
          record_member_group_id: blank.record_member_group_id ?? null,
          bg_color_by_field: blank.bg_color_by_field ?? {},
        };
        extendedRows.push(inserted);
      }
      
      // order_index를 최종적으로 재할당 (0부터 순차적으로)
      extendedRows.forEach((row, index) => {
        row.order_index = index;
      });
      
      // 확장된 행 기준으로 가상의 tableRows 생성
      const extendedTableRows = extendedRows.map((wr, idx) => {
        const annualKey = wr.source_annual_cell_key || `orphan:${wr.id}`;
        return {
          ...rows[idx], // 기존 행 속성 복사 (없으면 undefined)
          annualCellKey: annualKey,
          timetable_text: wr.timetable_text,
          unit_text: wr.unit_text,
          learning_text: wr.learning_text,
          // ... 필요한 필드들
        } as ProgramProgressRow;
      });
      
      // 이제 확장된 행에 붙여넣기
      const patches: Array<{
        rowIndex: number;
        fieldId: FieldId;
        value: string;
        bgColor?: string | null;
        recordMemberGroupId?: string | null;
      }> = [];
      
      for (const src of copied.cells) {
        const targetRow = targetRect.minRow + src.rowOffset;
        const targetCol = targetRect.minCol + src.colOffset;
        const fieldId = editableFieldOrder[targetCol];
        if (!fieldId) continue;
        
        patches.push({
          rowIndex: targetRow,
          fieldId,
          value: src.value,
          bgColor: protectedBgFields.has(fieldId) ? undefined : (src.bgColor ?? ''),
          recordMemberGroupId:
            fieldId === 'record_class_text'
              ? (() => {
                  const s = src.recordMemberGroupId?.trim();
                  if (s && memberGradeLayout.groups.some((g) => g.id === s)) return s;
                  return firstRecordGroupId(memberGradeLayout) || null;
                })()
              : undefined,
        });
      }
      
      // 패치를 워크스페이스 행에 적용
      const draftPatches: Array<{
        annualKey: string;
        fieldId: FieldId;
        value: string;
        bgColor?: string | null;
        recordMemberGroupId?: string | null;
      }> = [];
      
      for (const patch of patches) {
        const wr = extendedRows[patch.rowIndex];
        if (!wr) continue;
        (wr as any)[patch.fieldId] = patch.value;
        if (patch.recordMemberGroupId !== undefined && patch.fieldId === 'record_class_text') {
          wr.record_member_group_id = patch.recordMemberGroupId;
        }
        if (patch.bgColor !== undefined && !protectedBgFields.has(patch.fieldId)) {
          if (!wr.bg_color_by_field) wr.bg_color_by_field = {};
          wr.bg_color_by_field[patch.fieldId] = patch.bgColor || undefined;
        }
        
        // draftRef.current도 동시에 업데이트 (저장 시 반영되도록)
        const annualKey = wr.source_annual_cell_key || `orphan:${wr.id}`;
        draftPatches.push({
          annualKey,
          fieldId: patch.fieldId,
          value: patch.value,
          bgColor: patch.bgColor,
          recordMemberGroupId: patch.recordMemberGroupId,
        });
      }
      
      if (copied.isCut) {
        copied.sourceCellIds.forEach((cellId) => {
          const parsed = parseCellId(cellId);
          if (!parsed) return;
          draftPatches.push({
            annualKey: parsed.annualKey,
            fieldId: parsed.fieldId,
            value: '',
            bgColor: protectedBgFields.has(parsed.fieldId) ? undefined : '',
          });
        });
      }

      // draftRef.current 업데이트
      if (draftPatches.length > 0) {
        applyPatches(draftPatches, { skipUndoCapture: true });
      }
      
      // 워크스페이스 업데이트 (행 추가 + 데이터 채움)
      clearPendingFixedContentCellEditsAfterReplace();
      onWorkspaceRowsChange(extendedRows, { persistKind: 'replace' });
      
      // 다른 프로그램 표(스코프)에서 잘라낸 경우에만 LS 워크스페이스 동기화
      if (
        copied.isCut &&
        copied.sourceScopeId &&
        copied.sourceScopeId !== workspaceScopeId
      ) {
        applyCrossScopeCutClearToWorkspace(copied.sourceScopeId, copied.sourceCellIds);
      }
      
      return; // 붙여넣기 완료
    }

    type CellPatch = { annualKey: string; fieldId: FieldId; value: string; bgColor?: string | null };
    const byKey = new Map<string, CellPatch>();
    const recordGroupByAnnualKey = new Map<string, string | null>();

    const mergeCell = (
      annualKey: string,
      fieldId: FieldId,
      next: { value?: string; bgColor?: string | null; recordMemberGroupId?: string | null }
    ) => {
      const k = makeCellId(annualKey, fieldId);
      const cur = byKey.get(k) ?? { annualKey, fieldId, value: draftRef.current[annualKey]?.[fieldId] ?? '' };
      if (next.value !== undefined) cur.value = next.value;
      if (!protectedBgFields.has(fieldId) && next.bgColor !== undefined) cur.bgColor = next.bgColor;
      byKey.set(k, cur);
      if (fieldId === 'record_class_text' && next.recordMemberGroupId !== undefined) {
        recordGroupByAnnualKey.set(annualKey, next.recordMemberGroupId ?? null);
      }
    };

    const pendingSlideCopies: Array<{ from: ProgramContentSlideOwnerRef; to: ProgramContentSlideOwnerRef }> =
      [];
    const prepareLearningSlidePaste = (
      src: (typeof copied.cells)[number],
      targetValue: string
    ): string => {
      if (src.fieldId !== 'learning_text' || !src.slideCopyFrom) return targetValue;
      const raw = src.slideCopyFrom;
      const id = raw.id.trim();
      const k = raw.kind;
      if (!isPersistableUuid(id)) return targetValue;
      if (k !== 'scoped_row' && k !== 'fixed_row' && k !== 'program_cell' && k !== 'presentation_ref') return targetValue;
      const fromOwner: ProgramContentSlideOwnerRef = { kind: k, id };
      const targetParsed = parseMemoFieldData(targetValue);
      const nextRefId = copied.isCut && k === 'presentation_ref' ? id : createPresentationRefId();
      const valueWithRef = serializeMemoFieldData(
        targetParsed.text || targetValue,
        targetParsed.note,
        nextRefId,
        targetParsed.worksheetRefId
      );
      const toOwner: ProgramContentSlideOwnerRef = { kind: 'presentation_ref', id: nextRefId };
      if (programContentSlidePresenceKey(fromOwner) === programContentSlidePresenceKey(toOwner)) return valueWithRef;
      pendingSlideCopies.push({ from: fromOwner, to: toOwner });
      if (workspaceId?.trim() && workspaceScopeId.trim()) {
        queueProgramContentSlideCloneIntent({
          workspaceId: workspaceId.trim(),
          scopeId: workspaceScopeId.trim(),
          from: fromOwner,
          to: toOwner,
        });
      }
      return valueWithRef;
    };

    const getExistingForPaste = (annualKey: string, fieldId: FieldId) => {
      const k = makeCellId(annualKey, fieldId);
      return byKey.get(k)?.value ?? draftRef.current[annualKey]?.[fieldId] ?? '';
    };

    const adaptPasteValue = (annualKey: string, fieldId: FieldId, raw: string) =>
      normalizeExternalPasteCell(fieldId, raw, annualKey, rowByAnnualKey[annualKey], getExistingForPaste(annualKey, fieldId));

    if (copied.isCut) {
      if (copied.sourceScopeId && copied.sourceScopeId !== workspaceScopeId) {
        applyCrossScopeCutClearToWorkspace(copied.sourceScopeId, copied.sourceCellIds);
      }
      copied.sourceCellIds.forEach((cellId) => {
        const parsed = parseCellId(cellId);
        if (!parsed) return;
        mergeCell(parsed.annualKey, parsed.fieldId, {
          value: '',
          bgColor: protectedBgFields.has(parsed.fieldId) ? undefined : '',
        });
      });
    }

    if (copied.cells.length === 1) {
      const src = copied.cells[0];
      selection.forEach((cellId) => {
        const parsed = parseCellId(cellId);
        if (!parsed) return;
        const pastedValue = prepareLearningSlidePaste(
          src,
          adaptPasteValue(parsed.annualKey, parsed.fieldId, src.value)
        );
        mergeCell(parsed.annualKey, parsed.fieldId, {
          value: pastedValue,
          bgColor: protectedBgFields.has(parsed.fieldId) ? undefined : (src.bgColor ?? ''),
          recordMemberGroupId:
            parsed.fieldId === 'record_class_text'
              ? (() => {
                  const s = src.recordMemberGroupId?.trim();
                  if (s && memberGradeLayout.groups.some((g) => g.id === s)) return s;
                  return firstRecordGroupId(memberGradeLayout) || null;
                })()
              : undefined,
        });
      });
    } else {
      copied.cells.forEach((src) => {
        const targetRow = targetRect.minRow + src.rowOffset;
        const targetCol = targetRect.minCol + src.colOffset;
        const annualKey = tableRows[targetRow]?.annualCellKey;
        const fieldId = editableFieldOrder[targetCol];
        if (!annualKey || !fieldId) return;
        const pastedValue = prepareLearningSlidePaste(src, adaptPasteValue(annualKey, fieldId, src.value));
        mergeCell(annualKey, fieldId, {
          value: pastedValue,
          bgColor: protectedBgFields.has(fieldId) ? undefined : (src.bgColor ?? ''),
          recordMemberGroupId:
            fieldId === 'record_class_text'
              ? (() => {
                  const s = src.recordMemberGroupId?.trim();
                  if (s && memberGradeLayout.groups.some((g) => g.id === s)) return s;
                  return firstRecordGroupId(memberGradeLayout) || null;
                })()
              : undefined,
        });
      });
    }

    const patches: Array<{
      annualKey: string;
      fieldId: FieldId;
      value?: string;
      bgColor?: string | null;
      recordMemberGroupId?: string | null;
    }> = [];
    byKey.forEach((c) => {
      patches.push({ annualKey: c.annualKey, fieldId: c.fieldId, value: c.value });
      if (!protectedBgFields.has(c.fieldId) && c.bgColor !== undefined) {
        patches.push({ annualKey: c.annualKey, fieldId: c.fieldId, bgColor: c.bgColor });
      }
    });
    recordGroupByAnnualKey.forEach((gid, annualKey) => {
      patches.push({ annualKey, fieldId: 'record_class_text', recordMemberGroupId: gid });
    });

    applyPatches(patches, { skipUndoCapture: true });

    const sid = workspaceId?.trim();
    if (sid && pendingSlideCopies.length) {
      const copies = [...pendingSlideCopies];
      const isCutPaste = copied.isCut;
      const sourceCellIdsForSlide = copied.sourceCellIds;
      void (async () => {
        const pastedToOwners: ProgramContentSlideOwnerRef[] = [];
        for (const { from, to } of copies) {
          try {
            const copiedCount = workspaceScopeId.trim()
              ? await queueProgramContentSlideCopyDraft({
                  workspaceId: sid,
                  scopeId: workspaceScopeId,
                  from,
                  to,
                })
              : 1;
            if (!workspaceScopeId.trim()) {
              await copyProgramContentSlidePagesBetweenOwners(sid, from, to);
            }
            if (copiedCount <= 0) continue;
            pastedToOwners.push(to);
          } catch {
            /* 슬라이드 복제 실패 시 학습내용 텍스트만 반영된 상태 */
          }
        }
        if (pastedToOwners.length) {
          setProgramContentSlidePresenceKeys((prev) => {
            const n = new Set(prev);
            pastedToOwners.forEach((to) => n.add(programContentSlidePresenceKey(to)));
            return n;
          });
        }
        if (isCutPaste && copies.length) {
          const fromKeys = new Set(copies.map((p) => programContentSlidePresenceKey(p.from)));
          const clearedFromKeys: string[] = [];
          for (const cellId of sourceCellIdsForSlide) {
            const p = parseCellId(cellId);
            if (!p || p.fieldId !== 'learning_text') continue;
            const pr = rowByAnnualKey[p.annualKey];
            const owners = pr
              ? resolveProgramContentSlideOwnerCandidates(
                  mode,
                  pr,
                  programCellsByAnnualKey[p.annualKey],
                  draftRef.current[p.annualKey]?.learning_text ?? ''
                )
              : [];
            for (const owner of owners) {
              if (!isPersistableUuid(owner.id)) continue;
              const ownerKey = programContentSlidePresenceKey(owner);
              if (!fromKeys.has(ownerKey)) continue;
              try {
                if (workspaceScopeId.trim()) {
                  queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId: workspaceScopeId, owner });
                } else {
                  await deleteProgramContentSlidePagesForOwner(sid, owner);
                }
                clearedFromKeys.push(ownerKey);
              } catch {
                /* */
              }
            }
          }
          if (clearedFromKeys.length) {
            setProgramContentSlidePresenceKeys((prev) => {
              const n = new Set(prev);
              clearedFromKeys.forEach((k) => n.delete(k));
              return n;
            });
          }
        }
      })();
    }

    if (copied.isCut) {
      copiedRef.current = { cells: copied.cells, isCut: false, sourceCellIds: copied.sourceCellIds, sourceScopeId: copied.sourceScopeId };
      saveProgramWorkspaceClipboard({
        cells: copied.cells,
        isCut: false,
        sourceCellIds: copied.sourceCellIds,
        sourceScopeId: copied.sourceScopeId,
      });
    }
  }, [
    applyPatches,
    canMutateCellSelection,
    editableFieldOrder,
    getSelectionRect,
    makeCellId,
    memberGradeLayout,
    mode,
    normalizeExternalPasteCell,
    parseCellId,
    programCellsByAnnualKey,
    protectedBgFields,
    rowByAnnualKey,
    workspaceId,
    showPasteColumnMismatchToast,
    tableRows,
    workspaceScopeId,
    notifySlideEditStructureBlocked,
  ]);

  /** 엑셀 TSV·다행 텍스트를 선택 영역에 반영. 적용했으면 true */
  const applyExternalGridFromPlainText = React.useCallback(
    (text: string): boolean => {
      if (!shouldHandleExternalGridPaste(text)) return false;
      const grid = parseExternalGridPasteLines(text);
      const hasData = grid.some((row) => row.some((cell) => (cell ?? '').trim()));
      const selection = selectedCellsRef.current;
      if (!canMutateCellSelection(selection)) return false;
      if (notifySlideEditStructureBlocked()) return false;
      if (!hasData || !selection.length) return false;
      const targetRect = getSelectionRect(selection);
      if (!targetRect) return false;

      // 붙여넣을 데이터가 현재 행 수보다 많은 경우 행 자동 추가
      const requiredRows = targetRect.minRow + grid.length;
      const needsNewRows = requiredRows > tableRows.length;

      if (needsNewRows && onWorkspaceRowsChange && saveMode === 'fixed_content') {
        // + 버튼과 동일한 방식으로 행 추가하면서 기존 행과 새 행 모두 데이터 처리
        captureLocalUndoBeforeMutation();
        const workspaceRows = buildAllFixedContentWorkspaceRows(draftRef.current);
        const newRowsCount = requiredRows - tableRows.length;
        const slotKeys = scheduleSlotAnnualCellKeys ?? [];
        
        // 기존 행들의 데이터를 직접 workspaceRows에서 업데이트
        const nextRows = [...workspaceRows];
        
        // 기존 행들 처리 - workspaceRows에서 직접 수정
        for (let r = 0; r < grid.length && (targetRect.minRow + r) < tableRows.length; r += 1) {
          const rowIdx = targetRect.minRow + r;
          const annualKey = tableRows[rowIdx]?.annualCellKey;
          if (!annualKey) continue;
          
          // workspaceRows에서 해당 행 찾기
          const workspaceRowIndex = nextRows.findIndex(row => 
            row.source_annual_cell_key === annualKey || 
            row.id === annualKey ||
            (annualKey.startsWith('extra:') && row.order_index === rowIdx) ||
            (annualKey.startsWith('orphan:') && row.id === annualKey.replace('orphan:', ''))
          );
          
          if (workspaceRowIndex >= 0) {
            const cells = grid[r] ?? [];
            for (let c = 0; c < cells.length; c += 1) {
              const colIdx = targetRect.minCol + c;
              if (colIdx >= editableFieldOrder.length) break;
              const fieldId = editableFieldOrder[colIdx];
              if (!fieldId) continue;
              const raw = cells[c] ?? '';
              const value = normalizeExternalPasteCell(fieldId, raw, annualKey, rowByAnnualKey[annualKey]);
              
              // workspaceRows에서 직접 업데이트
              if (value !== undefined) {
                (nextRows[workspaceRowIndex] as any)[fieldId] = value;
              }
            }
          }
        }
        
        // 부족한 행만큼 추가하면서 데이터도 미리 채워넣기
        for (let i = 0; i < newRowsCount; i++) {
          const tempId = `temp-${Date.now()}-${tempFrameIdSeqRef.current++}`;
          const blank = makeEmptyDraftRow();
          const insertAt = tableRows.length + i;
          const gridRowIndex = tableRows.length + i - targetRect.minRow;
          
          // 새 행에 들어갈 데이터 미리 계산
          const newRowData = { ...blank };
          if (gridRowIndex >= 0 && gridRowIndex < grid.length) {
            const gridRow = grid[gridRowIndex];
            for (let c = 0; c < gridRow.length; c++) {
              const colIdx = targetRect.minCol + c;
              if (colIdx >= editableFieldOrder.length) break;
              const fieldId = editableFieldOrder[colIdx];
              if (!fieldId) continue;
              const raw = gridRow[c] ?? '';
              const value = normalizeExternalPasteCell(fieldId, raw, `temp:${tempId}`, undefined);
              if (value && (newRowData as any)[fieldId] !== undefined) {
                (newRowData as any)[fieldId] = value;
              }
            }
          }
          
          const inserted: ProgramFixedContentRowUpsert = {
            id: tempId,
            workspace_id: workspaceId,
            grade_id: tableRows[tableRows.length - 1]?.gradeId || progressGradeId,
            academic_year: academicYear,
            term_number: termNumber,
            order_index: insertAt,
            source_annual_cell_key: null,
            event_name: null,
            timetable_text: newRowData.timetable_text,
            unit_text: newRowData.unit_text,
            learning_text: newRowData.learning_text,
            page_text: newRowData.page_text,
            assistant_page_text: newRowData.assistant_page_text,
            current_period_text: newRowData.current_period_text,
            total_period_text: newRowData.total_period_text,
            material_text: newRowData.material_text,
            record_cross_curricular_text: newRowData.record_cross_curricular_text,
            record_memo_text: newRowData.record_memo_text,
            record_evaluation_text: newRowData.record_evaluation_text,
            record_class_text: newRowData.record_class_text,
            bg_color_by_field: {},
          };
          
          nextRows.push(inserted);
        }
        
        const normalized = remapFixedWorkspaceRowsToScheduleSlotSources(nextRows, slotKeys);
        clearPendingFixedContentCellEditsAfterReplace();
        onWorkspaceRowsChange(normalized, { persistKind: 'replace' });
        
        setCopiedSourceRegion(null);
        return true;
      }

      const patches: Array<{ annualKey: string; fieldId: FieldId; value?: string; bgColor?: string | null }> = [];
      for (let r = 0; r < grid.length; r += 1) {
        const rowIdx = targetRect.minRow + r;
        if (rowIdx >= tableRows.length) break;
        const annualKey = tableRows[rowIdx]?.annualCellKey;
        if (!annualKey) continue;
        const row = rowByAnnualKey[annualKey];
        const cells = grid[r] ?? [];
        for (let c = 0; c < cells.length; c += 1) {
          const colIdx = targetRect.minCol + c;
          if (colIdx >= editableFieldOrder.length) break;
          const fieldId = editableFieldOrder[colIdx];
          if (!fieldId) continue;
          const raw = cells[c] ?? '';
          const value = normalizeExternalPasteCell(fieldId, raw, annualKey, row);
          patches.push({ annualKey, fieldId, value });
          if (!protectedBgFields.has(fieldId)) {
            patches.push({ annualKey, fieldId, bgColor: '' });
          }
        }
      }
      if (!patches.length) return false;
      applyPatches(patches);
      setCopiedSourceRegion(null);
      return true;
    },
    [
      applyPatches,
      canMutateCellSelection,
      editableFieldOrder,
      getSelectionRect,
      normalizeExternalPasteCell,
      protectedBgFields,
      rowByAnnualKey,
      tableRows,
      notifySlideEditStructureBlocked,
      onWorkspaceRowsChange,
      saveMode,
      captureLocalUndoBeforeMutation,
      buildAllFixedContentWorkspaceRows,
      scheduleSlotAnnualCellKeys,
      makeEmptyDraftRow,
      workspaceId,
      progressGradeId,
      academicYear,
      termNumber,
      remapFixedWorkspaceRowsToScheduleSlotSources,
      clearPendingFixedContentCellEditsAfterReplace,
    ]
  );

  /** 편집 중인 셀에 포커스가 있을 때: 엑셀 다행 붙여넣기는 입력창에 들어가기 전에 가로챈다 */
  React.useEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;
    const onPasteCapture = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain') ?? '';
      if (!shouldHandleExternalGridPaste(text)) return;
      if (!el.contains(e.target as Node)) return;
      e.preventDefault();
      e.stopPropagation();
      commitEditingNow();
      applyExternalGridFromPlainText(text);
    };
    el.addEventListener('paste', onPasteCapture, true);
    return () => el.removeEventListener('paste', onPasteCapture, true);
  }, [applyExternalGridFromPlainText, commitEditingNow]);

  /** 엑셀 TSV와 앱 내부 복사본 구분: 클립보드가 방금 앱에서 쓴 TSV와 같으면 내부 붙여넣기(배경·잘라내기 반영) */
  const handlePasteFromClipboard = React.useCallback(async () => {
    commitEditingNow();
    const selection = selectedCellsRef.current;
    if (!canMutateCellSelection(selection)) return;
    if (!selection.length) return;
    if (notifySlideEditStructureBlocked()) return;

    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch {
      handlePaste();
      return;
    }

    const norm = normalizeClipboardTextForCompare(text);
    const internalSig = lastInternalClipNormalizedRef.current;
    const hasInternalBuffer = !!(copiedRef.current?.cells?.length);
    const isGridShape = shouldHandleExternalGridPaste(text);
    const isCrossScope = copiedRef.current?.sourceScopeId && copiedRef.current.sourceScopeId !== workspaceScopeId;

    /**
     * 엑셀 등 탭/다행 클립보드: 클립보드 문자열이 앱에서 방금 복사한 TSV와 같을 때만 내부 handlePaste(잘라내기 등).
     * internalSig == null 일 때 무조건 내부를 쓰면 엑셀 복사가 무시되던 문제가 있음.
     * 단, 다른 스코프에서 잘라낸 경우(isCrossScope)에는 시그니처 체크를 건너뛰고 내부 붙여넣기를 사용.
     */
    if (isGridShape) {
      if (hasInternalBuffer && (norm === internalSig || isCrossScope)) {
        handlePaste();
        return;
      }
      if (applyExternalGridFromPlainText(text)) {
        copiedRef.current = null;
        saveProgramWorkspaceClipboard(null);
        lastInternalClipNormalizedRef.current = null;
        setCopiedSourceRegion(null);
        return;
      }
    }

    if (hasInternalBuffer) {
      handlePaste();
    }
  }, [
    applyExternalGridFromPlainText,
    canMutateCellSelection,
    commitEditingNow,
    handlePaste,
    notifySlideEditStructureBlocked,
    workspaceScopeId,
  ]);

  const clearCopyCutState = React.useCallback(() => {
    copiedRef.current = null;
    saveProgramWorkspaceClipboard(null);
    lastInternalClipNormalizedRef.current = null;
    setCopiedSourceRegion(null);
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText('').catch(() => undefined);
    }
  }, []);

  React.useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  const saveChanges = React.useCallback(async () => {
    if (readOnly) return;
    // 입력 중인 값도 저장 대상에 포함 (blur 없이 저장 버튼 클릭 케이스)
    if (unitPopup) {
      commitUnitPopupNow(unitPopup);
      setUnitPopup(null);
    }
    if (memoFieldPopup) {
      if (memoFieldPopup.fieldId === 'record_class_text') {
        if (!tryCommitClassRecordModalAndClose(memoFieldPopup)) return;
      } else {
        commitMemoFieldPopupNow(memoFieldPopup);
        setMemoFieldPopup(null);
      }
    }
    if (evalDropdown) setEvalDropdown(null);
    if (crossPopup) {
      if (isUnitPopupDirtyFn(crossPopup, crossPopupOpenSnapshotRef.current)) {
        commitCrossPopupNow(crossPopup);
      }
      setCrossPopup(null);
    }
    if (editingCell) commitEditingNow();
    if (workspaceScopeId.trim()) {
      await flushPendingLearningWorksheetDrafts([workspaceScopeId]);
    }
    const rowsToSave = Object.values(pendingRowsRef.current);
    /** 수업 삭제 등은 워크스페이스만 갱신되고 pending이 비는 경우가 있어, fixed_content는 표 전체 upsert 경로를 유지한다. */
    const canPersistFixedWithoutPending =
      saveMode === 'fixed_content' && !!onUpsertFixedContentRows && tableRows.length > 0;
    if (!rowsToSave.length && !canPersistFixedWithoutPending) {
      setHasUnsavedChanges(false);
      if (saveMode === 'fixed_content') {
        onWorkspaceRowsChange?.(null);
      }
      return;
    }
    if (saveMode === 'fixed_content') {
      if (!onUpsertFixedContentRows) return;
      const allRowsToSave = buildAllFixedContentWorkspaceRows(draftRef.current);
      if (!allRowsToSave.length) {
        setHasUnsavedChanges(false);
        onWorkspaceRowsChange?.(null);
        return;
      }
      const fixedOnlyFieldSet = new Set(fixedModeProgramCellFields as FieldId[]);
      const rowsForFixedSave =
        fixedOnlyFieldSet.size > 0
          ? allRowsToSave.map((row) => {
              const nextRow = { ...row, bg_color_by_field: { ...(row.bg_color_by_field ?? {}) } };
              fixedOnlyFieldSet.forEach((fieldId) => {
                if (
                  fieldId === 'record_class_text' &&
                  !(row.source_annual_cell_key ?? '').trim()
                ) {
                  return;
                }
                (nextRow as Record<string, unknown>)[fieldId] = '';
                if (nextRow.bg_color_by_field) delete nextRow.bg_color_by_field[fieldId];
              });
              return nextRow;
            })
          : allRowsToSave;
      await onUpsertFixedContentRows(rowsForFixedSave);
      if (fixedOnlyFieldSet.size > 0 && onUpsertProgramCellsInFixedMode) {
        const scheduleAnnualKeysFromWorkspace = new Set(
          allRowsToSave
            .map((row) => (row.source_annual_cell_key ?? '').trim())
            .filter((annualKey) => !!annualKey)
        );
        const annualKeysForProgramCells =
          scheduleAnnualKeysFromWorkspace.size > 0
            ? scheduleAnnualKeysFromWorkspace
            : new Set(tableRows.map((row) => row.annualCellKey));
        const programRows = buildProgramUpsertRows(draftRef.current, annualKeysForProgramCells).map((row) => {
          const bgColorByField: ProgramProgressCellUpsert['bg_color_by_field'] = {};
          const nextRow: ProgramProgressCellUpsert = {
            ...row,
            timetable_text: fixedOnlyFieldSet.has('timetable_text') ? row.timetable_text : '',
            unit_text: fixedOnlyFieldSet.has('unit_text') ? row.unit_text : '',
            learning_text: fixedOnlyFieldSet.has('learning_text') ? row.learning_text : '',
            page_text: fixedOnlyFieldSet.has('page_text') ? row.page_text : '',
            assistant_page_text: fixedOnlyFieldSet.has('assistant_page_text') ? row.assistant_page_text : '',
            current_period_text: fixedOnlyFieldSet.has('current_period_text') ? row.current_period_text : '',
            total_period_text: fixedOnlyFieldSet.has('total_period_text') ? row.total_period_text : '',
            material_text: fixedOnlyFieldSet.has('material_text') ? row.material_text : '',
            record_cross_curricular_text: fixedOnlyFieldSet.has('record_cross_curricular_text')
              ? row.record_cross_curricular_text
              : '',
            record_memo_text: fixedOnlyFieldSet.has('record_memo_text') ? row.record_memo_text : '',
            record_evaluation_text: fixedOnlyFieldSet.has('record_evaluation_text') ? row.record_evaluation_text : '',
            record_class_text: fixedOnlyFieldSet.has('record_class_text') ? row.record_class_text : '',
            record_member_group_id: fixedOnlyFieldSet.has('record_class_text')
              ? row.record_member_group_id ?? null
              : null,
            bg_color_by_field: bgColorByField,
          };
          Object.entries(row.bg_color_by_field ?? {}).forEach(([bgKey, bgVal]) => {
            if (!bgVal) return;
            if (fixedOnlyFieldSet.has(bgKey as FieldId)) bgColorByField[bgKey as FieldId] = bgVal;
          });
          return nextRow;
        });
        /**
         * 고정 프로그램의 레거시 program_progress_cells는 수업기록 보존용으로만 사용한다.
         * 비어 있는 신규 행까지 전량 upsert하면(특히 첫 저장) 불필요한 셀이 대량 생성되어
         * 저장 직후 로드/재렌더 깜박임이 커진다.
         *
         * - 신규행(id 없음): 수업기록/그룹 정보가 있을 때만 생성
         * - 기존행(id 있음): 비우기 동작을 반영해야 하므로 빈 값이어도 유지
         */
        const programRowsForFixedMode = programRows.filter((row) => {
          const hasId = Boolean((row.id ?? '').trim());
          if (hasId) return true;
          const hasClassRecord = (row.record_class_text ?? '').trim().length > 0;
          const hasMemberGroup = (row.record_member_group_id ?? '').trim().length > 0;
          return hasClassRecord || hasMemberGroup;
        });
        if (programRowsForFixedMode.length > 0) {
          await onUpsertProgramCellsInFixedMode(programRowsForFixedMode);
        }
      }
    } else {
      if (!onUpsertProgramCells) return;
      await onUpsertProgramCells(rowsToSave as ProgramProgressCellUpsert[]);
    }
    pendingRowsRef.current = {};
    setHasUnsavedChanges(false);
    if (saveMode === 'fixed_content') {
      onWorkspaceRowsChange?.(null);
    }
    /** 저장 직후 선택 유지 시 행 키·인덱스 재배치와 어긋나 첫 행으로 보이는 경우가 있어, 편제별과 같이 해제한다. */
    setSelectedCells([]);
    selectedCellsRef.current = [];
    setCopiedSourceRegion(null);
    pendingStructuredSelectionRef.current = null;
    if (progressGradeId) {
      if (workspaceId?.trim()) {
        clearLearningAttachmentIndicatorWorkspace(workspaceId.trim());
      }
      clearAnnualProgressCrossTopicsForTerm(workspaceId, academicYear, progressGradeId, termNumber);
      emitProgramProgressSaved({
        workspaceId,
        academicYear,
        termNumber,
        gradeId: progressGradeId,
        /** 표 upsert가 이미 RQ/LS 캐시를 맞춤. 구조 변경은 이어지는 워크스페이스 replace가 full로 처리 */
        uiRefreshMode: 'cache',
      });
    }
  }, [
    buildProgramUpsertRows,
    buildAllFixedContentWorkspaceRows,
    buildFixedContentUpsertRows,
    commitCrossPopupNow,
    commitMemoFieldPopupNow,
    commitUnitPopupNow,
    commitEditingNow,
    crossPopup,
    isUnitPopupDirtyFn,
    evalDropdown,
    editingCell,
    memoFieldPopup,
    fixedModeProgramCellFields,
    onUpsertFixedContentRows,
    onUpsertProgramCellsInFixedMode,
    onUpsertProgramCells,
    onWorkspaceRowsChange,
    progressGradeId,
    saveMode,
    workspaceId,
    academicYear,
    termNumber,
    tableRows,
    unitPopup,
    readOnly,
    workspaceScopeId,
    tryCommitClassRecordModalAndClose,
    flushPendingLearningWorksheetDrafts,
  ]);
  const saveChangesRef = React.useRef(saveChanges);
  React.useEffect(() => {
    saveChangesRef.current = saveChanges;
  }, [saveChanges]);
  const boundSaveAction = React.useCallback(async () => {
    await saveChangesRef.current();
  }, []);

  const markDirtyWhileTyping = React.useCallback(
    (annualKey: string, fieldId: FieldId, nextValue: string) => {
      const baseValue = draftRef.current[annualKey]?.[fieldId] ?? '';
      const hasPending = Object.keys(pendingRowsRef.current).length > 0;
      setHasUnsavedChanges(hasPending || nextValue !== baseValue);
    },
    []
  );

  React.useEffect(() => {
    onBindSaveAction?.(boundSaveAction);
    return () => onBindSaveAction?.(null);
  }, [boundSaveAction, onBindSaveAction]);

  React.useEffect(() => {
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (programPendingLeaveTo != null || pendingLeaveTo != null) return;
      if (learningBackspaceDeleteConfirmOpenRef.current) return;
      /** 범교과 예시문 가져오기 모달이 열려 있으면 표 셀 키보드 탐색을 막는다 */
      if (crossCatalogPickerOpenRef.current) return;
      {
        const ae = document.activeElement;
        if (
          ae instanceof Element &&
          ae.closest(
            '.cp-cross-curricular-catalog-picker-backdrop, .cp-cross-curricular-catalog-picker-modal'
          )
        ) {
          return;
        }
      }
      if (isAppBrowseNavigationShortcutBlockedRoute(window.location.pathname)) return;
      // 활동지·평가지 등 캔버스 편집 모달이 열려 있으면 표 단축키(Backspace=선택 셀 삭제 등)를 막음
      if (shouldOuterPageIgnoreKey(e)) return;
      const target = e.target as HTMLElement;
      const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const key = (e.key || '').toLowerCase();
      const isCopyKey = e.code === 'KeyC' || key === 'c';
      const isCutKey = e.code === 'KeyX' || key === 'x';
      const isPasteKey = e.code === 'KeyV' || key === 'v';

      /**
       * 범교과·수업기록 모달은 열리면 입력에 포커스가 가서 Cmd/Ctrl+C·X·V 가 표 클립보드 동작에 닿지 않음.
       * 열 직후 내용을 바꾸지 않은 상태에서는 툴바 버튼과 동일하게 그리드 복사·잘라내기·붙여넣기로 처리.
       */
      if (mod && (isCopyKey || isCutKey || isPasteKey) && !e.isComposing) {
        const hasGridSelection = selectedCellsRef.current.length > 0;
        if (hasGridSelection && crossPopup && crossPopupRef.current?.contains(target)) {
          if (!isUnitPopupDirtyFn(crossPopup, crossPopupOpenSnapshotRef.current)) {
            e.preventDefault();
            e.stopPropagation();
            if (isCopyKey) handleCopy();
            else if (isCutKey) handleCut();
            else void handlePasteFromClipboard();
            return;
          }
        }
        if (
          hasGridSelection &&
          memoFieldPopup?.fieldId === 'record_class_text' &&
          memoFieldPopupRef.current?.contains(target)
        ) {
          if (!isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current)) {
            e.preventDefault();
            e.stopPropagation();
            if (isCopyKey) handleCopy();
            else if (isCutKey) handleCut();
            else void handlePasteFromClipboard();
            return;
          }
        }
      }

      /** 포털 모달·contenteditable 은 표 단축키(Backspace 삭제, Enter 편집 등) 대상 아님 */
      if (memoFieldPopupRef.current?.contains(target)) return;
      if (unitPopupRef.current?.contains(target)) return;
      if (crossPopupRef.current?.contains(target)) return;
      if (evalDropdownRef.current?.contains(target)) return;
      const wrapEl = tableWrapRef.current;
      const inWrapByTarget = !!(target && wrapEl?.contains(target));
      const hasGridSelection = selectedCellsRef.current.length > 0;
      const tableShortcutsEligible =
        inWrapByTarget || (hasGridSelection && isElementVisibleForBrowseScrollFocus(wrapEl));
      if (!tableShortcutsEligible) return;

      const inInput = !!target?.closest('input, textarea, [contenteditable="true"]');
      const isCellEditing = !!editingCellRef.current;
      if (e.key === 'Escape') {
        if (!copiedRef.current && !copiedSourceRegion) return;
        e.preventDefault();
        e.stopPropagation();
        clearCopyCutState();
        return;
      }

      /**
       * Cmd/Ctrl+C·X·V 는 inInput && !isCellEditing 조건보다 먼저 처리해야 함.
       * 다중 선택 시 포커스가 비편집 셀의 input 에 있으면 기존 순서에서는 여기서 return 되어
       * 네이티브 복사만 되고 setCopiedSourceRegion 이 갱신되지 않는다(점선 유지).
       */
      if (mod && (isCopyKey || isCutKey || isPasteKey)) {
        /** 편집 중 붙여넣기: 그리드 단축키로 가로채지 않음(일반 텍스트·paste 이벤트의 엑셀 다행 처리 유지) */
        if (isPasteKey && isCellEditing) {
          return;
        }
        /** 편집 중 텍스트 일부를 드래그 선택했으면 네이티브 복사·잘라내기(선택 부분만) */
        if ((isCopyKey || isCutKey) && isCellEditing && inInput) {
          const field = target as HTMLInputElement | HTMLTextAreaElement;
          const hasTextRange =
            typeof field.selectionStart === 'number' && typeof field.selectionEnd === 'number'
              ? field.selectionStart !== field.selectionEnd
              : !!window.getSelection()?.toString();
          if (hasTextRange) return;
        }
        const canMutateGridSelection = hasGridSelection && canMutateCellSelection(selectedCellsRef.current);
        if ((isCutKey || isPasteKey) && hasGridSelection && !canMutateGridSelection) {
          if (!hasGridSelection && inInput && !isCellEditing) {
            return;
          }
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        /**
         * 편집 중이 아닐 때: 그리드 붙여넣기는 handlePasteFromClipboard.
         * 엑셀 다행은 paste 캡처에서 처리.
         */
        if (!hasGridSelection && inInput && !isCellEditing) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (isCopyKey) handleCopy();
        else if (isCutKey) handleCut();
        else void handlePasteFromClipboard();
        return;
      }

      if (inInput && !isCellEditing) return;

      if (isCellEditing) return;

      const isClearSelected =
        e.key === 'Backspace' ||
        e.code === 'Backspace' ||
        e.key === 'Delete' ||
        e.code === 'Delete';
      if (isClearSelected && selectedCellsRef.current.length > 0) {
        if (notifySlideEditStructureBlocked()) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (!canMutateCellSelection(selectedCellsRef.current)) return;
        commitEditingNow();
        const patches = buildDeletePatchesForSelectedCells();
        if (!patches.length) return;
        const needsPresentationConfirm = patches.some(
          (p) => p.fieldId === 'learning_text' && learningTextAnnualKeyHasPresentationUi(p.annualKey)
        );
        if (needsPresentationConfirm) {
          pendingBackspaceDeletePatchesRef.current = patches;
          setLearningBackspaceDeleteConfirmOpen(true);
        } else {
          applyPatches(patches);
        }
        return;
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const delta = e.key === 'ArrowUp' ? -1 : 1;
        if (tryMoveSingleRowSelectionBandVertical(delta)) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      const one = selectedCellsRef.current[0];
      if (!one || selectedCellsRef.current.length !== 1) return;
      const parsed = parseCellId(one);
      if (!parsed) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (readOnlyRef.current || isFieldLocked(parsed.fieldId)) return;
        if (parsed.fieldId === 'record_evaluation_text') {
          setEvalDropdown({ annualKey: parsed.annualKey });
          setSelectedCells([makeCellId(parsed.annualKey, parsed.fieldId)]);
          return;
        }
        if (parsed.fieldId === 'record_class_text') {
          setEvalDropdown(null);
          openClassRecordMemoModal(parsed.annualKey);
          return;
        }
        if (parsed.fieldId === 'record_cross_curricular_text') {
          setEvalDropdown(null);
          const rowDraft =
            draftRef.current[parsed.annualKey] ?? ensureDraftRow(draftRef.current, parsed.annualKey);
          const cross = parseCrossCurricularData(rowDraft.record_cross_curricular_text ?? '');
          setCrossPopup({
            annualKey: parsed.annualKey,
            text: cross.text ?? '',
            note: cross.note ?? '',
          });
          return;
        }
        beginEdit(parsed.annualKey, parsed.fieldId);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        moveSelectionHorizontal(-1);
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        moveSelectionHorizontal(1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveSelectionVertical(-1);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveSelectionVertical(1);
        return;
      }
    };
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => document.removeEventListener('keydown', onDocKeyDown, true);
  }, [
    beginEdit,
    canMutateCellSelection,
    clearCopyCutState,
    commitEditingNow,
    copiedSourceRegion,
    ensureDraftRow,
    handleCopy,
    handleCut,
    applyPatches,
    buildDeletePatchesForSelectedCells,
    handleDeleteSelected,
    handlePasteFromClipboard,
    learningTextAnnualKeyHasPresentationUi,
    makeCellId,
    moveSelectionHorizontal,
    moveSingleSelectionByFlatStep,
    moveSelectionVertical,
    openClassRecordMemoModal,
    parseCellId,
    parseCrossCurricularData,
    isFieldLocked,
    crossPopup,
    memoFieldPopup,
    isMemoFieldPopupDirtyFn,
    isUnitPopupDirtyFn,
    tryMoveSingleRowSelectionBandVertical,
    programPendingLeaveTo,
    pendingLeaveTo,
    notifySlideEditStructureBlocked,
  ]);

  const displayFontSize = React.useCallback((value: string) => {
    const len = (value ?? '').trim().length;
    if (len >= 22) return '0.58rem';
    if (len >= 16) return '0.64rem';
    if (len >= 10) return '0.69rem';
    return '0.75rem';
  }, []);

  /** 편집 가능한 모든 셀: 기본은 편제 열과 동일 크기, 글자가 많아지면 동일 사다리로 축소 */
  const fontSizeForField = React.useCallback(
    (fieldId: FieldId, value: string) => {
      if (fieldId === 'learning_text') return '0.75rem';
      if (fieldId === 'material_text') return '0.62rem';
      if (fieldId === 'page_text' || fieldId === 'assistant_page_text') {
        const len = (value ?? '').trim().length;
        if (len >= 20) return '0.5rem';
        if (len >= 14) return '0.55rem';
        if (len >= 9) return '0.6rem';
        if (len >= 5) return '0.65rem';
        return '0.7rem';
      }
      return displayFontSize(value);
    },
    [displayFontSize]
  );

  /** 입력 셀 타이포그래피: 기본 크기 동일, 글자 길이에 따라 동일 규칙으로 축소 */
  const inputTypographyStyle = React.useCallback(
    (fieldId: FieldId, text: string): React.CSSProperties => ({
      fontSize: fontSizeForField(fieldId, text),
      lineHeight: '26px',
    }),
    [fontSizeForField]
  );

  const timetableInputTypographyStyle = React.useCallback(
    (text: string): React.CSSProperties => ({
      fontSize: displayFontSize(text),
      lineHeight: '26px',
    }),
    [displayFontSize]
  );
  const registeredTimetableNameSet = React.useMemo(() => {
    const set = new Set<string>();
    registeredTimetableNames.forEach((name) => {
      const trimmed = (name ?? '').trim();
      if (trimmed) set.add(trimmed);
    });
    return set;
  }, [registeredTimetableNames]);
  const registeredTimetableWildcardCharSet = React.useMemo(
    () => new Set(registeredTimetableWildcardChars.map((ch) => (ch ?? '').trim()).filter(Boolean)),
    [registeredTimetableWildcardChars]
  );
  const isTimetableTextUnregistered = React.useCallback(
    (raw: string): boolean =>
      isUnregisteredTimetableText(raw, registeredTimetableNameSet, registeredTimetableWildcardCharSet),
    [registeredTimetableNameSet, registeredTimetableWildcardCharSet]
  );

  const getSelectionEdgeClasses = React.useCallback(
    (annualKey: string, fieldId: FieldId) => {
      const cellId = makeCellId(annualKey, fieldId);
      if (!selectedCellSet.has(cellId)) return '';
      const rowIdx = rowIdxByAnnualKey[annualKey];
      const colIdx = colIdxByField[fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return '';
      const topKey = rowIdx > 0 ? tableRows[rowIdx - 1]?.annualCellKey : undefined;
      const bottomKey = rowIdx < tableRows.length - 1 ? tableRows[rowIdx + 1]?.annualCellKey : undefined;
      const leftField = colIdx > 0 ? editableFieldOrder[colIdx - 1] : undefined;
      const rightField = colIdx < editableFieldOrder.length - 1 ? editableFieldOrder[colIdx + 1] : undefined;
      const classes: string[] = [];
      if (!topKey || !selectedCellSet.has(makeCellId(topKey, fieldId))) classes.push('cp-base-timetable-cell-edge-top');
      if (!bottomKey || !selectedCellSet.has(makeCellId(bottomKey, fieldId)))
        classes.push('cp-base-timetable-cell-edge-bottom');
      if (!leftField || !selectedCellSet.has(makeCellId(annualKey, leftField)))
        classes.push('cp-base-timetable-cell-edge-left');
      if (!rightField || !selectedCellSet.has(makeCellId(annualKey, rightField)))
        classes.push('cp-base-timetable-cell-edge-right');
      return classes.join(' ');
    },
    [colIdxByField, editableFieldOrder, makeCellId, rowIdxByAnnualKey, selectedCellSet, tableRows]
  );
  const getCopiedSourceEdgeClasses = React.useCallback(
    (annualKey: string, fieldId: FieldId) => {
      const cellId = makeCellId(annualKey, fieldId);
      if (!copiedSourceSet.has(cellId)) return '';
      const rowIdx = rowIdxByAnnualKey[annualKey];
      const colIdx = colIdxByField[fieldId];
      if (!Number.isInteger(rowIdx) || !Number.isInteger(colIdx)) return '';
      const topKey = rowIdx > 0 ? tableRows[rowIdx - 1]?.annualCellKey : undefined;
      const bottomKey = rowIdx < tableRows.length - 1 ? tableRows[rowIdx + 1]?.annualCellKey : undefined;
      const leftField = colIdx > 0 ? editableFieldOrder[colIdx - 1] : undefined;
      const rightField = colIdx < editableFieldOrder.length - 1 ? editableFieldOrder[colIdx + 1] : undefined;
      const classes: string[] = [];
      if (!topKey || !copiedSourceSet.has(makeCellId(topKey, fieldId))) classes.push('cp-base-timetable-cell-copied-edge-top');
      if (!bottomKey || !copiedSourceSet.has(makeCellId(bottomKey, fieldId)))
        classes.push('cp-base-timetable-cell-copied-edge-bottom');
      if (!leftField || !copiedSourceSet.has(makeCellId(annualKey, leftField)))
        classes.push('cp-base-timetable-cell-copied-edge-left');
      if (!rightField || !copiedSourceSet.has(makeCellId(annualKey, rightField)))
        classes.push('cp-base-timetable-cell-copied-edge-right');
      return classes.join(' ');
    },
    [colIdxByField, copiedSourceSet, editableFieldOrder, makeCellId, rowIdxByAnnualKey, tableRows]
  );

  const tableModeClass =
    mode === 'fixed_to_unit'
      ? 'cp-annual-progress-table-mode-fixed'
      : mode === 'project_to_template'
        ? 'cp-annual-progress-table-mode-project'
        : '';
  const renderTimetableCell = (row: ProgramProgressRow) => {
    const annualKey = row.annualCellKey;
    const fieldId: FieldId = 'timetable_text';
    const fieldLocked = isFieldLocked(fieldId);
    const selectable = isSelectableField(fieldId);
    const canEdit = selectable && !fieldLocked;
    const draft = draftByAnnualKey[annualKey] ?? makeDraftRow(row);
    const value = draft[fieldId] ?? row.timetableText;
    const timetableTextInvalid = isTimetableTextUnregistered(value);
    const cellId = makeCellId(annualKey, fieldId);
    const isEditing = editingCell?.annualKey === annualKey && editingCell?.fieldId === fieldId;
    const selected = isSelectedCell(annualKey, fieldId);
    const selectedSingle = selected && selectedCells.length === 1;
    const selectedMulti = selected && selectedCells.length > 1;
    /** 시간표열은 프로그램 일정 구간에 포함 — 내용 열 워시와 같이 표시 */
    const rowContentWash =
      isContentRowContextWash(annualKey, fieldId, selected) ||
      (!selected && isScheduleRowContextWash(annualKey));
    const edgeClasses = selected ? getSelectionEdgeClasses(annualKey, fieldId) : '';
    const copiedSource = copiedSourceSet.has(cellId);
    const copiedSourceSingle = copiedSource && copiedSourceSet.size === 1;
    const copiedSourceMulti = copiedSource && copiedSourceSet.size > 1;
    const copiedEdgeClasses = copiedSourceMulti ? getCopiedSourceEdgeClasses(annualKey, fieldId) : '';
    const bgColor = draft.bg_color_by_field?.[fieldId] ?? '';
    const bgRender = programCellBgRenderProps(bgColor);
    const timetableCellStyle = bgRender.style;
    return (
    <td
      ref={(el) => {
        const anchorKey = `${annualKey}\t${fieldId}`;
        if (el) cellAnchorByKeyRef.current.set(anchorKey, el);
        else cellAnchorByKeyRef.current.delete(anchorKey);
      }}
      className={[
        'cp-annual-progress-col-timetable',
        canEdit ? 'cp-program-progress-editable-cell' : '',
        selectedSingle ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single' : '',
        selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
        rowContentWash ? 'cp-program-progress-row-context' : '',
        edgeClasses,
        copiedSource ? 'cp-base-timetable-cell-copied-source' : '',
        copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
        copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
        copiedEdgeClasses,
        bgRender.className,
        fieldLocked ? 'cp-program-progress-cell-locked' : '',
        row.timetableTextShrink ? 'cp-annual-progress-col-timetable-shrink' : '',
        row.timetableColorHex ? 'cp-base-timetable-cell-has-bg' : '',
        mode === 'project_to_template' ? 'cp-annual-project-cell' : '',
        !row.timetableColorHex && row.timetableSlotKind === 'out_of_term' ? 'cp-weekly-cell-out-of-range' : '',
        !row.timetableColorHex && row.timetableSlotKind === 'holiday' ? 'cp-weekly-cell-holiday' : '',
        row.timetableSlotKind === 'activity' ? 'cp-weekly-cell-activity-hatch' : '',
        row.timetableSlotKind === 'non_class' ? 'cp-weekly-cell-unused-kind' : '',
        row.timetableSlotKind === 'non_class' ? 'cp-weekly-cell-no-class' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={timetableCellStyle}
      data-cp-annual-cell={annualKey}
      data-cp-field-id={fieldId}
      onMouseDown={(e) => {
        if (!selectable) return;
        if (isProgramTableTextControlTarget(e.target) || isEditing) {
          clearLongPressTimer();
          return;
        }
        if (tryApplyShiftRectSelect(e, cellId)) return;
        const editingOtherCell =
          editingCellRef.current &&
          (editingCellRef.current.annualKey !== annualKey ||
            editingCellRef.current.fieldId !== fieldId);
        const beginTimetablePointerDownAfterCommit = () => {
          clearLongPressTimer();
          shiftSelectionAnchorCellIdRef.current = cellId;
          setSelectedCells([cellId]);
          programProgressScrollRef.current?.focus({ preventScroll: true });
          pointerDownRef.current = {
            cellId,
            annualKey,
            fieldId,
            didDrag: false,
            openedByLongPress: false,
          };
          attachCellDragMoveListener();
        };
        if (editingOtherCell) {
          e.preventDefault();
          commitEditingNow();
          beginTimetablePointerDownAfterCommit();
          if (readOnly) return;
          return;
        }
        e.preventDefault();
        commitEditingNow();
        beginTimetablePointerDownAfterCommit();
        if (readOnly) return;
      }}
      onMouseEnter={() => {
        const pending = pointerDownRef.current;
        if (!selectable || !pending) return;
        if (pending.cellId === cellId) return;
        clearLongPressTimer();
        pending.didDrag = true;
        selectRect(pending.cellId, cellId);
      }}
      onDoubleClick={(e) => {
        if (readOnly || !canEdit) return;
        e.preventDefault();
        e.stopPropagation();
        clearLongPressTimer();
        commitEditingNow();
        beginEdit(annualKey, fieldId);
      }}
    >
      {rowContentWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null}
      {row.timetableColorHex ? (
        <div
          className={
            'cp-base-timetable-cell-bg' +
            (mode === 'project_to_template' ? ' cp-annual-project-grid-fill' : '') +
            (row.timetableSlotKind === 'non_class'
              ? ' cp-base-timetable-cell-bg--non-class-stripes-over-fill'
              : row.timetableSlotKind === 'activity'
                ? ' cp-base-timetable-cell-bg--activity-hatch-over-fill'
                : row.programKindLabel === '고정'
                  ? ' cp-base-timetable-cell-bg--fixed-program-hatch-over-fill'
                  : '')
          }
          style={{ backgroundColor: row.timetableColorHex }}
          aria-hidden
        />
      ) : null}
      {isEditing ? (
        <input
          ref={editingInputRef}
          type="text"
          className={`cp-program-progress-cell-input${timetableTextInvalid ? ' cp-base-timetable-invalid' : ''}`}
          aria-invalid={timetableTextInvalid}
          title={timetableTextInvalid ? '편제/기준 시수에 등록되지 않은 시간표명입니다.' : undefined}
          style={{
            ...timetableInputTypographyStyle(editingCell?.draft ?? ''),
            ...(editingCell?.displayFontSizePx != null
              ? { fontSize: `${editingCell.displayFontSizePx}px` }
              : {}),
          }}
          value={editingCell?.draft ?? ''}
          onChange={(e) => {
            const nextValue = e.target.value;
            markDirtyWhileTyping(annualKey, fieldId, nextValue);
            setEditingCell((prev) => (prev ? { ...prev, draft: nextValue } : prev));
          }}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={(e) => {
            isComposingRef.current = false;
            const v = e.currentTarget?.value;
            if (v === undefined) return;
            markDirtyWhileTyping(annualKey, fieldId, v);
            setEditingCell((prev) => (prev ? { ...prev, draft: v } : prev));
          }}
          onBlur={(e) => commitEditing((e.currentTarget as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (isComposingRef.current) return;
              e.preventDefault();
              commitEditing((e.currentTarget as HTMLInputElement).value);
              moveSelectionVertical(1, true);
              return;
            }
            if (e.key === 'Tab') {
              if (isComposingRef.current) return;
              e.preventDefault();
              commitEditing((e.currentTarget as HTMLInputElement).value);
              moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1, true);
              return;
            }
            if (e.key === 'Escape') setEditingCell(null);
          }}
          onMouseDown={stopCellPointerDuringTextEdit}
          onPointerDown={stopCellPointerDuringTextEdit}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className={`cp-annual-progress-timetable-text${timetableTextInvalid ? ' cp-base-timetable-invalid' : ''}`}
          style={{ fontSize: displayFontSize(value) }}
          title={timetableTextInvalid ? '편제/기준 시수에 등록되지 않은 시간표명입니다.' : undefined}
        >
          {value}
        </span>
      )}
    </td>
  );
  };

  const renderEditableCell = (
    row: ProgramProgressRow,
    fieldId: FieldId,
    className: string,
    defaultText = '',
    forceRecordFill = false
  ) => {
    const annualKey = row.annualCellKey;
    const fieldLocked = isFieldLocked(fieldId);
    const selectable = isSelectableField(fieldId);
    const editable = selectable && !fieldLocked;
    const draft = draftByAnnualKey[annualKey] ?? makeDraftRow(row);
    const value =
      fieldId === 'learning_text'
        ? canonicalLearningTextByAnnualKey[annualKey] ?? draft[fieldId] ?? defaultText
        : draft[fieldId] ?? defaultText;
    const cellId = makeCellId(annualKey, fieldId);
    const selected = isSelectedCell(annualKey, fieldId);
    const selectedSingle = selected && selectedCells.length === 1;
    const selectedMulti = selected && selectedCells.length > 1;
    const rowContentWash = isContentRowContextWash(annualKey, fieldId, selected);
    const isEditing = editingCell?.annualKey === annualKey && editingCell?.fieldId === fieldId;
    const edgeClasses = selected ? getSelectionEdgeClasses(annualKey, fieldId) : '';
    const copiedSource = copiedSourceSet.has(cellId);
    const copiedSourceSingle = copiedSource && copiedSourceSet.size === 1;
    const copiedSourceMulti = copiedSource && copiedSourceSet.size > 1;
    const copiedEdgeClasses = copiedSourceMulti ? getCopiedSourceEdgeClasses(annualKey, fieldId) : '';
    const bgColor = draft.bg_color_by_field?.[fieldId] ?? '';
    const bgRender = programCellBgRenderProps(bgColor);
    const unitLearningTd =
      fieldId === 'unit_text' || fieldId === 'learning_text' ? 'cp-program-progress-td-unit-learning' : '';
    const isUnitField = fieldId === 'unit_text';
    const unit = isUnitField ? parseUnitData(value) : null;
    const unitCellText = unit?.text ?? '';
    const unitCellNote = unit?.note ?? '';
    const hasUnitNote = !!unitCellNote.trim();
    const isUnitPopupOpen = isUnitField && unitPopup?.annualKey === annualKey;
    const isMemoModalField = memoModalFieldSet.has(fieldId);
    const memoModalData = isMemoModalField ? parseMemoFieldData(value) : null;
    const memoModalText = memoModalData?.text ?? '';
    const memoModalNote = memoModalData?.note ?? '';
    const memoModalBehaviorNote = memoModalData?.behaviorNote ?? '';
    const hasClassRecordBehavior = !!memoModalBehaviorNote.trim();
    const canonicalLearningRaw =
      fieldId === 'learning_text' ? canonicalLearningTextByAnnualKey[annualKey] ?? value : '';
    const canonicalLearningParsed =
      fieldId === 'learning_text' ? parseMemoFieldData(canonicalLearningRaw) : null;
    const hasMemoModalNote =
      fieldId === 'learning_text'
        ? !!canonicalLearningParsed?.note.trim()
        : fieldId === 'record_evaluation_text'
          ? !!memoModalNote.trim() || !!(memoModalData?.evaluationPlanAppliedId ?? '').trim()
          : !!memoModalNote.trim();
    const evaluationMemoHoverNote =
      fieldId === 'record_evaluation_text'
        ? composeEvaluationMemoDisplayNote(memoModalNote, {
            evaluationPlanAppliedId: memoModalData?.evaluationPlanAppliedId,
            evaluationPlanElement: memoModalData?.evaluationPlanElement,
            evaluationPlanMethod: memoModalData?.evaluationPlanMethod,
            evaluationPlanSummary: memoModalData?.evaluationPlanSummary,
          })
        : memoModalNote;
    const isMemoModalPopupOpen =
      isMemoModalField && memoFieldPopup?.annualKey === annualKey && memoFieldPopup?.fieldId === fieldId;
    const isEvaluation = isEvaluationField(fieldId);
    const isClassRecord = isClassRecordField(fieldId);
    const isEvalMemoModalOpen =
      memoFieldPopup?.annualKey === annualKey && memoFieldPopup?.fieldId === 'record_evaluation_text';
    const isEvalDropdownOpen =
      isEvaluation && evalDropdown?.annualKey === annualKey && !isEvalMemoModalOpen;
    const isNotePopupRightAligned =
      fieldId === 'record_memo_text' || fieldId === 'record_evaluation_text' || fieldId === 'record_class_text';
    const isCrossField = fieldId === 'record_cross_curricular_text';
    const cross = isCrossField ? parseCrossCurricularData(value) : null;
    const crossCellText = cross?.text ?? '';
    const crossCellNote = cross?.note ?? '';
    const hasCrossNote = !!crossCellNote.trim();
    const isCrossPopupOpen = isCrossField && crossPopup?.annualKey === annualKey;
    const isLearningField = fieldId === 'learning_text';
    /** 추가 캔버스(2번째 슬라이드 이상)에 DB·의미 있는 초안이 있을 때 */
    const hasSlidePages = isLearningField && learningSlidePageIndicatorAnnualKeys.has(annualKey);
    const hasWorksheet = isLearningField && learningWorksheetIndicatorAnnualKeys.has(annualKey);
    const showLearningIndicators =
      isLearningField && (hasMemoModalNote || hasSlidePages || hasWorksheet);
    const showLearningPresentationOverlay =
      isLearningField && presentationLockedAnnualCellKeys.has(annualKey);
    const baseClassRecordType = isClassRecord
      ? normalizeClassRecordType(memoModalText, memoModalNote)
      : '';
    const classRecordCellLabel = (() => {
      if (!isClassRecord) return '';
      if (baseClassRecordType === 'lesson') return '수업기록';
      if (baseClassRecordType === 'eval_log') return '평가기록';
      if ((memoModalText ?? '').trim()) return classRecordStatusLabel(memoModalText);
      return '';
    })();
    const classRecordVisualKind = (() => {
      if (!isClassRecord) return 'empty';
      if (baseClassRecordType === 'lesson') return 'lesson';
      if (baseClassRecordType === 'eval_log') return 'eval_log';
      if (baseClassRecordType) return 'custom';
      return 'empty';
    })();
    const learningCross = isLearningField ? parseCrossCurricularData(draft.record_cross_curricular_text ?? '') : null;
    const learningCrossTopic = (learningCross?.text ?? '').trim();
    const learningCrossNote = (learningCross?.note ?? '').trim();
    const learningCrossSuffixText = learningCrossTopic
      ? `${`[${learningCrossTopic}]`}${learningCrossNote ? ` ${learningCrossNote}` : ''}`
      : learningCrossNote;

    const anchorMapKey = `${annualKey}\t${fieldId}`;
    /** 셀에 저장된 전체 문자열(JSON·상세 메모 등) 길이로 축소하지 않고, 화면에 보이는 본문 길이만 반영 */
    const textForDisplayFontSize = isUnitField
      ? unitCellText
      : isCrossField
        ? crossCellText
        : isMemoModalField
          ? memoModalText
          : value;
    const isMaterialField = fieldId === 'material_text';
    const isPageMetricField = fieldId === 'page_text' || fieldId === 'assistant_page_text';

    const displayCellBody = isUnitField ? (
      unitCellText
    ) : isCrossField ? (
      crossCellText
    ) : isMemoModalField ? (
      isEvaluation ? (
        <span className={`cp-program-eval-inline cp-program-eval-inline-${(memoModalText ?? '').trim() || '0'}`}>
          {evalStatusLabel(memoModalText)}
        </span>
      ) : isClassRecord ? (
        <span className={`cp-program-class-rec-inline cp-program-class-rec-inline-${classRecordVisualKind}`}>
          {classRecordCellLabel}
        </span>
      ) : isLearningField && learningCrossSuffixText ? (
        <span className="cp-program-learning-combined-text">
          {memoModalText}
          <span className="cp-program-learning-cross-suffix">
            {memoModalText ? ' ' : ''}
            {learningCrossSuffixText}
          </span>
        </span>
      ) : (
        memoModalText
      )
    ) : (
      value
    );

    const learningCellTitleTip = isLearningField
      ? `${(memoModalText ?? '').trim()}${learningCrossSuffixText ? `${(memoModalText ?? '').trim() ? ' ' : ''}${learningCrossSuffixText}` : ''}`.trim() ||
        undefined
      : undefined;

    return (
      <td
        ref={(el) => {
          if (el) cellAnchorByKeyRef.current.set(anchorMapKey, el);
          else cellAnchorByKeyRef.current.delete(anchorMapKey);
        }}
        data-cp-annual-cell={annualKey}
        data-cp-field-id={fieldId}
        className={[
          className,
          unitLearningTd,
          forceRecordFill ? 'cp-annual-progress-col-record-fill' : '',
          editable || showLearningIndicators ? 'cp-program-progress-editable-cell' : '',
          showLearningIndicators ? 'cp-annual-progress-read-note-cell cp-annual-progress-col-learning' : '',
          isUnitField ? 'cp-program-unit-cell' : '',
          isMemoModalField ? 'cp-program-note-cell' : '',
          isEvaluation ? 'cp-program-eval-cell' : '',
          isClassRecord ? 'cp-program-class-rec-cell' : '',
          isCrossField ? 'cp-program-cross-cell' : '',
          selectedSingle ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-single' : '',
          selectedMulti ? 'cp-base-timetable-cell-selected cp-base-timetable-cell-selected-multi' : '',
          rowContentWash ? 'cp-program-progress-row-context' : '',
          edgeClasses,
          copiedSource ? 'cp-base-timetable-cell-copied-source' : '',
          copiedSourceSingle ? 'cp-base-timetable-cell-copied-source-single' : '',
          copiedSourceMulti ? 'cp-base-timetable-cell-copied-source-multi' : '',
          copiedEdgeClasses,
          bgRender.className,
          fieldLocked ? 'cp-program-progress-cell-locked' : '',
          hasUnitNote ? 'cp-program-unit-has-criterion' : '',
          hasMemoModalNote || (isClassRecord && hasClassRecordBehavior) ? 'cp-program-note-has-note' : '',
          hasSlidePages ? 'cp-program-slide-has-pages' : '',
          hasWorksheet ? 'cp-program-worksheet-has-document' : '',
          hasCrossNote ? 'cp-program-cross-has-content' : '',
          isMaterialField ? 'cp-program-td-material' : '',
          isPageMetricField ? 'cp-program-td-page-metric' : '',
          showLearningPresentationOverlay ? 'cp-program-learning-cell-presentation-locked' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={bgRender.style}
        onMouseDown={(e) => {
          if (!selectable) return;
          if (isProgramTableTextControlTarget(e.target) || isEditing) {
            clearLongPressTimer();
            return;
          }
          if (tryApplyShiftRectSelect(e, cellId)) return;
          if (readOnly) {
            const editingOtherCellReadOnly =
              editingCellRef.current &&
              (editingCellRef.current.annualKey !== annualKey ||
                editingCellRef.current.fieldId !== fieldId);
            if (editingOtherCellReadOnly) {
              e.preventDefault();
              commitEditingNow();
              clearLongPressTimer();
              shiftSelectionAnchorCellIdRef.current = cellId;
              setSelectedCells([cellId]);
              programProgressScrollRef.current?.focus({ preventScroll: true });
              pointerDownRef.current = {
                cellId,
                annualKey,
                fieldId,
                didDrag: false,
                openedByLongPress: false,
              };
              attachCellDragMoveListener();
              return;
            }
            e.preventDefault();
            commitEditingNow();
            shiftSelectionAnchorCellIdRef.current = cellId;
            setSelectedCells([cellId]);
            programProgressScrollRef.current?.focus({ preventScroll: true });
            pointerDownRef.current = {
              cellId,
              annualKey,
              fieldId,
              didDrag: false,
              openedByLongPress: false,
            };
            attachCellDragMoveListener();
            return;
          }
          const beginCellPointerDownAfterCommit = () => {
            clearLongPressTimer();
            shiftSelectionAnchorCellIdRef.current = cellId;
            setSelectedCells([cellId]);
            programProgressScrollRef.current?.focus({ preventScroll: true });
            evalLongPressSuppressClickRef.current = false;
            pointerDownRef.current = {
              cellId,
              annualKey,
              fieldId,
              didDrag: false,
              openedByLongPress: false,
            };
            attachCellDragMoveListener();
            if (editable && isLongPressPopupField(fieldId) && !editingCellRef.current) {
              clearLongPressTimer();
              longPressTimerRef.current = window.setTimeout(() => {
                if (editingCellRef.current) return;
                const pending = pointerDownRef.current;
                if (!pending || pending.cellId !== cellId || pending.didDrag) return;
                pending.openedByLongPress = true;
                if (fieldId === 'record_evaluation_text') {
                  evalLongPressSuppressClickRef.current = true;
                  setEvalDropdown(null);
                }
                openLongPressPopup(annualKey, fieldId);
              }, 420);
            }
          };
          if (unitPopup && (!isUnitPopupOpen || !unitPopupRef.current?.contains(e.target as Node))) {
            if (!isUnitPopupDirtyFn(unitPopup, unitPopupOpenSnapshotRef.current)) {
              commitUnitPopupNow(unitPopup);
              setUnitPopup(null);
            }
          }
          /**
           * 포털은 DOM상 body에 있어도 React 이벤트는 이 td로 버블됨.
           * ref 미부착 시 contains가 항상 false처럼 처리되어 닫기 클릭이 바깥 클릭으로 오인 → 잘못 commit 될 수 있음.
           * ref가 있을 때만 팝업 바깥으로 간주해 닫기/커밋 처리.
           */
          if (memoFieldPopup && memoFieldPopupRef.current) {
            const clickedInsideMemoPopup = memoFieldPopupRef.current.contains(e.target as Node);
            if (!isMemoModalPopupOpen || !clickedInsideMemoPopup) {
              const memoDirty =
                isDoubleClickMemoOrClassRecordField(memoFieldPopup.fieldId) &&
                isMemoFieldPopupDirtyFn(memoFieldPopup, memoFieldPopupOpenSnapshotRef.current);
              if (!memoDirty) {
                if (memoFieldPopup.fieldId === 'record_class_text') {
                  setClassRecordModalError(null);
                  setMemoFieldPopup(null);
                } else if (memoFieldPopup.fieldId === 'record_evaluation_text') {
                  setEvalPlanPickerOpen(false);
                  setEvalPlanViewSnap(null);
                  setMemoFieldPopup(null);
                } else {
                  commitMemoFieldPopupNow(memoFieldPopup);
                  setMemoFieldPopup(null);
                }
              }
            }
          }
          if (evalDropdown && !isEvalDropdownOpen) setEvalDropdown(null);
          if (crossPopup && (!isCrossPopupOpen || !crossPopupRef.current?.contains(e.target as Node))) {
            if (isUnitPopupDirtyFn(crossPopup, crossPopupOpenSnapshotRef.current)) {
              commitCrossPopupNow(crossPopup);
            }
            setCrossPopup(null);
          }
          const editingOtherCell =
            editingCellRef.current &&
            (editingCellRef.current.annualKey !== annualKey ||
              editingCellRef.current.fieldId !== fieldId);
          if (editingOtherCell) {
            e.preventDefault();
            commitEditingNow();
            beginCellPointerDownAfterCommit();
            return;
          }
          e.preventDefault();
          commitEditingNow();
          beginCellPointerDownAfterCommit();
        }}
        onMouseEnter={(e) => {
          const hoverTarget = e.target as HTMLElement | null;
          if (
            hoverTarget?.closest?.(
              '.cp-program-note-popup, .cp-program-table-floating-popup, .cp-modal-backdrop, .cp-program-table-hover-memo-tooltip'
            )
          ) {
            return;
          }
          if (
            memoFieldPopup &&
            memoFieldPopup.annualKey === annualKey &&
            memoFieldPopup.fieldId === fieldId
          ) {
            return;
          }
          const openPlanTip = hoverTableTooltipRef.current;
          if (
            openPlanTip?.anchorKey === anchorMapKey &&
            openPlanTip.kind === 'note' &&
            !openPlanTip.isClassRecord &&
            learningPlanGuideHasPlanMarkers(openPlanTip.note)
          ) {
            clearHoverTableTooltipHideTimer();
          }
          const el = e.currentTarget as HTMLTableCellElement;
          const r = el.getBoundingClientRect();
          const classRecHoverNote =
            isClassRecord && hasClassRecordBehavior
              ? [memoModalNote.trim() ? memoModalNote : null, `[행동특성] ${memoModalBehaviorNote}`]
                  .filter(Boolean)
                  .join('\n\n')
              : memoModalNote;
          if (isMemoModalField && (hasMemoModalNote || (isClassRecord && hasClassRecordBehavior))) {
            const layoutKind: ProgramProgressOverlayWidthKind =
              fieldId === 'record_evaluation_text' || fieldId === 'record_class_text'
                ? 'memo_wide'
                : 'memo_narrow';
            const memberTagMembers =
              isClassRecord && fieldId === 'record_class_text'
                ? membersForRecordGroup(memberGradeLayout, draft.record_member_group_id, classMembers)
                : undefined;
            scheduleHoverTableTooltip({
              anchorKey: anchorMapKey,
              kind: 'note',
              note: isClassRecord
                ? classRecHoverNote
                : fieldId === 'record_evaluation_text'
                  ? evaluationMemoHoverNote
                  : memoModalNote,
              isClassRecord: !!isClassRecord,
              memberTagMembers,
              applyBulkMemberTagRules:
                isClassRecord && fieldId === 'record_class_text'
                  ? !!(draft.record_member_group_id ?? '').trim()
                  : undefined,
              layoutKind,
              style:
                fieldId === 'learning_text' && learningPlanGuideHasPlanMarkers(memoModalNote)
                  ? computeLearningPlanHoverTooltipStyle(r)
                  : computeProgramProgressHoverTooltipStyle(r, layoutKind),
            });
          } else if (isMaterialField && (value ?? '').trim()) {
            scheduleHoverTableTooltip({
              anchorKey: anchorMapKey,
              kind: 'material',
              note: (value ?? '').trim(),
              isClassRecord: false,
              layoutKind: 'memo_narrow',
              style: computeProgramProgressHoverTooltipStyle(r, 'memo_narrow'),
            });
          } else if (isUnitField && hasUnitNote) {
            scheduleHoverTableTooltip({
              anchorKey: anchorMapKey,
              kind: 'unit',
              note: unitCellNote,
              isClassRecord: false,
              layoutKind: 'unit',
              style: computeProgramProgressHoverTooltipStyle(r, 'unit'),
            });
          } else if (isCrossField && hasCrossNote) {
            scheduleHoverTableTooltip({
              anchorKey: anchorMapKey,
              kind: 'cross',
              note: crossCellNote,
              isClassRecord: false,
              layoutKind: 'cross',
              style: computeProgramProgressHoverTooltipStyle(r, 'cross'),
            });
          }
          const pending = pointerDownRef.current;
          if (!selectable || !pending) return;
          if (pending.cellId === cellId) return;
          clearLongPressTimer();
          pending.didDrag = true;
          selectRect(pending.cellId, cellId);
        }}
        onMouseLeave={() => {
          clearHoverTableTooltipTimer();
          const cur = hoverTableTooltipRef.current;
          const keepForScroll =
            cur?.anchorKey === anchorMapKey &&
            cur.kind === 'note' &&
            !cur.isClassRecord &&
            learningPlanGuideHasPlanMarkers(cur.note);
          if (keepForScroll) {
            scheduleHoverTableTooltipHide(anchorMapKey);
            return;
          }
          setHoverTableTooltip((prev) => (prev?.anchorKey === anchorMapKey ? null : prev));
        }}
        onDoubleClick={(e) => {
          if (readOnly || !editable) return;
          e.preventDefault();
          e.stopPropagation();
          commitEditingNow();
          clearLongPressTimer();
          shiftSelectionAnchorCellIdRef.current = cellId;
          setSelectedCells([cellId]);
          programProgressScrollRef.current?.focus({ preventScroll: true });
          if (isCrossField) {
            setEvalDropdown(null);
            const rowDraft = draftRef.current[annualKey] ?? ensureDraftRow(draftRef.current, annualKey);
            const cross = parseCrossCurricularData(rowDraft.record_cross_curricular_text ?? '');
            setCrossPopup({
              annualKey,
              text: cross.text ?? '',
              note: cross.note ?? '',
            });
            return;
          }
          if (isEvaluation) {
            setEvalDropdown({ annualKey });
            return;
          }
          if (isClassRecord) {
            setEvalDropdown(null);
            openClassRecordMemoModal(annualKey);
            return;
          }
          beginEdit(annualKey, fieldId);
        }}
      >
        {rowContentWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null}
        {showLearningPresentationOverlay ? (
          <div className="cp-program-learning-presentation-overlay" aria-hidden>
            <span className="cp-program-learning-presentation-overlay-label">프레젠테이션 중</span>
          </div>
        ) : null}
        {isEditing && !isCrossField && !isEvaluation && !isClassRecord ? (
          <input
            ref={editingInputRef}
            type="text"
            className={
              'cp-program-progress-cell-input' +
              (isMaterialField ? ' cp-program-progress-cell-input--material' : '') +
              (isPageMetricField ? ' cp-program-progress-cell-input--page-metric' : '')
            }
            style={{
              ...inputTypographyStyle(fieldId, editingCell?.draft ?? ''),
              ...(editingCell?.displayFontSizePx != null
                ? { fontSize: `${editingCell.displayFontSizePx}px` }
                : {}),
            }}
            title={isLearningField ? (editingCell?.draft ?? '').trim() || undefined : undefined}
            value={editingCell?.draft ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEditingCell((prev) => {
                if (!prev) return prev;
                const rawNext = e.target.value ?? '';
                if (isLearningField && mode === 'fixed_to_unit' && fieldId === 'learning_text') {
                  const fixedLearningPrefix = resolveFixedLearningPrefix(annualKey, row, rawNext);
                  if (fixedLearningPrefix) {
                    const normalized = rawNext.startsWith(fixedLearningPrefix)
                      ? rawNext
                      : `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(rawNext, row?.memo ?? row?.fixedEventName ?? '')}`;
                    markDirtyWhileTyping(annualKey, fieldId, normalized);
                    return { ...prev, draft: normalized };
                  }
                }
                markDirtyWhileTyping(annualKey, fieldId, rawNext);
                return { ...prev, draft: rawNext };
              })
            }
            onCompositionStart={() => {
              isComposingRef.current = true;
            }}
            onCompositionEnd={(e: React.CompositionEvent<HTMLInputElement>) => {
              isComposingRef.current = false;
              const v = e.currentTarget?.value;
              if (v === undefined) return;
              setEditingCell((prev) => {
                if (!prev) return prev;
                if (isLearningField && mode === 'fixed_to_unit' && fieldId === 'learning_text') {
                  const fixedLearningPrefix = resolveFixedLearningPrefix(annualKey, row, v);
                  if (fixedLearningPrefix) {
                    const normalized = v.startsWith(fixedLearningPrefix)
                      ? v
                      : `${fixedLearningPrefix}${stripConflictingFixedLearningDiamondPrefixes(v, row?.memo ?? row?.fixedEventName ?? '')}`;
                    markDirtyWhileTyping(annualKey, fieldId, normalized);
                    return { ...prev, draft: normalized };
                  }
                }
                markDirtyWhileTyping(annualKey, fieldId, v);
                return { ...prev, draft: v };
              });
            }}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              commitEditing((e.currentTarget as HTMLInputElement).value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                if (isComposingRef.current) return;
                e.preventDefault();
                commitEditing((e.currentTarget as HTMLInputElement).value);
                moveSelectionVertical(1, true);
                return;
              }
              if (e.key === 'Tab') {
                if (isComposingRef.current) return;
                e.preventDefault();
                commitEditing((e.currentTarget as HTMLInputElement).value);
                moveSingleSelectionByFlatStep(e.shiftKey ? -1 : 1, true);
                return;
              }
              if (e.key === 'Escape') setEditingCell(null);
            }}
            onMouseDown={stopCellPointerDuringTextEdit}
            onPointerDown={stopCellPointerDuringTextEdit}
            onDoubleClick={(e: React.MouseEvent<HTMLInputElement>) => {
              e.stopPropagation();
            }}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
          />
        ) : isLearningField ? (
          <ProgramProgressLearningTextFitSpan
            measureKey={`${annualKey}|${value}`}
            className={
              'cp-program-progress-cell-text' +
              (isCrossField ? ' cp-program-cross-topic-text' : '') +
              (isMaterialField ? ' cp-program-progress-cell-text--material' : '') +
              (isPageMetricField ? ' cp-program-progress-cell-text--page-metric' : '')
            }
            title={learningCellTitleTip}
          >
            {displayCellBody}
          </ProgramProgressLearningTextFitSpan>
        ) : (
          <span
            className={
              'cp-program-progress-cell-text' +
              (isCrossField ? ' cp-program-cross-topic-text' : '') +
              (isMaterialField ? ' cp-program-progress-cell-text--material' : '') +
              (isPageMetricField ? ' cp-program-progress-cell-text--page-metric' : '')
            }
            title={isPageMetricField ? (value ?? '').trim() || undefined : undefined}
            style={
              unitLearningTd
                ? { fontSize: fontSizeForField(fieldId, textForDisplayFontSize), lineHeight: '26px' }
                : { fontSize: fontSizeForField(fieldId, textForDisplayFontSize) }
            }
          >
            {displayCellBody}
          </span>
        )}
        {isUnitPopupOpen && floatingAnchorStyle
          ? createPortal(
              <div style={floatingAnchorStyle} className="cp-program-table-floating-popup">
                <div
                  ref={unitPopupRef}
                  className={
                    'cp-program-unit-popup cp-program-unit-popup--with-head' +
                    (isUnitPopupExpanded ? ' cp-program-unit-popup-expanded' : '')
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-unit-popup-stack">
                    <div className="cp-program-note-popup-head">
                      <button
                        type="button"
                        className="cp-program-note-popup-expand-btn"
                        aria-label={isUnitPopupExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                        title={isUnitPopupExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={() => setIsUnitPopupExpanded((prev) => !prev)}
                      >
                        <img
                          src={isUnitPopupExpanded ? '/Minimize.svg' : '/Expand.svg'}
                          alt=""
                          aria-hidden
                          className="cp-program-note-popup-expand-icon"
                        />
                      </button>
                    </div>
                    <div className="cp-program-unit-popup-field">
                      <input
                        ref={unitNameInputRef}
                        type="text"
                        value={unitPopup?.text ?? ''}
                        onChange={(e) =>
                          setUnitPopup((prev) => (prev ? { ...prev, text: e.target.value } : prev))
                        }
                        onKeyDown={(e) => {
                          const native = e.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          unitCriterionInputRef.current?.focus({ preventScroll: true });
                        }}
                        placeholder="단원명"
                      />
                    </div>
                    <div className="cp-program-unit-popup-field">
                      <textarea
                        ref={unitCriterionInputRef}
                        rows={4}
                        value={unitPopup?.note ?? ''}
                        onChange={(e) =>
                          setUnitPopup((prev) => (prev ? { ...prev, note: e.target.value } : prev))
                        }
                        placeholder="성취기준"
                      />
                    </div>
                    <div className="cp-program-unit-popup-actions">
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setUnitPopup(null);
                        }}
                      >
                        닫기
                      </button>
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onClick={() => {
                          if (!unitPopup) return;
                          commitUnitPopupNow(unitPopup);
                          setUnitPopup(null);
                        }}
                        onKeyDown={(e) => {
                          const native = e.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          if (!unitPopup) return;
                          commitUnitPopupNow(unitPopup);
                          setUnitPopup(null);
                        }}
                        aria-label="단원 입력 확인"
                        title="단원 입력 확인"
                      >
                        확인
                      </button>
                      <button
                        type="button"
                        className="cp-program-learning-slide-action-btn cp-program-note-popup-trash-btn"
                        disabled={
                          mutationLocked ||
                          (!(unitPopup?.text ?? '').trim() && !(unitPopup?.note ?? '').trim())
                        }
                        title="단원 내용 삭제"
                        aria-label="단원 내용 삭제"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={() => {
                          if (!unitPopup || mutationLocked) return;
                          commitUnitPopupNow({ ...unitPopup, text: '', note: '' });
                          setUnitPopup(null);
                        }}
                      >
                        <img
                          src="/Trash.svg"
                          alt=""
                          width={15}
                          height={15}
                          aria-hidden
                          className="cp-program-learning-slide-trash-icon"
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
        {isMemoModalPopupOpen && floatingAnchorStyle
          ? createPortal(
              <div
                {...(memoFieldPopup?.fieldId === 'record_class_text'
                  ? { className: 'cp-modal-backdrop', role: 'presentation' }
                  : { style: floatingAnchorStyle, className: 'cp-program-table-floating-popup' })}
              >
                <div
                  ref={memoFieldPopupRef}
                  className={
                    'cp-program-note-popup' +
                    (memoFieldPopup?.fieldId === 'record_class_text' ||
                    memoFieldPopup?.fieldId === 'record_evaluation_text' ||
                    memoFieldPopup?.fieldId === 'learning_text' ||
                    memoFieldPopup?.fieldId === 'record_memo_text'
                      ? ' cp-program-note-popup--with-head'
                      : '') +
                    (memoFieldPopup?.fieldId === 'record_class_text'
                      ? ' cp-program-note-popup--dashboard-lesson'
                      : isNotePopupRightAligned
                        ? ' cp-program-note-popup-right'
                        : '') +
                    ((memoFieldPopup?.fieldId === 'record_class_text' && isClassRecordModalExpanded) ||
                    (memoFieldPopup?.fieldId === 'record_evaluation_text' && isEvaluationModalExpanded) ||
                    ((memoFieldPopup?.fieldId === 'learning_text' ||
                      memoFieldPopup?.fieldId === 'record_memo_text') &&
                      isGenericMemoModalExpanded)
                      ? ' cp-program-note-popup-expanded'
                      : '')
                  }
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-note-popup-stack">
                    {isEvaluationField(fieldId) ? (
                      <>
                        <div className="cp-program-note-popup-head">
                          <button
                            type="button"
                            className="cp-program-note-popup-expand-btn"
                            aria-label={isEvaluationModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            title={isEvaluationModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={() => setIsEvaluationModalExpanded((prev) => !prev)}
                          >
                            <img
                              src={isEvaluationModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                              alt=""
                              aria-hidden
                              className="cp-program-note-popup-expand-icon"
                            />
                          </button>
                        </div>
                        <div className="cp-program-eval-choice-tabs">
                          <div className="cp-annual-progress-tabs cp-program-eval-choice-row-full" role="group">
                            {(() => {
                              const textTrim = (memoFieldPopup?.text ?? '').trim();
                              const active = !textTrim;
                              return (
                                <button
                                  type="button"
                                  className={'cp-annual-progress-tab cp-program-eval-tab-clear' + (active ? ' is-active' : '')}
                                  onClick={() => {
                                    if (!memoFieldPopup) return;
                                    setMemoFieldPopup({
                                      ...memoFieldPopup,
                                      text: '',
                                      note: '',
                                      evaluationPlanAppliedId: null,
                                      evaluationPlanSummary: null,
                                      evaluationPlanElement: null,
                                      evaluationPlanMethod: null,
                                    });
                                  }}
                                >
                                  <span className="cp-program-eval-tab-clear-ring" aria-hidden />
                                  <span className="cp-program-eval-tab-clear-label">입력없음</span>
                                </button>
                              );
                            })()}
                          </div>
                          <div
                            className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-eval-choice-row-thirds"
                            role="group"
                          >
                            {evalStatusOptions.map((opt) => {
                              const textTrim = (memoFieldPopup?.text ?? '').trim();
                              const active = textTrim === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  className={
                                    'cp-annual-progress-tab cp-program-eval-tab-' +
                                    opt.tone +
                                    (active ? ' is-active' : '')
                                  }
                                  onClick={() => {
                                    if (!memoFieldPopup) return;
                                    setMemoFieldPopup({ ...memoFieldPopup, text: opt.value });
                                  }}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                          {evaluationPopupContext ? (
                            <div className="cp-program-note-popup-context-wrap">
                              <p className="cp-program-note-popup-context cp-program-note-popup-context-primary">
                                {evaluationPopupContext.line1}
                              </p>
                              <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                                {evaluationPopupContext.line2}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : isClassRecordField(fieldId) ? (
                      <>
                        <div className="cp-program-note-popup-head">
                          <button
                            type="button"
                            className="cp-program-note-popup-expand-btn"
                            aria-label={isClassRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            title={isClassRecordModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={() => setIsClassRecordModalExpanded((prev) => !prev)}
                          >
                            <img
                              src={isClassRecordModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                              alt=""
                              aria-hidden
                              className="cp-program-note-popup-expand-icon"
                            />
                          </button>
                        </div>
                        <div className="cp-program-eval-choice-tabs">
                          <div
                            className="cp-annual-progress-tabs cp-annual-project-term-tabs cp-program-class-rec-choice-row-half"
                            role="group"
                          >
                          {classRecordStatusOptions.map((opt) => {
                            const textTrim = (memoFieldPopup?.text ?? '').trim();
                            const active = textTrim === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                className={
                                  'cp-annual-progress-tab cp-program-class-rec-tab-' +
                                  opt.tone +
                                  (active ? ' is-active' : '')
                                }
                                onClick={() => {
                                  if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return;
                                  setClassRecordModalError(null);
                                  if (opt.value === 'eval_log') {
                                    setMemoFieldPopup((prev) => {
                                      if (!prev || prev.fieldId !== 'record_class_text') return prev;
                                      const rowDraft = draftRef.current[prev.annualKey];
                                      const ne = prev.noteEval ?? '';
                                      let evalMemo = prev.evalMemoMemberGroupId ?? null;
                                      if (ne.trim() && !evalMemo) {
                                        const sg = (rowDraft?.record_member_group_id ?? '').trim();
                                        if (sg && memberGradeLayout.groups.some((g) => g.id === sg)) {
                                          evalMemo = sg;
                                        }
                                      }
                                      return { ...prev, text: 'eval_log', evalMemoMemberGroupId: evalMemo };
                                    });
                                  } else {
                                    setMemoFieldPopup((prev) => {
                                      if (!prev || prev.fieldId !== 'record_class_text') return prev;
                                      return { ...prev, text: 'lesson' };
                                    });
                                  }
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                          </div>
                          {classRecordModalError ? (
                            <p className="cp-program-class-rec-modal-error" role="alert">
                              {classRecordModalError}
                            </p>
                          ) : null}
                          {classRecordPopupContext ? (
                            <div className="cp-program-note-popup-context-wrap">
                              <p className="cp-program-note-popup-context cp-program-note-popup-context-primary">
                                {classRecordPopupContext.line1}
                              </p>
                              <p className="cp-program-note-popup-context cp-program-note-popup-context-secondary">
                                {classRecordPopupContext.line2}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="cp-program-note-popup-head">
                          <button
                            type="button"
                            className="cp-program-note-popup-expand-btn"
                            aria-label={isGenericMemoModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            title={isGenericMemoModalExpanded ? '기본 크기로 축소' : '확장 모달로 보기'}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={() => setIsGenericMemoModalExpanded((prev) => !prev)}
                          >
                            <img
                              src={isGenericMemoModalExpanded ? '/Minimize.svg' : '/Expand.svg'}
                              alt=""
                              aria-hidden
                              className="cp-program-note-popup-expand-icon"
                            />
                          </button>
                        </div>
                        {fieldId === 'learning_text' ? (
                          <div className="cp-program-eval-choice-tabs">
                            <div className="cp-annual-progress-tabs cp-program-eval-choice-row-full" role="group">
                              <button
                                type="button"
                                className={
                                  'cp-annual-progress-tab cp-program-eval-tab-clear' +
                                  (!(memoFieldPopup?.text ?? '').trim() && !(memoFieldPopup?.note ?? '').trim()
                                    ? ' is-active'
                                    : '')
                                }
                                onClick={() => {
                                  if (!memoFieldPopup || memoFieldPopup.fieldId !== 'learning_text') return;
                                  const next = { ...memoFieldPopup, text: '', note: '' };
                                  setMemoFieldPopup(next);
                                  commitMemoFieldPopupNow(next);
                                }}
                              >
                                <span className="cp-program-eval-tab-clear-ring" aria-hidden />
                                <span className="cp-program-eval-tab-clear-label">입력없음</span>
                              </button>
                            </div>
                          </div>
                        ) : null}
                        <div className="cp-program-note-popup-field">
                          <input
                            ref={memoFieldTextInputRef}
                            type="text"
                            value={memoFieldPopup?.text ?? ''}
                            onChange={(e) =>
                              setMemoFieldPopup((prev) => (prev ? { ...prev, text: e.target.value } : prev))
                            }
                            onKeyDown={(e) => {
                              const native = e.nativeEvent as KeyboardEvent;
                              if (native.isComposing) return;
                              if (e.key !== 'Enter') return;
                              e.preventDefault();
                              memoFieldNoteInputRef.current?.focus({ preventScroll: true });
                            }}
                            placeholder={memoFieldPrimaryPlaceholder(fieldId)}
                          />
                        </div>
                      </>
                    )}
                    <div
                      className={
                        'cp-program-note-popup-field' +
                        (fieldId === 'learning_text' ? ' cp-program-learning-memo-field' : '') +
                        (fieldId === 'record_evaluation_text' ? ' cp-program-eval-memo-field' : '') +
                        (fieldId === 'record_class_text' &&
                        memoFieldPopup &&
                        memoFieldPopup.fieldId === 'record_class_text'
                          ? ' cp-program-class-rec-memo-field'
                          : '')
                      }
                    >
                      {/* record_class_text: 수업·평가 메모 분리 저장, 평가는 멤버 칩 줄 단위 */}
                      {fieldId === 'record_class_text' &&
                      memoFieldPopup &&
                      memoFieldPopup.fieldId === 'record_class_text' ? (
                        <>
                          <div
                            className={
                              'cp-class-rec-member-toolbar' +
                              (memoFieldPopup.text === 'eval_log'
                                ? ' cp-class-rec-member-toolbar-eval'
                                : ' cp-class-rec-member-toolbar-lesson')
                            }
                          >
                            <RecordMemberGroupSelect
                              id="cp-program-class-rec-member-group"
                              layout={memberGradeLayout}
                              value={memoFieldPopup.recordMemberGroupId ?? null}
                              requireGroup
                              onChange={(gid) => {
                                setMemoFieldPopup((prev) =>
                                  prev && prev.fieldId === 'record_class_text'
                                    ? { ...prev, recordMemberGroupId: gid }
                                    : prev
                                );
                              }}
                              disabled={readOnly}
                              wrapClassName="cp-class-rec-member-group-select-half"
                            />
                            <button
                              type="button"
                              className="cp-btn-secondary cp-class-rec-member-tag-gen-btn"
                              disabled={
                                readOnly ||
                                !(memoFieldPopup.recordMemberGroupId ?? '').trim() ||
                                !firstRecordGroupId(memberGradeLayout)
                              }
                              onClick={() => {
                                if (!memoFieldPopup || memoFieldPopup.fieldId !== 'record_class_text') return;
                                const gid = (memoFieldPopup.recordMemberGroupId ?? '').trim();
                                if (!gid) {
                                  setClassRecordModalError('멤버 그룹을 선택한 뒤 멤버 태그 생성을 눌러 주세요.');
                                  return;
                                }
                                setClassRecordModalError(null);
                                const isEval = memoFieldPopup.text === 'eval_log';
                                const domNote =
                                  classRecordMemoNoteInputRef.current?.readSerializedNoteFromDom?.();
                                const current =
                                  domNote !== undefined
                                    ? domNote
                                    : isEval
                                      ? (memoFieldPopup.noteEval ?? '')
                                      : (memoFieldPopup.noteLesson ?? '');
                                const members = membersForRecordGroup(memberGradeLayout, gid, classMembers);
                                if (members.length === 0) return;
                                const nextNote = buildDefaultEvalMemberTagsNote(members);
                                if (evalSerializedNoteHasNonTagText(current)) {
                                  setEvalTagGenConfirm({ nextNote, sourceGroupId: gid });
                                  return;
                                }
                                setMemoFieldPopup((prev) => {
                                  if (!prev || prev.fieldId !== 'record_class_text') return prev;
                                  if (prev.text === 'eval_log') {
                                    return { ...prev, noteEval: nextNote, evalMemoMemberGroupId: gid };
                                  }
                                  return { ...prev, noteLesson: nextNote };
                                });
                              }}
                            >
                              멤버 태그 생성
                            </button>
                          </div>
                          <div className="cp-program-eval-plan-link-wrap">
                            {(memoFieldPopup.assignmentMemoItemId ?? '').trim() ? (
                              <div className="cp-program-assignment-memo-link-bar">
                                <span className="cp-program-assignment-memo-link-title">
                                  {(memoFieldPopup.assignmentMemoItemTitle ?? '').trim() || '과제 기록'}
                                  {(memoFieldPopup.assignmentMemoGroupTitle ?? '').trim()
                                    ? ` · ${(memoFieldPopup.assignmentMemoGroupTitle ?? '').trim()}`
                                    : ''}
                                </span>
                                <button
                                  type="button"
                                  className="cp-program-assignment-memo-unlink-btn"
                                  disabled={readOnly}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={() => {
                                    setMemoFieldPopup((prev) => {
                                      if (!prev || prev.fieldId !== 'record_class_text') return prev;
                                      const next = {
                                        ...prev,
                                        assignmentMemoItemId: null as string | null,
                                        assignmentMemoItemTitle: null as string | null,
                                        assignmentMemoGroupId: null as string | null,
                                        assignmentMemoGroupTitle: null as string | null,
                                      };
                                      commitMemoFieldPopupNow(next);
                                      const snap = memoFieldPopupOpenSnapshotRef.current;
                                      if (snap && snap.fieldId === 'record_class_text') {
                                        memoFieldPopupOpenSnapshotRef.current = {
                                          ...snap,
                                          assignmentMemoItemId: null,
                                          assignmentMemoItemTitle: null,
                                          assignmentMemoGroupId: null,
                                          assignmentMemoGroupTitle: null,
                                        };
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  연결 해제
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="cp-program-eval-plan-import-btn"
                                disabled={readOnly || !progressGradeId.trim()}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onClick={() => setAssignmentMemoLinkPickerOpen(true)}
                              >
                                과제 기록 가져오기
                              </button>
                            )}
                          </div>
                          {(memoFieldPopup.assignmentMemoItemId ?? '').trim() ? (
                            <div
                              className="cp-program-assignment-memo-field-display"
                              aria-label={
                                memoFieldPopup.text === 'eval_log' ? '평가기록 메모' : '수업기록 메모'
                              }
                            >
                              {classRecordLinkedAssignmentPreview
                                ? renderNoteWithMemberTags(
                                    classRecordLinkedAssignmentPreview,
                                    classMembers,
                                    { applyBulkMemberTagRules: false }
                                  )
                                : (
                                  <span className="cp-program-assignment-memo-field-empty">
                                    연결된 과제에 표시할 메모가 없습니다.
                                  </span>
                                )}
                            </div>
                          ) : memoFieldPopup.text === 'eval_log' ? (
                            <ClassRecordMemoNoteEditor
                              key={`${memoFieldPopup.annualKey}:eval`}
                              ref={classRecordMemoNoteInputRef}
                              value={memoFieldPopup.noteEval ?? ''}
                              onChange={(note) => {
                                setClassRecordModalError(null);
                                setMemoFieldPopup((prev) => {
                                  if (!prev || prev.fieldId !== 'record_class_text') return prev;
                                  const trimmed = note.trim();
                                  return {
                                    ...prev,
                                    noteEval: note,
                                    evalMemoMemberGroupId:
                                      prev.text === 'eval_log' && !trimmed
                                        ? null
                                        : prev.evalMemoMemberGroupId,
                                  };
                                });
                              }}
                              members={classRecordModalMembers}
                              applyBulkMemberTagRules={!!(memoFieldPopup.evalMemoMemberGroupId ?? '').trim()}
                              sealedLabelByToken={sealedLabelByToken}
                              onHashtagTagFinalized={finalizeClassRecordHashtagForMemberGroup}
                              placeholder={memoFieldNotePlaceholder(fieldId)}
                            />
                          ) : (
                            <ClassRecordMemoNoteEditor
                              key={`${memoFieldPopup.annualKey}:lesson`}
                              ref={classRecordMemoNoteInputRef}
                              value={memoFieldPopup.noteLesson ?? ''}
                              onChange={(note) => {
                                setClassRecordModalError(null);
                                setMemoFieldPopup((prev) =>
                                  prev && prev.fieldId === 'record_class_text'
                                    ? { ...prev, noteLesson: note }
                                    : prev
                                );
                              }}
                              members={classRecordModalMembers}
                              applyBulkMemberTagRules={!!memoFieldPopup.recordMemberGroupId}
                              sealedLabelByToken={sealedLabelByToken}
                              onHashtagTagFinalized={finalizeClassRecordHashtagForMemberGroup}
                              placeholder={memoFieldNotePlaceholder(fieldId)}
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {isEvaluationField(fieldId) ? (
                            <div className="cp-program-eval-plan-link-wrap">
                              {(memoFieldPopup?.evaluationPlanAppliedId ?? '').trim() ? (
                                <div className="cp-program-eval-plan-link-row">
                                  <button
                                    type="button"
                                    className="cp-program-eval-plan-link-summary-btn"
                                    title="연결된 평가계획 보기"
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={() => {
                                      const appliedId = (memoFieldPopup?.evaluationPlanAppliedId ?? '').trim();
                                      if (!appliedId || !workspaceId.trim() || !progressGradeId.trim()) return;
                                      setEvalPlanViewLoading(true);
                                      void resolveWorkspaceEvaluationPlanApplied(workspaceId, progressGradeId)
                                        .then((rows) => {
                                          const hit = rows.find((row) => row.id === appliedId) ?? null;
                                          if (hit) {
                                            setEvalPlanViewSnap(hit);
                                            return;
                                          }
                                          const summary = (memoFieldPopup?.evaluationPlanSummary ?? '').trim();
                                          setEvalPlanViewSnap({
                                            id: appliedId,
                                            workspace_id: workspaceId,
                                            grade_id: progressGradeId,
                                            source_plan_id: null,
                                            source_category_name: '',
                                            source_category_scope: 'user',
                                            source_plan_scope: 'user',
                                            plan: {
                                              id: appliedId,
                                              grade_id: progressGradeId,
                                              category_name: '',
                                              standard_ids: [],
                                              evaluation_element_text: summary || '(삭제되었거나 찾을 수 없는 평가계획)',
                                              evaluation_method_text: '',
                                              unit_text: '',
                                              lesson_evaluation_text: '',
                                              criterion_rubric_attachment: null,
                                              evaluation_sheet_attachment: null,
                                              level_step_count: DEFAULT_LEVEL_STEP_COUNT,
                                              sort_order: 0,
                                              level_a: '',
                                              level_b: '',
                                              level_c: '',
                                              level_d: '',
                                              level_e: '',
                                            },
                                            standards: [],
                                            sort_order: 0,
                                            created_at: '',
                                            updated_at: '',
                                          });
                                        })
                                        .catch(() => {
                                          setEvalPlanViewSnap(null);
                                        })
                                        .finally(() => setEvalPlanViewLoading(false));
                                    }}
                                  >
                                    <span className="cp-program-eval-plan-link-summary-text">
                                      {(memoFieldPopup?.evaluationPlanSummary ?? '').trim() || '평가계획'}
                                    </span>
                                  </button>
                                  <EvaluationPlanDeleteHoldButton
                                    itemId={`eval-plan-link:${memoFieldPopup?.annualKey ?? ''}`}
                                    className="cp-program-eval-plan-link-delete"
                                    disabled={mutationLocked}
                                    onRemove={() => {
                                      if (!memoFieldPopup || mutationLocked) return;
                                      setMemoFieldPopup({
                                        ...memoFieldPopup,
                                        evaluationPlanAppliedId: null,
                                        evaluationPlanSummary: null,
                                        evaluationPlanElement: null,
                                        evaluationPlanMethod: null,
                                      });
                                    }}
                                  />
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className="cp-program-eval-plan-import-btn"
                                  disabled={mutationLocked || !progressGradeId.trim()}
                                  title={
                                    progressGradeId.trim()
                                      ? '클래스에 적용된 평가계획 가져오기'
                                      : '클래스를 먼저 선택해 주세요'
                                  }
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }}
                                  onClick={() => setEvalPlanPickerOpen(true)}
                                >
                                  평가계획 가져오기
                                </button>
                              )}
                              {evalPlanViewLoading ? (
                                <p className="cp-program-eval-plan-link-loading" role="status">
                                  평가계획 불러오는 중…
                                </p>
                              ) : null}
                            </div>
                          ) : null}
                          {(() => {
                            const hasLinkedPlan = !!(memoFieldPopup?.evaluationPlanAppliedId ?? '').trim();
                            const autoLines = hasLinkedPlan
                              ? resolveEvaluationPlanMemoAutoLines({
                                  evaluationPlanElement: memoFieldPopup?.evaluationPlanElement,
                                  evaluationPlanMethod: memoFieldPopup?.evaluationPlanMethod,
                                  evaluationPlanSummary: memoFieldPopup?.evaluationPlanSummary,
                                })
                              : { element: '', method: '' };
                            const hasAutoPrefix = !!(autoLines.element || autoLines.method);
                            const noteTextarea = (
                              <textarea
                                ref={memoFieldNoteInputRef}
                                rows={
                                  fieldId === 'learning_text'
                                    ? 4
                                    : fieldId === 'record_evaluation_text'
                                      ? hasAutoPrefix
                                        ? 4
                                        : 6
                                      : 8
                                }
                                value={memoFieldPopup?.note ?? ''}
                                onChange={(e) =>
                                  setMemoFieldPopup((prev) =>
                                    prev ? { ...prev, note: e.target.value } : prev
                                  )
                                }
                                placeholder={memoFieldNotePlaceholder(fieldId)}
                              />
                            );
                            if (fieldId === 'learning_text' && learningPlanGuideIsStructured(memoFieldPopup?.note ?? '')) {
                              return (
                                <LearningPlanGuideView
                                  guide={memoFieldPopup?.note ?? ''}
                                  editable={!readOnly && !mutationLocked}
                                  ariaLabel="학습 메모"
                                  className="cp-learning-plan-memo-guide"
                                  onChange={(note) =>
                                    setMemoFieldPopup((prev) => (prev ? { ...prev, note } : prev))
                                  }
                                />
                              );
                            }
                            if (fieldId !== 'record_evaluation_text' || !hasAutoPrefix) {
                              return noteTextarea;
                            }
                            return (
                              <div className="cp-program-eval-memo-compose">
                                <div
                                  className="cp-program-eval-memo-plan-auto"
                                  aria-readonly="true"
                                  title="평가계획에서 자동 반영(수정 불가)"
                                >
                                  {autoLines.element ? (
                                    <div className="cp-program-eval-memo-plan-auto-line">{autoLines.element}</div>
                                  ) : null}
                                  {autoLines.method ? (
                                    <div className="cp-program-eval-memo-plan-auto-line">{autoLines.method}</div>
                                  ) : null}
                                </div>
                                {noteTextarea}
                              </div>
                            );
                          })()}
                          {fieldId === 'learning_text' && aiAccess.can('learning_plan') ? (
                            <button
                              type="button"
                              className="cp-ai-feedback-btn cp-learning-plan-open-btn"
                              disabled={readOnly || mutationLocked}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={() => setLearningPlanRequestOpen(true)}
                            >
                              <AiSparkIcon />
                              AI 학습계획 생성
                            </button>
                          ) : null}
                          {learningPlanRequestOpen && fieldId === 'learning_text' && memoFieldPopup?.fieldId === 'learning_text' ? (
                            <LearningPlanRequestModal
                              learningTitle={memoFieldPopup.text}
                              subject={learningPlanRequestContext.subject}
                              grade={learningPlanRequestContext.grade}
                              unit={learningPlanRequestContext.unit}
                              achievement={learningPlanRequestContext.achievement}
                              siblingLearnings={learningPlanRequestContext.siblingLearnings}
                              onClose={() => setLearningPlanRequestOpen(false)}
                              onApply={(memo) => {
                                setMemoFieldPopup((prev) => {
                                  if (!prev || prev.fieldId !== 'learning_text') return prev;
                                  const current = prev.note.trim();
                                  return { ...prev, note: current ? `${current}\n\n${memo}` : memo };
                                });
                              }}
                            />
                          ) : null}
                          {fieldId === 'learning_text'
                            ? (() => {
                                const presentationState = resolveLearningPresentationActionState(annualKey);
                                const canDeletePresentation = presentationState.canDeletePresentation;
                                const canCopyPresentation = !mutationLocked && !!presentationState.copySourceOwner;
                                const canPastePresentation = !mutationLocked && hasLearningPresentationClipboard;
                                const learningSlideTrashDisabled = mutationLocked || !canDeletePresentation;

                                const worksheetState = resolveLearningWorksheetActionState(annualKey);
                                const canDeleteWorksheet = worksheetState.canDelete;
                                const canCopyWorksheet =
                                  !mutationLocked &&
                                  !!(worksheetState.copySourceRefId || worksheetState.copyDraftRowKey);
                                const canPasteWorksheet = !mutationLocked && hasLearningWorksheetClipboard;
                                const learningWorksheetTrashDisabled = mutationLocked || !canDeleteWorksheet;
                                // 복사·삭제 버튼 활성 조건과 같은 '있음' 판정
                                const hasPresentation = canDeletePresentation || !!presentationState.copySourceOwner;
                                const hasWorksheet =
                                  canDeleteWorksheet ||
                                  !!(worksheetState.copySourceRefId || worksheetState.copyDraftRowKey);

                                return (
                                  <>
                                    <div
                                      className="cp-program-learning-slide-modal-actions"
                                      role="group"
                                      aria-label="프레젠테이션 복사·붙여넣기 및 삭제"
                                    >
                                      <button
                                        type="button"
                                        className={`cp-program-note-confirm-btn cp-program-learning-slide-open-btn${passLockClassOf(presentationLocked)}`}
                                        disabled={mutationLocked}
                                        title={
                                          presentationLocked
                                            ? '이용권 구매 후 이용할 수 있습니다'
                                            : '슬라이드 편집 탭 열기 (Shift+U와 동일)'
                                        }
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          openProgramContentSlideForAnnualKey(annualKey);
                                        }}
                                      >
                                        <LearningMaterialStatusMark done={hasPresentation} />
                                        프레젠테이션
                                      </button>
                                      {(memoFieldPopup?.note ?? '').trim() && aiAccess.can('slides') ? (
                                        <button
                                          type="button"
                                          className="cp-ai-feedback-btn cp-learning-material-ai-btn"
                                          disabled={mutationLocked || learningMaterialBusy != null}
                                          aria-busy={learningMaterialBusy === 'presentation' || undefined}
                                          title="학습계획으로 프레젠테이션 초안을 만듭니다. 저장하기 전에는 반영되지 않습니다."
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            startAiLearningMaterial(
                                              'presentation',
                                              annualKey,
                                              memoFieldPopup?.note ?? ''
                                            );
                                          }}
                                        >
                                          <AiSparkIcon />
                                          {learningMaterialBusy === 'presentation' ? '생성 중' : 'AI 생성'}
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="cp-program-learning-slide-action-btn"
                                        disabled={!canCopyPresentation}
                                        title={
                                          canCopyPresentation
                                            ? '현재 학습내용의 프레젠테이션 복사'
                                            : '복사할 프레젠테이션이 없습니다'
                                        }
                                        aria-label="프레젠테이션 복사"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          if (!copyLearningPresentationAtAnnualKey(annualKey)) {
                                            setLearningSlideDeleteToast(
                                              '복사할 프레젠테이션이 없습니다. 먼저 프레젠테이션을 만들어 주세요.'
                                            );
                                          }
                                        }}
                                      >
                                        <img
                                          src="/Copy.svg"
                                          alt=""
                                          width={15}
                                          height={15}
                                          aria-hidden
                                          className="cp-program-learning-slide-action-icon"
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        className="cp-program-learning-slide-action-btn"
                                        disabled={!canPastePresentation}
                                        title={
                                          hasLearningPresentationClipboard
                                            ? '복사한 프레젠테이션 붙여넣기'
                                            : '먼저 복사할 프레젠테이션을 선택해 주세요'
                                        }
                                        aria-label="프레젠테이션 붙여넣기"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          if (!pasteLearningPresentationAtAnnualKey(annualKey)) {
                                            setLearningSlideDeleteToast(
                                              '붙여넣을 프레젠테이션이 없습니다. 먼저 프레젠테이션을 복사해 주세요.'
                                            );
                                          }
                                        }}
                                      >
                                        <img
                                          src="/paste.svg"
                                          alt=""
                                          width={15}
                                          height={15}
                                          aria-hidden
                                          className="cp-program-learning-slide-action-icon"
                                        />
                                      </button>
                                      <DeleteHoldButton
                                        className="cp-program-learning-slide-action-btn cp-program-learning-slide-trash-btn"
                                        holdBarClassName="cp-program-learning-slide-trash-hold-bar"
                                        holdMs={1000}
                                        disabled={learningSlideTrashDisabled}
                                        disabledTitle="삭제할 프레젠테이션이 없습니다"
                                        disabledAriaLabel="삭제할 프레젠테이션이 없습니다"
                                        title="프레젠테이션 삭제 (짧게: 확인 후 삭제 · 길게: 바로 삭제)"
                                        ariaLabel="프레젠테이션 삭제"
                                        confirmTitle="프레젠테이션을 삭제할까요?"
                                        confirmDescription="해당 학습내용의 프레젠테이션이 삭제되며 되돌릴 수 없습니다."
                                        onDelete={() => {
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          setLearningSlideDeleteToast(
                                            deleteSlidesForLearningAtAnnualKey(annualKey) ? '해당 학습내용의 프레젠테이션이 삭제되었습니다.' : '프레젠테이션을 삭제하지 못했습니다.'
                                          );
                                        }}
                                      >
                                        <img src="/Trash.svg" alt="" width={15} height={15} aria-hidden className="cp-program-learning-slide-trash-icon" />
                                      </DeleteHoldButton>
                                    </div>
                                    <div
                                      className="cp-program-learning-slide-modal-actions"
                                      role="group"
                                      aria-label="활동지 복사·붙여넣기 및 삭제"
                                    >
                                      <button
                                        type="button"
                                        className={`cp-program-note-confirm-btn cp-program-learning-slide-open-btn${passLockClassOf(worksheetLocked)}`}
                                        disabled={mutationLocked || syncMirrorView}
                                        title={
                                          worksheetLocked
                                            ? '이용권 구매 후 이용할 수 있습니다'
                                            : '활동지 편집 모달 열기 (Shift+I와 동일)'
                                        }
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          openWorksheetEditorForAnnualKey(annualKey);
                                        }}
                                      >
                                        <LearningMaterialStatusMark done={hasWorksheet} />
                                        활동지
                                      </button>
                                      {(memoFieldPopup?.note ?? '').trim() && !syncMirrorView && aiAccess.can('worksheet') ? (
                                        <button
                                          type="button"
                                          className="cp-ai-feedback-btn cp-learning-material-ai-btn"
                                          disabled={mutationLocked || learningMaterialBusy != null}
                                          aria-busy={learningMaterialBusy === 'worksheet' || undefined}
                                          title="학습계획으로 활동지 초안을 만듭니다. 저장하기 전에는 반영되지 않습니다."
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            startAiLearningMaterial('worksheet', annualKey, memoFieldPopup?.note ?? '');
                                          }}
                                        >
                                          <AiSparkIcon />
                                          {learningMaterialBusy === 'worksheet' ? '생성 중' : 'AI 생성'}
                                        </button>
                                      ) : null}
                                      <button
                                        type="button"
                                        className="cp-program-learning-slide-action-btn"
                                        disabled={!canCopyWorksheet}
                                        title={
                                          canCopyWorksheet
                                            ? '현재 학습내용의 활동지 복사'
                                            : '복사할 활동지가 없습니다'
                                        }
                                        aria-label="활동지 복사"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          if (!copyLearningWorksheetAtAnnualKey(annualKey)) {
                                            setLearningSlideDeleteToast(
                                              '복사할 활동지가 없습니다. 먼저 활동지를 만들어 주세요.'
                                            );
                                          }
                                        }}
                                      >
                                        <img
                                          src="/Copy.svg"
                                          alt=""
                                          width={15}
                                          height={15}
                                          aria-hidden
                                          className="cp-program-learning-slide-action-icon"
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        className="cp-program-learning-slide-action-btn"
                                        disabled={!canPasteWorksheet}
                                        title={
                                          hasLearningWorksheetClipboard
                                            ? '복사한 활동지 붙여넣기'
                                            : '먼저 복사할 활동지를 선택해 주세요'
                                        }
                                        aria-label="활동지 붙여넣기"
                                        onMouseDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          if (!pasteLearningWorksheetAtAnnualKey(annualKey)) {
                                            setLearningSlideDeleteToast(
                                              '붙여넣을 활동지가 없습니다. 먼저 활동지를 복사해 주세요.'
                                            );
                                          }
                                        }}
                                      >
                                        <img
                                          src="/paste.svg"
                                          alt=""
                                          width={15}
                                          height={15}
                                          aria-hidden
                                          className="cp-program-learning-slide-action-icon"
                                        />
                                      </button>
                                      <DeleteHoldButton
                                        className="cp-program-learning-slide-action-btn cp-program-learning-slide-trash-btn"
                                        holdBarClassName="cp-program-learning-slide-trash-hold-bar"
                                        holdMs={1000}
                                        disabled={learningWorksheetTrashDisabled}
                                        disabledTitle="삭제할 활동지이 없습니다"
                                        disabledAriaLabel="삭제할 활동지이 없습니다"
                                        title="활동지 삭제 (짧게: 확인 후 삭제 · 길게: 바로 삭제)"
                                        ariaLabel="활동지 삭제"
                                        confirmTitle="활동지를 삭제할까요?"
                                        confirmDescription="해당 학습내용의 활동지이 삭제되며 되돌릴 수 없습니다."
                                        onDelete={() => {
                                          flushLearningMemoPopupBeforeAttachmentAction(annualKey);
                                          setLearningSlideDeleteToast(
                                            deleteWorksheetForLearningAtAnnualKey(annualKey) ? '해당 학습내용의 활동지가 삭제되었습니다.' : '활동지를 삭제하지 못했습니다.'
                                          );
                                        }}
                                      >
                                        <img src="/Trash.svg" alt="" width={15} height={15} aria-hidden className="cp-program-learning-slide-trash-icon" />
                                      </DeleteHoldButton>
                                    </div>
                                  </>
                                );
                              })()
                            : null}
                        </>
                      )}
                    </div>
                    {memoFieldPopup?.fieldId === 'record_class_text' ? (
                      <AiRecordFeedbackPanel
                        ref={classRecordAiRef}
                        workspaceId={workspaceId}
                        recordKey={memoFieldPopup.annualKey}
                        kind={memoFieldPopup.text === 'eval_log' ? 'eval_log' : 'lesson'}
                        onDirtyChange={setClassRecordAiDirty}
                        embeddedText={
                          parseMemoFieldStorageDeep(draftByAnnualKey[memoFieldPopup.annualKey]?.record_class_text ?? '')
                            .aiFeedback?.[memoFieldPopup.text === 'eval_log' ? 'eval_log' : 'lesson'] ?? null
                        }
                        note={
                          memoFieldPopup.text === 'eval_log'
                            ? memoFieldPopup.noteEval ?? ''
                            : memoFieldPopup.noteLesson ?? ''
                        }
                        contextLines={[
                          classRecordPopupContext?.line1 && `편제: ${classRecordPopupContext.line1}`,
                          classRecordPopupContext?.line2 && `학습내용: ${classRecordPopupContext.line2}`,
                          ...(() => {
                            const raw = draftByAnnualKey[memoFieldPopup.annualKey]?.unit_text ?? '';
                            const parsed = parseMemoFieldData(raw);
                            const unit = (parsed.text || raw || '').trim();
                            return [unit && `단원: ${unit}`, parsed.note && `성취기준: ${parsed.note}`];
                          })(),
                        ]}
                        members={classRecordModalMembers}
                        allMembers={classMembers}
                      />
                    ) : null}
                    <div
                      className={
                        'cp-program-note-popup-actions' +
                        (memoFieldPopup?.fieldId === 'record_class_text'
                          ? ' cp-annual-behavior-note-popup-actions'
                          : '')
                      }
                    >
                      <button
                        type="button"
                        className="cp-program-note-confirm-btn"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!memoFieldPopup) return;
                          if (memoFieldPopup.fieldId === 'record_class_text') {
                            if (
                              isMemoFieldPopupDirtyFn(
                                memoFieldPopup,
                                memoFieldPopupOpenSnapshotRef.current
                              )
                            ) {
                              void classRecordAiRef.current?.persist();
                            tryCommitClassRecordModalAndClose(memoFieldPopup);
                              return;
                            }
                            setClassRecordModalError(null);
                            setMemoFieldPopup(null);
                            return;
                          }
                          /** 평가 메모: 닫기 = 변경 취소(셀 미반영) */
                          if (memoFieldPopup.fieldId === 'record_evaluation_text') {
                            setEvalPlanPickerOpen(false);
                            setEvalPlanViewSnap(null);
                            setMemoFieldPopup(null);
                            return;
                          }
                          commitMemoFieldPopupNow(memoFieldPopup);
                          setMemoFieldPopup(null);
                        }}
                      >
                        닫기
                      </button>
                      <button
                        type="button"
                        className={
                          'cp-program-note-confirm-btn' + (memoFieldPopup?.fieldId === 'record_class_text' ? ' cp-record-save-btn' : '') +
                          (memoFieldPopup?.fieldId === 'record_class_text' ? ' cp-program-note-confirm-btn--primary' : '')
                        }
                        disabled={memoFieldPopup?.fieldId === 'record_class_text' && !classRecordModalDirty}
                        onClick={() => {
                          if (!memoFieldPopup) return;
                          if (memoFieldPopup.fieldId === 'record_class_text') {
                            void classRecordAiRef.current?.persist();
                            tryCommitClassRecordModalAndClose(memoFieldPopup);
                            return;
                          }
                          commitMemoFieldPopupNow(memoFieldPopup);
                          setMemoFieldPopup(null);
                        }}
                        onKeyDown={(e) => {
                          const native = e.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          if (!memoFieldPopup) return;
                          if (memoFieldPopup.fieldId === 'record_class_text') {
                            void classRecordAiRef.current?.persist();
                            tryCommitClassRecordModalAndClose(memoFieldPopup);
                            return;
                          }
                          commitMemoFieldPopupNow(memoFieldPopup);
                          setMemoFieldPopup(null);
                        }}
                      >
                        {memoFieldPopup?.fieldId === 'record_class_text'
                          ? classRecordModalDirty
                            ? '저장'
                            : '변경사항 없음'
                          : '확인'}
                      </button>
                      {memoFieldPopup?.fieldId === 'record_class_text' ? (
                        <DeleteHoldButton
                          disabled={readOnly || isFieldLocked('record_class_text') || !classRecordModalCanDelete}
                          disabledTitle="삭제할 수업·평가 기록이 없습니다"
                          disabledAriaLabel="삭제할 수업·평가 기록이 없습니다"
                          ariaLabel="수업·평가 기록 삭제 (길게 누르기)"
                          onDelete={() => {
                            if (!memoFieldPopup) return;
                            void classRecordAiRef.current?.remove(['lesson', 'eval_log']);
                            deleteClassRecordFromModal(memoFieldPopup);
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="cp-program-learning-slide-action-btn cp-program-note-popup-trash-btn"
                          disabled={mutationLocked || !memoFieldPopupCanDelete}
                          title="내용 삭제"
                          aria-label="내용 삭제"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={() => clearMemoFieldPopupContent()}
                        >
                          <img
                            src="/Trash.svg"
                            alt=""
                            width={15}
                            height={15}
                            aria-hidden
                            className="cp-program-learning-slide-trash-icon"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
        {isEvalDropdownOpen && floatingAnchorStyle
          ? createPortal(
              <div style={floatingAnchorStyle} className="cp-program-table-floating-popup">
                <div
                  ref={evalDropdownRef}
                  className="cp-program-eval-dropdown"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {evalDropdownRows.map((opt) => (
                    <button
                      key={opt.value === '' ? '__clear' : opt.value}
                      type="button"
                      className={'cp-program-eval-dropdown-item cp-program-eval-dropdown-item-' + opt.tone}
                      onClick={() => {
                        const current = parseMemoFieldData(
                          draftRef.current[annualKey]?.record_evaluation_text ?? ''
                        );
                        commitMemoFieldPopupNow({
                          annualKey,
                          fieldId: 'record_evaluation_text',
                          text: opt.value,
                          note: opt.value === '' ? '' : memoModalNote,
                          evaluationPlanAppliedId: opt.value === '' ? null : current.evaluationPlanAppliedId ?? null,
                          evaluationPlanSummary: opt.value === '' ? null : current.evaluationPlanSummary ?? null,
                          evaluationPlanElement: opt.value === '' ? null : current.evaluationPlanElement ?? null,
                          evaluationPlanMethod: opt.value === '' ? null : current.evaluationPlanMethod ?? null,
                        });
                        setEvalDropdown(null);
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>,
              document.body
            )
          : null}
        {isCrossPopupOpen && floatingAnchorStyle
          ? createPortal(
              <div style={floatingAnchorStyle} className="cp-program-table-floating-popup">
                <div
                  ref={crossPopupRef}
                  className="cp-program-cross-popup"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-cross-popup-row cp-program-cross-popup-row-horizontal">
                    <div className="cp-program-cross-popup-field">
                      <input
                        ref={crossTopicInputRef}
                        type="text"
                        value={crossPopup?.text ?? ''}
                        onChange={(e) =>
                          setCrossPopup((prev) => (prev ? { ...prev, text: e.target.value } : prev))
                        }
                        onKeyDown={(e) => {
                          const native = e.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          crossContentInputRef.current?.focus({ preventScroll: true });
                        }}
                        placeholder="주제어"
                        disabled={mutationLocked}
                      />
                    </div>
                    <div className="cp-program-cross-popup-field">
                      <input
                        ref={crossContentInputRef}
                        type="text"
                        value={crossPopup?.note ?? ''}
                        onChange={(e) =>
                          setCrossPopup((prev) => (prev ? { ...prev, note: e.target.value } : prev))
                        }
                        onKeyDown={(e) => {
                          const native = e.nativeEvent as KeyboardEvent;
                          if (native.isComposing) return;
                          if (e.key !== 'Enter' && e.key !== 'Tab') return;
                          e.preventDefault();
                          if (!crossPopup) return;
                          commitCrossPopupNow(crossPopup);
                          setCrossPopup(null);
                        }}
                        placeholder="내용"
                        disabled={mutationLocked}
                      />
                    </div>
                  </div>
                  <div className="cp-program-cross-popup-action-row">
                    <button
                      type="button"
                      className="cp-program-eval-plan-import-btn cp-program-cross-catalog-import-btn"
                      disabled={mutationLocked}
                      title="관리자 범교과 관리에 등록된 예시문 가져오기"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => setCrossCatalogPickerOpen(true)}
                    >
                      범교과 예시문 가져오기
                    </button>
                    <button
                      type="button"
                      className="cp-program-eval-plan-import-btn cp-program-cross-count-float-open-btn"
                      title="범교과 카운팅 창 열기"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => setCrossTopicCountFloatOpen(true)}
                    >
                      카운팅 창 열기
                    </button>
                    <button
                      type="button"
                      className="cp-program-learning-slide-action-btn cp-program-cross-popup-trash-btn"
                      disabled={
                        mutationLocked ||
                        (!(crossPopup?.text ?? '').trim() && !(crossPopup?.note ?? '').trim())
                      }
                      title="범교과 내용 삭제"
                      aria-label="범교과 내용 삭제"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => {
                        if (!crossPopup || mutationLocked) return;
                        commitCrossPopupNow({ ...crossPopup, text: '', note: '' });
                        setCrossPopup(null);
                      }}
                    >
                      <img
                        src="/Trash.svg"
                        alt=""
                        width={15}
                        height={15}
                        aria-hidden
                        className="cp-program-learning-slide-trash-icon"
                      />
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )
          : null}
      </td>
    );
  };

  const topGroups =
    mode === 'fixed_to_unit'
      ? {
          baseColSpan: 10, // 단원까지 진도표 일정
          learningColSpan: 6,
          recordColSpan: 4,
        }
      : mode === 'project_to_template'
        ? {
            // 구분(8열)까지 프로그램 일정 — 시간표(9열)부터 프로그램 내용(편집)
            baseColSpan: 8,
            learningColSpan: 8,
            recordColSpan: 4,
          }
        : {
            // default: 구분·시간표까지 프로그램 일정(연간진도표와 동일)
            baseColSpan: 9,
            learningColSpan: 7,
            recordColSpan: 4,
          };

  return (
    <div
      className={'cp-program-progress-table-wrap' + (readOnly ? ' cp-program-progress-table-wrap--readonly' : '')}
      ref={tableWrapRef}
    >
      <div className="cp-program-progress-toolbar">
        <div className="cp-program-progress-toolbar-leading">
          <div className="cp-program-progress-toolbar-leading-set" role="group" aria-label="탐색·오늘·내일">
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav"
            data-tutorial-browse="annual-progress"
            onClick={() => {
              if (!annualProgressBrowseTarget) return;
              const toPath = annualProgressBrowseTarget.split('?')[0] ?? '';
              if (isProgramProgressFamilyLeaveDirty(programProgressDirty) && !isProgramProgressFamilyRoute(toPath)) {
                setProgramPendingLeaveTo(annualProgressBrowseTarget);
                return;
              }
              markAnnualEntryFocusClick();
              navigate(annualProgressBrowseTarget);
            }}
            disabled={!annualProgressBrowseTarget}
            title="선택된 셀 행의 연간진도표로 이동 (Shift+F)"
          >
            <span className="cp-btn-program-browse-nav-icon" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" focusable="false">
                <path
                  d="M7 2.2 2.4 6.8l1.6 1.6 1.9-1.9V12h2.2V6.5L10 8.4l1.6-1.6L7 2.2z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span className="cp-btn-label-desktop">선택셀 연간진도표 탐색</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              탐색
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick"
            onClick={() => handleTodayLessonInTable('cycle')}
            disabled={!tableRows.length}
            title="오늘 학습내용: 첫 클릭은 현재 시각 교시, 이어서 누르면 다음 교시 순환(마지막 다음은 첫 교시). Shift+T는 버튼과 동일. 표에 오늘 일정이 없으면 선택하지 않음"
          >
            <span className="cp-btn-label-desktop">오늘 학습내용 보기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              오늘 학습내용
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick"
            onClick={() => handleTomorrowLessonInTable()}
            disabled={!tableRows.length}
            title="내일 학습내용 보기 (Shift+Y). 내일 일정이 없으면 가장 가까운 미래 날짜를 선택"
          >
            <span className="cp-btn-label-desktop">내일 학습내용 보기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              내일 학습내용
            </span>
          </button>
          <button
            type="button"
            className={`cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick${passLockClassOf(presentationLocked)}`}
            data-tutorial-slide="open-presentation"
            onClick={() => handleOpenPresentationForSelectedRow()}
            disabled={!tableRows.length}
            title={
              presentationLocked
                ? '이용권 구매 후 이용할 수 있습니다'
                : '선택한 행의 슬라이드·프레젠테이션을 새 탭에서 엽니다 (Shift+U와 동일). 한 행만 선택해야 합니다'
            }
          >
            <span className="cp-btn-label-desktop">프레젠테이션 열기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              프레젠테이션
            </span>
          </button>
          <button
            type="button"
            className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(presentationLocked)}`}
            onClick={() => {
              if (requireFeature('presentation')) return;
              setProgramPresentationListOpen(true);
            }}
            disabled={!tableRows.length}
            title={
              presentationLocked
                ? '이용권 구매 후 이용할 수 있습니다'
                : '이 프로그램 표에 기록된 프레젠테이션 목록'
            }
          >
            <span className="cp-btn-label-desktop">프레젠테이션 목록</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              프레젠테이션 목록
            </span>
          </button>
          <button
            type="button"
            className={`cp-btn-copy-paste cp-btn-program-browse-nav cp-btn-lesson-quick${passLockClassOf(worksheetLocked)}`}
            data-tutorial-ws="open-worksheet"
            onClick={() => handleOpenWorksheetForSelectedRow()}
            disabled={!tableRows.length}
            title={
              worksheetLocked
                ? '이용권 구매 후 이용할 수 있습니다'
                : '선택한 행의 활동지를 모달에서 엽니다 (Shift+I와 동일). 한 행만 선택해야 합니다'
            }
          >
            <span className="cp-btn-label-desktop">활동지</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              활동지
            </span>
          </button>
          <button
            type="button"
            className={`cp-btn-copy-paste cp-btn-program-browse-nav${passLockClassOf(worksheetLocked)}`}
            onClick={() => {
              if (requireFeature('worksheet')) return;
              setProgramWorksheetListOpen(true);
            }}
            disabled={!tableRows.length}
            title={
              worksheetLocked
                ? '이용권 구매 후 이용할 수 있습니다'
                : '이 프로그램 표에 연결된 활동지 목록'
            }
          >
            <span className="cp-btn-label-desktop">활동지 목록</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              활동지 목록
            </span>
          </button>
          {(programDataCleanupGradeId ?? '').trim() ? (
            <button
              type="button"
              className="cp-btn-copy-paste cp-btn-program-browse-nav"
              onClick={() => setProgramDataCleanupOpen(true)}
              title="이 화면의 시간표 슬롯·프로그램 스코프에 해당하는 DB 행을 불러와 불필요한 행을 선택 삭제"
            >
              <span className="cp-btn-label-desktop">프로그램 데이터 정리</span>
              <span className="cp-btn-label-mobile-icon" aria-hidden>
                데이터 정리
              </span>
            </button>
          ) : null}
          </div>
        </div>
        <div className="cp-program-progress-toolbar-trailing cp-annual-term-title-inline-actions">
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="행 편집 도구">
          {mutationLocked ? null : (
            <button
              type="button"
              className="cp-program-cell-bg-clear cp-program-row-bg-clear-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => applyRowBgColor(null)}
              disabled={!canApplyRowBgColor}
              title={
                canApplyRowBgColor
                  ? `선택한 ${selectedAnnualKeys.length}개 행의 셀 배경색 지우기`
                  : '셀을 선택하면 해당 행의 배경색을 지울 수 있습니다'
              }
              aria-label="행 셀 배경색 지우기"
            />
          )}
          {mutationLocked ? null : (
            <button
              type="button"
              className="cp-program-row-bg-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!canApplyRowBgColor) return;
                setRowBgModalOpen(true);
              }}
              disabled={!canApplyRowBgColor}
              title={
                canApplyRowBgColor
                  ? `선택한 ${selectedAnnualKeys.length}개 행의 셀 배경색 지정`
                  : '셀을 선택하면 해당 행의 배경색을 지정할 수 있습니다'
              }
              aria-label="행 셀 배경색"
            >
              <span
                className={[
                  'cp-program-row-bg-btn-inner',
                  lastRowBgColor ? programCellBgSwatchProps(lastRowBgColor).className : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={lastRowBgColor ? programCellBgSwatchProps(lastRowBgColor).style : undefined}
                aria-hidden
              />
            </button>
          )}
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
            onClick={() => handleInsertContentRow()}
            disabled={
              mutationLocked ||
              slideEditStructureBlocked ||
              (tableRows.length > 0 && !selectedAnnualKeys.length) ||
              saveMode !== 'fixed_content' ||
              !onWorkspaceRowsChange
            }
            title={
              tableRows.length === 0
                ? '첫 수업(편집내용) 행 추가 — 연간 일정이 없어도 내용부터 입력할 수 있습니다'
                : '선택 행 아래에 수업(편집내용) 행 추가'
            }
          >
            <span className="cp-btn-label-desktop">수업 추가</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              +
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-plusminus"
            onClick={() => handleDeleteContentRows()}
            disabled={
              mutationLocked ||
              slideEditStructureBlocked ||
              !selectedAnnualKeys.length ||
              saveMode !== 'fixed_content' ||
              !onWorkspaceRowsChange
            }
            title="선택한 수업(편집내용) 행 삭제"
          >
            <span className="cp-btn-label-desktop">수업 삭제</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              -
            </span>
          </button>
          {mode !== 'fixed_to_unit' ? (
            <>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-swap-region"
                onClick={() => {
                  commitEditingNow();
                  handleMergeLessons();
                }}
                disabled={mutationLocked || slideEditStructureBlocked || !canMergeLessons}
                title="선택 행의 수업 내용을 규칙에 따라 통합"
              >
                <span className="cp-btn-label-desktop">수업 통합</span>
                <span className="cp-btn-label-mobile-icon" aria-hidden>
                  통합
                </span>
              </button>
              <button
                type="button"
                className="cp-btn-copy-paste cp-btn-swap-region"
                onClick={() => {
                  commitEditingNow();
                  handleDuplicateLesson();
                }}
                disabled={mutationLocked || slideEditStructureBlocked || !canDuplicateLesson}
                title="선택한 단일 행을 바로 아래에 복제 삽입(수업기록 제외)"
              >
                <span className="cp-btn-label-desktop">수업 증배</span>
                <span className="cp-btn-label-mobile-icon" aria-hidden>
                  증배
                </span>
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region"
            onClick={() => {
              commitEditingNow();
              moveSelectedRowsByOne('up');
            }}
            disabled={mutationLocked || slideEditStructureBlocked || !selectedRowIndexSet.size || !canMoveSelectedRowsUp}
            title="선택한 행의 편집 영역을 한 칸 위로 이동"
          >
            <span className="cp-btn-label-desktop">편집내용 올리기</span>
            <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
              <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                <path d="M6 2 2.5 5.5l1.2 1.2L5 5.4V12h2V5.4l1.3 1.3 1.2-1.2L6 2z" fill="currentColor" />
                <path
                  d="M14 12l3.5-3.5-1.2-1.2L15 8.6V2h-2v6.6l-1.3-1.3-1.2 1.2L14 12z"
                  fill="currentColor"
                  opacity="0.45"
                />
              </svg>
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region"
            onClick={() => {
              commitEditingNow();
              moveSelectedRowsByOne('down');
            }}
            disabled={mutationLocked || slideEditStructureBlocked || !selectedRowIndexSet.size || !canMoveSelectedRowsDown}
            title="선택한 행의 편집 영역을 한 칸 아래로 이동"
          >
            <span className="cp-btn-label-desktop">편집내용 내리기</span>
            <span className="cp-btn-label-mobile-icon cp-btn-mobile-shift-icon" aria-hidden>
              <svg viewBox="0 0 20 14" width="20" height="14" focusable="false">
                <path d="M6 12l3.5-3.5-1.2-1.2L7 8.6V2H5v6.6L3.7 7.3 2.5 8.5 6 12z" fill="currentColor" />
                <path
                  d="M14 2l-3.5 3.5 1.2 1.2L13 5.4V12h2V5.4l1.3 1.3 1.2-1.2L14 2z"
                  fill="currentColor"
                  opacity="0.45"
                />
              </svg>
            </span>
          </button>
        </div>
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="차시 자동 카운트">
          <button
            type="button"
            className="cp-btn-copy-paste cp-btn-swap-region cp-btn-period-count"
            onClick={openLessonCountModal}
            disabled={
              mutationLocked ||
              !tableRows.length ||
              (saveMode === 'fixed_content' && slideEditStructureBlocked)
            }
            title="대단원·소단원 구분 기호를 지정한 뒤 해당차시·전체차시를 자동 번호 매김"
          >
            <span className="cp-btn-label-desktop">차시 자동 카운트</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              차시 카운트
            </span>
          </button>
        </div>
        <div className="cp-program-progress-toolbar-group" role="group" aria-label="클립보드·삭제">
          <button
            type="button"
            className="cp-btn-copy-paste"
            onClick={() => handleCopy()}
            disabled={slideEditStructureBlocked || !selectedCells.length}
            title="복사 (Ctrl/Cmd + C)"
          >
            <span className="cp-btn-label-desktop">복사</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/copy2.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            onClick={() => handleCut()}
            disabled={slideEditStructureBlocked || !canMutateSelectedCells}
            title="잘라내기 (Ctrl/Cmd + X)"
          >
            <span className="cp-btn-label-desktop">잘라내기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/cut.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            onClick={() => void handlePasteFromClipboard()}
            disabled={slideEditStructureBlocked || !canMutateSelectedCells}
            title="엑셀에서 복사한 표(탭·줄) 또는 앱에서 복사한 영역 붙여넣기 (Ctrl/Cmd + V)"
          >
            <span className="cp-btn-label-desktop">붙여넣기</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/paste.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
          <button
            type="button"
            className="cp-btn-copy-paste"
            onClick={() => handleDeleteSelected()}
            disabled={slideEditStructureBlocked || !canMutateSelectedCells}
            title="선택 영역 데이터 삭제"
          >
            <span className="cp-btn-label-desktop">데이터 삭제</span>
            <span className="cp-btn-label-mobile-icon" aria-hidden>
              <img src="/del.svg" alt="" className="cp-btn-mobile-action-svg" />
            </span>
          </button>
        </div>
        </div>
      </div>
    <div
      ref={programProgressScrollRef}
      tabIndex={-1}
      className="cp-annual-progress-table-wrap cp-annual-progress-table-wrap--fluid cp-program-progress-scroll-wrap"
    >
    <table className={`cp-annual-progress-table ${tableModeClass}`.trim()}>
      <colgroup>
        <col className="cp-annual-progress-col-seq" />
        <col className="cp-annual-progress-col-date" />
        <col className="cp-annual-progress-col-weekday" />
        <col className="cp-annual-progress-col-period" />
        <col className="cp-annual-progress-col-grade" />
        <col className="cp-annual-progress-col-semester" />
        <col className="cp-annual-progress-col-template" />
        <col className="cp-annual-progress-col-program" />
        <col className="cp-annual-progress-col-timetable" />
        <col className="cp-annual-progress-col-unit" />
        <col className="cp-annual-progress-col-learning" />
        <col className="cp-annual-progress-col-metric" />
        <col className="cp-annual-progress-col-metric" />
        <col className="cp-annual-progress-col-metric-period" />
        <col className="cp-annual-progress-col-metric-period" />
        <col className="cp-annual-progress-col-metric" />
        <col className="cp-annual-progress-col-record" />
        <col className="cp-annual-progress-col-record" />
        <col className="cp-annual-progress-col-record" />
        <col className="cp-annual-progress-col-suhub-girog" />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={topGroups.baseColSpan} className="cp-annual-progress-head-group cp-annual-progress-head-group-base">
            프로그램 일정
          </th>
          <th colSpan={topGroups.learningColSpan} className="cp-annual-progress-head-group cp-annual-progress-head-group-learning">
            프로그램 내용(편집)
          </th>
          <th colSpan={topGroups.recordColSpan} className="cp-annual-progress-head-group cp-annual-progress-head-group-record">
            메모 및 기록(편집)
          </th>
        </tr>
        <tr>
          <th className="cp-annual-progress-col-seq">순</th>
          <th className="cp-annual-progress-col-date">날짜</th>
          <th className="cp-annual-progress-col-weekday">요일</th>
          <th className="cp-annual-progress-col-period">교시</th>
          <th className="cp-annual-progress-col-grade">학년</th>
          <th className="cp-annual-progress-col-semester">학기</th>
          <th className="cp-annual-progress-col-template">편제</th>
          <th className="cp-annual-progress-col-program">구분</th>
          <th className="cp-annual-progress-col-timetable">
            {mode === 'project_to_template' ? (
              <button
                type="button"
                className="cp-program-progress-record-head-btn"
                onClick={openProjectTimetableRenameModal}
                disabled={readOnly || projectTimetableSyncBusy != null}
                title="시간표 이름 바꾸기"
              >
                시간표
              </button>
            ) : (
              '시간표'
            )}
          </th>
          <th className="cp-annual-progress-col-unit">단원</th>
          <th className="cp-annual-progress-col-learning cp-program-progress-metric-head-th">
            <button
              type="button"
              className="cp-program-progress-record-head-btn"
              onClick={openLearningSessionModal}
              disabled={readOnly}
              title="학습내용 연차시 표시 조정"
            >
              학습내용
            </button>
          </th>
          <th className="cp-annual-progress-col-metric cp-program-progress-metric-head-th">
            <button type="button" className="cp-program-progress-record-head-btn" onClick={openPageAssistantSplitModal}>
              쪽수
            </button>
          </th>
          <th className="cp-annual-progress-col-metric">보조쪽수</th>
          <th className="cp-annual-progress-col-metric-period">
            해당
            <br />
            차시
          </th>
          <th className="cp-annual-progress-col-metric-period">
            전체
            <br />
            차시
          </th>
          <th className="cp-annual-progress-col-metric">준비물</th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button
              type="button"
              className="cp-program-progress-record-head-btn"
              onClick={(e) => onRecordHeadButtonClick('record_cross_curricular_text', e)}
            >
              범교과
            </button>
          </th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button
              type="button"
              className="cp-program-progress-record-head-btn"
              onClick={(e) => onRecordHeadButtonClick('record_memo_text', e)}
            >
              메모
            </button>
          </th>
          <th className="cp-annual-progress-col-record cp-annual-progress-col-record-fill cp-program-progress-record-head-th">
            <button
              type="button"
              className="cp-program-progress-record-head-btn"
              onClick={(e) => onRecordHeadButtonClick('record_evaluation_text', e)}
            >
              평가
            </button>
          </th>
          <th className={`cp-annual-progress-col-suhub-girog cp-annual-progress-col-record-fill cp-program-progress-record-head-th${passLockClassOf(classRecordLocked)}`}>
            <button
              type="button"
              className={`cp-program-progress-record-head-btn${passLockClassOf(classRecordLocked)}`}
              onClick={(e) => onRecordHeadButtonClick('record_class_text', e)}
              title={classRecordLocked ? '이용권 구매 후 이용할 수 있습니다' : undefined}
            >
              수업기록
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        {tableRows.map((row, rowIndex) => {
          const scheduleRowWash = isScheduleRowContextWash(row.annualCellKey);
          const scheduleRowWashClass = scheduleRowWash ? ' cp-program-progress-row-context' : '';
          const renderScheduleWash = () =>
            scheduleRowWash ? <span className="cp-annual-timetable-week-context-wash" aria-hidden /> : null;
          return (
          <tr key={`${row.annualCellKey}-${row.seq}-${row.periodLabel}`} className={row.isScheduleAvailable === false ? 'cp-program-progress-row-inactive' : ''}>
            <td className={`cp-annual-progress-col-seq${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {rowIndex + 1}
            </td>
            <td className={`cp-annual-progress-col-date${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.dateLabel}
            </td>
            <td className={`cp-annual-progress-col-weekday${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.weekdayLabel}
            </td>
            <td
              className={
                'cp-annual-progress-col-period' +
                (row.periodLabel.length > 3 ? ' cp-annual-progress-col-period-shrink' : '') +
                scheduleRowWashClass
              }
            >
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.periodLabel}
            </td>
            <td className={`cp-annual-progress-col-grade${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.gradeLabel}
            </td>
            <td className={`cp-annual-progress-col-semester${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.semesterLabel}
            </td>
            <td
              className={
                'cp-annual-progress-col-template' +
                (row.templateLabel.length > 2 ? ' cp-annual-progress-col-template-shrink' : '') +
                scheduleRowWashClass
              }
            >
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : row.templateLabel}
            </td>
            <td className={`cp-annual-progress-col-program${scheduleRowWashClass}`}>
              {renderScheduleWash()}
              {row.isScheduleAvailable === false ? '' : mode === 'project_to_template' ? '프로' : row.programKindLabel}
            </td>
            {renderTimetableCell(row)}
            {renderEditableCell(row, 'unit_text', 'cp-annual-progress-col-unit')}
            {renderEditableCell(row, 'learning_text', 'cp-annual-progress-col-learning')}
            {renderEditableCell(row, 'page_text', 'cp-annual-progress-col-metric')}
            {renderEditableCell(row, 'assistant_page_text', 'cp-annual-progress-col-metric')}
            {renderEditableCell(row, 'current_period_text', 'cp-annual-progress-col-metric-period')}
            {renderEditableCell(row, 'total_period_text', 'cp-annual-progress-col-metric-period')}
            {renderEditableCell(row, 'material_text', 'cp-annual-progress-col-metric')}
            {renderEditableCell(row, 'record_cross_curricular_text', 'cp-annual-progress-col-record', '', true)}
            {renderEditableCell(row, 'record_memo_text', 'cp-annual-progress-col-record', row.memo, true)}
            {renderEditableCell(row, 'record_evaluation_text', 'cp-annual-progress-col-record', '', true)}
            {renderEditableCell(row, 'record_class_text', 'cp-annual-progress-col-suhub-girog', '', true)}
          </tr>
          );
        })}
      </tbody>
    </table>
    </div>
    {hoverTableTooltip
      ? createPortal(
          <div
            className={[
              'cp-program-table-hover-memo-tooltip',
              'cp-annual-cell-memo-tooltip',
              hoverTableTooltip.kind === 'note'
                ? 'cp-program-note-tooltip'
                : hoverTableTooltip.kind === 'unit'
                  ? 'cp-program-unit-tooltip'
                  : hoverTableTooltip.kind === 'material'
                    ? 'cp-program-material-tooltip'
                    : 'cp-program-cross-tooltip',
            ].join(' ')}
            style={hoverTableTooltip.style}
            role="note"
            onMouseEnter={
              hoverTableTooltip.kind === 'note' &&
              !hoverTableTooltip.isClassRecord &&
              learningPlanGuideHasPlanMarkers(hoverTableTooltip.note)
                ? () => clearHoverTableTooltipHideTimer()
                : undefined
            }
            onMouseLeave={
              hoverTableTooltip.kind === 'note' &&
              !hoverTableTooltip.isClassRecord &&
              learningPlanGuideHasPlanMarkers(hoverTableTooltip.note)
                ? () => scheduleHoverTableTooltipHide(hoverTableTooltip.anchorKey)
                : undefined
            }
          >
            {hoverTableTooltip.kind === 'note' &&
            !hoverTableTooltip.isClassRecord &&
            learningPlanGuideHasPlanMarkers(hoverTableTooltip.note) ? (
              <LearningPlanGuideView
                guide={hoverTableTooltip.note}
                className="cp-program-hover-learning-guide"
                ariaLabel="학습계획"
              />
            ) : hoverTableTooltip.kind === 'note' && hoverTableTooltip.isClassRecord ? (
              renderNoteWithMemberTags(
                hoverTableTooltip.note,
                hoverTableTooltip.memberTagMembers ?? classMembers,
                {
                  ...(hoverTableTooltip.applyBulkMemberTagRules !== undefined
                    ? { applyBulkMemberTagRules: hoverTableTooltip.applyBulkMemberTagRules }
                    : {}),
                  sealedLabelByToken,
                }
              )
            ) : (
              hoverTableTooltip.note
            )}
          </div>,
          document.body
        )
      : null}
    {pasteColumnMismatchToast ||
    mergeTimetableMismatchToast ||
    projectTimetableSyncToast ||
    learningSlideDeleteToast ? (
      <div className="cp-toast-auto" role="status" aria-live="polite">
        <div className="cp-toast-auto-box cp-program-paste-column-toast">
          {projectTimetableSyncToast ??
            mergeTimetableMismatchToast ??
            pasteColumnMismatchToast ??
            learningSlideDeleteToast}
        </div>
      </div>
    ) : null}
    {projectTimetableRenameModalOpen ? (
      <ProgramProjectTimetableRenameModal
        open={projectTimetableRenameModalOpen}
        initialNames={projectTimetableRenameNames}
        onClose={() => setProjectTimetableRenameModalOpen(false)}
        onConfirm={handleConfirmProjectTimetableRename}
      />
    ) : null}
    {slideEditStructureBlockedModalOpen
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) setSlideEditStructureBlockedModalOpen(false);
            }}
          >
            <div
              className="cp-modal cp-program-page-split-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-slide-edit-structure-blocked-title"
              onMouseDown={(ev) => ev.stopPropagation()}
            >
              <h3 id="cp-slide-edit-structure-blocked-title">프레젠테이션 편집 안내</h3>
              <div className="cp-modal-body">
                <p className="page-desc">
                  슬라이드 프레젠테이션 탭이 열려있습니다. 프레젠테이션 탭을 닫으면 편집이 가능합니다.
                </p>
              </div>
              <div className="cp-modal-footer">
                <button
                  type="button"
                  className="cp-btn-secondary"
                  onClick={() => setSlideEditStructureBlockedModalOpen(false)}
                >
                  확인
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    {rowBgModalOpen ? (
      <ProgramCellBgColorModal
        targetRowCount={selectedAnnualKeys.length}
        registeredColors={rowBgPalette.colors}
        paletteBusy={rowBgPalette.busy}
        onApply={applyRowBgColor}
        onRegisterColor={(hex) => void rowBgPalette.addColor(hex).catch(() => undefined)}
        onReplaceColor={(from, to) => void rowBgPalette.replaceColor(from, to).catch(() => undefined)}
        onRemoveColor={(hex) => void rowBgPalette.removeColor(hex).catch(() => undefined)}
        onClose={() => setRowBgModalOpen(false)}
      />
    ) : null}
    {(programDataCleanupGradeId ?? '').trim() ? (
      <ProgramDataCleanupModal
        open={programDataCleanupOpen}
        onClose={() => setProgramDataCleanupOpen(false)}
        workspaceId={workspaceId}
        academicYear={academicYear}
        termNumber={termNumber}
        gradeId={(programDataCleanupGradeId ?? '').trim()}
        scheduleSlotKeys={programDataCleanupScheduleSlotKeys}
        scopeType={programDataCleanupScopeType}
        scopeKey={programDataCleanupScopeKey}
        includeFixedRows={programDataCleanupIncludeFixedRows}
        onCleanupComplete={() => {
          onProgramDataCleanupComplete?.();
        }}
      />
    ) : null}
    <ProgramPresentationListModal
      open={programPresentationListOpen}
      onClose={() => setProgramPresentationListOpen(false)}
      workspaceId={workspaceId}
      items={presentationListItemsMerged}
      onOpenItem={(annualKey) => openProgramContentSlideForAnnualKey(annualKey)}
      title="프로그램 프레젠테이션 목록"
      getActionState={(annualKey) => {
        const state = resolveLearningPresentationActionState(annualKey);
        return {
          canCopy: !mutationLocked && !!state.copySourceOwner,
          canDelete: state.canDeletePresentation,
        };
      }}
      onCopy={(annualKey) => copyLearningPresentationAtAnnualKey(annualKey)}
      onDelete={(annualKey) => deleteSlidesForLearningAtAnnualKey(annualKey)}
      actionsDisabled={mutationLocked}
      onNotify={setLearningSlideDeleteToast}
    />
    <ProgramWorksheetListModal
      open={programWorksheetListOpen}
      onClose={() => setProgramWorksheetListOpen(false)}
      workspaceId={workspaceId}
      items={worksheetListItems}
      onOpenItem={(annualKey) => openWorksheetEditorForAnnualKey(annualKey)}
      title="프로그램 활동지 목록"
      getActionState={(annualKey) => {
        const state = resolveLearningWorksheetActionState(annualKey);
        return {
          canCopy: !mutationLocked && !!(state.copySourceRefId || state.copyDraftRowKey),
          canDelete: state.canDelete,
        };
      }}
      onCopy={(annualKey) => copyLearningWorksheetAtAnnualKey(annualKey)}
      onDelete={(annualKey) => deleteWorksheetForLearningAtAnnualKey(annualKey)}
      actionsDisabled={mutationLocked}
      onNotify={setLearningSlideDeleteToast}
    />
    <EvaluationPlanAppliedPickerModal
      open={evalPlanPickerOpen}
      workspaceId={workspaceId}
      gradeId={progressGradeId}
      onClose={() => setEvalPlanPickerOpen(false)}
      onPick={(snap) => {
        setEvalPlanPickerOpen(false);
        const popup = memoFieldPopupStateRef.current;
        if (!popup || popup.fieldId !== 'record_evaluation_text' || mutationLocked) return;
        setMemoFieldPopup({
          ...popup,
          evaluationPlanAppliedId: snap.id,
          evaluationPlanSummary: formatPlanSummary(snap.plan),
          evaluationPlanElement: (snap.plan.evaluation_element_text ?? '').trim() || null,
          evaluationPlanMethod: (snap.plan.evaluation_method_text ?? '').trim() || null,
        });
      }}
    />
    <AssignmentMemoLinkPickerModal
      open={assignmentMemoLinkPickerOpen}
      workspaceId={workspaceId}
      gradeId={progressGradeId}
      membersById={assignmentMemoMembersById}
      memberLayout={memberGradeLayout}
      doc={assignmentPointsDocForLink}
      initialGroupId={
        memoFieldPopup?.fieldId === 'record_class_text'
          ? memoFieldPopup.text === 'eval_log'
            ? memoFieldPopup.evalMemoMemberGroupId ?? memoFieldPopup.recordMemberGroupId
            : memoFieldPopup.recordMemberGroupId
          : null
      }
      onClose={() => setAssignmentMemoLinkPickerOpen(false)}
      onPick={(candidate) => {
        setAssignmentMemoLinkPickerOpen(false);
        if (mutationLocked) return;
        const popup = memoFieldPopupStateRef.current;
        if (!popup || popup.fieldId !== 'record_class_text') return;
        const next = {
          ...popup,
          assignmentMemoItemId: candidate.itemId,
          assignmentMemoItemTitle: candidate.title,
          assignmentMemoGroupId: candidate.groupId,
          assignmentMemoGroupTitle: candidate.groupLabel || null,
          recordMemberGroupId: candidate.groupId ?? popup.recordMemberGroupId ?? null,
          evalMemoMemberGroupId:
            popup.text === 'eval_log'
              ? candidate.groupId ?? popup.evalMemoMemberGroupId ?? null
              : popup.evalMemoMemberGroupId ?? null,
        };
        setMemoFieldPopup(next);
        commitMemoFieldPopupNow(next);
        const snap = memoFieldPopupOpenSnapshotRef.current;
        if (snap && snap.fieldId === 'record_class_text') {
          memoFieldPopupOpenSnapshotRef.current = {
            ...snap,
            assignmentMemoItemId: candidate.itemId,
            assignmentMemoItemTitle: candidate.title,
            assignmentMemoGroupId: candidate.groupId,
            assignmentMemoGroupTitle: candidate.groupLabel || null,
            recordMemberGroupId: next.recordMemberGroupId,
            evalMemoMemberGroupId: next.evalMemoMemberGroupId,
          };
        }
      }}
    />
    <CrossCurricularCatalogPickerModal
      open={crossCatalogPickerOpen}
      onClose={() => setCrossCatalogPickerOpen(false)}
      onApply={(pick) => {
        setCrossCatalogPickerOpen(false);
        if (mutationLocked) return;
        if (!crossPopup) return;
        const next = {
          ...crossPopup,
          text: pick.topicName,
          note: pick.body,
        };
        setCrossPopup(next);
        commitCrossPopupNow(next);
      }}
    />
    <CrossTopicCountFloatingModal
      open={crossTopicCountFloatOpen}
      onClose={() => setCrossTopicCountFloatOpen(false)}
      workspaceId={workspaceId}
      grades={grades}
      academicYear={academicYear}
      programGradeId={progressGradeId}
      currentTermNumber={termNumber}
      currentTermDraftEntries={currentTermDraftEntries}
      currentTermIsSyncMirror={syncMirrorView}
      syncMirrorRepAnnualCellKeyByMirrorKey={syncMirrorRepAnnualCellKeyByMirrorKey}
      syncMirrorMirrorAnnualCellKeysByRepKey={syncMirrorMirrorAnnualCellKeysByRepKey}
    />
    <EvaluationPlanFormModal
      open={!!evalPlanViewSnap}
      mode="edit"
      readOnly
      categoryName={
        (evalPlanViewSnap?.source_category_name || evalPlanViewSnap?.plan.category_name || '평가계획').trim() ||
        '평가계획'
      }
      standards={evalPlanViewSnap?.standards ?? []}
      initial={evalPlanViewSnap?.plan ?? null}
      initialStandardIds={evalPlanViewSnap?.plan.standard_ids ?? []}
      sortOrder={evalPlanViewSnap?.sort_order ?? 0}
      defaultLevelStepCount={evalPlanViewSnap?.plan.level_step_count ?? DEFAULT_LEVEL_STEP_COUNT}
      workspaceId={workspaceId}
      imageScope="personal"
      onClose={() => setEvalPlanViewSnap(null)}
      onSubmit={() => {
        /* read-only */
      }}
    />
    <LearningContentWorksheetEditorModal
      key={worksheetEditorAnnualKey ?? 'worksheet-editor-closed'}
      open={worksheetEditorOpen}
      draftStorageKey={
        worksheetEditorAnnualKey
          ? resolveWorksheetDraftStorageRowKey(
              worksheetEditorAnnualKey,
              syncMirrorView,
              syncMirrorRepAnnualCellKeyByMirrorKey
            )
          : ''
      }
      workspaceId={workspaceId}
      initialDocument={worksheetEditorInitialDocument}
      initialDirty={worksheetEditorInitialDirty}
      onClose={() => {
        setWorksheetEditorOpen(false);
        setWorksheetEditorAnnualKey(null);
        setWorksheetEditorInitialDocument(null);
        setWorksheetEditorInitialDirty(false);
        setWorksheetAiRequest(null);
      }}
      onSave={handleSaveWorksheetDocument}
      onRegenerateAi={
        worksheetAiRequest && worksheetAiRequest.annualKey === worksheetEditorAnnualKey
          ? handleRegenerateWorksheetAi
          : undefined
      }
    />
    {classRecordTagGroupPick
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) setClassRecordTagGroupPick(null);
            }}
          >
            <div
              className="cp-modal"
              role="dialog"
              aria-modal="true"
              onMouseDown={(ev) => ev.stopPropagation()}
            >
              <h3>멤버 그룹 선택</h3>
              <p className="page-desc">
                &quot;{classRecordTagGroupPick.keyword}&quot;에 해당하는 학생이 여러 그룹에 있습니다. 기록에 사용할
                학생을 선택해 주세요.
              </p>
              <div className="cp-modal-body cp-class-rec-tag-group-pick-list">
                {classRecordTagGroupPick.candidates.map((c) => (
                  <button
                    key={`${c.groupId}-${c.memberId}`}
                    type="button"
                    className="cp-btn-secondary cp-class-rec-tag-group-pick-item"
                    onClick={() => {
                      const gid = c.groupId;
                      setMemoFieldPopup((prev) =>
                        prev && prev.fieldId === 'record_class_text'
                          ? prev.text === 'eval_log'
                            ? { ...prev, recordMemberGroupId: gid, evalMemoMemberGroupId: gid }
                            : { ...prev, recordMemberGroupId: gid }
                          : prev
                      );
                      setClassRecordTagGroupPick(null);
                    }}
                  >
                    {c.groupName} · {c.memberName}
                  </button>
                ))}
              </div>
              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-secondary" onClick={() => setClassRecordTagGroupPick(null)}>
                  취소
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    {evalTagGenConfirm
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) setEvalTagGenConfirm(null);
            }}
          >
            <div
              className="cp-modal"
              role="dialog"
              aria-modal="true"
              onMouseDown={(ev) => ev.stopPropagation()}
            >
              <h3>멤버 태그 생성</h3>
              <p className="page-desc">
                태그(#이름)가 아닌 입력 내용은 모두 초기화되고, 선택한 그룹의 멤버 태그만 남습니다. 계속할까요?
              </p>
              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-secondary" onClick={() => setEvalTagGenConfirm(null)}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  onClick={() => {
                    const next = evalTagGenConfirm.nextNote;
                    const sourceGroupId = evalTagGenConfirm.sourceGroupId;
                    setMemoFieldPopup((prev) => {
                      if (!prev || prev.fieldId !== 'record_class_text') return prev;
                      if (prev.text === 'eval_log') {
                        return { ...prev, noteEval: next, evalMemoMemberGroupId: sourceGroupId };
                      }
                      return { ...prev, noteLesson: next, recordMemberGroupId: sourceGroupId };
                    });
                    setEvalTagGenConfirm(null);
                  }}
                >
                  초기화 후 생성
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    {learningBackspaceDeleteConfirmOpen
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) cancelLearningBackspaceDeleteConfirm();
            }}
          >
            <div
              className="cp-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-program-learning-backspace-delete-title"
              onMouseDown={(ev) => ev.stopPropagation()}
            >
              <h3 id="cp-program-learning-backspace-delete-title">삭제 확인</h3>
              <div className="cp-modal-body">
                <p>
                  선택 영역에 프레젠테이션이 연결된 학습내용이 포함되어 있습니다. 삭제를 누르면 해당 셀 값과
                  프레젠테이션(슬라이드) 데이터가 함께 제거됩니다.
                </p>
              </div>
              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-secondary" onClick={cancelLearningBackspaceDeleteConfirm}>
                  취소
                </button>
                <button type="button" className="cp-btn-primary" onClick={confirmLearningBackspaceDelete}>
                  삭제
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    <UnitLessonCountDelimiterModal
      open={lessonCountModalOpen}
      delimiter={lessonCountDelimiterInput}
      onDelimiterChange={setLessonCountDelimiterInput}
      onCancel={() => setLessonCountModalOpen(false)}
      onConfirm={() => handleAutoPeriodCount(lessonCountDelimiterInput)}
      inputRef={lessonCountDelimiterInputRef}
    />
    {learningSessionPlan
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) setLearningSessionPlan(null);
            }}
          >
            <div
              className="cp-modal cp-program-page-split-modal cp-program-learning-session-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-program-learning-session-title"
              onMouseDown={(ev) => ev.stopPropagation()}
              onKeyDown={(ev) => {
                if (ev.key === 'Escape') {
                  ev.preventDefault();
                  setLearningSessionPlan(null);
                }
              }}
            >
              <h3 id="cp-program-learning-session-title">학습내용 연차시 표시 조정</h3>
              <p className="cp-program-page-split-desc">
                연속된 학습내용의 연차시 표시를 자동 조정합니다.
              </p>
              <div className="cp-modal-body">
                {learningSessionPlan.length === 0 ? (
                  <p className="cp-program-learning-session-empty">조정할 학습내용이 없습니다.</p>
                ) : (
                  <ul className="cp-program-learning-session-list">
                    {learningSessionPlan.map((p) => (
                      <li key={p.annualKey}>
                        <span className="cp-program-learning-session-before">{p.before.trim() || '(빈 칸)'}</span>
                        <span className="cp-program-learning-session-arrow" aria-hidden>
                          →
                        </span>
                        <span className="cp-program-learning-session-after">{p.after}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-secondary" onClick={() => setLearningSessionPlan(null)}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  autoFocus
                  onClick={commitLearningSessionPlan}
                >
                  확인
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    {pageSplitModalOpen
      ? createPortal(
          <div
            className="cp-modal-backdrop"
            role="presentation"
            onMouseDown={(ev) => {
              if (ev.target === ev.currentTarget) setPageSplitModalOpen(false);
            }}
          >
            <div
              className="cp-modal cp-program-page-split-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cp-program-page-split-title"
              onMouseDown={(ev) => ev.stopPropagation()}
            >
              <h3 id="cp-program-page-split-title">쪽수 - 보조쪽수 분리</h3>
              <p className="cp-program-page-split-desc">
                구분 기호를 시작으로 보조쪽수로 분리하여 표시합니다.
                <br />
                예: 28-29(30-32) 인 경우 구분 기호 ( 를 넣으면 → 쪽수 28-29, 보조쪽수 (30-32)로 분리됩니다.
              </p>
              <div className="cp-modal-body">
                <label htmlFor="cp-program-page-split-delimiter" className="cp-program-page-split-field-label">
                  구분 기호
                </label>
                <input
                  ref={pageSplitDelimiterInputRef}
                  id="cp-program-page-split-delimiter"
                  type="text"
                  className="cp-program-page-split-delimiter-input"
                  value={pageSplitDelimiterInput}
                  onChange={(ev) => setPageSplitDelimiterInput(ev.target.value)}
                  onKeyDown={(ev) => {
                    const native = ev.nativeEvent as KeyboardEvent;
                    if (native.isComposing) return;
                    if (ev.key === 'Escape') {
                      ev.preventDefault();
                      setPageSplitModalOpen(false);
                      return;
                    }
                    if (ev.key !== 'Enter') return;
                    ev.preventDefault();
                    if (!pageSplitDelimiterInput.length) return;
                    commitPageAssistantSplit();
                  }}
                  placeholder="예: ( 또는 공백"
                  autoComplete="off"
                />
              </div>
              <div className="cp-modal-footer">
                <button type="button" className="cp-btn-secondary" onClick={() => setPageSplitModalOpen(false)}>
                  취소
                </button>
                <button
                  type="button"
                  className="cp-btn-primary"
                  disabled={!pageSplitDelimiterInput.length}
                  onClick={() => commitPageAssistantSplit()}
                >
                  분리하기
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null}
    </div>
  );
};

export default ProgramProgressTable;
